// ╔══════════════════════════════════════════════════════════════════════╗
// ║  QUALITY MANAGEMENT MODULE — COMPLETE SYSTEM                        ║
// ║  Part 1 of N  →  BASE + APP SHELL + DASHBOARD                      ║
// ║  Paste each department block BELOW the export default line          ║
// ╚══════════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, ReferenceLine,
} from 'recharts';

// ─────────────────────────────────────────────────────────────
// REGISTRY — each dept block below will populate this
// ─────────────────────────────────────────────────────────────
const DEPT_REGISTRY = {};

// ─────────────────────────────────────────────────────────────
// GLOBAL STYLES
// ─────────────────────────────────────────────────────────────
const _css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
  html,body,#root{height:100%;font-family:'Inter','Inter',system-ui,sans-serif}
  ::-webkit-scrollbar{width:4px;height:4px}
  ::-webkit-scrollbar-track{background:#F4F6F8}
  ::-webkit-scrollbar-thumb{background:#ADB5BD;border-radius:4px}
  input,select,textarea,button{font-family:inherit}
  input[type=date]::-webkit-calendar-picker-indicator{opacity:.5;cursor:pointer}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideRight{from{opacity:0;transform:translateX(-12px)}to{opacity:1;transform:translateX(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
  .q-fadein{animation:fadeUp .25s ease-out both}
  .q-slide{animation:slideRight .2s ease-out both}
  .q-pulse{animation:pulse 2s ease-in-out infinite}
  @media print{.no-print{display:none!important}.print-only{display:block!important}}
  .print-only{display:none}
`;

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────
const S = {
  // Core palette
  navy:     '#0D1B2A', navyMd: '#0D1B2A', navyLt: '#1A2A3A',
  blue:     '#1565C0', blueDk: '#0A5AD4', blueLt: '#E3F2FD',
  teal:     '#00897B', tealBg: '#E0F2F1',
  // Status
  green:    '#2E7D32', greenBg: '#E8F5E9', greenBd: '#A5D6A7',
  red:      '#C62828', redBg:   '#FFEBEE', redBd:   '#FFCDD2',
  amber:    '#E65100', amberBg: '#FFF8E1', amberBd: '#FFE082',
  purple:   '#6A1B9A', purpleBg:'#EDE7F6',
  // Neutral
  white:    '#FFFFFF', slate50:  '#F8F9FA', slate100: '#F4F6F8',
  slate200: '#DEE2E6', slate300: '#CED4DA', slate400: '#ADB5BD',
  slate500: '#6C757D', slate600: '#495057', slate700: '#2C3E50',
  slate800: '#1A2A3A', slate900: '#0D1B2A',
  // Dept accent colors
  dSampling:    '#7B1FA2',
  dWarehouse:   '#00897B',
  dCutting:     '#C62828',
  dSewing:      '#2E7D32',
  dWashing:     '#1565C0',
  dWF:          '#6A1B9A',
  dFinishing:   '#E65100',
  dMending:     '#DB2777',
  dKilled:      '#9F1239',
  dProcess:     '#0F766E',
};

const r = { sm:'4px', md:'6px', lg:'8px', xl:'12px' };

// ─────────────────────────────────────────────────────────────
// CONSTANTS / MASTER DATA
// ─────────────────────────────────────────────────────────────
const API     = 'https://nirvikalp.integrashion.com/api/save_universal.php';
const QC_GET  = 'https://nirvikalp.integrashion.com/api/get_all_data.php';
const TODAY   = new Date().toISOString().split('T')[0];

// ── Fallback static lists (used when DB not yet loaded) ────────────────────
const _FB_BRANDS   = ['ZARA','H&M','GAP','NEXT','PRIMARK','WALMART','TARGET','TOMMY HILFIGER','CALVIN KLEIN','RALPH LAUREN',"DILLARD'S",'OTHER'];
const _FB_BUYERS   = ['UK BUYER','EU BUYER','US BUYER','AUSTRALIA','CANADA','DOMESTIC','OTHER'];
const _FB_FABRICS  = ['100% COTTON','POLYESTER BLEND','COTTON POLY','DENIM','KNIT','WOVEN','LINEN','VISCOSE','SPANDEX','NYLON','RAYON','WOOL','TENCEL','BAMBOO'];
const _FB_PRODUCTS = ['T-SHIRT','POLO SHIRT','FORMAL SHIRT','TROUSER','JACKET','BLAZER','DRESS','SKIRT','SHORTS','HOODIE','JOGGER','DENIM JEANS','CARDIGAN','SWEATSHIRT','OTHER'];
const _FB_COLOURS  = ['WHITE','BLACK','NAVY','RED','BLUE','GREEN','GREY','BEIGE','YELLOW','PINK','PURPLE','BROWN','KHAKI','MAROON','ORANGE','OTHER'];
const _FB_LINES    = ['LINE-1','LINE-2','LINE-3','LINE-4','LINE-5','LINE-6','FINISHING','PACKING','WAREHOUSE'];
const _FB_SHIFTS   = ['DAY','NIGHT','GENERAL'];
const _FB_UNITS    = ['UNIT-1','UNIT-2','UNIT-3'];

// ── Shared DB cache — populated by syncDbCache() in the main component ───────
const _QM_CACHE = { brands:[], buyers:[], fabrics:[], products:[], colours:[], seasons:[], styles:[], lines:[], trimTypes:[], poNumbers:[] };

function syncDbCache(db) {
  if (!db) return;
  _QM_CACHE.brands    = (db.brands   ||[]).map(b=>b.name||b).filter(Boolean);
  _QM_CACHE.buyers    = (db.buyers   ||[]).map(b=>b.name||b).filter(Boolean);
  _QM_CACHE.fabrics   = (db.fabrics  ||db.fabricDifficulties||[]).map(f=>f.name||f.fabric||f).filter(Boolean);
  _QM_CACHE.products  = (db.products ||[]).map(p=>p.name||p).filter(Boolean);
  _QM_CACHE.seasons   = (db.seasons  ||[]).map(s=>s.name||s).filter(Boolean);
  _QM_CACHE.styles    = [...new Set((db.mgmtStyles||[]).map(s=>s.styleRef||s.name||'').filter(Boolean))].sort();
  _QM_CACHE.lines     = (db.factories||[]).map(f=>f.name||f).filter(Boolean).length > 0
                        ? (db.factories||[]).map(f=>f.name||f).filter(Boolean)
                        : _FB_LINES;
  _QM_CACHE.trimTypes = (db.trimTypes||[]).map(t=>t.name||t).filter(Boolean);
  _QM_CACHE.colours   = (db.colours  ||[]).map(c=>c.name||c).filter(Boolean).length > 0
                        ? (db.colours||[]).map(c=>c.name||c).filter(Boolean)
                        : _FB_COLOURS;

  // ── PO Size Table — source of truth for PO#, colours, sizes, ex-fac dates ──
  const poRows = db.poSizeTable || [];

  // PO numbers — use top-level array if available (pre-built by get_all_data.php),
  // otherwise scan poSizeTable rows
  _QM_CACHE.poNumbers  = (db.poNumbers && db.poNumbers.length > 0)
    ? [...db.poNumbers].sort()
    : [...new Set(poRows.map(r => r.po_no || r.poNo || '').filter(Boolean))].sort();

  // Sizes — use top-level array if available
  const poSizesArr = (db.poSizes && db.poSizes.length > 0)
    ? db.poSizes
    : [...new Set(poRows.map(r => r.size || '').filter(Boolean))];
  _QM_CACHE.sizes = poSizesArr.length > 0
    ? poSizesArr
    : ['XS','S','M','L','XL','XXL','3XL','28','30','32','34','36','38','40'];

  // Ex-factory dates
  _QM_CACHE.exFacDates = (db.poExFacDates && db.poExFacDates.length > 0)
    ? [...db.poExFacDates].sort()
    : [...new Set(poRows.map(r => r.ex_fty_date || '').filter(Boolean))].sort();

  // Colours from PO data (merge with master colours)
  const poColours = (db.poColours && db.poColours.length > 0)
    ? db.poColours
    : [...new Set(poRows.map(r => r.color || r.colour || '').filter(Boolean))];
  if (poColours.length > 0) _QM_CACHE.colours = [...new Set([..._QM_CACHE.colours, ...poColours])];
}

function dbGet(key, fallback) {
  const val = _QM_CACHE[key];
  return (val && val.length > 0) ? val : (fallback || []);
}

// Get colours for a specific style+PO combination from poSizeTable
function getPoColours(db, styleRef, poNo) {
  const rows = (db.poSizeTable || []).filter(r =>
    (!styleRef || (r.style_ref || r.styleRef || '').toLowerCase() === styleRef.toLowerCase()) &&
    (!poNo     || (r.po_no     || r.poNo     || '').toLowerCase() === poNo.toLowerCase())
  );
  const colours = [...new Set(rows.map(r => r.color || r.colour || '').filter(Boolean))];
  return colours.length > 0 ? colours : dbGet('colours', _FB_COLOURS);
}

// Get POs for a specific style
function getPosForStyle(db, styleRef) {
  const rows = (db.poSizeTable || []).filter(r =>
    !styleRef || (r.style_ref || r.styleRef || '').toLowerCase() === styleRef.toLowerCase()
  );
  const pos = [...new Set(rows.map(r => r.po_no || r.poNo || '').filter(Boolean))];
  return pos.length > 0 ? pos : dbGet('poNumbers', []);
}

// Get qty for a style+PO
function getPoQty(db, styleRef, poNo) {
  const rows = (db.poSizeTable || []).filter(r =>
    (!styleRef || (r.style_ref || r.styleRef || '').toLowerCase() === styleRef.toLowerCase()) &&
    (!poNo     || (r.po_no     || r.poNo     || '').toLowerCase() === poNo.toLowerCase())
  );
  return rows.reduce((s, r) => s + (parseInt(r.qty) || 0), 0);
}

// Get sizes available for a style+PO
function getPoSizes(db, styleRef, poNo) {
  const rows = (db.poSizeTable || []).filter(r =>
    (!styleRef || (r.style_ref || r.styleRef || '').toLowerCase() === styleRef.toLowerCase()) &&
    (!poNo     || (r.po_no     || r.poNo     || '').toLowerCase() === poNo.toLowerCase())
  );
  const sizes = [...new Set(rows.map(r => r.size || '').filter(Boolean))];
  return sizes.length > 0 ? sizes : dbGet('sizes', []);
}

// Get ex-factory date for a style+PO (earliest non-null)
function getPoExFac(db, styleRef, poNo) {
  const rows = (db.poSizeTable || []).filter(r =>
    (!styleRef || (r.style_ref || r.styleRef || '').toLowerCase() === styleRef.toLowerCase()) &&
    (!poNo     || (r.po_no     || r.poNo     || '').toLowerCase() === poNo.toLowerCase())
  ).map(r => r.ex_fty_date || '').filter(Boolean).sort();
  return rows[0] || '';
}

// Auto-fill form fields from poSizeTable when style/PO changes
// Returns partial form update: { orderQty, colour options, sizes, exFac }
function getPoAutoFill(db, styleRef, poNo) {
  const qty    = getPoQty(db, styleRef, poNo);
  const cols   = getPoColours(db, styleRef, poNo);
  const sizes  = getPoSizes(db, styleRef, poNo);
  const exFac  = getPoExFac(db, styleRef, poNo);
  return { qty: qty || '', colours: cols, sizes, exFac };
}

// ── Named exports (keep backward compat with existing code that uses BRANDS etc) ─
// Each is now a function-backed array that returns live DB data or fallback
const BRANDS   = new Proxy(_FB_BRANDS,  {get:(t,k)=>{const l=dbGet('brands',_FB_BRANDS);  return k==='length'?l.length:typeof k==='symbol'?t[k]:isNaN(k)?l[k]??t[k]:l[k];}});
const BUYERS   = new Proxy(_FB_BUYERS,  {get:(t,k)=>{const l=dbGet('buyers',_FB_BUYERS);  return k==='length'?l.length:typeof k==='symbol'?t[k]:isNaN(k)?l[k]??t[k]:l[k];}});
const FABRICS  = new Proxy(_FB_FABRICS, {get:(t,k)=>{const l=dbGet('fabrics',_FB_FABRICS);return k==='length'?l.length:typeof k==='symbol'?t[k]:isNaN(k)?l[k]??t[k]:l[k];}});
const PRODUCTS = new Proxy(_FB_PRODUCTS,{get:(t,k)=>{const l=dbGet('products',_FB_PRODUCTS);return k==='length'?l.length:typeof k==='symbol'?t[k]:isNaN(k)?l[k]??t[k]:l[k];}});
const COLOURS  = new Proxy(_FB_COLOURS, {get:(t,k)=>{const l=dbGet('colours',_FB_COLOURS);return k==='length'?l.length:typeof k==='symbol'?t[k]:isNaN(k)?l[k]??t[k]:l[k];}});
const LINES    = new Proxy(_FB_LINES,   {get:(t,k)=>{const l=dbGet('lines',_FB_LINES);    return k==='length'?l.length:typeof k==='symbol'?t[k]:isNaN(k)?l[k]??t[k]:l[k];}});
const SHIFTS   = _FB_SHIFTS;
const UNITS    = _FB_UNITS;

const SAMPLE_TYPES = ['PROTO','FIT SAMPLE','SEALING SAMPLE','PP SAMPLE','TOP OF PRODUCTION','SHIPMENT SAMPLE','AD SAMPLE','OTHER'];

// ─────────────────────────────────────────────────────────────
// AQL TABLE — ISO 2859-1 / ANSI Z1.4  Normal Inspection  General Level II
// Columns: n=sample size | ac/re per AQL level: 0.65 | 1.0 | 1.5 | 2.5 | 4.0
// Garment industry standard: Critical=0.0 (zero tolerance) | Major=2.5 | Minor=4.0
// ─────────────────────────────────────────────────────────────
const AQL_TABLE = [
  // lot range      code  n    AQL0.65    AQL1.0    AQL1.5    AQL2.5    AQL4.0
  {min:2,   max:8,  code:'A', n:2,   aql:{0.65:{ac:0,re:1},1.0:{ac:0,re:1},1.5:{ac:0,re:1},2.5:{ac:0,re:1},4.0:{ac:0,re:1}}},
  {min:9,   max:15, code:'B', n:3,   aql:{0.65:{ac:0,re:1},1.0:{ac:0,re:1},1.5:{ac:0,re:1},2.5:{ac:0,re:1},4.0:{ac:0,re:1}}},
  {min:16,  max:25, code:'C', n:5,   aql:{0.65:{ac:0,re:1},1.0:{ac:0,re:1},1.5:{ac:0,re:1},2.5:{ac:0,re:1},4.0:{ac:0,re:1}}},
  {min:26,  max:50, code:'D', n:8,   aql:{0.65:{ac:0,re:1},1.0:{ac:0,re:1},1.5:{ac:0,re:1},2.5:{ac:0,re:1},4.0:{ac:1,re:2}}},
  {min:51,  max:90, code:'E', n:13,  aql:{0.65:{ac:0,re:1},1.0:{ac:0,re:1},1.5:{ac:0,re:1},2.5:{ac:1,re:2},4.0:{ac:1,re:2}}},
  {min:91,  max:150,code:'F', n:20,  aql:{0.65:{ac:0,re:1},1.0:{ac:0,re:1},1.5:{ac:1,re:2},2.5:{ac:1,re:2},4.0:{ac:2,re:3}}},
  {min:151, max:280,code:'G', n:32,  aql:{0.65:{ac:0,re:1},1.0:{ac:1,re:2},1.5:{ac:1,re:2},2.5:{ac:2,re:3},4.0:{ac:3,re:4}}},
  {min:281, max:500,code:'H', n:50,  aql:{0.65:{ac:0,re:1},1.0:{ac:1,re:2},1.5:{ac:2,re:3},2.5:{ac:3,re:4},4.0:{ac:5,re:6}}},
  {min:501, max:1200,code:'J',n:80,  aql:{0.65:{ac:1,re:2},1.0:{ac:2,re:3},1.5:{ac:3,re:4},2.5:{ac:5,re:6},4.0:{ac:7,re:8}}},
  {min:1201,max:3200,code:'K',n:125, aql:{0.65:{ac:1,re:2},1.0:{ac:3,re:4},1.5:{ac:5,re:6},2.5:{ac:7,re:8},4.0:{ac:10,re:11}}},
  {min:3201,max:10000,code:'L',n:200,aql:{0.65:{ac:2,re:3},1.0:{ac:5,re:6},1.5:{ac:7,re:8},2.5:{ac:10,re:11},4.0:{ac:14,re:15}}},
  {min:10001,max:35000,code:'M',n:315,aql:{0.65:{ac:3,re:4},1.0:{ac:7,re:8},1.5:{ac:10,re:11},2.5:{ac:14,re:15},4.0:{ac:21,re:22}}},
  {min:35001,max:150000,code:'N',n:500,aql:{0.65:{ac:5,re:6},1.0:{ac:10,re:11},1.5:{ac:14,re:15},2.5:{ac:21,re:22},4.0:{ac:21,re:22}}},
  {min:150001,max:500000,code:'P',n:800,aql:{0.65:{ac:7,re:8},1.0:{ac:14,re:15},1.5:{ac:21,re:22},2.5:{ac:21,re:22},4.0:{ac:21,re:22}}},
  {min:500001,max:99999999,code:'Q',n:1250,aql:{0.65:{ac:10,re:11},1.0:{ac:21,re:22},1.5:{ac:21,re:22},2.5:{ac:21,re:22},4.0:{ac:21,re:22}}},
];

// getAQL(lotQty, aqlLevel)
// aqlLevel: '0.65' | '1.0' | '1.5' | '2.5' (default) | '4.0'
// Returns: { code, n, ac, re }  for the given lot size and AQL level
function getAQL(qty, aqlLevel) {
  const q   = parseInt(qty)  || 0;
  const lvl = String(aqlLevel || '2.5');
  const row = AQL_TABLE.find(r => q >= r.min && q <= r.max) || AQL_TABLE[AQL_TABLE.length - 1];
  const aq  = row.aql[parseFloat(lvl)] || row.aql[2.5];
  return { code: row.code, n: row.n, ac: aq.ac, re: aq.re };
}

// ─────────────────────────────────────────────────────────────
// API HELPER
// ─────────────────────────────────────────────────────────────
// Universal QC save — routes ALL form saves through category:'qc'
// PHP save_universal.php stores in qc_dept_logs table keyed by dept + type
async function apiSave(category, data) {
  // dept = first segment e.g. 'warehouse', 'sewing', 'cutting'
  // type = full uppercased category e.g. 'SEWING_HOURLY_AUDIT'
  const record = { ...data, _dept: category.split('_')[0], _formType: category };
  try {
    const res = await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category: 'qc', type: category.toUpperCase(), data: record }),
    });
    const text = await res.text();
    let json;
    try { json = JSON.parse(text); } catch (_) { json = { raw: text }; }
    if (json.id && Number(json.id) < 2000000000) return json.id;
    console.warn('QC save no valid id [' + category + ']:', json);
    return Date.now();
  } catch (e) {
    console.error('QC save error [' + category + ']:', e);
    return Date.now();
  }
}

// ─────────────────────────────────────────────────────────────
// SHARED UI COMPONENTS
// ─────────────────────────────────────────────────────────────

// Field — text / select / date / number / textarea
export function QField({ label, value, onChange, type='text', readOnly=false,
  placeholder='', options=null, required=false, style={}, rows=3, note='' }) {
  const base = {
    width:'100%', padding:'9px 11px', fontSize:13, borderRadius:r.md,
    border:`1px solid ${S.slate200}`, background: readOnly ? S.slate50 : S.white,
    color: readOnly ? S.slate500 : S.slate800, outline:'none', transition:'border .15s',
  };
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4, ...style }}>
      <label style={{ fontSize:11, fontWeight:600, color:S.slate500, textTransform:'uppercase', letterSpacing:'.4px' }}>
        {label}{required && <span style={{ color:S.red }}> *</span>}
      </label>
      {options ? (
        <select value={value} onChange={onChange} disabled={readOnly} style={{ ...base, cursor: readOnly?'default':'pointer' }}>
          {options.map((o,i) => <option key={i} value={o.value??o}>{o.label??o}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value} onChange={onChange} readOnly={readOnly} placeholder={placeholder} rows={rows}
          style={{ ...base, resize:'vertical', lineHeight:1.5 }} />
      ) : (
        <input type={type} value={value} onChange={onChange} readOnly={readOnly} placeholder={placeholder}
          style={base} />
      )}
      {note && <div style={{ fontSize:11, color:S.slate400 }}>{note}</div>}
    </div>
  );
}

// Button
export function QBtn({ children, onClick, color=S.blue, outline=false, sm=false,
  disabled=false, icon=null, type='button', fullWidth=false }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{
        display:'flex', alignItems:'center', justifyContent:'center', gap:6,
        padding: sm ? '7px 14px' : '10px 20px',
        borderRadius: r.md, border: outline ? `2px solid ${color}` : 'none',
        background: disabled ? S.slate100 : outline ? 'transparent' : color,
        color: disabled ? S.slate400 : outline ? color : S.white,
        fontWeight:600, fontSize: sm ? 12 : 13,
        cursor: disabled ? 'default' : 'pointer',
        width: fullWidth ? '100%' : 'auto',
        transition:'all .15s', opacity: disabled ? .7 : 1,
      }}>
      {icon && <span style={{ fontSize:15 }}>{icon}</span>}{children}
    </button>
  );
}

// Badge
export function QBadge({ children, color=S.blue, bg }) {
  return (
    <span style={{
      padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
      color, background: bg || color+'22', display:'inline-block', whiteSpace:'nowrap',
    }}>{children}</span>
  );
}

// Status badge — PASS / FAIL / HOLD / PENDING
export function QStatus({ status }) {
  const map = {
    PASS:    { color:S.green, bg:S.greenBg,  label:'✓ PASS'    },
    FAIL:    { color:S.red,   bg:S.redBg,    label:'✗ FAIL'    },
    HOLD:    { color:S.amber, bg:S.amberBg,  label:'⚠ HOLD'   },
    PENDING: { color:S.slate400, bg:S.slate100, label:'— PENDING'},
    APPROVED:{ color:S.green, bg:S.greenBg,  label:'✓ APPROVED'},
    REJECTED:{ color:S.red,   bg:S.redBg,    label:'✗ REJECTED'},
    REWORK:  { color:S.amber, bg:S.amberBg,  label:'↩ REWORK' },
  };
  const c = map[status?.toUpperCase()] || map.PENDING;
  return <QBadge color={c.color} bg={c.bg}>{c.label}</QBadge>;
}

// Section header inside a card
export function QSecHead({ title, action=null, color=S.blue }) {
  return (
    <div style={{
      display:'flex', justifyContent:'space-between', alignItems:'center',
      marginBottom:16, paddingBottom:12, borderBottom:`2px solid ${S.slate100}`,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ width:3, height:18, background:color, borderRadius:2 }}/>
        <h3 style={{ fontSize:13, fontWeight:700, color:S.slate700, textTransform:'uppercase', letterSpacing:'.4px' }}>{title}</h3>
      </div>
      {action}
    </div>
  );
}

// Card wrapper
export function QCard({ children, style={}, className='' }) {
  return (
    <div className={className} style={{
      background:S.white, border:`1px solid ${S.slate200}`, borderRadius:r.lg,
      padding:20, ...style,
    }}>{children}</div>
  );
}

// KPI tile
export function QKpi({ label, value, sub, color=S.blue, icon='', trend='' }) {
  return (
    <div style={{
      background:S.white, border:`1px solid ${S.slate200}`, borderRadius:r.lg,
      padding:'16px 18px', position:'relative', overflow:'hidden',
    }}>
      <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:color, borderRadius:`${r.lg} 0 0 ${r.lg}` }}/>
      {icon && <div style={{ fontSize:22, marginBottom:6 }}>{icon}</div>}
      <div style={{ fontSize:26, fontWeight:800, color:S.slate800, lineHeight:1, fontFamily:"'JetBrains Mono',monospace" }}>{value}</div>
      <div style={{ fontSize:11, fontWeight:600, color:S.slate500, textTransform:'uppercase', letterSpacing:'.4px', marginTop:5 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color: trend==='up' ? S.green : trend==='dn' ? S.red : S.slate400, marginTop:3, fontWeight:500 }}>{sub}</div>}
    </div>
  );
}

// Responsive grid form
export function QGrid({ children, cols=4, gap=14 }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(180px,1fr))`, gap }}>{children}</div>
  );
}

// Table
export function QTable({ headers=[], rows=[], emptyMsg='No records found.' }) {
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead>
          <tr style={{ background:S.slate50 }}>
            {headers.map((h,i) => (
              <th key={i} style={{ padding:'10px 12px', textAlign:'left', fontWeight:700, color:S.slate500, fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${S.slate200}`, whiteSpace:'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={headers.length} style={{ padding:24, textAlign:'center', color:S.slate400, fontStyle:'italic' }}>{emptyMsg}</td></tr>
            : rows.map((row,i) => (
              <tr key={i} style={{ borderBottom:`1px solid ${S.slate100}`, background: i%2===0 ? S.white : S.slate50 }}>
                {row.map((cell,j) => <td key={j} style={{ padding:'9px 12px', color:S.slate700, verticalAlign:'middle' }}>{cell}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// Checklist row (OK / NG + remark)
export function QCheckRow({ label, value, onChange, remark, onRemark }) {
  return (
    <div style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'8px 0', borderBottom:`1px solid ${S.slate100}` }}>
      <div style={{ display:'flex', gap:6, flexShrink:0, paddingTop:1 }}>
        {[['OK', true, S.green], ['NG', false, S.red]].map(([lbl, val, col]) => (
          <label key={lbl} style={{ display:'flex', alignItems:'center', gap:4, cursor:'pointer' }}>
            <input type="radio" checked={value===val} onChange={() => onChange(val)}
              style={{ accentColor:col, width:14, height:14, cursor:'pointer' }}/>
            <span style={{ fontSize:11, fontWeight:700, color: value===val ? col : S.slate400 }}>{lbl}</span>
          </label>
        ))}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, color:S.slate700, marginBottom: value===false ? 4 : 0 }}>{label}</div>
        {value === false && (
          <input placeholder="Corrective action / remark..." value={remark||''} onChange={e => onRemark(e.target.value)}
            style={{ width:'100%', padding:'5px 8px', fontSize:11, borderRadius:r.sm, border:`1px solid ${S.redBd}`, background:S.redBg, color:S.red }}/>
        )}
      </div>
    </div>
  );
}

// Divider
export function QDiv({ label='' }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0 12px' }}>
      <div style={{ flex:1, height:1, background:S.slate200 }}/>
      {label && <span style={{ fontSize:10, fontWeight:700, color:S.slate400, textTransform:'uppercase', whiteSpace:'nowrap' }}>{label}</span>}
      <div style={{ flex:1, height:1, background:S.slate200 }}/>
    </div>
  );
}

// Print button helper
export function QPrint() {
  return <QBtn sm icon="🖨" onClick={() => window.print()} color={S.slate600}>Print</QBtn>;
}

// ─────────────────────────────────────────────────────────────
// GLOBAL FILTER BAR
// ─────────────────────────────────────────────────────────────
function GlobalFilterBar({ filters, setFilters, db }) {
  const [exp, setExp] = useState(false);
  const active = Object.values(filters).filter(Boolean).length;
  const poRows = (db && db.poSizeTable) || [];

  // ── Cascading filter helpers ─────────────────────────────────────────────
  // Each level filters based on all upstream selections

  // Styles filtered by brand/buyer/product/fabric
  const cascadeStyles = useMemo(() => {
    let styles = (db && db.mgmtStyles) || [];
    if (filters.brand)   styles = styles.filter(s => (s.brand   ||'').toLowerCase() === filters.brand.toLowerCase());
    if (filters.buyer)   styles = styles.filter(s => (s.buyer   ||'').toLowerCase() === filters.buyer.toLowerCase());
    if (filters.product) styles = styles.filter(s => (s.productType||s.category||'').toLowerCase() === filters.product.toLowerCase());
    if (filters.fabric)  styles = styles.filter(s => (s.fabricType||'').toLowerCase() === filters.fabric.toLowerCase());
    if (filters.season)  styles = styles.filter(s => (s.season  ||'').toLowerCase() === filters.season.toLowerCase());
    return [...new Set(styles.map(s => s.styleRef || s.name || '').filter(Boolean))].sort();
  }, [db, filters.brand, filters.buyer, filters.product, filters.fabric, filters.season]);

  // Buyers filtered by brand
  const cascadeBuyers = useMemo(() => {
    if (!filters.brand) return dbGet('buyers', _FB_BUYERS);
    const styles = ((db && db.mgmtStyles) || []).filter(s => (s.brand||'').toLowerCase() === filters.brand.toLowerCase());
    const buyers = [...new Set(styles.map(s => s.buyer||'').filter(Boolean))];
    return buyers.length > 0 ? buyers : dbGet('buyers', _FB_BUYERS);
  }, [db, filters.brand]);

  // Seasons filtered by brand
  const cascadeSeasons = useMemo(() => {
    if (!filters.brand) return dbGet('seasons', []);
    const styles = ((db && db.mgmtStyles) || []).filter(s => (s.brand||'').toLowerCase() === filters.brand.toLowerCase());
    return [...new Set(styles.map(s => s.season||'').filter(Boolean))].sort();
  }, [db, filters.brand]);

  // POs filtered by style (from poSizeTable)
  const cascadePOs = useMemo(() => {
    const styleKey = filters.style || '';
    if (!styleKey) return dbGet('poNumbers', []);
    const rows = poRows.filter(r => (r.style_ref||'').toLowerCase() === styleKey.toLowerCase());
    const pos = [...new Set(rows.map(r => r.po_no||'').filter(Boolean))].sort();
    return pos.length > 0 ? pos : dbGet('poNumbers', []);
  }, [poRows, filters.style]);

  // Colours filtered by style+PO (from poSizeTable)
  const cascadeColours = useMemo(() => {
    const rows = poRows.filter(r =>
      (!filters.style || (r.style_ref||'').toLowerCase() === filters.style.toLowerCase()) &&
      (!filters.po    || (r.po_no   ||'').toLowerCase() === filters.po.toLowerCase())
    );
    const cols = [...new Set(rows.map(r => r.color||r.colour||'').filter(Boolean))];
    return cols.length > 0 ? cols : dbGet('colours', _FB_COLOURS);
  }, [poRows, filters.style, filters.po]);

  // Sizes filtered by style+PO+colour
  const cascadeSizes = useMemo(() => {
    const rows = poRows.filter(r =>
      (!filters.style  || (r.style_ref||'').toLowerCase() === filters.style.toLowerCase()) &&
      (!filters.po     || (r.po_no   ||'').toLowerCase() === filters.po.toLowerCase()) &&
      (!filters.colour || (r.color||r.colour||'').toLowerCase() === filters.colour.toLowerCase())
    );
    const sizes = [...new Set(rows.map(r => r.size||'').filter(Boolean))];
    return sizes.length > 0 ? sizes : dbGet('sizes', []);
  }, [poRows, filters.style, filters.po, filters.colour]);

  // When PO is selected, auto-fill qty and ex-factory from poSizeTable
  const handlePoChange = (poNo) => {
    const styleKey = filters.style || '';
    const rows = poRows.filter(r =>
      (r.po_no||'') === poNo &&
      (!styleKey || (r.style_ref||'').toLowerCase() === styleKey.toLowerCase())
    );
    const totalQty = rows.reduce((s, r) => s + (parseInt(r.qty)||0), 0);
    const exFac = rows.map(r => r.ex_fty_date||'').filter(Boolean).sort()[0] || '';
    setFilters(prev => ({ ...prev, po: poNo, qty: totalQty ? String(totalQty) : prev.qty, exFac: exFac || prev.exFac }));
  };

  // When Style is selected, pull plan dates from stylePlans
  const handleStyleChange = (styleRef) => {
    const plan = ((db && db.stylePlans) || []).find(p =>
      (p.style||'').toLowerCase() === styleRef.toLowerCase()
    );
    const getDate = (keywords) => {
      if (!plan) return '';
      const proc = (plan.processes||[]).find(p =>
        keywords.some(k => (p.name||'').toLowerCase().includes(k))
      );
      return proc ? (proc.planDate||proc.actualDate||'') : '';
    };
    setFilters(prev => ({
      ...prev,
      style:   styleRef,
      planFab: getDate(['fabric','fab in','fab-in']) || prev.planFab,
      planCut: getDate(['cutting','cut','pcd'])      || prev.planCut,
      planSew: getDate(['sewing','sew start'])        || prev.planSew,
      exFac:   getDate(['ex-factory','ex factory','exfty']) || prev.exFac,
      inspDate:getDate(['final audit','inspection','aql'])  || prev.inspDate,
    }));
  };

  // ── Reusable styled select ───────────────────────────────────────────────
  const sel = (key, opts, label, customOnChange) => (
    <div key={key} style={{ display:'flex', flexDirection:'column', gap:2 }}>
      <span style={{ fontSize:9, fontWeight:700, color:'#ADB5BD', textTransform:'uppercase' }}>{label}</span>
      <select value={filters[key]||''} onChange={e => customOnChange ? customOnChange(e.target.value) : setFilters({...filters,[key]:e.target.value})}
        style={{ padding:'6px 8px', fontSize:12, borderRadius:r.sm, border:`1px solid #1E3A5F`, background:'#0D1B2A', color:'white', cursor:'pointer', minWidth:90 }}>
        <option value="">All</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  const txt = (key, label, ph='') => (
    <div key={key} style={{ display:'flex', flexDirection:'column', gap:2 }}>
      <span style={{ fontSize:9, fontWeight:700, color:'#ADB5BD', textTransform:'uppercase' }}>{label}</span>
      <input value={filters[key]||''} onChange={e => setFilters({...filters,[key]:e.target.value})}
        placeholder={ph} style={{ padding:'6px 8px', fontSize:12, borderRadius:r.sm, border:`1px solid #1E3A5F`, background:'#0D1B2A', color:'white', width:90 }}/>
    </div>
  );

  const dateField = (key, label) => (
    <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
      <span style={{ fontSize:9, fontWeight:700, color:'#ADB5BD', textTransform:'uppercase' }}>{label}</span>
      <input type="date" value={filters[key]||''} onChange={e => setFilters({...filters,[key]:e.target.value})}
        style={{ padding:'6px 8px', fontSize:12, borderRadius:r.sm, border:`1px solid #1E3A5F`, background:'#0D1B2A', color:'white' }}/>
    </div>
  );

  return (
    <div style={{ background:'#0D1B2A', borderBottom:'1px solid #1E3A5F' }}>
      <div style={{ padding:'8px 16px', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
        <button onClick={() => setExp(!exp)}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:r.md, border:`1px solid #1E3A5F`, background: active>0 ? S.blue+'33' : 'transparent', color:'white', cursor:'pointer', fontSize:12, fontWeight:600 }}>
          🔽 Filters {active>0 && <QBadge color={S.blue} bg={S.blue+'33'}>{active}</QBadge>}
        </button>
        {filters.brand  && <QBadge color={S.dSampling}>{filters.brand}</QBadge>}
        {filters.style  && <QBadge color={S.teal}>Style: {filters.style}</QBadge>}
        {filters.po     && <QBadge color={S.amber}>PO: {filters.po}{filters.qty ? ` · ${parseInt(filters.qty).toLocaleString()} pcs` : ''}</QBadge>}
        {filters.colour && <QBadge color={S.purple}>{filters.colour}</QBadge>}
        {filters.exFac  && <QBadge color={S.red}>Ex-Fac: {filters.exFac}</QBadge>}
        {active > 0 && (
          <button onClick={() => setFilters({})}
            style={{ fontSize:11, color:S.red, background:'transparent', border:'none', cursor:'pointer', fontWeight:700 }}>✕ Clear</button>
        )}
      </div>

      {exp && (
        <div style={{ padding:'0 16px 14px', display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>

          {/* ROW 1: Master cascades — Brand drives everything */}
          {sel('brand', dbGet('brands',_FB_BRANDS), 'Brand', (v) => {
            setFilters(prev => ({ ...prev, brand:v, buyer:'', season:'', style:'', po:'', qty:'', colour:'', size:'', exFac:'' }));
          })}
          {sel('buyer',   cascadeBuyers,                     'Buyer'  )}
          {sel('fabric',  dbGet('fabrics',_FB_FABRICS),      'Fabric' )}
          {sel('product', dbGet('products',_FB_PRODUCTS),    'Product')}
          {cascadeSeasons.length > 0 && sel('season', cascadeSeasons,   'Season')}

          {/* ROW 2: Style — filtered by brand/buyer/product/fabric */}
          {sel('style', cascadeStyles.length > 0 ? cascadeStyles : dbGet('styles',[]), 'Style No', handleStyleChange)}

          {/* ROW 3: PO — filtered by style, auto-fills qty + ex-fac */}
          {sel('po', cascadePOs, 'PO No', handlePoChange)}

          {/* PO Qty — auto-filled when PO selected, read-only display */}
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            <span style={{ fontSize:9, fontWeight:700, color:'#ADB5BD', textTransform:'uppercase' }}>PO Qty</span>
            <input value={filters.qty ? parseInt(filters.qty).toLocaleString() : ''} readOnly
              placeholder="Auto from PO"
              style={{ padding:'6px 8px', fontSize:12, borderRadius:r.sm, border:`1px solid #1E3A5F`, background:'#0D2040', color:'#ADB5BD', width:90, cursor:'default' }}/>
          </div>

          {/* ROW 4: Colour + Size — filtered by style+PO */}
          {sel('colour', cascadeColours, 'Colour')}
          {cascadeSizes.length > 0 && sel('size', cascadeSizes, 'Size')}

          {/* Date fields */}
          {dateField('exFac',    'Ex-Factory')}
          {dateField('inspDate', 'Insp Date' )}
          {dateField('planFab',  'Plan Fab'  )}
          {dateField('planCut',  'Plan Cut'  )}
          {dateField('planSew',  'Plan Sew'  )}
          {dateField('actFab',   'Actual Fab')}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LOGIN SCREEN
// ─────────────────────────────────────────────────────────────
const USERS = [
  { id:'admin',      pwd:'admin123',  name:'Admin',           role:'QC Manager',    depts:'all'     },
  { id:'qc1',        pwd:'qc123',     name:'QC Inspector 1',  role:'QC Inspector',  depts:'all'     },
  { id:'supervisor', pwd:'sup123',    name:'Supervisor',      role:'Supervisor',    depts:'all'     },
  { id:'warehouse',  pwd:'wh123',     name:'WH Inspector',    role:'WH Inspector',  depts:'wh'      },
  { id:'cutting',    pwd:'cut123',    name:'Cutting QC',      role:'Cutting QC',    depts:'cutting' },
  { id:'sewing',     pwd:'sew123',    name:'Sewing QC',       role:'Sewing QC',     depts:'sewing'  },
  { id:'finishing',  pwd:'fin123',    name:'Finishing QC',    role:'Finishing QC',  depts:'finishing'},
  { id:'demo',       pwd:'demo',      name:'Demo User',       role:'QC Inspector',  depts:'all'     },
];

function LoginScreen({ onLogin }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');

  const login = () => {
    const user = USERS.find(x => x.id===u && x.pwd===p);
    if (user) onLogin(user);
    else setErr('Invalid username or password');
  };

  return (
    <div style={{ minHeight:'100vh', background:`linear-gradient(135deg,${S.navy} 0%,${S.navyMd} 60%,${S.navyLt} 100%)`, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ width:'100%', maxWidth:420 }}>
        {/* Brand */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ width:64, height:64, borderRadius:r.xl, background:`linear-gradient(135deg,${S.blue},${S.teal})`, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:28, marginBottom:12, boxShadow:`0 8px 24px ${S.blue}55` }}>🔍</div>
          <h1 style={{ color:'white', fontSize:24, fontWeight:800, letterSpacing:'-.5px' }}>Quality Module</h1>
          <p style={{ color:S.slate400, fontSize:13, marginTop:6 }}>Garment Quality Management System · 10 Departments · 60 Forms</p>
        </div>

        <div style={{ background:S.navyLt, borderRadius:r.xl, padding:28, border:`1px solid #1E3A5F`, boxShadow:`0 20px 60px rgba(0,0,0,.4)` }}>
          <h2 style={{ color:'white', fontSize:17, fontWeight:700, marginBottom:20 }}>Sign In</h2>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {[['Username',u,setU,'text'],['Password',p,setP,'password']].map(([lbl,val,set,type]) => (
              <div key={lbl}>
                <div style={{ fontSize:11, fontWeight:700, color:S.slate400, textTransform:'uppercase', marginBottom:5 }}>{lbl}</div>
                <input type={type} value={val} placeholder={lbl} onKeyDown={e=>e.key==='Enter'&&login()}
                  onChange={e=>{set(e.target.value);setErr('');}}
                  style={{ width:'100%', padding:'11px 14px', borderRadius:r.md, border:`1px solid #1E3A5F`, background:'#0D2040', color:'white', fontSize:14, outline:'none' }}/>
              </div>
            ))}
            {err && <div style={{ padding:'9px 12px', background:S.redBg, borderRadius:r.sm, color:S.red, fontSize:12, fontWeight:600 }}>{err}</div>}
            <button onClick={login}
              style={{ width:'100%', padding:13, background:`linear-gradient(135deg,${S.blue},${S.teal})`, border:'none', borderRadius:r.md, color:'white', fontWeight:700, fontSize:14, cursor:'pointer', marginTop:4, boxShadow:`0 4px 16px ${S.blue}44` }}>
              Sign In →
            </button>
          </div>
          <div style={{ marginTop:18, padding:'12px 14px', background:'#0D2040', borderRadius:r.sm, border:`1px solid #1E3A5F` }}>
            <div style={{ fontSize:10, fontWeight:700, color:S.slate400, textTransform:'uppercase', marginBottom:6 }}>Demo Logins</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 16px' }}>
              {[['demo/demo','All Access'],['qc1/qc123','Inspector'],['admin/admin123','Manager']].map(([c,r]) => (
                <span key={c} style={{ fontSize:11 }}>
                  <span style={{ fontFamily:"'JetBrains Mono',monospace", color:'#7DD3FC' }}>{c}</span>
                  <span style={{ color:S.slate500 }}> ({r})</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DASHBOARD (cross-dept analytics)
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// ENHANCED DASHBOARD — DB-connected, Style+Process drill-down,
// Sort filters, UCL/LCL control charts
// ─────────────────────────────────────────────────────────────

// ── Helpers ─────────────────────────────────────────────────
const API_BASE = 'https://nirvikalp.integrashion.com/api';

async function qcApiSave(category, type, data) {
  try {
    const res = await fetch(`${API_BASE}/save_universal.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, type, data }),
    });
    const t = await res.text();
    try { return JSON.parse(t); } catch (_) { return { success: false, raw: t }; }
  } catch (e) { return { success: false, error: e.message }; }
}

// UCL/LCL calculator (3-sigma)
function calcControlLimits(values) {
  if (!values.length) return { mean: 0, ucl: 0, lcl: 0 };
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const std  = Math.sqrt(values.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / values.length);
  return { mean: +mean.toFixed(2), ucl: +(mean + 3 * std).toFixed(2), lcl: +Math.max(0, mean - 3 * std).toFixed(2) };
}

// Sample stage status color
function sampleStatusColor(status) {
  const s = (status || '').toUpperCase();
  if (s.includes('APPROVED')) return S.green;
  if (s.includes('REJECT'))   return S.red;
  if (s.includes('RESUBMIT')) return S.amber;
  if (s.includes('SUBMIT'))   return S.blue;
  return S.slate400;
}

// ── Style Process Drill-Down Modal ───────────────────────────
function StyleProcessModal({ style, db, onClose }) {
  const allQcRecs = [
    ...(db.sampling  || []).map(r => ({ ...r, _dept: 'SAMPLING'  })),
    ...(db.sewing    || []).map(r => ({ ...r, _dept: 'SEWING'    })),
    ...(db.cutting   || []).map(r => ({ ...r, _dept: 'CUTTING'   })),
    ...(db.finishing || []).map(r => ({ ...r, _dept: 'FINISHING' })),
    ...(db.warehouse || []).map(r => ({ ...r, _dept: 'WAREHOUSE' })),
    ...(db.washing   || []).map(r => ({ ...r, _dept: 'WASHING'   })),
    ...(db.qcLogs    || []).map(r => ({ ...r, _dept: r.dept || r.line || 'QC' })),
  ];

  // Match QC records to this style
  const styleRef = style.styleRef || style.name || '';
  const styleRecs = allQcRecs.filter(r =>
    (r.style || r.styleRef || r.styleNo || '').toLowerCase() === styleRef.toLowerCase()
  );

  // Get processes from workstation OB
  const processes = style.workstationData?.obSequence || style.workstationData?.aggregatedOps || [];
  const isSampleStyle = (style.totalQty || 0) < 50 || styleRef.toLowerCase().includes('sample');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: S.white, borderRadius: r.xl, width: '100%', maxWidth: 860, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', background: S.navy, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>{styleRef}</div>
            <div style={{ color: S.slate400, fontSize: 12, marginTop: 2 }}>
              {style.brand} · {style.season} · Qty: {(style.totalQty || 0).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #1E3A5F', borderRadius: r.md, color: 'white', padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}>✕ Close</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Processes + QC Status ── */}
          {processes.length > 0 && (
            <QCard>
              <QSecHead title="Process Quality Status" color={S.teal} />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: S.slate50 }}>
                      {['SL', 'Part', 'Process', 'Machine', 'SMV', 'Stage', 'Status', 'Date', 'Remarks'].map((h, i) => (
                        <th key={i} style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: S.slate500, fontSize: 11, textTransform: 'uppercase', borderBottom: `2px solid ${S.slate200}`, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {processes.map((proc, idx) => {
                      const procName = proc.procName || proc.name || `Process ${idx + 1}`;
                      const matched = styleRecs.find(r =>
                        (r.process || r.processName || '').toLowerCase().includes(procName.toLowerCase().slice(0, 8)) ||
                        r._dept === 'SEWING'
                      );
                      const isSample = isSampleStyle || proc.partName?.toLowerCase().includes('sample');

                      // Build status display
                      let stageLabel = isSample ? 'SAMPLE STAGE' : 'PRODUCTION';
                      let statusEl = null;

                      if (isSample && matched) {
                        const st = matched.sampleType || matched.formType || 'SAMPLE';
                        const status = matched.result || matched.status || 'IN PROCESS';
                        const date = matched.date || matched.createdAt || '';
                        const comments = matched.comments || matched.remarks || '';
                        stageLabel = st;
                        const col = sampleStatusColor(status);
                        statusEl = (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontWeight: 700, color: col, fontSize: 11 }}>{status}</span>
                            {date && <span style={{ fontSize: 10, color: S.slate400 }}>{date}</span>}
                            {comments && <span style={{ fontSize: 10, color: S.amber, fontStyle: 'italic' }}>{comments.slice(0, 40)}{comments.length > 40 ? '…' : ''}</span>}
                          </div>
                        );
                      } else if (!isSample && matched) {
                        const produced   = matched.producedQty  || matched.cutQty    || 0;
                        const offered    = matched.offeredQty   || matched.offerQty  || 0;
                        const inspected  = matched.totalChecked || matched.inspectedQty || 0;
                        const defects    = matched.totalDefects || matched.defects    || 0;
                        const dhu        = inspected > 0 ? ((defects / inspected) * 100).toFixed(1) : '—';
                        const pass       = matched.result === 'PASS' || matched.result === 'APPROVED';
                        statusEl = (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,auto)', gap: '2px 10px', fontSize: 10 }}>
                            <span style={{ color: S.slate500 }}>Produced</span><span style={{ fontWeight: 700 }}>{produced}</span>
                            <span style={{ color: S.slate500 }}>Offered</span><span style={{ fontWeight: 700 }}>{offered}</span>
                            <span style={{ color: S.slate500 }}>Inspected</span><span style={{ fontWeight: 700 }}>{inspected}</span>
                            <span style={{ color: S.slate500 }}>DHU</span><span style={{ fontWeight: 700, color: parseFloat(dhu) > 5 ? S.red : S.green }}>{dhu}{dhu !== '—' ? '%' : ''}</span>
                            <span style={{ color: S.slate500 }}>Result</span>
                            <span style={{ fontWeight: 700, color: pass ? S.green : S.red, gridColumn: 'span 3' }}>{matched.result || 'PENDING'}</span>
                          </div>
                        );
                      } else {
                        statusEl = <span style={{ fontSize: 11, color: S.slate300 }}>— No QC record —</span>;
                      }

                      return (
                        <tr key={idx} style={{ borderBottom: `1px solid ${S.slate100}`, background: idx % 2 === 0 ? S.white : S.slate50 }}>
                          <td style={{ padding: '8px 10px', color: S.slate400, fontSize: 11 }}>{idx + 1}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 600, fontSize: 11 }}>{proc.partName || '—'}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 600 }}>{procName}</td>
                          <td style={{ padding: '8px 10px', color: S.slate500 }}>{proc.machine || '—'}</td>
                          <td style={{ padding: '8px 10px', fontFamily: "'JetBrains Mono',monospace", color: S.teal, fontSize: 11 }}>{proc.smv || '—'}</td>
                          <td style={{ padding: '8px 10px' }}><QBadge color={isSample ? S.dSampling : S.dSewing}>{stageLabel}</QBadge></td>
                          <td style={{ padding: '8px 10px' }}>{statusEl}</td>
                          <td style={{ padding: '8px 10px', fontSize: 10, color: S.slate400 }}>{matched?.date || '—'}</td>
                          <td style={{ padding: '8px 10px', fontSize: 10, color: S.slate500 }}>{matched?.comments?.slice(0, 30) || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </QCard>
          )}

          {/* ── Production Summary ── */}
          {!isSampleStyle && (
            <QCard>
              <QSecHead title="Production Inspection Summary" color={S.dSewing} />
              {/* Monitoring Summary */}
              {(() => {
                const monRecs = db.monitoring || [];
                const fptRecs   = monRecs.filter(r=>r.monType==='fpt');
                const gptRecs   = monRecs.filter(r=>r.monType==='gpt');
                const inlineRecs= monRecs.filter(r=>r.monType==='inline');
                const finalRecs = monRecs.filter(r=>r.monType==='final');
                const cartonRecs= monRecs.filter(r=>r.monType==='carton');
                if (!monRecs.length) return null;
                return (
                  <div style={{marginTop:16,padding:'14px 16px',background:S.blueLt,borderRadius:10,border:`1px solid ${S.blue}22`}}>
                    <div style={{fontSize:12,fontWeight:700,color:S.blue,textTransform:'uppercase',letterSpacing:'.4px',marginBottom:12}}>
                      Monitoring Summary
                    </div>
                    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                      {[
                        ['FPT',fptRecs.length,fptRecs.filter(r=>r.result==='APPROVED').length,'APPROVED','#00897B'],
                        ['GPT',gptRecs.length,gptRecs.filter(r=>r.result==='APPROVED').length,'APPROVED','#2E7D32'],
                        ['Inline',inlineRecs.length,inlineRecs.filter(r=>r.result==='PASS').length,'PASS',S.amber],
                        ['Final',finalRecs.length,finalRecs.filter(r=>r.result==='PASS').length,'PASS',S.green],
                        ['Carton Audit',cartonRecs.length,cartonRecs.filter(r=>r.result==='PASS').length,'PASS','#0F766E'],
                      ].map(([label,total,pass,passWord,color])=>(
                        <div key={label} style={{padding:'8px 14px',background:S.white,borderRadius:8,
                          border:`1px solid ${color}33`,textAlign:'center',minWidth:90}}>
                          <div style={{fontSize:10,fontWeight:700,color,textTransform:'uppercase',marginBottom:4}}>{label}</div>
                          <div style={{fontSize:18,fontWeight:800,color}}>{total}</div>
                          <div style={{fontSize:10,color:S.green,fontWeight:600}}>{pass} {passWord}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10 }}>
                {[
                  ['Order Qty',    (style.totalQty || 0).toLocaleString(),                S.blue  ],
                  ['Produced Qty', styleRecs.reduce((s, r) => s + (r.producedQty || r.cutQty || 0), 0).toLocaleString(), S.teal  ],
                  ['Offered Qty',  styleRecs.reduce((s, r) => s + (r.offeredQty  || 0), 0).toLocaleString(),             S.purple],
                  ['Inspected',    styleRecs.reduce((s, r) => s + (r.totalChecked || 0), 0).toLocaleString(),            S.amber ],
                  ['Total Defects',styleRecs.reduce((s, r) => s + (r.totalDefects || 0), 0).toLocaleString(),            S.red   ],
                  ['Avg DHU',      (() => { const c = styleRecs.reduce((s, r) => s + (r.totalChecked || 0), 0); const d = styleRecs.reduce((s, r) => s + (r.totalDefects || 0), 0); return c > 0 ? ((d / c) * 100).toFixed(2) + '%' : '—'; })(), S.slate700],
                ].map(([label, value, color]) => (
                  <QKpi key={label} label={label} value={value} color={color} />
                ))}
              </div>
            </QCard>
          )}

          {/* ── Sample Stage History ── */}
          {isSampleStyle && styleRecs.length > 0 && (
            <QCard>
              <QSecHead title="Sample Submission History" color={S.dSampling} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {styleRecs.map((r, i) => {
                  const status = r.result || r.status || 'IN PROCESS';
                  const col = sampleStatusColor(status);
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 12px', background: S.slate50, borderRadius: r.md, border: `1px solid ${S.slate200}` }}>
                      <div style={{ width: 3, height: '100%', minHeight: 40, background: col, borderRadius: 2, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 12 }}>{r.sampleType || r.formType || r._dept}</span>
                          <QBadge color={col}>{status}</QBadge>
                        </div>
                        <div style={{ fontSize: 11, color: S.slate400 }}>{r.date || '—'} · Inspector: {r.inspector || r.createdBy || '—'}</div>
                        {r.comments && <div style={{ fontSize: 11, color: S.slate600, marginTop: 4, fontStyle: 'italic' }}>"{r.comments}"</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </QCard>
          )}

          {processes.length === 0 && styleRecs.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: S.slate400 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>No QC records found for this style</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>No processes in OB and no QC entries linked to "{styleRef}"</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────
function DashboardView({ db, setDb, filters }) {
  const [sortBy,        setSortBy]        = useState('none');
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [chartFilter,   setChartFilter]   = useState({ brand: '', product: '', dateFrom: '', dateTo: '' });
  const [poTab,         setPoTab]         = useState('list');   // 'list' | 'add'
  const [poSaving,      setPoSaving]      = useState(false);
  const [poForm,        setPoForm]        = useState({
    styleRef:'', poNo:'', colour:'', destination:'', fob:'',
    exFtyDate:'',
    sizes: [{ size:'', qty:'' }]
  });
  const resetPoForm = () => setPoForm({ styleRef:'', poNo:'', colour:'', destination:'', fob:'', exFtyDate:'', sizes:[{ size:'', qty:'' }] });

  const samplingRecs  = db.sampling  || [];
  const warehouseRecs = db.warehouse || [];
  const cuttingRecs   = db.cutting   || [];
  const sewingRecs    = db.sewing    || [];
  const washingRecs   = db.washing   || [];
  const wfRecs        = db.wf        || [];
  const finishingRecs = db.finishing || [];
  const mendingRecs   = db.mending   || [];
  const killedRecs    = db.killed    || [];
  const mgmtStyles    = db.mgmtStyles || db.parts || [];

  // ── KPIs ─────────────────────────────────────────────────
  const totalInspected = sewingRecs.reduce((s, r) => s + (r.totalChecked || 0), 0);
  const totalDefects   = sewingRecs.reduce((s, r) => s + (r.totalDefects || 0), 0);
  const overallDHU     = totalInspected > 0 ? ((totalDefects / totalInspected) * 100).toFixed(2) : '—';
  const finalAudits    = finishingRecs.filter(r => r.formType === 'FINAL_AUDIT');
  const aqlPass        = finalAudits.filter(r => r.result === 'PASS').length;
  const aqlRate        = finalAudits.length > 0 ? Math.round((aqlPass / finalAudits.length) * 100) : 0;

  // ── Sort styles ────────────────────────────────────────────
  const allQcRecs = [
    ...sewingRecs, ...(db.qcLogs || []),
    ...finishingRecs, ...cuttingRecs,
  ];

  const getStyleQty      = (s) => s.totalQty || s.qty || 0;
  const getStyleProduced = (s) => allQcRecs.filter(r => (r.style || r.styleRef || '') === (s.styleRef || s.name || '')).reduce((sum, r) => sum + (r.producedQty || r.cutQty || 0), 0);
  const getStyleInspected= (s) => allQcRecs.filter(r => (r.style || r.styleRef || '') === (s.styleRef || s.name || '')).reduce((sum, r) => sum + (r.totalChecked || 0), 0);
  const getStyleDefRate  = (s) => {
    const recs = allQcRecs.filter(r => (r.style || r.styleRef || '') === (s.styleRef || s.name || ''));
    const chk  = recs.reduce((sum, r) => sum + (r.totalChecked || 0), 0);
    const def  = recs.reduce((sum, r) => sum + (r.totalDefects || 0), 0);
    return chk > 0 ? (def / chk) * 100 : 0;
  };

  // Apply global filters first
  let filteredStyles = mgmtStyles.filter(s => {
    if (filters.brand   && (s.brand   || '').toLowerCase() !== filters.brand.toLowerCase()  ) return false;
    if (filters.buyer   && (s.buyer   || '').toLowerCase() !== filters.buyer.toLowerCase()  ) return false;
    if (filters.fabric  && (s.fabricType || '').toLowerCase() !== filters.fabric.toLowerCase()) return false;
    if (filters.product && (s.productType || s.category || '').toLowerCase() !== filters.product.toLowerCase()) return false;
    if (filters.style   && !(s.styleRef || s.name || '').toLowerCase().includes(filters.style.toLowerCase())) return false;
    return true;
  });

  // Sort
  const sortedStyles = useMemo(() => {
    const arr = [...filteredStyles];
    switch (sortBy) {
      case 'qty_hl':       return arr.sort((a, b) => getStyleQty(b)       - getStyleQty(a));
      case 'produced_hl':  return arr.sort((a, b) => getStyleProduced(b)  - getStyleProduced(a));
      case 'inspected_hl': return arr.sort((a, b) => getStyleInspected(b) - getStyleInspected(a));
      case 'defect_hl':    return arr.sort((a, b) => getStyleDefRate(b)   - getStyleDefRate(a));
      default: return arr;
    }
  }, [filteredStyles, sortBy, allQcRecs]);

  // ── DHU Trend with UCL/LCL ────────────────────────────────
  const dhuTrend = useMemo(() => {
    let recs = sewingRecs;
    if (chartFilter.brand)    recs = recs.filter(r => (r.brand   || '').toLowerCase() === chartFilter.brand.toLowerCase());
    if (chartFilter.product)  recs = recs.filter(r => (r.product || '').toLowerCase() === chartFilter.product.toLowerCase());
    if (chartFilter.dateFrom) recs = recs.filter(r => (r.date || '') >= chartFilter.dateFrom);
    if (chartFilter.dateTo)   recs = recs.filter(r => (r.date || '') <= chartFilter.dateTo);

    const byDate = {};
    recs.forEach(r => {
      if (!byDate[r.date]) byDate[r.date] = { c: 0, d: 0 };
      byDate[r.date].c += r.totalChecked || 0;
      byDate[r.date].d += r.totalDefects || 0;
    });
    const pts = Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).slice(-30).map(([dt, v]) => ({
      date: dt.slice(5), dhu: v.c > 0 ? +((v.d / v.c) * 100).toFixed(2) : 0,
    }));

    const limits = calcControlLimits(pts.map(p => p.dhu));
    return pts.map(p => ({ ...p, ucl: limits.ucl, lcl: limits.lcl, mean: limits.mean }));
  }, [sewingRecs, chartFilter]);

  // ── Defect Rate Trend with UCL/LCL ───────────────────────
  const defectTrend = useMemo(() => {
    let recs = [...finishingRecs, ...cuttingRecs];
    if (chartFilter.dateFrom) recs = recs.filter(r => (r.date || '') >= chartFilter.dateFrom);
    if (chartFilter.dateTo)   recs = recs.filter(r => (r.date || '') <= chartFilter.dateTo);

    const byDate = {};
    recs.forEach(r => {
      if (!r.date) return;
      if (!byDate[r.date]) byDate[r.date] = { pass: 0, total: 0 };
      byDate[r.date].total++;
      if (r.result === 'PASS' || r.result === 'APPROVED') byDate[r.date].pass++;
    });

    const pts = Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).slice(-30).map(([dt, v]) => ({
      date: dt.slice(5), passRate: v.total > 0 ? +((v.pass / v.total) * 100).toFixed(1) : 0,
    }));

    const limits = calcControlLimits(pts.map(p => p.passRate));
    return pts.map(p => ({ ...p, ucl: Math.min(100, limits.ucl), lcl: limits.lcl, mean: limits.mean }));
  }, [finishingRecs, cuttingRecs, chartFilter]);

  // ── Dept stats ────────────────────────────────────────────
  const deptStats = [
    { id: 'sampling',  label: 'Sampling',  icon: '🎯', count: samplingRecs.length,  pass: samplingRecs.filter(r => r.result === 'APPROVED' || r.result === 'PASS').length,  color: S.dSampling  },
    { id: 'warehouse', label: 'Warehouse', icon: '🧵', count: warehouseRecs.length, pass: warehouseRecs.filter(r => r.result === 'PASS').length,                              color: S.dWarehouse },
    { id: 'cutting',   label: 'Cutting',   icon: '✂️', count: cuttingRecs.length,   pass: cuttingRecs.filter(r => r.result === 'PASS').length,                                color: S.dCutting   },
    { id: 'sewing',    label: 'Sewing',    icon: '🪡', count: sewingRecs.length,    pass: sewingRecs.filter(r => (r.dhu || 0) <= 5).length,                                   color: S.dSewing    },
    { id: 'washing',   label: 'Washing',   icon: '💧', count: washingRecs.length,   pass: washingRecs.filter(r => r.result === 'PASS').length,                                color: S.dWashing   },
    { id: 'finishing', label: 'Finishing', icon: '✨', count: finishingRecs.length, pass: finishingRecs.filter(r => r.result === 'PASS').length,                              color: S.dFinishing },
  ];

  const planActual = [
    { stage: 'Fabric Insp',   plan: filters.planFab  || '—', actual: warehouseRecs.filter(r => r.formType === 'FABRIC_INSP')[0]?.date || '—', status: warehouseRecs.filter(r => r.formType === 'FABRIC_INSP').length > 0 ? 'DONE' : 'PENDING' },
    { stage: 'Cutting Start', plan: filters.planCut  || '—', actual: cuttingRecs[0]?.date || '—',                                              status: cuttingRecs.length > 0 ? 'DONE' : 'PENDING'   },
    { stage: 'Sewing Inline', plan: filters.planSew  || '—', actual: sewingRecs[0]?.date  || '—',                                              status: sewingRecs.length > 0 ? 'DONE' : 'PENDING'    },
    { stage: 'Final Audit',   plan: filters.inspDate || '—', actual: finalAudits[0]?.date || '—',                                              status: finalAudits.length > 0 ? finalAudits.slice(-1)[0].result : 'PENDING' },
    { stage: 'Ex-Factory',    plan: filters.exFac    || '—', actual: '—',                                                                      status: 'PENDING' },
  ];

  return (
    <div className="q-fadein" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── KPI Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        <QKpi icon="🔍" label="Total Inspected"  value={totalInspected.toLocaleString()} color={S.teal}  sub={`${sewingRecs.length} sessions`}/>
        <QKpi icon="📉" label="Overall DHU"       value={overallDHU === '—' ? '—' : `${overallDHU}%`} color={parseFloat(overallDHU) > 5 ? S.red : S.green} sub={overallDHU !== '—' ? (parseFloat(overallDHU) > 5 ? '▲ Above target' : '✓ Within target') : ''} trend={parseFloat(overallDHU) > 5 ? 'dn' : 'up'}/>
        <QKpi icon="✅" label="AQL Pass Rate"     value={`${aqlRate}%`} color={aqlRate >= 80 ? S.green : S.red} sub={`${aqlPass}/${finalAudits.length} passed`}/>
        <QKpi icon="📦" label="Styles Tracked"    value={mgmtStyles.length} color={S.blue} sub={`${filteredStyles.length} filtered`}/>
        <QKpi icon="🚢" label="Ex-Factory"        value={filters.exFac || '—'} color={S.amber} sub={filters.style || 'Set style'}/>
        <QKpi icon="☠️" label="Killed GMT"        value={killedRecs.reduce((s, r) => s + (r.killedQty || 0), 0).toLocaleString()} color={S.red} sub="total killed"/>
      </div>

      {/* ── Chart Filters ── */}
      <QCard style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.slate500, alignSelf: 'center', marginRight: 4 }}>CHART FILTERS:</div>
          {[
            ['brand',    dbGet('brands',_FB_BRANDS),   'Brand'   ],
            ['product',  dbGet('products',_FB_PRODUCTS), 'Product' ],
          ].map(([key, opts, label]) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: S.slate400, textTransform: 'uppercase' }}>{label}</span>
              <select value={chartFilter[key] || ''} onChange={e => setChartFilter(f => ({ ...f, [key]: e.target.value }))}
                style={{ padding: '5px 8px', fontSize: 12, borderRadius: r.sm, border: `1px solid ${S.slate200}`, background: S.white, cursor: 'pointer', minWidth: 100 }}>
                <option value="">All</option>
                {opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
          {['dateFrom', 'dateTo'].map(key => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: S.slate400, textTransform: 'uppercase' }}>{key === 'dateFrom' ? 'From' : 'To'}</span>
              <input type="date" value={chartFilter[key] || ''} onChange={e => setChartFilter(f => ({ ...f, [key]: e.target.value }))}
                style={{ padding: '5px 8px', fontSize: 12, borderRadius: r.sm, border: `1px solid ${S.slate200}`, background: S.white }} />
            </div>
          ))}
          {Object.values(chartFilter).some(Boolean) && (
            <button onClick={() => setChartFilter({ brand: '', product: '', dateFrom: '', dateTo: '' })}
              style={{ padding: '5px 12px', background: S.redBg, color: S.red, border: `1px solid ${S.redBd}`, borderRadius: r.sm, cursor: 'pointer', fontSize: 11, fontWeight: 700, alignSelf: 'flex-end' }}>
              Clear
            </button>
          )}
        </div>
      </QCard>

      {/* ── Charts Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* DHU Control Chart */}
        <QCard>
          <QSecHead title="DHU Trend — Control Chart (3-sigma)" color={S.teal} />
          {dhuTrend.length > 0 ? (
            <>
              {dhuTrend[0]?.ucl > 0 && (
                <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: S.red,   fontWeight: 700 }}>UCL: {dhuTrend[0].ucl}%</span>
                  <span style={{ color: S.teal,  fontWeight: 700 }}>Mean: {dhuTrend[0].mean}%</span>
                  <span style={{ color: S.green, fontWeight: 700 }}>LCL: {dhuTrend[0].lcl}%</span>
                </div>
              )}
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={dhuTrend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={S.slate100} />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v, n) => [`${v}%`, n === 'dhu' ? 'DHU' : n === 'ucl' ? 'UCL' : n === 'lcl' ? 'LCL' : 'Mean']} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey="ucl"  stroke={S.red}   strokeDasharray="5 5" strokeWidth={1} dot={false} name="UCL" />
                  <Line type="monotone" dataKey="mean" stroke={S.amber} strokeDasharray="4 4" strokeWidth={1} dot={false} name="Mean" />
                  <Line type="monotone" dataKey="lcl"  stroke={S.green} strokeDasharray="5 5" strokeWidth={1} dot={false} name="LCL" />
                  <Line type="monotone" dataKey="dhu"  stroke={S.teal}  strokeWidth={2} dot={{ r: 3 }} name="DHU %" activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: S.slate400, fontSize: 13 }}>Log sewing sessions to see trend</div>}
        </QCard>

        {/* Pass Rate Control Chart */}
        <QCard>
          <QSecHead title="Pass Rate Trend — Control Chart (3-sigma)" color={S.green} />
          {defectTrend.length > 0 ? (
            <>
              {defectTrend[0]?.ucl > 0 && (
                <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: S.red,   fontWeight: 700 }}>UCL: {defectTrend[0].ucl}%</span>
                  <span style={{ color: S.green, fontWeight: 700 }}>Mean: {defectTrend[0].mean}%</span>
                  <span style={{ color: S.teal,  fontWeight: 700 }}>LCL: {defectTrend[0].lcl}%</span>
                </div>
              )}
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={defectTrend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={S.slate100} />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
                  <Tooltip formatter={(v, n) => [`${v}%`, n === 'passRate' ? 'Pass Rate' : n]} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey="ucl"      stroke={S.red}   strokeDasharray="5 5" strokeWidth={1} dot={false} name="UCL" />
                  <Line type="monotone" dataKey="mean"     stroke={S.amber} strokeDasharray="4 4" strokeWidth={1} dot={false} name="Mean" />
                  <Line type="monotone" dataKey="lcl"      stroke={S.teal}  strokeDasharray="5 5" strokeWidth={1} dot={false} name="LCL" />
                  <Line type="monotone" dataKey="passRate" stroke={S.green} strokeWidth={2} dot={{ r: 3 }} name="Pass %" activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: S.slate400, fontSize: 13 }}>Log finishing/cutting audits to see trend</div>}
        </QCard>
      </div>

      {/* ── Dept Activity ── */}
      <QCard>
        <QSecHead title="Department Activity" color={S.blue} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {deptStats.map(d => {
            const rate = d.count > 0 ? Math.round((d.pass / d.count) * 100) : 0;
            return (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 16, width: 24, flexShrink: 0 }}>{d.icon}</span>
                <div style={{ width: 80, fontSize: 12, fontWeight: 600, color: S.slate700, flexShrink: 0 }}>{d.label}</div>
                <div style={{ flex: 1, height: 7, background: S.slate100, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${rate}%`, height: '100%', background: d.color, borderRadius: 4, transition: 'width .5s' }} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: S.slate500, width: 40, textAlign: 'right', flexShrink: 0 }}>{d.count > 0 ? `${rate}%` : '—'}</div>
                <div style={{ fontSize: 10, color: S.slate400, width: 40, flexShrink: 0 }}>{d.pass}/{d.count}</div>
              </div>
            );
          })}
        </div>
      </QCard>

      {/* ── Style List with Sort ── */}
      <QCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 12, borderBottom: `2px solid ${S.slate100}`, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 3, height: 18, background: S.blue, borderRadius: 2 }} />
            <h3 style={{ fontSize: 13, fontWeight: 700, color: S.slate700, textTransform: 'uppercase', letterSpacing: '.4px' }}>Styles — QC Overview</h3>
            <QBadge color={S.slate500}>{sortedStyles.length}</QBadge>
          </div>
          {/* Sort Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: S.slate500, fontWeight: 600 }}>Sort:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              style={{ padding: '5px 10px', fontSize: 12, borderRadius: r.sm, border: `1px solid ${S.slate200}`, background: S.white, cursor: 'pointer' }}>
              <option value="none">Default</option>
              <option value="qty_hl">Order Qty — High to Low</option>
              <option value="produced_hl">Produced Qty — High to Low</option>
              <option value="inspected_hl">Inspected Qty — High to Low</option>
              <option value="defect_hl">Defect Rate — High to Low</option>
            </select>
          </div>
        </div>

        {sortedStyles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: S.slate400, fontSize: 13 }}>
            No styles found. Adjust filters or add styles in the Workstation module.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: S.slate50 }}>
                  {['Style', 'Brand', 'Season', 'Order Qty', 'Produced', 'Inspected', 'Defect Rate', 'Status', 'Action'].map((h, i) => (
                    <th key={i} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: S.slate500, fontSize: 11, textTransform: 'uppercase', borderBottom: `2px solid ${S.slate200}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedStyles.slice(0, 50).map((s, i) => {
                  const qty       = getStyleQty(s);
                  const produced  = getStyleProduced(s);
                  const inspected = getStyleInspected(s);
                  const defRate   = getStyleDefRate(s);
                  const hasQc     = inspected > 0;
                  return (
                    <tr key={s.id || i} style={{ borderBottom: `1px solid ${S.slate100}`, background: i % 2 === 0 ? S.white : S.slate50, cursor: 'pointer' }}
                      onClick={() => setSelectedStyle(s)}>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: S.slate800 }}>{s.styleRef || s.name || '—'}</td>
                      <td style={{ padding: '9px 12px', color: S.slate600 }}>{s.brand || '—'}</td>
                      <td style={{ padding: '9px 12px', color: S.slate500 }}>{s.season || '—'}</td>
                      <td style={{ padding: '9px 12px', fontFamily: "'JetBrains Mono',monospace", color: S.blue }}>{qty > 0 ? qty.toLocaleString() : '—'}</td>
                      <td style={{ padding: '9px 12px', fontFamily: "'JetBrains Mono',monospace", color: S.teal }}>{produced > 0 ? produced.toLocaleString() : '—'}</td>
                      <td style={{ padding: '9px 12px', fontFamily: "'JetBrains Mono',monospace", color: S.purple }}>{inspected > 0 ? inspected.toLocaleString() : '—'}</td>
                      <td style={{ padding: '9px 12px' }}>
                        {hasQc ? (
                          <span style={{ fontWeight: 700, color: defRate > 5 ? S.red : defRate > 2 ? S.amber : S.green, fontFamily: "'JetBrains Mono',monospace" }}>
                            {defRate.toFixed(1)}%
                          </span>
                        ) : <span style={{ color: S.slate300 }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {hasQc
                          ? <QBadge color={defRate > 5 ? S.red : S.green}>{defRate > 5 ? 'REVIEW' : 'OK'}</QBadge>
                          : <QBadge color={S.slate400}>NO QC</QBadge>}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        <button onClick={e => { e.stopPropagation(); setSelectedStyle(s); }}
                          style={{ padding: '4px 10px', background: S.blueLt, color: S.blue, border: `1px solid ${S.blue}22`, borderRadius: r.sm, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {sortedStyles.length > 50 && (
              <div style={{ textAlign: 'center', padding: '10px', fontSize: 11, color: S.slate400 }}>
                Showing 50 of {sortedStyles.length} styles. Use filters to narrow down.
              </div>
            )}
          </div>
        )}
      </QCard>

      {/* ── Plan vs Actual ── */}
      <QCard>
        <QSecHead title="Plan vs Actual — Production Flow" color={S.amber} />
        <QTable
          headers={['Stage', 'Plan Date', 'Actual Date', 'Status']}
          rows={planActual.map(row => [
            <strong>{row.stage}</strong>,
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: S.slate500 }}>{row.plan}</span>,
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }}>{row.actual}</span>,
            <QStatus status={row.status === 'DONE' ? 'PASS' : row.status === 'PENDING' ? 'PENDING' : row.status} />,
          ])}
        />
      </QCard>


      {/* ── PO Management Panel ── */}
      <QCard>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, paddingBottom:12, borderBottom:`2px solid ${S.slate100}`, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:3, height:18, background:S.amber, borderRadius:2 }}/>
            <h3 style={{ fontSize:13, fontWeight:700, color:S.slate700, textTransform:'uppercase', letterSpacing:'.4px' }}>PO Management</h3>
            <QBadge color={S.amber}>{(db.poSizeTable||[]).length} rows</QBadge>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setPoTab('list')} style={{ padding:'5px 14px', borderRadius:r.sm, border:`1px solid ${S.slate200}`, background: poTab==='list' ? S.amber : S.white, color: poTab==='list' ? S.white : S.slate600, cursor:'pointer', fontSize:12, fontWeight:600 }}>📋 View POs</button>
            <button onClick={() => setPoTab('add')}  style={{ padding:'5px 14px', borderRadius:r.sm, border:`1px solid ${S.slate200}`, background: poTab==='add'  ? S.blue  : S.white, color: poTab==='add'  ? S.white : S.slate600, cursor:'pointer', fontSize:12, fontWeight:600 }}>+ Add PO</button>
          </div>
        </div>

        {poTab === 'add' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {/* Header fields */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:10 }}>
              {/* Style ref — select from mgmtStyles */}
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:700, color:S.slate500, textTransform:'uppercase' }}>Style Ref *</label>
                <select value={poForm.styleRef} onChange={e => setPoForm(f=>({...f, styleRef:e.target.value}))}
                  style={{ padding:'8px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13 }}>
                  <option value="">Select style…</option>
                  {dbGet('styles',[]).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {/* PO Number — free text (new PO) */}
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:700, color:S.slate500, textTransform:'uppercase' }}>PO Number *</label>
                <input value={poForm.poNo} onChange={e => setPoForm(f=>({...f, poNo:e.target.value}))}
                  placeholder="e.g. PO-2025-001"
                  style={{ padding:'8px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13 }} />
              </div>
              {/* Colour — from DB colours */}
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:700, color:S.slate500, textTransform:'uppercase' }}>Colour *</label>
                <select value={poForm.colour} onChange={e => setPoForm(f=>({...f, colour:e.target.value}))}
                  style={{ padding:'8px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13 }}>
                  <option value="">Select colour…</option>
                  {dbGet('colours',_FB_COLOURS).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {/* Ex-Factory Date */}
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:700, color:S.slate500, textTransform:'uppercase' }}>Ex-Factory Date</label>
                <input type="date" value={poForm.exFtyDate} onChange={e => setPoForm(f=>({...f, exFtyDate:e.target.value}))}
                  style={{ padding:'8px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13 }} />
              </div>
              {/* Destination */}
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:700, color:S.slate500, textTransform:'uppercase' }}>Destination</label>
                <input value={poForm.destination} onChange={e => setPoForm(f=>({...f, destination:e.target.value}))}
                  placeholder="e.g. UK, USA"
                  style={{ padding:'8px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13 }} />
              </div>
              {/* FOB */}
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:700, color:S.slate500, textTransform:'uppercase' }}>FOB Price</label>
                <input type="number" value={poForm.fob} onChange={e => setPoForm(f=>({...f, fob:e.target.value}))}
                  placeholder="0.00"
                  style={{ padding:'8px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13 }} />
              </div>
            </div>

            {/* Size-Qty rows */}
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <label style={{ fontSize:11, fontWeight:700, color:S.slate500, textTransform:'uppercase' }}>Size / Qty Breakdown *</label>
                <button onClick={() => setPoForm(f=>({...f, sizes:[...f.sizes,{size:'',qty:''}]}))}
                  style={{ padding:'4px 12px', background:S.teal, color:S.white, border:'none', borderRadius:r.sm, cursor:'pointer', fontSize:12, fontWeight:700 }}>
                  + Add Size
                </button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {poForm.sizes.map((row, i) => (
                  <div key={i} style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <select value={row.size} onChange={e => setPoForm(f=>({ ...f, sizes:f.sizes.map((r,ri)=>ri===i?{...r,size:e.target.value}:r) }))}
                      style={{ padding:'7px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13, flex:'0 0 120px' }}>
                      <option value="">Size…</option>
                      {dbGet('sizes',['XS','S','M','L','XL','XXL','3XL','28','30','32','34','36','38','40']).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input type="number" value={row.qty} onChange={e => setPoForm(f=>({ ...f, sizes:f.sizes.map((r,ri)=>ri===i?{...r,qty:e.target.value}:r) }))}
                      placeholder="Qty"
                      style={{ padding:'7px 10px', border:`1px solid ${S.slate200}`, borderRadius:r.md, fontSize:13, flex:'0 0 100px' }} />
                    <div style={{ fontSize:12, color:S.teal, fontWeight:700, flex:1 }}>
                      {row.size && row.qty ? `${row.size}: ${parseInt(row.qty||0).toLocaleString()} pcs` : ''}
                    </div>
                    {poForm.sizes.length > 1 && (
                      <button onClick={() => setPoForm(f=>({...f, sizes:f.sizes.filter((_,ri)=>ri!==i)}))}
                        style={{ padding:'4px 8px', background:S.redBg, color:S.red, border:`1px solid ${S.redBd}`, borderRadius:r.sm, cursor:'pointer', fontSize:11 }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop:8, fontSize:12, color:S.slate500, fontWeight:600 }}>
                Total: {poForm.sizes.reduce((s,r)=>s+(parseInt(r.qty)||0),0).toLocaleString()} pcs
              </div>
            </div>

            {/* Save button */}
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <button disabled={poSaving || !poForm.styleRef || !poForm.poNo || !poForm.colour || !poForm.sizes[0]?.qty}
                onClick={async () => {
                  if (!poForm.styleRef || !poForm.poNo || !poForm.colour) return alert('Style, PO Number and Colour are required');
                  const validSizes = poForm.sizes.filter(r => r.size && r.qty);
                  if (!validSizes.length) return alert('At least one size with qty is required');
                  setPoSaving(true);
                  const rows = validSizes.map(r => ({
                    po_no:       poForm.poNo,
                    color:       poForm.colour,
                    size:        r.size,
                    qty:         parseInt(r.qty) || 0,
                    ex_fty_date: poForm.exFtyDate || null,
                    fob:         parseFloat(poForm.fob) || 0,
                    destination: poForm.destination,
                  }));
                  try {
                    const res = await fetch('https://nirvikalp.integrashion.com/api/save_universal.php', {
                      method:'POST', headers:{'Content-Type':'application/json'},
                      body: JSON.stringify({ category:'po_size_table', data:{ styleRef:poForm.styleRef, rows } })
                    });
                    const result = await res.json().catch(()=>({}));
                    if (result.success !== false) {
                      // Update local db state immediately
                      const newRows = rows.map(r => ({ ...r, style_ref:poForm.styleRef, id: Date.now() }));
                      setDb(prev => ({
                        ...prev,
                        poSizeTable: [...(prev.poSizeTable||[]), ...newRows],
                        poNumbers:   [...new Set([...(prev.poNumbers||[]), poForm.poNo])].sort(),
                        poColours:   [...new Set([...(prev.poColours||[]), poForm.colour])],
                        poSizes:     [...new Set([...(prev.poSizes||[]), ...validSizes.map(r=>r.size)])],
                      }));
                      syncDbCache({
                        ...(typeof window !== 'undefined' && window.__QM_DB || {}),
                        poSizeTable:[...(db.poSizeTable||[]), ...newRows],
                        poNumbers:[...(db.poNumbers||[]), poForm.poNo],
                      });
                      resetPoForm();
                      setPoTab('list');
                      alert(`PO ${poForm.poNo} saved — ${validSizes.length} size(s) for ${poForm.styleRef}`);
                    } else {
                      alert('Save failed: ' + (result.error || JSON.stringify(result)));
                    }
                  } catch(e) { alert('Network error: ' + e.message); }
                  setPoSaving(false);
                }}
                style={{ padding:'9px 24px', background: poSaving ? S.slate300 : S.green, color:S.white, border:'none', borderRadius:r.md, cursor: poSaving ? 'wait' : 'pointer', fontWeight:700, fontSize:13 }}>
                {poSaving ? '…Saving' : '💾 Save PO'}
              </button>
              <button onClick={() => { resetPoForm(); setPoTab('list'); }}
                style={{ padding:'9px 16px', background:S.slate100, color:S.slate600, border:'none', borderRadius:r.md, cursor:'pointer', fontSize:13 }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {poTab === 'list' && (
          <div>
            {(db.poSizeTable||[]).length === 0 ? (
              <div style={{ textAlign:'center', padding:32, color:S.slate400 }}>
                <div style={{ fontSize:36, marginBottom:12 }}>📦</div>
                <div style={{ fontWeight:600, marginBottom:6 }}>No PO data in database yet</div>
                <div style={{ fontSize:12, marginBottom:16 }}>Add PO details here and they will populate all QC form dropdowns automatically</div>
                <button onClick={() => setPoTab('add')}
                  style={{ padding:'8px 20px', background:S.blue, color:S.white, border:'none', borderRadius:r.md, cursor:'pointer', fontWeight:700 }}>
                  + Add First PO
                </button>
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                {/* Summary by PO */}
                {(() => {
                  const byPo = {};
                  (db.poSizeTable||[]).forEach(r => {
                    const key = `${r.style_ref||r.styleRef||'?'}__${r.po_no||r.poNo||'?'}__${r.color||r.colour||'?'}`;
                    if (!byPo[key]) byPo[key] = { styleRef:r.style_ref||r.styleRef||'?', poNo:r.po_no||r.poNo||'?', colour:r.color||r.colour||'?', exFac:r.ex_fty_date||'', dest:r.destination||'', totalQty:0, sizes:[] };
                    byPo[key].totalQty += parseInt(r.qty)||0;
                    byPo[key].sizes.push(`${r.size}:${r.qty}`);
                  });
                  const rows = Object.values(byPo);
                  return (
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead>
                        <tr style={{ background:S.slate50 }}>
                          {['Style','PO Number','Colour','Ex-Factory','Total Qty','Sizes','Destination'].map((h,i) => (
                            <th key={i} style={{ padding:'9px 12px', textAlign:'left', fontWeight:700, color:S.slate500, fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${S.slate200}`, whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r,i) => (
                          <tr key={i} style={{ borderBottom:`1px solid ${S.slate100}`, background:i%2===0?S.white:S.slate50 }}>
                            <td style={{ padding:'8px 12px', fontWeight:700 }}>{r.styleRef}</td>
                            <td style={{ padding:'8px 12px', fontFamily:"'JetBrains Mono',monospace", color:S.blue }}>{r.poNo}</td>
                            <td style={{ padding:'8px 12px' }}><span style={{ padding:'2px 10px', borderRadius:10, background:S.purpleBg, color:S.purple, fontWeight:600, fontSize:11 }}>{r.colour}</span></td>
                            <td style={{ padding:'8px 12px', color:S.amber, fontWeight:600 }}>{r.exFac||'—'}</td>
                            <td style={{ padding:'8px 12px', fontFamily:"'JetBrains Mono',monospace", color:S.teal, fontWeight:700 }}>{r.totalQty.toLocaleString()}</td>
                            <td style={{ padding:'8px 12px', fontSize:11, color:S.slate500 }}>{r.sizes.join('  ')}</td>
                            <td style={{ padding:'8px 12px', color:S.slate500 }}>{r.dest||'—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </QCard>

      {/* ── Planning → QC Integration ── */}
      {(db.stylePlans||[]).length > 0 && (
        <QCard>
          <QSecHead title="Production Plan — QC Status by Process" color={S.dProcess}
            action={<span style={{fontSize:11,color:S.slate400}}>From Planning Module · {(db.stylePlans||[]).length} active plans</span>}
          />
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:S.slate50 }}>
                  {['Style','Brand','Process / Stage','Plan Date','Actual Date','Plan Status','QC Records','QC Result'].map((h,i)=>(
                    <th key={i} style={{ padding:'8px 10px', textAlign:'left', fontWeight:700, color:S.slate500, fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${S.slate200}`, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(db.stylePlans||[])
                  .filter(plan =>
                    (!filters.brand  || (plan.brand||'').toLowerCase() === filters.brand.toLowerCase()) &&
                    (!filters.style  || (plan.style||'').toLowerCase().includes(filters.style.toLowerCase()))
                  )
                  .slice(0, 30)
                  .flatMap((plan, pi) => {
                    const styleObj = (db.mgmtStyles||[]).find(s => (s.styleRef||s.name||'') === plan.style);
                    const allQc = [
                      ...(db.sampling||[]), ...(db.sewing||[]), ...(db.cutting||[]),
                      ...(db.finishing||[]), ...(db.warehouse||[]), ...(db.washing||[]),
                      ...(db.qcDeptLogs||[])
                    ].filter(r => (r.style||r.styleRef||'').toLowerCase() === (plan.style||'').toLowerCase());

                    return (plan.processes||[]).map((proc, idx) => {
                      // Map process name to QC dept
                      const pn = (proc.name||'').toLowerCase();
                      const deptKey = pn.includes('fabric') || pn.includes('warehouse') ? 'warehouse'
                        : pn.includes('cut') || pn.includes('pcd') ? 'cutting'
                        : pn.includes('sew') ? 'sewing'
                        : pn.includes('wash') ? 'washing'
                        : pn.includes('finish') ? 'finishing'
                        : pn.includes('sample') || pn.includes('proto') || pn.includes('pp') || pn.includes('lab') ? 'sampling'
                        : '';
                      const qcRecs = deptKey ? allQc.filter(r => (r._dept||r.dept||'').toLowerCase() === deptKey || (r.formType||'').toLowerCase().includes(deptKey)) : [];
                      const latestQc = qcRecs.sort((a,b) => new Date(b.date||0) - new Date(a.date||0))[0];

                      // Status pill colour
                      const planStatus = proc.status || 'Pending';
                      const planCol = planStatus === 'Approved' ? S.green : planStatus === 'Rejected' ? S.red : planStatus === 'Sent' ? S.blue : S.slate300;
                      const qcResult = latestQc?.result || '';
                      const qcCol = qcResult === 'PASS' || qcResult === 'APPROVED' ? S.green : qcResult === 'FAIL' || qcResult === 'REJECTED' ? S.red : qcResult ? S.amber : S.slate300;

                      // Late indicator
                      const today = new Date().toISOString().split('T')[0];
                      const isLate = proc.planDate && !proc.actualDate && proc.planDate < today && planStatus === 'Pending';

                      return (
                        <tr key={`${pi}-${idx}`} style={{ borderBottom:`1px solid ${S.slate100}`, background: isLate ? '#FFEBEE' : idx%2===0 ? S.white : S.slate50 }}>
                          <td style={{ padding:'7px 10px', fontWeight:600, color:S.slate800, whiteSpace:'nowrap' }}>
                            {idx===0 ? plan.style : ''}
                          </td>
                          <td style={{ padding:'7px 10px', color:S.slate500, fontSize:11 }}>
                            {idx===0 ? (styleObj?.brand || plan.brand || '—') : ''}
                          </td>
                          <td style={{ padding:'7px 10px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              {proc.isSample && <span style={{ fontSize:9, background:S.purpleBg, color:S.purple, padding:'1px 5px', borderRadius:8, fontWeight:700 }}>SAMPLE</span>}
                              <span style={{ fontWeight: proc.isSample ? 600 : 400 }}>{proc.name}</span>
                              {isLate && <span style={{ fontSize:9, background:S.redBg, color:S.red, padding:'1px 5px', borderRadius:8, fontWeight:700 }}>LATE</span>}
                            </div>
                          </td>
                          <td style={{ padding:'7px 10px', fontFamily:"'JetBrains Mono',monospace", fontSize:11, color: isLate ? S.red : S.slate600 }}>{proc.planDate || '—'}</td>
                          <td style={{ padding:'7px 10px', fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:S.teal }}>{proc.actualDate || '—'}</td>
                          <td style={{ padding:'7px 10px' }}>
                            <span style={{ background:planCol+'22', color:planCol, padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700 }}>{planStatus}</span>
                          </td>
                          <td style={{ padding:'7px 10px', color:S.slate500, fontSize:11 }}>
                            {deptKey ? `${qcRecs.length} record${qcRecs.length!==1?'s':''}` : '—'}
                          </td>
                          <td style={{ padding:'7px 10px' }}>
                            {qcResult
                              ? <span style={{ background:qcCol+'22', color:qcCol, padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700 }}>{qcResult}</span>
                              : <span style={{ color:S.slate300, fontSize:11 }}>No QC yet</span>}
                          </td>
                        </tr>
                      );
                    });
                  })}
              </tbody>
            </table>
          </div>
          {(db.stylePlans||[]).filter(p => !filters.brand || (p.brand||'').toLowerCase()===filters.brand.toLowerCase()).length > 30 && (
            <div style={{ textAlign:'center', padding:8, fontSize:11, color:S.slate400 }}>Showing 30 plans. Filter by Brand or Style to narrow down.</div>
          )}
        </QCard>
      )}

      {/* Style drill-down modal */}
      {selectedStyle && (
        <StyleProcessModal
          style={selectedStyle}
          db={db}
          onClose={() => setSelectedStyle(null)}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// NAV CONFIG — all 10 departments (stubs for unbuilt ones)

// ════════════════════════════════════════════════════════════════════════════
//  MONITORING SECTION — Pre-production & Inspection Reports
//  FPT · GPT · Risk Assessment · Pilot Run · Inline · Final · Carton Audit
// ════════════════════════════════════════════════════════════════════════════

function MonitoringView({ db, setDb, filters }) {
  const [activeReport, setActiveReport] = React.useState('fpt');

  const MON_REPORTS = [
    { id:'fpt',    label:'FPT',              icon:'🔬', color:'#00897B', desc:'Fabric Performance Test'  },
    { id:'gpt',    label:'GPT',              icon:'🧪', color:'#2E7D32', desc:'Garment Performance Test' },
    { id:'risk',   label:'Risk Assessment',  icon:'⚠️', color:'#C62828', desc:'Style Risk Report'       },
    { id:'preprod',label:'Pre-Prod Checklist', icon:'📝', color:'#6A1B9A', desc:'Fabric Pre-Production'   },
    { id:'pilot',  label:'Pilot Run',        icon:'🚀', color:'#6A1B9A', desc:'Pre-production Pilot'    },
    { id:'inline', label:'Inline Inspection',icon:'🔍', color:'#E65100', desc:'In-line QC Report'       },
    { id:'final',  label:'Final Inspection', icon:'✅', color:'#2E7D32', desc:'Final AQL Inspection'    },
    { id:'carton', label:'Carton Audit',     icon:'📦', color:'#0F766E', desc:'CTN Audit Report'        },
  ];

  const active = MON_REPORTS.find(r=>r.id===activeReport)||MON_REPORTS[0];

  // Get all monitoring records
  const monRecs = db.monitoring || [];
  const getRecs = (type) => monRecs.filter(r=>r.monType===type);

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>

      {/* Report nav */}
      <div style={{ background:S.white, borderBottom:`1px solid ${S.slate200}`,
        overflowX:'auto', flexShrink:0 }}>
        <div style={{ display:'flex', paddingTop:4, paddingLeft:8, minWidth:'max-content' }}>
          {MON_REPORTS.map(r=>(
            <button key={r.id} onClick={()=>setActiveReport(r.id)} style={{
              padding:'10px 16px', fontSize:12, display:'flex', alignItems:'center', gap:5,
              fontWeight:activeReport===r.id?700:400,
              color:activeReport===r.id?r.color:S.slate500,
              background:'transparent', border:'none', cursor:'pointer',
              borderBottom:activeReport===r.id?`2.5px solid ${r.color}`:'2.5px solid transparent',
              whiteSpace:'nowrap',
            }}>
              <span>{r.icon}</span>{r.label}
              {getRecs(r.id).length>0 && (
                <span style={{ background:r.color+'22', color:r.color,
                  padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>
                  {getRecs(r.id).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Report content */}
      <div style={{ flex:1, overflowY:'auto' }}>
        {activeReport==='fpt'    && <FPTReport    db={db} setDb={setDb} filters={filters}/>}
        {activeReport==='gpt'    && <GPTReport    db={db} setDb={setDb} filters={filters}/>}
        {activeReport==='risk'   && <RiskReport   db={db} setDb={setDb} filters={filters}/>}
        {activeReport==='preprod' && <PreProdChecklist db={db} setDb={setDb} filters={filters}/>}
        {activeReport==='pilot'  && <PilotReport  db={db} setDb={setDb} filters={filters}/>}
        {activeReport==='inline' && <InlineReport db={db} setDb={setDb} filters={filters}/>}
        {activeReport==='final'  && <FinalReport  db={db} setDb={setDb} filters={filters}/>}
        {activeReport==='carton' && <CartonReport db={db} setDb={setDb} filters={filters}/>}
      </div>
    </div>
  );
}

// ── Shared helpers ──────────────────────────────────────────────────────────
function _uid() { return Date.now().toString(36)+Math.random().toString(36).slice(2,6); }
const _API = 'https://nirvikalpa.integrashion.com/api/save_universal.php';
const _TODAY = () => new Date().toISOString().split('T')[0];

async function _save(monType, record, db, setDb) {
  try {
    const res = await fetch(_API, { method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({ category:`monitoring_${monType}`, data:record }) });
    const j = await res.json();
    record.serverId = j.id || Date.now();
  } catch { record.serverId = Date.now(); }
  setDb(prev => {
    const list = prev.monitoring || [];
    const idx  = list.findIndex(r=>r.id===record.id);
    return { ...prev, monitoring: idx>=0
      ? list.map(r=>r.id===record.id?record:r)
      : [...list, record] };
  });
}

function _useSave(monType) {
  const [saving,setSaving]=React.useState(false);
  const [saved, setSaved] =React.useState(false);
  async function doSave(record,db,setDb) {
    setSaving(true);
    await _save(monType,record,db,setDb);
    setSaving(false);setSaved(true);
    setTimeout(()=>setSaved(false),2500);
  }
  return {saving,saved,doSave};
}

function _MFld({label,value,onChange,type='text',options=null,placeholder='',note='',rows=3}){
  const base={width:'100%',padding:'9px 11px',fontSize:13,borderRadius:8,
    border:`1px solid ${S.slate200}`,background:S.white,color:S.slate800,
    outline:'none',fontFamily:'inherit'};
  return(
    <div style={{display:'flex',flexDirection:'column',gap:4}}>
      <label style={{fontSize:11,fontWeight:600,color:S.slate500,textTransform:'uppercase',letterSpacing:'.4px'}}>{label}</label>
      {options?<select value={value} onChange={onChange} style={{...base,cursor:'pointer'}}>
        {options.map((o,i)=><option key={i} value={o.value??o}>{o.label??o}</option>)}
      </select>:type==='textarea'?<textarea value={value} onChange={onChange}
        placeholder={placeholder} rows={rows} style={{...base,resize:'vertical'}}/>
      :<input type={type} value={value} onChange={onChange} placeholder={placeholder} style={base}/>}
      {note&&<div style={{fontSize:11,color:S.slate400}}>{note}</div>}
    </div>
  );
}

function _MGrid({children,gap=14,min=180}){
  return <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fit,minmax(${min}px,1fr))`,gap}}>{children}</div>;
}

function _MCard({children,style={}}){
  return <div style={{background:S.white,border:`1px solid ${S.slate200}`,borderRadius:12,padding:20,...style}}>{children}</div>;
}

function _MSH({title,color=S.teal,action=null}){
  return(
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,paddingBottom:12,borderBottom:`2px solid ${S.slate200}`}}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <div style={{width:3,height:18,background:color,borderRadius:2}}/>
        <h3 style={{fontSize:13,fontWeight:700,color:S.slate800,textTransform:'uppercase',letterSpacing:'.4px'}}>{title}</h3>
      </div>
      {action}
    </div>
  );
}

function _MBtn({children,onClick,color=S.teal,sm=false,disabled=false,outline=false}){
  return(
    <button onClick={onClick} disabled={disabled} style={{
      display:'flex',alignItems:'center',gap:5,
      padding:sm?'7px 13px':'10px 20px',borderRadius:8,
      border:outline?`2px solid ${color}`:'none',
      background:disabled?S.slate100:outline?'transparent':color,
      color:disabled?S.slate400:outline?color:S.white,
      fontWeight:700,fontSize:sm?12:13,cursor:disabled?'default':'pointer',fontFamily:'inherit',
    }}>{children}</button>
  );
}

function _MRes({result}){
  const m={PASS:{c:S.green,b:S.greenBg},FAIL:{c:S.red,b:S.redBg},
    HOLD:{c:S.amber,b:S.amberBg},PENDING:{c:S.slate400,b:S.slate100},
    APPROVED:{c:S.green,b:S.greenBg},REJECTED:{c:S.red,b:S.redBg}};
  const x=m[result?.toUpperCase()]||m.PENDING;
  return <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,
    color:x.c,background:x.b,display:'inline-block'}}>{result||'PENDING'}</span>;
}

function _MTbl({headers=[],rows=[],emptyMsg='No records yet.'}){
  return(
    <div style={{overflowX:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
        <thead><tr style={{background:S.slate50}}>
          {headers.map((h,i)=><th key={i} style={{padding:'9px 11px',textAlign:'left',fontWeight:700,
            color:S.slate500,fontSize:11,textTransform:'uppercase',
            borderBottom:`2px solid ${S.slate200}`,whiteSpace:'nowrap'}}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length===0?<tr><td colSpan={headers.length}
            style={{padding:24,textAlign:'center',color:S.slate400,fontStyle:'italic'}}>{emptyMsg}</td></tr>
          :rows.map((row,i)=><tr key={i} style={{borderBottom:`1px solid ${S.slate200}`,
            background:i%2===0?S.white:S.slate50}}>
            {row.map((cell,j)=><td key={j} style={{padding:'9px 11px',color:S.slate800,verticalAlign:'middle'}}>{cell}</td>)}
          </tr>)}
        </tbody>
      </table>
    </div>
  );
}

const _BUYERS = ['','UK BUYER','EU BUYER','US BUYER','AUSTRALIA',"DILLARD'S",'OTHER'];

const FPT_CHECKS = [
  'Styling sample available & matches spec',
  'Measurement spec sheet confirmed',
  'Approved trim card available on line',
  'Correct fabric shade / lot in use',
  'SPI (Stitches Per Inch) as per spec',
  'Needle type & size correct',
  'Thread type, colour & tex correct',
  'Seam appearance matches approved sample',
  'Seam allowance as per spec',
  'Label (main, size, care) correct & in correct position',
  'Zipper type, colour & length correct',
  'Button type, size & colour correct',
  'Waistband / hem width as per spec',
  'Pocket position & size as per spec',
  'Bartack positions correct',
  'Loop count, size & position correct',
  'Iron / finishing standard confirmed',
  'First output matches bulk fabric shade',
  'Operator understands all critical operations',
  'No outstanding issues from previous style',
];

const GPT_CHECKS = [
  ...FPT_CHECKS,
  'Golden piece approved by QC Manager',
  'Golden piece displayed on line',
  'Bulk production matches golden piece',
  'Golden piece photograph taken & filed',
];

const RISK_CATEGORIES = [
  { cat:'Fabric',       items:['Fabric shrinkage risk','Shade variation','Weight deviation','Fabric defect rate high'] },
  { cat:'Trims',        items:['Button / snap risk','Zipper quality','Label correctness','Elastic performance'] },
  { cat:'Construction', items:['Complex seam operations','Special machine requirement','Operator skill gap','Tight measurement tolerance'] },
  { cat:'Wash/Finish',  items:['Color fastness risk','Shrinkage post wash','WF process complexity','Iron/press difficulty'] },
  { cat:'Packing',      items:['Complex folding method','Pre-pack ratio complexity','Special labelling requirement','Carton strength'] },
];

const INLINE_DEFECTS = [
  'Broken/Skip Stitch','Open Seam','Raw Edge','Puckering','Uneven Stitch',
  'Wrong Measurement','Thread Untrimmed','Stain / Oil Spot','Fabric Defect',
  'Button Defect','Zipper Issue','Label Problem','Bartack Missing','Other',
];


// ══════════════════════════════════════════════════════════════
//  FPT — FIRST PIECE TRIAL



// ══════════════════════════════════════════════════════════════
//  FPT — FABRIC TECHNICAL SUBMISSION
//  Based on industry-standard FPT template
//  All company / factory / person names removed
// ══════════════════════════════════════════════════════════════
function FPTReport({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('fpt');
  const [view,setView]=React.useState('form');
  const [extracting,setExtracting]=React.useState(false);
  const [extractMsg,setExtractMsg]=React.useState('');
  const fileRef=React.useRef();

  // Colour fastness tests (from template)
  const CF_TESTS=[
    {code:'C4A', name:'Detergent Washing'},
    {code:'C5',  name:'Dry Cleaning'},
    {code:'C6',  name:'Water at 37°C'},
    {code:'C7a', name:'Acid Perspiration'},
    {code:'C7b', name:'Alkali Perspiration'},
    {code:'C8',  name:'Dry Rubbing'},
    {code:'C9',  name:'Light'},
    {code:'C10A',name:'Oxidative Bleach'},
    {code:'C9A', name:'Wet Alkali Light'},
    {code:'C10', name:'Perborate'},
    {code:'C11', name:'Hydrolysis'},
    {code:'C15', name:'Print Durability'},
    {code:'C18B',name:'Released Formaldehyde'},
    {code:'C26', name:'Contrast Trims'},
    {code:'C37', name:'Chlorinated Water'},
    {code:'C20B',name:'Yellowing'},
    {code:'',    name:'Other'},
  ];
  // Staining adjacent fabric columns
  const STAIN_COLS=[
    {key:'a', label:'A', full:'Acetate'},
    {key:'c', label:'C', full:'Cotton'},
    {key:'n', label:'N', full:'Nylon'},
    {key:'p', label:'P', full:'Polyester'},
    {key:'ac',label:'Ac',full:'Acrylic'},
    {key:'w', label:'W', full:'Wool'},
  ];

  // Physical performance tests (from template)
  const PHYS_TESTS=[
    {code:'P1A', name:'Dimensional Stability',        cols:['Length (L)','Width (W)'],   method:'ISO 6330 / AATCC 135'},
    {code:'P1B', name:'Spirality',                    cols:['Result'],                    method:'AATCC 179'},
    {code:'P8',  name:'WIRA Steam Stability',         cols:['Length (L)','Width (W)'],   method:'WIRA'},
    {code:'P10', name:'Thermal Stability',            cols:['Length (L)','Width (W)'],   method:'ISO 13936'},
    {code:'P11', name:'Tensile Strength',             cols:['Warp','Weft'],              method:'ISO 13934 / ASTM D5034'},
    {code:'P12a',name:'Slippage — Warp over Weft',   cols:['Before Wash','After Wash'], method:'ISO 13936'},
    {code:'P12b',name:'Slippage — Weft over Warp',   cols:['Before Wash','After Wash'], method:'ISO 13936'},
    {code:'P15', name:'Residual Extension',           cols:['Warp','Weft'],              method:'ISO 13934'},
    {code:'P18A',name:'Pilling Drum',                 cols:['Result'],                   method:'ISO 12945 / ASTM D3512'},
    {code:'P19', name:'Martindale Abrasion',          cols:['Result'],                   method:'ISO 12947'},
    {code:'P21A',name:'Snagging',                     cols:['Result'],                   method:'ASTM D3939'},
    {code:'P22', name:'Crease Recovery',              cols:['Result'],                   method:'ISO 2313'},
    {code:'P29', name:'Tear Strength',                cols:['Warp','Weft'],              method:'ISO 13937 / ASTM D1424'},
    {code:'P65', name:'Weight (GSM)',                 cols:['Result'],                   method:'ISO 3801'},
    {code:'P66', name:'Dry Clean Stability',          cols:['Result'],                   method:'ISO 3175'},
    {code:'P91', name:'Easy to Iron',                 cols:['Result'],                   method:'ISO 7768'},
    {code:'P116',name:'Flammability',                 cols:['Result'],                   method:'ISO 6941'},
    {code:'P117',name:'Surface Flash',                cols:['Result'],                   method:'EN 1102'},
    {code:'P134',name:'Non Iron',                     cols:['Result'],                   method:'ISO 7768'},
    {code:'',    name:'Burst Strength',               cols:['Result'],                   method:'ISO 13938 / ASTM D3786'},
    {code:'',    name:'Other',                        cols:['Result'],                   method:''},
  ];

  const makeCFRow=()=>({change:'',a:'',c:'',n:'',p:'',ac:'',w:'',req:'',result:''});
  const makePhysRow=()=>({v1:'',v2:'',req:'',result:''});

  const fresh=()=>({
    id:_uid(), monType:'fpt', date:_TODAY(),
    // Product Details
    department:'', stroke:'', garmentSupplier:'',
    phyPerfCode:'', colourPerfCode:'',
    fabricSupplier:'', fabricQuality:'', fabricPricePerMetre:'',
    description:'', composition:'', countryOfOrigin:'', careRefCode:'',
    style:filters?.style||'', po:filters?.po||'',
    buyer:filters?.buyer||'', colour:filters?.colour||'',
    // Summary
    physColFastResult:'PENDING', supplierComments:'',
    // Wearer Trial
    noOfWearers:'', noOfHrsCleans:'', wearerTrialComments:'',
    // Construction
    weight:'', width:'', ends:'', picks:'',
    gauge:'', courses:'', wales:'',
    count:'', openEndRing:'', combedCarded:'',
    cottonFibreOrigins:'', greigeSource:'', spinners:'',
    // Dyeing
    dyerFinisherName:'',
    chemComplianceApproved:'', chemComplianceDate:'',
    finishingRoute:'',
    // Colour Fastness — each test gets {change, a, c, n, p, ac, w, req, result}
    cfTests: CF_TESTS.map(t=>({...t,...makeCFRow()})),
    cfColourTested:'',
    // Physical Performance
    physTests: PHYS_TESTS.map(t=>({...t,...makePhysRow()})),
    // Wash Info
    laundry:'', washRecipe:'',
    addComponent:'', addSource:'', addQuality:'', addComposition:'',
    // Comments
    buyerComments:'', labReportRef:'',
    pdfFileName:'', pdfExtracted:false,
    overallRemarks:'',
  });

  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const setCF=(i,k,v)=>setF(f=>({...f,cfTests:f.cfTests.map((t,ti)=>ti===i?{...t,[k]:v}:t)}));
  const setPH=(i,k,v)=>setF(f=>({...f,physTests:f.physTests.map((t,ti)=>ti===i?{...t,[k]:v}:t)}));

  const recs=(db.monitoring||[]).filter(r=>r.monType==='fpt');

  // ── PDF extraction ──────────────────────────────────────────
  async function handlePDF(e){
    const file=e.target.files[0]; if(!file) return;
    set('pdfFileName',file.name);
    setExtracting(true); setExtractMsg('Reading PDF...');
    try{
      const b64=await new Promise((res,rej)=>{
        const r=new FileReader();
        r.onload=()=>res(r.result.split(',')[1]);
        r.onerror=rej;
        r.readAsDataURL(file);
      });
      setExtractMsg('Extracting test data...');
      const cfList=CF_TESTS.map((t,i)=>`${i}. ${t.code} ${t.name}`).join('\n');
      const phList=PHYS_TESTS.map((t,i)=>`${i}. ${t.code} ${t.name} [${t.cols.join('/')}]`).join('\n');
      const resp=await fetch('https://api.anthropic.com/v1/messages',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          model:'claude-sonnet-4-6', max_tokens:2000,
          messages:[{role:'user',content:[
            {type:'document',source:{type:'base64',media_type:'application/pdf',data:b64}},
            {type:'text',text:`This is a Fabric Technical Submission / FPT lab report. Extract all values.

Colour Fastness Tests (index, code, name):
${cfList}

Physical Tests (index, code, name, columns):
${phList}

Also extract: fabricSupplier, fabricQuality, composition, weight (GSM), width, fabricDescription, dyerFinisher, finishingRoute, washRecipe, labReportRef

Return ONLY JSON (no markdown):
{
  "fabricSupplier":"","fabricQuality":"","composition":"","weight":"","width":"",
  "fabricDescription":"","dyerFinisher":"","finishingRoute":"","washRecipe":"","labReportRef":"",
  "cfTests":[{"index":0,"change":"","a":"","c":"","n":"","p":"","ac":"","w":""}],
  "physTests":[{"index":0,"v1":"","v2":""}]
}

For colour fastness, grades are 1-5. For physical tests, v1=first column value, v2=second column value (if 2 cols).
Omit tests not found in the report.`}
          ]}]
        })
      });
      const data=await resp.json();
      const raw=data.content?.find(c=>c.type==='text')?.text||'{}';
      const ex=JSON.parse(raw.replace(/```json|```/g,'').trim());
      setF(f=>{
        const cfTests=[...f.cfTests];
        (ex.cfTests||[]).forEach(et=>{
          if(et.index!=null&&cfTests[et.index]){
            Object.keys(et).filter(k=>k!=='index').forEach(k=>{ if(et[k]) cfTests[et.index][k]=et[k]; });
          }
        });
        const physTests=[...f.physTests];
        (ex.physTests||[]).forEach(et=>{
          if(et.index!=null&&physTests[et.index]){
            if(et.v1) physTests[et.index].v1=et.v1;
            if(et.v2) physTests[et.index].v2=et.v2;
          }
        });
        return{...f,cfTests,physTests,pdfExtracted:true,
          fabricSupplier:ex.fabricSupplier||f.fabricSupplier,
          fabricQuality:ex.fabricQuality||f.fabricQuality,
          composition:ex.composition||f.composition,
          weight:ex.weight||f.weight, width:ex.width||f.width,
          description:ex.fabricDescription||f.description,
          dyerFinisherName:ex.dyerFinisher||f.dyerFinisherName,
          finishingRoute:ex.finishingRoute||f.finishingRoute,
          washRecipe:ex.washRecipe||f.washRecipe,
          labReportRef:ex.labReportRef||f.labReportRef,
        };
      });
      setExtractMsg(`✓ Extracted from lab report`);
    }catch(err){ setExtractMsg('Could not extract — fill in manually.'); }
    setExtracting(false);
  }

  const handleSave=async()=>{
    await doSave({...F,savedAt:new Date().toISOString()},db,setDb);
    setF(fresh()); setExtractMsg('');
  };

  // ── Styles ──────────────────────────────────────────────────
  const inp=(w=80,extra={})=>({
    padding:'5px 8px',borderRadius:6,border:`1px solid ${S.slate200}`,
    fontSize:12,width:w,background:S.white,fontFamily:'inherit',...extra
  });
  const cellInp=(w=50)=>({
    width:w,padding:'4px',borderRadius:4,border:`1px solid ${S.slate200}`,
    fontSize:11,textAlign:'center',fontFamily:'inherit'
  });
  const sectionHead=(title,color='#00897B')=>(
    <div style={{gridColumn:'1/-1',marginTop:8,marginBottom:4,
      padding:'6px 12px',background:color+'18',borderLeft:`3px solid ${color}`,
      fontSize:12,fontWeight:700,color:color,textTransform:'uppercase',letterSpacing:'.4px'}}>
      {title}
    </div>
  );
  const LR=({l,children,span=1})=>(
    <div style={{display:'flex',flexDirection:'column',gap:3,gridColumn:`span ${span}`}}>
      <label style={{fontSize:10,fontWeight:700,color:S.slate500,textTransform:'uppercase'}}>{l}</label>
      {children}
    </div>
  );
  const TI=(k,ph='',w='100%')=>(
    <input value={F[k]||''} onChange={e=>set(k,e.target.value)} placeholder={ph}
      style={{...inp(w),width:'100%'}}/>
  );

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:16}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'#00897B',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:4}}>
            FABRIC TECHNICAL SUBMISSION
          </div>
          <h2 style={{fontSize:18,fontWeight:800,color:S.slate800}}>Fabric Performance Test (FPT)</h2>
          <p style={{fontSize:12,color:S.slate500}}>Colour fastness · Physical performance · Construction · Dyeing specification</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:11,color:S.slate400}}>Version 1.0</span>
          <_MBtn sm outline onClick={()=>setView('form')} color="#00897B">＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color="#00897B">📋 Records ({recs.length})</_MBtn>
        </div>
      </div>

      {view==='history'?(
        <_MCard>
          <_MSH title="FPT Records" color="#00897B"/>
          <_MTbl headers={['Date','Fabric Supplier','Quality','Composition','Style/PO','Overall']}
            rows={recs.map(r=>[r.date,r.fabricSupplier||'—',r.fabricQuality||'—',
              r.composition||'—',`${r.style||'—'} / ${r.po||'—'}`,
              <_MRes result={r.physColFastResult}/>])}
            emptyMsg="No FPT records yet."/>
        </_MCard>
      ):(
        <>
          {/* PDF Upload */}
          <_MCard>
            <_MSH title="Upload Third-Party Lab Report PDF" color="#00897B"/>
            <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
              <input ref={fileRef} type="file" accept=".pdf" style={{display:'none'}} onChange={handlePDF}/>
              <_MBtn onClick={()=>fileRef.current.click()} disabled={extracting} color="#00897B">
                📎 {F.pdfFileName||'Upload FPT Lab Report (PDF)'}
              </_MBtn>
              {F.pdfFileName&&<span style={{fontSize:12,color:F.pdfExtracted?S.green:S.amber,fontWeight:600}}>
                {F.pdfExtracted?'✓ Data extracted':'📄 Uploaded — not yet extracted'}</span>}
              {extracting&&<span style={{fontSize:12,color:'#0369A1'}}>⏳ {extractMsg}</span>}
              {!extracting&&extractMsg&&<span style={{fontSize:12,
                color:extractMsg.includes('✓')?S.green:S.red,fontWeight:600}}>{extractMsg}</span>}
            </div>
          </_MCard>

          {/* ── PRODUCT DETAILS ── */}
          <_MCard>
            <_MSH title="Product Details" color="#00897B"/>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
              {sectionHead('Order Information')}
              {[['date','Date','date'],['style','Style / Design No.','text'],
                ['po','PO / Stroke No.','text'],['buyer','Buyer','text'],
                ['colour','Colour','text'],['department','Department','text'],
                ['phyPerfCode','Physical Perf. Code','text'],
                ['colourPerfCode','Colour Perf. Code','text'],
                ['careRefCode','Care Ref Code','text'],
                ['labReportRef','Lab Report Ref','text'],
              ].map(([k,l,type])=>(
                <LR key={k} l={l}>
                  <input type={type} value={F[k]||''} onChange={e=>set(k,e.target.value)}
                    style={{...inp('100%'),width:'100%'}}/>
                </LR>
              ))}
              {sectionHead('Fabric Details')}
              {[['fabricSupplier','Fabric Supplier'],['fabricQuality','Fabric Quality'],
                ['fabricPricePerMetre','Fabric Price / Metre'],['description','Description'],
                ['composition','Composition'],['countryOfOrigin','Country of Origin'],
              ].map(([k,l])=>(
                <LR key={k} l={l}><TI k={k}/></LR>
              ))}
              {sectionHead('Supply Chain')}
              {[['garmentSupplier','Garment Supplier']].map(([k,l])=>(
                <LR key={k} l={l}><TI k={k}/></LR>
              ))}
            </div>
          </_MCard>

          {/* ── SUMMARY OF PERFORMANCE ── */}
          <_MCard>
            <_MSH title="Summary of Performance" color="#00897B"
              action={
                <div style={{display:'flex',gap:6}}>
                  {['APPROVED','REJECTED','PENDING'].map(v=>(
                    <button key={v} onClick={()=>set('physColFastResult',v)}
                      style={{padding:'5px 12px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',
                        border:`1px solid ${F.physColFastResult===v
                          ?(v==='APPROVED'?S.green:v==='REJECTED'?S.red:S.slate400):S.slate200}`,
                        background:F.physColFastResult===v
                          ?(v==='APPROVED'?S.greenBg:v==='REJECTED'?S.redBg:S.slate100):S.white,
                        color:F.physColFastResult===v
                          ?(v==='APPROVED'?S.green:v==='REJECTED'?S.red:S.slate500):S.slate400}}>
                      {v}
                    </button>
                  ))}
                </div>
              }/>
            <_MGrid gap={14}>
              <LR l="Physical & Colour Fastness Test Result" span={2}>
                <div style={{padding:'10px 14px',borderRadius:8,fontWeight:700,fontSize:14,
                  background:F.physColFastResult==='APPROVED'?S.greenBg:F.physColFastResult==='REJECTED'?S.redBg:S.slate50,
                  color:F.physColFastResult==='APPROVED'?S.green:F.physColFastResult==='REJECTED'?S.red:S.slate500}}>
                  {F.physColFastResult==='APPROVED'?'✅ APPROVED':F.physColFastResult==='REJECTED'?'❌ REJECTED':'⏳ PENDING'}
                </div>
              </LR>
              <LR l="Supplier Comments" span={2}>
                <textarea value={F.supplierComments} onChange={e=>set('supplierComments',e.target.value)}
                  rows={3} placeholder="Supplier technical comments..."
                  style={{...inp('100%'),width:'100%',resize:'vertical'}}/>
              </LR>
            </_MGrid>
          </_MCard>

          {/* ── FABRIC WEARER TRIAL ── */}
          <_MCard>
            <_MSH title="Fabric Wearer Trial" color="#00897B"/>
            <_MGrid gap={14}>
              <LR l="No. of Wearers"><TI k="noOfWearers" ph="e.g. 12"/></LR>
              <LR l="No. of Hours / Cleans"><TI k="noOfHrsCleans" ph="e.g. 5 cycles"/></LR>
              <LR l="Wearer Trial Comments" span={2}>
                <textarea value={F.wearerTrialComments} onChange={e=>set('wearerTrialComments',e.target.value)}
                  rows={2} placeholder="Comments on wearer trial results..."
                  style={{...inp('100%'),width:'100%',resize:'vertical'}}/>
              </LR>
            </_MGrid>
          </_MCard>

          {/* ── FINISHED FABRIC CONSTRUCTION ── */}
          <_MCard>
            <_MSH title="Finished Fabric Construction" color="#00897B"/>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12}}>
              {[
                ['weight','Weight (g/m²)'],['width','Width (cm)'],
                ['ends','Ends/cm'],['picks','Picks/cm'],
                ['gauge','Gauge'],['courses','Courses'],['wales','Wales'],
                ['count','Count'],['openEndRing','Open End / Ring'],['combedCarded','Combed / Carded'],
              ].map(([k,l])=>(
                <LR key={k} l={l}><TI k={k}/></LR>
              ))}
              <LR l="Cotton Fibre Countries of Origin" span={2}>
                <TI k="cottonFibreOrigins" ph="Countries of origin..."/>
              </LR>
              <LR l="Greige Source"><TI k="greigeSource" ph="Greige source..."/></LR>
              <LR l="Spinners"><TI k="spinners" ph="Spinners..."/></LR>
            </div>
          </_MCard>

          {/* ── DYEING AND FINISHING SPECIFICATION ── */}
          <_MCard>
            <_MSH title="Dyeing & Finishing Specification" color="#00897B"/>
            <_MGrid gap={14}>
              <LR l="Name of Dyer / Finisher" span={2}>
                <TI k="dyerFinisherName" ph="Dyer / Finisher name..."/>
              </LR>
              <LR l="Chemical Compliance Standard Approved">
                <select value={F.chemComplianceApproved} onChange={e=>set('chemComplianceApproved',e.target.value)}
                  style={{...inp('100%'),width:'100%'}}>
                  <option value="">Select...</option>
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                  <option value="PENDING">PENDING</option>
                </select>
              </LR>
              <LR l="Date Approved">
                <input type="date" value={F.chemComplianceDate||''} onChange={e=>set('chemComplianceDate',e.target.value)}
                  style={{...inp('100%'),width:'100%'}}/>
              </LR>
              <LR l="Finishing Route" span={2}>
                <textarea value={F.finishingRoute} onChange={e=>set('finishingRoute',e.target.value)}
                  rows={3} placeholder="Detail the full finishing route..."
                  style={{...inp('100%'),width:'100%',resize:'vertical'}}/>
              </LR>
            </_MGrid>
          </_MCard>

          {/* ── COLOUR FASTNESS TESTS ── */}
          <_MCard>
            <_MSH title="Colour Fastness Tests" color="#00897B"
              action={
                <div style={{display:'flex',gap:8,alignItems:'center'}}>
                  <label style={{fontSize:11,color:S.slate500}}>Colour Tested:</label>
                  <input value={F.cfColourTested} onChange={e=>set('cfColourTested',e.target.value)}
                    placeholder="e.g. Navy" style={{...inp(100)}}/>
                </div>
              }/>
            {/* Grade legend */}
            <div style={{marginBottom:10,padding:'6px 12px',background:S.slate50,borderRadius:6,fontSize:11,color:S.slate500}}>
              <strong>Colour Change:</strong> Grade 1–5 (1=Severe, 5=No change) &nbsp;|&nbsp;
              <strong>Staining (A/C/N/P/Ac/W):</strong> Adjacent fabric staining — Acetate, Cotton, Nylon, Polyester, Acrylic, Wool
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:S.slate50}}>
                    <th style={{padding:'8px 10px',textAlign:'left',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,minWidth:160}}>Test</th>
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`}}>Code</th>
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'#6A1B9A',fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,background:'#EDE7F6'}}>Change</th>
                    {STAIN_COLS.map(sc=>(
                      <th key={sc.key} title={sc.full} style={{padding:'8px 6px',textAlign:'center',fontWeight:700,color:'#00897B',fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,background:'#EFF6FF',minWidth:36}}>
                        {sc.label}<br/><span style={{fontWeight:400,fontSize:9}}>{sc.full.slice(0,3)}</span>
                      </th>
                    ))}
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`}}>Requirement</th>
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`}}>Pass/Fail</th>
                  </tr>
                </thead>
                <tbody>
                  {F.cfTests.map((t,i)=>{
                    const allGrades=[t.change,...STAIN_COLS.map(sc=>t[sc.key])].filter(Boolean).map(parseFloat);
                    const minGrade=allGrades.length?Math.min(...allGrades):null;
                    const rowFail=minGrade!==null&&minGrade<3;
                    return(
                      <tr key={i} style={{borderBottom:`1px solid ${S.slate200}`,background:rowFail?S.redBg:S.white}}>
                        <td style={{padding:'6px 10px',color:S.slate800,fontWeight:500}}>{t.name}</td>
                        <td style={{padding:'6px 8px',textAlign:'center',color:S.slate500,fontFamily:'monospace',fontSize:11}}>{t.code}</td>
                        {/* Colour Change */}
                        <td style={{padding:'4px 6px',background:'#FAF5FF'}}>
                          <input value={t.change} onChange={e=>setCF(i,'change',e.target.value)}
                            placeholder="—" style={{...cellInp(40),color:parseFloat(t.change)<3?S.red:S.green}}/>
                        </td>
                        {/* Staining columns */}
                        {STAIN_COLS.map(sc=>(
                          <td key={sc.key} style={{padding:'4px 3px',background:'#EFF6FF'}}>
                            <input value={t[sc.key]} onChange={e=>setCF(i,sc.key,e.target.value)}
                              placeholder="—" style={{...cellInp(34),color:parseFloat(t[sc.key])<3?S.red:S.green}}/>
                          </td>
                        ))}
                        <td style={{padding:'4px 6px'}}>
                          <input value={t.req} onChange={e=>setCF(i,'req',e.target.value)}
                            placeholder="Min 3-4" style={{...cellInp(70)}}/>
                        </td>
                        <td style={{padding:'4px 8px',textAlign:'center'}}>
                          {minGrade!==null
                            ? <span style={{fontWeight:700,color:rowFail?S.red:S.green,fontSize:12}}>
                                {rowFail?'FAIL':'PASS'}
                              </span>
                            : <span style={{color:S.slate300}}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </_MCard>

          {/* ── PHYSICAL PERFORMANCE ── */}
          <_MCard>
            <_MSH title="Physical Performance Tests" color="#00897B"/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:S.slate50}}>
                    <th style={{padding:'8px 10px',textAlign:'left',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,minWidth:180}}>Test</th>
                    <th style={{padding:'8px 8px',textAlign:'center',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`}}>Code</th>
                    <th style={{padding:'8px 8px',textAlign:'left',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`}}>Method</th>
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'#00897B',fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,background:'#EFF6FF'}}>Value 1</th>
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'#00897B',fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,background:'#EFF6FF'}}>Value 2</th>
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`}}>Requirement</th>
                    <th style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`}}>Pass/Fail</th>
                  </tr>
                </thead>
                <tbody>
                  {F.physTests.map((t,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${S.slate200}`}}>
                      <td style={{padding:'6px 10px',color:S.slate800,fontWeight:500}}>
                        {t.name}
                        {t.cols.length>1&&(
                          <div style={{fontSize:10,color:S.slate400}}>
                            {t.cols[0]} / {t.cols[1]}
                          </div>
                        )}
                      </td>
                      <td style={{padding:'5px 8px',textAlign:'center',color:S.slate500,fontFamily:'monospace',fontSize:11}}>{t.code}</td>
                      <td style={{padding:'5px 8px',color:S.slate400,fontSize:11,fontStyle:'italic'}}>{t.method}</td>
                      <td style={{padding:'4px 8px',background:'#EFF6FF'}}>
                        <input value={t.v1} onChange={e=>setPH(i,'v1',e.target.value)}
                          placeholder={t.cols[0]||'—'}
                          style={{...cellInp(70),width:70}}/>
                      </td>
                      <td style={{padding:'4px 8px',background:'#EFF6FF'}}>
                        {t.cols.length>1
                          ? <input value={t.v2} onChange={e=>setPH(i,'v2',e.target.value)}
                              placeholder={t.cols[1]||'—'}
                              style={{...cellInp(70),width:70}}/>
                          : <span style={{color:S.slate300,fontSize:11}}>—</span>}
                      </td>
                      <td style={{padding:'4px 8px'}}>
                        <input value={t.req} onChange={e=>setPH(i,'req',e.target.value)}
                          placeholder="Spec..." style={{...cellInp(80),width:80}}/>
                      </td>
                      <td style={{padding:'5px 8px',textAlign:'center'}}>
                        <select value={t.result} onChange={e=>setPH(i,'result',e.target.value)}
                          style={{...cellInp(70),width:70,cursor:'pointer',
                            background:t.result==='PASS'?S.greenBg:t.result==='FAIL'?S.redBg:S.white,
                            color:t.result==='PASS'?S.green:t.result==='FAIL'?S.red:S.slate400,fontWeight:700}}>
                          <option value="">—</option>
                          <option value="PASS">PASS</option>
                          <option value="FAIL">FAIL</option>
                          <option value="N/A">N/A</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </_MCard>

          {/* ── WASH INFORMATION ── */}
          <_MCard>
            <_MSH title="Wash Information & Additional Components" color="#00897B"/>
            <_MGrid gap={14}>
              <LR l="Laundry"><TI k="laundry" ph="Laundry name / location..."/></LR>
              <LR l="Wash Recipe" span={2}>
                <textarea value={F.washRecipe} onChange={e=>set('washRecipe',e.target.value)}
                  rows={3} placeholder="Full wash recipe..."
                  style={{...inp('100%'),width:'100%',resize:'vertical'}}/>
              </LR>
              <LR l="Additional Component"><TI k="addComponent" ph="Component name..."/></LR>
              <LR l="Source"><TI k="addSource" ph="Source..."/></LR>
              <LR l="Quality"><TI k="addQuality" ph="Quality..."/></LR>
              <LR l="Composition"><TI k="addComposition" ph="Composition..."/></LR>
            </_MGrid>
          </_MCard>

          {/* ── COMMENTS ── */}
          <_MCard>
            <_MSH title="Comments" color="#00897B"/>
            <_MGrid gap={14}>
              <LR l="Supplier Comments" span={2}>
                <textarea value={F.supplierComments} onChange={e=>set('supplierComments',e.target.value)}
                  rows={3} placeholder="Supplier comments..." style={{...inp('100%'),width:'100%',resize:'vertical'}}/>
              </LR>
              <LR l="Buyer Comments" span={2}>
                <textarea value={F.buyerComments} onChange={e=>set('buyerComments',e.target.value)}
                  rows={3} placeholder="Buyer / Technical team comments..." style={{...inp('100%'),width:'100%',resize:'vertical'}}/>
              </LR>
            </_MGrid>
          </_MCard>

          {/* Save */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
            padding:'14px 20px',background:S.white,borderRadius:12,
            border:`1px solid ${S.slate200}`}}>
            <_MRes result={F.physColFastResult}/>
            <_MBtn onClick={handleSave} disabled={saving} color="#00897B">
              {saved?'✓ Saved':saving?'Saving…':'💾 Save FPT Report'}
            </_MBtn>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  FABRIC PRE-PRODUCTION CHECKLIST
//  Based on industry-standard pre-production meeting document
//  All company / factory / person names removed
// ══════════════════════════════════════════════════════════════
function PreProdChecklist({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('preprod');
  const [view,setView]=React.useState('form');

  const PHYS_CODES=[
    {code:'P1A', name:'Stability (Washing)'},
    {code:'P1B', name:'Spirality'},
    {code:'P4C', name:'Colour Loss, Collars & Cuffs'},
    {code:'P5',  name:'Wascator Durability Wash'},
    {code:'P5L', name:'Label Durability (×5 washes)'},
    {code:'P8',  name:'WIRA Steam Stability'},
    {code:'P10', name:'Thermal Stability'},
    {code:'P11', name:'Tensile Strength'},
    {code:'P12', name:'Fabric Slippage'},
    {code:'P15', name:'Extension and Recovery'},
    {code:'P15A',name:'Extension and Modulus'},
    {code:'P18A',name:'Pilling Drum'},
    {code:'P19', name:'Martindale Abrasion'},
    {code:'P21A',name:'Snagging'},
    {code:'P22', name:'Crease Recovery'},
    {code:'P23', name:'Spray Rating'},
    {code:'P26', name:'Accelerator'},
    {code:'P29', name:'Tear Strength'},
    {code:'P65', name:'Weight (Ambient)'},
    {code:'P66', name:'Dry Clean Stability'},
    {code:'P91', name:'Easy to Iron'},
    {code:'P116',name:'Flammability'},
    {code:'P117',name:'Surface Flash Flame'},
    {code:'P134',name:'Non Iron'},
    {code:'FAST',name:'Finger Print Profile (Tailored Fabrics)'},
    {code:'',    name:'Teflon'},
    {code:'',    name:'Composition'},
    {code:'',    name:'Other'},
  ];
  const CF_CODES=[
    {code:'C4A', name:'Detergent Washing'},
    {code:'C5',  name:'Dry Cleaning'},
    {code:'C6',  name:'Water at 37°C'},
    {code:'C7',  name:'Perspiration (Acid and Alkali)'},
    {code:'C8',  name:'Dry Rubbing'},
    {code:'C9',  name:'Light'},
    {code:'C9A', name:'Wet Alkali Light'},
    {code:'C10', name:'Perborate'},
    {code:'C10A',name:'Oxidative Bleach Damage'},
    {code:'C11', name:'Hydrolysis'},
    {code:'C15', name:'Print Durability'},
    {code:'C18B',name:'Released Formaldehyde'},
    {code:'C26', name:'Contrast Trims'},
    {code:'C37', name:'Chlorinated Water'},
    {code:'C20B',name:'Elusive Yellowing'},
  ];

  const DYES={pieceDyeSolid:false,yarnDyed:false,fibreDyed:false,pieceCrossDye:false,garmentDyed:false,printed:false};

  const YN_QUESTIONS=[
    // FABRIC QUALITY
    {section:'Fabric Quality', key:'fabricSupplierNew',     q:'Is fabric supplier new? (If yes, state quantity produced)'},
    {section:'Fabric Quality', key:'dyeHousesConfirmed',    q:'All dye houses, printers, tanneries, laundries and finishers confirmed?'},
    {section:'Fabric Quality', key:'chemComplianceConf',    q:'Have all parties confirmed chemical compliance (C99 / REACH / OEKO-TEX)?'},
    {section:'Fabric Quality', key:'hasTestingLab',         q:'Does supplier have an accredited testing laboratory?'},
    {section:'Fabric Quality', key:'verivideBox',           q:'Is supplier using Verivide CAC 60 or 150 lightbox for shade assessment?'},
    {section:'Fabric Quality', key:'spectrophotometer',     q:'Does supplier have instrumental colour measurement (spectrophotometer)?'},
    {section:'Fabric Quality', key:'labAccredited',         q:'Is the laboratory ISO/IEC 17025 accredited?'},
    // PROCESSING
    {section:'Processing',     key:'finishesCompliant',     q:'Do all finishes applied comply with chemical compliance standard?'},
    {section:'Processing',     key:'formaldehydeUsed',      q:'Has formaldehyde been used in the finish?'},
    {section:'Processing',     key:'formaldehydeReport',    q:'Is a free and released formaldehyde test report available (C18A & B)?'},
    {section:'Processing',     key:'pileDirection',         q:'Is pile direction confirmed — rough up or smooth down?'},
    {section:'Processing',     key:'fabricPrinted',         q:'Is fabric printed / embroidered?'},
    {section:'Processing',     key:'printEmbDir',           q:'Is print / embroidery 1-way or 2-way confirmed?'},
    {section:'Processing',     key:'printDurability',       q:'Has print durability / label durability for embroidery been carried out?'},
    {section:'Processing',     key:'printRecordsKept',      q:'Are records of print continuity kept?'},
    {section:'Processing',     key:'garmentDyed',           q:'Is garment dyed?'},
    {section:'Processing',     key:'recipeEvalDone',        q:'Have all recipe evaluation tests been carried out?'},
    {section:'Processing',     key:'garmentWashed',         q:'Is garment washed?'},
    {section:'Processing',     key:'ethicalAuditLaundry',   q:'Has an ethical audit of the laundry been carried out?'},
    {section:'Processing',     key:'colourStdToLaundry',    q:'Has an approved colour, handle and appearance standard been submitted to the laundry?'},
    {section:'Processing',     key:'shadeBandsRequired',    q:'Are shade bands required?'},
    // FABRIC TO GARMENT
    {section:'Fabric to Garment', key:'moreThanOneFabric',  q:'Is garment made from more than 1 fabric? (Differential shrinkage must be evaluated)'},
    {section:'Fabric to Garment', key:'crossStainingRisk',  q:'Are there any critical coloured trims that could lead to cross staining on fabric body?'},
    {section:'Fabric to Garment', key:'multipleFabricStain',q:'Is there more than 1 fabric in the garment that could lead to cross staining issues?'},
    {section:'Fabric to Garment', key:'p5Required',         q:'Is P5 label durability required?'},
    {section:'Fabric to Garment', key:'careCodeSuitable',   q:'Is the agreed care code suitable for this fabric?'},
    {section:'Fabric to Garment', key:'wearerTrialRequired',q:'Are wearer trials required for fabric evaluation?'},
    // TESTING
    {section:'Testing Requirements', key:'physStandardsAgreed', q:'Have physical and colour fastness performance standards been agreed?'},
    {section:'Testing Requirements', key:'meetsStandards',      q:'Does this fabric quality meet the agreed performance standards?'},
    {section:'Testing Requirements', key:'borderlineApproved',  q:'Are there any borderline / fail results commercially approved by technical team?'},
    {section:'Testing Requirements', key:'bulkTestFreqAgreed',  q:'Has the frequency of bulk testing been agreed?'},
  ];

  const fresh=()=>({
    id:_uid(), monType:'preprod', date:_TODAY(),
    // Header
    style:filters?.style||'', po:filters?.po||'',
    buyer:filters?.buyer||'', colour:filters?.colour||'',
    season:'', department:'', garmentDescription:'',
    fabricQuality:'', estimatedQty:'', fabricDescription:'',
    garmentSupplier:'', fabricMill:'',
    converter:'', dyerFinisher:'',
    converterAddress:'', dyerFinisherAddress:'',
    laundryName:'', contactDetails:'', emailAddress:'',
    // Critical Path
    techSubDueDate:'', techSubActual:'',
    labDipApproved:'', labDipActual:'',
    startDyeingDate:'', startDyeingActual:'',
    fabricExMillDate:'', fabricExMillActual:'',
    bulkSubmissionDate:'', bulkSubmissionActual:'',
    garmentCutDate:'', garmentCutActual:'',
    garmentFobDate:'', garmentFobActual:'',
    shadeBandApproved:'', shadeBandActual:'',
    // Meeting
    personnel:'', signedBy:'',
    actionItems: Array.from({length:12},(_,i)=>({id:i+1,item:'',actionBy:'',dateBy:''})),
    // YES/NO questions
    ynAnswers: Object.fromEntries(YN_QUESTIONS.map(q=>[q.key,''])),
    ynComments: Object.fromEntries(YN_QUESTIONS.map(q=>[q.key+'_comment',''])),
    // Raw Material
    fibreSupplier:'', spinner:'',
    yarnCountDecitex:'', spinningSystem:'',
    dyeMethod:{...DYES},
    dyeMachineType:'', printMachineType:'',
    printerDetails:'', processingRouteDetail:'',
    processingRouteItems: Array.from({length:8},()=>({step:'',description:'',status:''})),
    // Dyeing additional
    garmentDyedLocation:'',
    pilotWashPlanDate:'', garmentsForShadeBand:'', refinishMethod:'',
    wearerTrialComment:'',
    // Testing
    physCodes: PHYS_CODES.map(c=>({...c,suppAccred:'',stdRequired:'',frequency:'',required:false})),
    cfCodes:   CF_CODES.map(c=>({...c,suppAccred:'',stdRequired:'',frequency:'',required:false})),
    testFrequencyDetail:'',
    additionalComments:'',
  });

  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const setYN=(k,v)=>setF(f=>({...f,ynAnswers:{...f.ynAnswers,[k]:v}}));
  const setYNComment=(k,v)=>setF(f=>({...f,ynComments:{...f.ynComments,[k+'_comment']:v}}));
  const setDyeMethod=(k,v)=>setF(f=>({...f,dyeMethod:{...f.dyeMethod,[k]:v}}));
  const setPhys=(i,k,v)=>setF(f=>({...f,physCodes:f.physCodes.map((c,ci)=>ci===i?{...c,[k]:v}:c)}));
  const setCF=(i,k,v)=>setF(f=>({...f,cfCodes:f.cfCodes.map((c,ci)=>ci===i?{...c,[k]:v}:c)}));
  const setAction=(i,k,v)=>setF(f=>({...f,actionItems:f.actionItems.map((a,ai)=>ai===i?{...a,[k]:v}:a)}));
  const setRoute=(i,k,v)=>setF(f=>({...f,processingRouteItems:f.processingRouteItems.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));

  const recs=(db.monitoring||[]).filter(r=>r.monType==='preprod');

  const handleSave=async()=>{
    await doSave({...F,savedAt:new Date().toISOString()},db,setDb);
    setF(fresh());
  };

  const inp=(extra={})=>({padding:'6px 10px',borderRadius:6,border:`1px solid ${S.slate200}`,
    fontSize:12,background:S.white,fontFamily:'inherit',width:'100%',...extra});
  const LR=({l,children,span=1})=>(
    <div style={{display:'flex',flexDirection:'column',gap:3,gridColumn:`span ${span}`}}>
      <label style={{fontSize:10,fontWeight:700,color:S.slate500,textTransform:'uppercase'}}>{l}</label>
      {children}
    </div>
  );
  const SH=({title,color='#6A1B9A'})=>(
    <div style={{padding:'8px 14px',background:color+'18',borderLeft:`3px solid ${color}`,
      borderRadius:4,fontSize:12,fontWeight:700,color:color,textTransform:'uppercase',letterSpacing:'.4px'}}>
      {title}
    </div>
  );

  // Group YES/NO questions by section
  const sections=[...new Set(YN_QUESTIONS.map(q=>q.section))];

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'#6A1B9A',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:4}}>
            FABRIC PRE-PRODUCTION
          </div>
          <h2 style={{fontSize:18,fontWeight:800,color:S.slate800}}>Pre-Production Checklist</h2>
          <p style={{fontSize:12,color:S.slate500}}>Complete before bulk cutting — fabric quality, processing, testing requirements</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:11,color:S.slate400}}>Version 1.0</span>
          <_MBtn sm outline onClick={()=>setView('form')} color="#6A1B9A">＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color="#6A1B9A">📋 Records ({recs.length})</_MBtn>
        </div>
      </div>

      {view==='history'?(
        <_MCard>
          <_MSH title="Pre-Production Checklist Records" color="#6A1B9A"/>
          <_MTbl headers={['Date','Style','Buyer','Season','Fabric Quality','Signed By']}
            rows={recs.map(r=>[r.date,r.style||'—',r.buyer||'—',r.season||'—',r.fabricQuality||'—',r.signedBy||'—'])}
            emptyMsg="No pre-production checklists yet."/>
        </_MCard>
      ):(
        <>
          {/* ── HEADER ── */}
          <_MCard>
            <_MSH title="Order & Supplier Details" color="#6A1B9A"/>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
              {[
                ['date','Date','date'],['style','Design / Style No.','text'],
                ['po','PO No.','text'],['buyer','Buyer','text'],
                ['season','Season','text'],['department','Department','text'],
                ['colour','Colour','text'],['garmentDescription','Garment Description','text'],
                ['fabricQuality','Fabric Quality','text'],['estimatedQty','Estimated Buying Qty','text'],
                ['fabricDescription','Fabric Description (woven/knit)','text'],
              ].map(([k,l,type])=>(
                <LR key={k} l={l}>
                  <input type={type} value={F[k]||''} onChange={e=>set(k,e.target.value)}
                    style={inp()}/>
                </LR>
              ))}
              <div style={{gridColumn:'1/-1',marginTop:4}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  {[
                    ['garmentSupplier','Garment Supplier'],['fabricMill','Fabric Mill'],
                    ['converter','Converter (if applicable)'],['dyerFinisher','Dyer & Finisher'],
                    ['converterAddress','Converter Address'],['dyerFinisherAddress','Dyer & Finisher Address'],
                    ['laundryName','Garment Laundry Name'],['contactDetails','Contact Details'],
                    ['emailAddress','Email Address / Tel'],
                  ].map(([k,l])=>(
                    <LR key={k} l={l}><input value={F[k]||''} onChange={e=>set(k,e.target.value)} style={inp()}/></LR>
                  ))}
                </div>
              </div>
            </div>
          </_MCard>

          {/* ── CRITICAL PATH ── */}
          <_MCard>
            <_MSH title="Critical Path — Plan vs Actual" color="#6A1B9A"/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:S.slate50}}>
                  {['Milestone','Plan Date','Actual Date'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',fontWeight:700,color:S.slate500,fontSize:11,
                      textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,textAlign:'left'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {[
                    ['Technical Submission Due Date','techSubDueDate','techSubActual'],
                    ['Lab Dip Approved','labDipApproved','labDipActual'],
                    ['Start Dyeing Date','startDyeingDate','startDyeingActual'],
                    ['Fabric Ex-Mill Date','fabricExMillDate','fabricExMillActual'],
                    ['Bulk Submission Date','bulkSubmissionDate','bulkSubmissionActual'],
                    ['Garment Cut Date','garmentCutDate','garmentCutActual'],
                    ['Garment FOB Date','garmentFobDate','garmentFobActual'],
                    ['Shade Band Approved Date','shadeBandApproved','shadeBandActual'],
                  ].map(([label,planKey,actualKey])=>(
                    <tr key={label} style={{borderBottom:`1px solid ${S.slate200}`}}>
                      <td style={{padding:'7px 12px',color:S.slate800,fontWeight:500}}>{label}</td>
                      <td style={{padding:'5px 10px'}}>
                        <input type="date" value={F[planKey]||''} onChange={e=>set(planKey,e.target.value)}
                          style={{...inp(),width:140}}/>
                      </td>
                      <td style={{padding:'5px 10px'}}>
                        <input type="date" value={F[actualKey]||''} onChange={e=>set(actualKey,e.target.value)}
                          style={{...inp(),width:140,
                            background:F[actualKey]&&F[planKey]&&F[actualKey]>F[planKey]?S.redBg:F[actualKey]?S.greenBg:S.white}}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </_MCard>

          {/* ── ACTION ITEMS (Meeting) ── */}
          <_MCard>
            <_MSH title="Pre-Production Meeting — Action Items" color="#6A1B9A"/>
            <div style={{marginBottom:10,fontSize:12,color:S.slate500}}>
              Record action items discussed at the pre-production meeting.
              Personnel attending: fabric technologist, supplier technologist, fabric mill quality personnel.
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:S.slate50}}>
                  {['#','Action Item','Action By','Date By'].map(h=>(
                    <th key={h} style={{padding:'8px 10px',fontWeight:700,color:S.slate500,fontSize:10,
                      textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,textAlign:'left'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {F.actionItems.map((a,i)=>(
                    <tr key={a.id} style={{borderBottom:`1px solid ${S.slate200}`}}>
                      <td style={{padding:'5px 10px',color:S.slate400,fontWeight:700,textAlign:'center',width:30}}>{a.id}</td>
                      <td style={{padding:'4px 8px'}}>
                        <input value={a.item} onChange={e=>setAction(i,'item',e.target.value)} placeholder="Action item..."
                          style={{...inp()}}/>
                      </td>
                      <td style={{padding:'4px 8px'}}>
                        <input value={a.actionBy} onChange={e=>setAction(i,'actionBy',e.target.value)} placeholder="Responsible..."
                          style={{...inp(),width:130}}/>
                      </td>
                      <td style={{padding:'4px 8px'}}>
                        <input type="date" value={a.dateBy||''} onChange={e=>setAction(i,'dateBy',e.target.value)}
                          style={{...inp(),width:130}}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{marginTop:12,display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <LR l="Personnel Attending">
                <textarea value={F.personnel} onChange={e=>set('personnel',e.target.value)}
                  rows={3} placeholder="List names and roles of all attendees..."
                  style={{...inp(),resize:'vertical'}}/>
              </LR>
              <LR l="Signed and Agreed By">
                <textarea value={F.signedBy} onChange={e=>set('signedBy',e.target.value)}
                  rows={3} placeholder="Signatures / names of approving parties..."
                  style={{...inp(),resize:'vertical'}}/>
              </LR>
            </div>
          </_MCard>

          {/* ── YES/NO QUESTIONS (grouped) ── */}
          {sections.map(section=>{
            const qs=YN_QUESTIONS.filter(q=>q.section===section);
            const color=section==='Fabric Quality'?'#00897B':section==='Processing'?'#E65100':section==='Fabric to Garment'?'#2E7D32':'#6A1B9A';
            return(
              <_MCard key={section}>
                <_MSH title={section} color={color}/>
                {qs.map(q=>{
                  const ans=F.ynAnswers[q.key]||'';
                  const showComment=ans==='NO'||ans==='YES';
                  return(
                    <div key={q.key} style={{padding:'8px 0',borderBottom:`1px solid ${S.slate200}`}}>
                      <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
                        <div style={{flex:1,fontSize:12,color:S.slate800,paddingTop:6,lineHeight:1.4}}>{q.q}</div>
                        <div style={{display:'flex',gap:5,flexShrink:0}}>
                          {['YES','NO','N/A'].map(v=>(
                            <button key={v} onClick={()=>setYN(q.key,v)}
                              style={{padding:'5px 14px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',
                                border:`1px solid ${ans===v?(v==='YES'?S.green:v==='NO'?S.red:S.slate400):S.slate200}`,
                                background:ans===v?(v==='YES'?S.greenBg:v==='NO'?S.redBg:S.slate100):S.white,
                                color:ans===v?(v==='YES'?S.green:v==='NO'?S.red:S.slate500):S.slate400}}>
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                      {showComment&&(
                        <input value={F.ynComments[q.key+'_comment']||''} onChange={e=>setYNComment(q.key,e.target.value)}
                          placeholder={ans==='NO'?'State reason / action required...':'Additional details...'}
                          style={{...inp(),marginTop:5,borderColor:ans==='NO'?S.redBd:S.greenBd,
                            background:ans==='NO'?S.redBg:S.greenBg}}/>
                      )}
                    </div>
                  );
                })}
              </_MCard>
            );
          })}

          {/* ── RAW MATERIAL ── */}
          <_MCard>
            <_MSH title="Raw Material" color="#00897B"/>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
              {[['fibreSupplier','Fibre Supplier'],['spinner','Spinner'],
                ['yarnCountDecitex','Yarn Count & Decitex (inc. no. of filaments)'],
                ['spinningSystem','Spinning System (e.g. Ring or OE; carded/combed)'],
              ].map(([k,l])=>(
                <LR key={k} l={l}><input value={F[k]||''} onChange={e=>set(k,e.target.value)} style={inp()}/></LR>
              ))}
            </div>
          </_MCard>

          {/* ── DYEING METHOD ── */}
          <_MCard>
            <_MSH title="Dyeing Method" color="#E65100"/>
            <div style={{display:'flex',flexWrap:'wrap',gap:12,marginBottom:14}}>
              {[
                ['pieceDyeSolid','Piece Dye — Solid'],['yarnDyed','Yarn Dyed'],['fibreDyed','Fibre Dyed'],
                ['pieceCrossDye','Piece Dye / Cross Dye'],['garmentDyed','Garment Dyed'],['printed','Printed'],
              ].map(([k,l])=>(
                <label key={k} style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',
                  padding:'7px 12px',borderRadius:8,border:`1px solid ${F.dyeMethod[k]?S.amber:S.slate200}`,
                  background:F.dyeMethod[k]?S.amberBg:S.white,fontSize:12,fontWeight:F.dyeMethod[k]?700:400,
                  color:F.dyeMethod[k]?S.amber:S.slate500}}>
                  <input type="checkbox" checked={!!F.dyeMethod[k]} onChange={e=>setDyeMethod(k,e.target.checked)}
                    style={{accentColor:S.amber,width:15,height:15}}/>
                  {l}
                </label>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
              {[['dyeMachineType','Dye Machine — State type of dyestuffs'],
                ['printMachineType','Print Machine — State type of dyestuffs'],
                ['printerDetails','Printer Details (name and location)'],
              ].map(([k,l])=>(
                <LR key={k} l={l}><input value={F[k]||''} onChange={e=>set(k,e.target.value)} style={inp()}/></LR>
              ))}
              <LR l="Full Detail of Processing Route" span={2}>
                <textarea value={F.processingRouteDetail} onChange={e=>set('processingRouteDetail',e.target.value)}
                  rows={4} placeholder="Detail full processing route step by step..."
                  style={{...inp(),resize:'vertical'}}/>
              </LR>
            </div>
          </_MCard>

          {/* ── TESTING REQUIREMENTS ── */}
          <_MCard>
            <_MSH title="Bulk Testing Requirements — Physical Performance Codes" color="#6A1B9A"/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:S.slate50}}>
                  {['Req?','Code','Test Name','Supplier Accred. Y/N','Standard Required','Frequency'].map(h=>(
                    <th key={h} style={{padding:'7px 10px',fontWeight:700,color:S.slate500,fontSize:10,
                      textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,textAlign:'left'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {F.physCodes.map((c,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${S.slate200}`,
                      background:c.required?'#FAF5FF':S.white}}>
                      <td style={{padding:'5px 8px',textAlign:'center'}}>
                        <input type="checkbox" checked={!!c.required} onChange={e=>setPhys(i,'required',e.target.checked)}
                          style={{accentColor:'#6A1B9A',width:15,height:15,cursor:'pointer'}}/>
                      </td>
                      <td style={{padding:'5px 8px',fontFamily:'monospace',color:'#6A1B9A',fontWeight:700,fontSize:11}}>{c.code}</td>
                      <td style={{padding:'5px 8px',color:S.slate800}}>{c.name}</td>
                      {['suppAccred','stdRequired','frequency'].map(k=>(
                        <td key={k} style={{padding:'4px 6px'}}>
                          <input value={c[k]||''} onChange={e=>setPhys(i,k,e.target.value)}
                            style={{width:'100%',minWidth:90,padding:'4px 7px',borderRadius:5,
                              border:`1px solid ${S.slate200}`,fontSize:11,background:c.required?'#FAF5FF':S.white}}/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </_MCard>

          <_MCard>
            <_MSH title="Bulk Testing Requirements — Colour Fastness Codes" color="#00897B"/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:S.slate50}}>
                  {['Req?','Code','Test Name','Supplier Accred. Y/N','Standard Required','Frequency'].map(h=>(
                    <th key={h} style={{padding:'7px 10px',fontWeight:700,color:S.slate500,fontSize:10,
                      textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,textAlign:'left'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {F.cfCodes.map((c,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${S.slate200}`,
                      background:c.required?'#EFF6FF':S.white}}>
                      <td style={{padding:'5px 8px',textAlign:'center'}}>
                        <input type="checkbox" checked={!!c.required} onChange={e=>setCF(i,'required',e.target.checked)}
                          style={{accentColor:'#00897B',width:15,height:15,cursor:'pointer'}}/>
                      </td>
                      <td style={{padding:'5px 8px',fontFamily:'monospace',color:'#00897B',fontWeight:700,fontSize:11}}>{c.code}</td>
                      <td style={{padding:'5px 8px',color:S.slate800}}>{c.name}</td>
                      {['suppAccred','stdRequired','frequency'].map(k=>(
                        <td key={k} style={{padding:'4px 6px'}}>
                          <input value={c[k]||''} onChange={e=>setCF(i,k,e.target.value)}
                            style={{width:'100%',minWidth:90,padding:'4px 7px',borderRadius:5,
                              border:`1px solid ${S.slate200}`,fontSize:11,background:c.required?'#EFF6FF':S.white}}/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </_MCard>

          {/* ── ADDITIONAL COMMENTS ── */}
          <_MCard>
            <_MSH title="Additional Comments / Additional Testing" color="#6A1B9A"/>
            <textarea value={F.additionalComments} onChange={e=>set('additionalComments',e.target.value)}
              rows={4} placeholder="Additional comments, agreed actions, additional testing requirements..."
              style={{...inp(),resize:'vertical'}}/>
          </_MCard>

          {/* Save */}
          <div style={{display:'flex',justifyContent:'flex-end',padding:'14px 20px',
            background:S.white,borderRadius:12,border:`1px solid ${S.slate200}`}}>
            <_MBtn onClick={handleSave} disabled={saving} color="#6A1B9A">
              {saved?'✓ Saved':saving?'Saving…':'💾 Save Pre-Production Checklist'}
            </_MBtn>
          </div>
        </>
      )}
    </div>
  );
}



// ══════════════════════════════════════════════════════════════
//  GPT — GARMENT PERFORMANCE TEST
//  Third-party lab report for finished garment testing
// ══════════════════════════════════════════════════════════════
function GPTReport({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('gpt');
  const [view,setView]=React.useState('form');
  const [extracting,setExtracting]=React.useState(false);
  const [extractMsg,setExtractMsg]=React.useState('');
  const fileRef=React.useRef();

  const GPT_TESTS=[
    // DIMENSIONAL STABILITY ON GARMENT
    {group:'Dimensional Stability',code:'P1A', name:'Dimensional Change after Washing — Length', method:'AATCC 135 / ISO 6330',   unit:'%',    req:'Max ±5%'},
    {group:'Dimensional Stability',code:'P1A', name:'Dimensional Change after Washing — Width',  method:'AATCC 135 / ISO 6330',   unit:'%',    req:'Max ±5%'},
    {group:'Dimensional Stability',code:'P1B', name:'Spirality / Torque after Washing',           method:'AATCC 179',              unit:'%',    req:'Max 5%'},
    {group:'Dimensional Stability',code:'',    name:'Appearance after Washing',                   method:'AATCC 143 / ISO 15487',  unit:'Grade 1-5',req:'Min 3'},
    // COLOUR FASTNESS ON GARMENT
    {group:'Colour Fastness',      code:'C4A', name:'Colour Fastness to Washing — Change',        method:'AATCC 61 / ISO 105-C06', unit:'Grade 1-5',req:'Min 3-4'},
    {group:'Colour Fastness',      code:'C4A', name:'Colour Fastness to Washing — Staining',      method:'AATCC 61 / ISO 105-C06', unit:'Grade 1-5',req:'Min 3-4'},
    {group:'Colour Fastness',      code:'C8',  name:'Colour Fastness to Crocking — Dry',          method:'AATCC 8 / ISO 105-X12',  unit:'Grade 1-5',req:'Min 4'},
    {group:'Colour Fastness',      code:'C8',  name:'Colour Fastness to Crocking — Wet',          method:'AATCC 8 / ISO 105-X12',  unit:'Grade 1-5',req:'Min 3'},
    {group:'Colour Fastness',      code:'C7',  name:'Colour Fastness to Perspiration — Acid',     method:'AATCC 15 / ISO 105-E04', unit:'Grade 1-5',req:'Min 3-4'},
    {group:'Colour Fastness',      code:'C7',  name:'Colour Fastness to Perspiration — Alkaline', method:'AATCC 15 / ISO 105-E04', unit:'Grade 1-5',req:'Min 3-4'},
    {group:'Colour Fastness',      code:'P18A',name:'Pilling Resistance',                          method:'ASTM D3512 / ISO 12945', unit:'Grade 1-5',req:'Min 3'},
    // SEAM & CONSTRUCTION
    {group:'Seam & Construction',  code:'',    name:'Seam Strength — Chain Stitch',               method:'ISO 13935-1',            unit:'N',    req:'Min 200N'},
    {group:'Seam & Construction',  code:'',    name:'Seam Strength — Lock Stitch',                method:'ISO 13935-1',            unit:'N',    req:'Min 200N'},
    {group:'Seam & Construction',  code:'P12', name:'Seam Slippage — Warp',                       method:'ISO 13936-1',            unit:'mm',   req:'Min 6mm'},
    {group:'Seam & Construction',  code:'P12', name:'Seam Slippage — Weft',                       method:'ISO 13936-1',            unit:'mm',   req:'Min 6mm'},
    // TRIMS & ACCESSORIES
    {group:'Trims & Accessories',  code:'',    name:'Button Attachment Strength',                  method:'ASTM D565 / EN 12127',   unit:'N',    req:'Min 45N'},
    {group:'Trims & Accessories',  code:'',    name:'Buttonhole Strength',                        method:'ISO 13938',              unit:'N',    req:'Min 45N'},
    {group:'Trims & Accessories',  code:'',    name:'Snap / Rivet Attachment Strength',           method:'ASTM D565',              unit:'N',    req:'Min 45N'},
    {group:'Trims & Accessories',  code:'',    name:'Zipper Tensile Strength',                    method:'ISO 2062',               unit:'N',    req:'Min 45N'},
    {group:'Trims & Accessories',  code:'',    name:'Zipper Smoothness',                          method:'JIS S3015',              unit:'Cycles',req:'Min 500'},
    {group:'Trims & Accessories',  code:'P5',  name:'Label Legibility after 5 Washes',            method:'ISO 6330',               unit:'Grade 1-5',req:'Min 3'},
    // CHEMICAL
    {group:'Chemical / Safety',    code:'',    name:'pH Value',                                   method:'AATCC 81 / ISO 3071',    unit:'pH',   req:'4.0-7.5'},
    {group:'Chemical / Safety',    code:'C18B',name:'Formaldehyde Content',                       method:'AATCC 112 / ISO 14184',  unit:'ppm',  req:'<75 ppm (EU)'},
    {group:'Chemical / Safety',    code:'',    name:'Nickel Release (metal trims)',               method:'EN 1811',                unit:'µg/cm²/wk',req:'<0.5'},
  ];

  const fresh=()=>({
    id:_uid(),monType:'gpt',date:_TODAY(),
    style:filters?.style||'',po:filters?.po||'',
    buyer:filters?.buyer||'',colour:filters?.colour||'',
    labName:'',reportNo:'',testDate:'',receivedDate:'',
    garmentDescription:'',sampleRef:'',sizesTested:'',washCare:'',countryOfTest:'',
    tests:GPT_TESTS.map(t=>({...t,result:'',status:'PENDING'})),
    pdfFileName:'',pdfExtracted:false,overallRemarks:'',
  });
  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const setTest=(i,k,v)=>setF(f=>{
    const tests=[...f.tests];
    tests[i]={...tests[i],[k]:v};
    if(k==='result'){
      const req=tests[i].req||'';
      const val=parseFloat(v);
      if(req.includes('Min ')&&!isNaN(val)){
        const minV=parseFloat(req.replace(/Min\s*/,'').replace(/[NmpHg²\/wk]*/g,''));
        if(!isNaN(minV)) tests[i].status=val>=minV?'PASS':'FAIL';
      }
      if(req.includes('Max')&&!isNaN(val)){
        const maxV=parseFloat(req.replace('Max','').replace(/[%Nmppm]*/g,''));
        if(!isNaN(maxV)) tests[i].status=Math.abs(val)<=Math.abs(maxV)?'PASS':'FAIL';
      }
    }
    return {...f,tests};
  });

  const recs=(db.monitoring||[]).filter(r=>r.monType==='gpt');
  const passCount=F.tests.filter(t=>t.status==='PASS').length;
  const failCount=F.tests.filter(t=>t.status==='FAIL').length;
  const autoRes=()=>failCount>0?'FAIL':F.tests.some(t=>t.status==='PENDING')?'PENDING':'PASS';
  const groups=[...new Set(GPT_TESTS.map(t=>t.group))];

  async function handlePDF(e){
    const file=e.target.files[0];if(!file)return;
    set('pdfFileName',file.name);setExtracting(true);setExtractMsg('Reading PDF...');
    try{
      const b64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(',')[1]);r.onerror=rej;r.readAsDataURL(file);});
      setExtractMsg('Extracting garment test results...');
      const testList=F.tests.map((t,i)=>`${i}. [${t.code}] ${t.name} (${t.method}) req:${t.req}`).join('\n');
      const resp=await fetch('https://api.anthropic.com/v1/messages',{method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({model:'claude-sonnet-4-6',max_tokens:1500,
          messages:[{role:'user',content:[
            {type:'document',source:{type:'base64',media_type:'application/pdf',data:b64}},
            {type:'text',text:'Garment performance test report. Extract results for:\n'+testList+'\n\nAlso extract: labName, reportNo, testDate, garmentDescription, sizesTested, washCare\n\nReturn ONLY JSON: {"labName":"","reportNo":"","testDate":"","garmentDescription":"","sizesTested":"","washCare":"","tests":[{"index":0,"result":"","status":"PASS|FAIL|PENDING"}]}\n\nFor tests not found, set result="" and status="PENDING".'}
          ]}]})});
      const data=await resp.json();
      const raw=data.content?.find(c=>c.type==='text')?.text||'{}';
      const ex=JSON.parse(raw.replace(/```json|```/g,'').trim());
      setF(f=>{
        const tests=[...f.tests];
        (ex.tests||[]).forEach(et=>{if(et.index!=null&&tests[et.index]){if(et.result)tests[et.index].result=et.result;if(et.status&&et.status!=='PENDING')tests[et.index].status=et.status;}});
        return{...f,tests,pdfExtracted:true,labName:ex.labName||f.labName,reportNo:ex.reportNo||f.reportNo,testDate:ex.testDate||f.testDate,garmentDescription:ex.garmentDescription||f.garmentDescription,sizesTested:ex.sizesTested||f.sizesTested,washCare:ex.washCare||f.washCare};
      });
      setExtractMsg(`✓ Extracted ${(ex.tests||[]).filter(t=>t.result).length} results`);
    }catch(err){setExtractMsg('Extraction failed — fill manually.');}
    setExtracting(false);
  }

  const handleSave=async()=>{await doSave({...F,result:autoRes(),passCount,failCount,savedAt:new Date().toISOString()},db,setDb);setF(fresh());setExtractMsg('');};
  const sColor=s=>s==='PASS'?S.green:s==='FAIL'?S.red:S.slate400;
  const sBg=s=>s==='PASS'?S.greenBg:s==='FAIL'?S.redBg:S.slate50;

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'#2E7D32',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:4}}>GARMENT TECHNICAL SUBMISSION</div>
          <h2 style={{fontSize:16,fontWeight:800,color:S.slate800}}>Garment Performance Test (GPT)</h2>
          <p style={{fontSize:12,color:S.slate500}}>Third-party lab report — AATCC / ISO tests on finished garment</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <_MBtn sm outline onClick={()=>setView('form')} color="#2E7D32">＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color="#2E7D32">📋 Records ({recs.length})</_MBtn>
        </div>
      </div>
      {view==='history'?(
        <_MCard><_MSH title="GPT Records" color="#2E7D32"/>
          <_MTbl headers={['Date','Lab','Report No.','Style','Buyer','Pass','Fail','Result']}
            rows={recs.map(r=>[r.date,r.labName||'—',r.reportNo||'—',r.style||'—',r.buyer||'—',
              <span style={{color:S.green,fontWeight:700}}>{r.passCount||0}</span>,
              <span style={{color:S.red,fontWeight:700}}>{r.failCount||0}</span>,<_MRes result={r.result}/>])}
            emptyMsg="No GPT records yet."/>
        </_MCard>
      ):(
        <>
          <_MCard><_MSH title="Order & Laboratory Details" color="#2E7D32"/>
            <_MGrid gap={14}>
              {[['date','Date','date'],['style','Style No.','text'],['po','PO No.','text'],['buyer','Buyer','text'],['colour','Colour','text'],
                ['labName','Lab Name','text'],['reportNo','Lab Report No.','text'],['testDate','Test Date','date'],['receivedDate','Date Received','date'],
                ['garmentDescription','Garment Description','text'],['sampleRef','Sample Ref','text'],['sizesTested','Sizes Tested','text'],
                ['washCare','Wash Care','text'],['countryOfTest','Country of Test','text'],
              ].map(([k,l,type])=>(
                <div key={k} style={{display:'flex',flexDirection:'column',gap:3}}>
                  <label style={{fontSize:10,fontWeight:700,color:S.slate500,textTransform:'uppercase'}}>{l}</label>
                  <input type={type} value={F[k]||''} onChange={e=>set(k,e.target.value)}
                    style={{padding:'8px 10px',borderRadius:7,border:`1px solid ${S.slate200}`,fontSize:12,fontFamily:'inherit'}}/>
                </div>
              ))}
            </_MGrid>
          </_MCard>
          <_MCard><_MSH title="Upload Third-Party Lab Report PDF" color="#2E7D32"/>
            <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
              <input ref={fileRef} type="file" accept=".pdf" style={{display:'none'}} onChange={handlePDF}/>
              <_MBtn onClick={()=>fileRef.current.click()} disabled={extracting} color="#2E7D32">📎 {F.pdfFileName||'Upload GPT Lab Report (PDF)'}</_MBtn>
              {F.pdfFileName&&<span style={{fontSize:12,color:F.pdfExtracted?S.green:S.amber,fontWeight:600}}>{F.pdfExtracted?'✓ Data extracted':'📄 Uploaded'}</span>}
              {extracting&&<span style={{fontSize:12,color:'#065F46'}}>⏳ {extractMsg}</span>}
              {!extracting&&extractMsg&&<span style={{fontSize:12,color:extractMsg.includes('✓')?S.green:S.red,fontWeight:600}}>{extractMsg}</span>}
            </div>
          </_MCard>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
            {[['PASS',passCount,S.green,S.greenBg],['FAIL',failCount,S.red,S.redBg],['PENDING',F.tests.filter(t=>t.status==='PENDING').length,S.slate400,S.slate50]].map(([l,v,c,b])=>(
              <div key={l} style={{padding:'8px 16px',background:b,borderRadius:8,textAlign:'center',border:`1px solid ${c}22`,minWidth:80}}>
                <div style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
                <div style={{fontSize:10,fontWeight:700,color:c,textTransform:'uppercase'}}>{l}</div>
              </div>
            ))}
            <div style={{padding:'8px 18px',borderRadius:8,fontSize:14,fontWeight:800,
              background:autoRes()==='PASS'?S.greenBg:autoRes()==='FAIL'?S.redBg:S.amberBg,
              border:`2px solid ${autoRes()==='PASS'?S.greenBd:autoRes()==='FAIL'?S.redBd:S.amberBd}`,
              color:autoRes()==='PASS'?S.green:autoRes()==='FAIL'?S.red:S.amber}}>
              {autoRes()==='PASS'?'✅ ALL PASS':autoRes()==='FAIL'?'❌ FAIL':'⏳ Pending'}
            </div>
          </div>
          {groups.map(grp=>{
            const grpTests=F.tests.map((t,i)=>({...t,i})).filter(t=>t.group===grp);
            const gf=grpTests.filter(t=>t.status==='FAIL').length;
            return(
              <_MCard key={grp}><_MSH title={grp} color="#2E7D32"
                action={gf>0?<span style={{padding:'3px 10px',borderRadius:20,background:S.redBg,color:S.red,fontSize:11,fontWeight:700}}>{gf} FAIL</span>:null}/>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:S.slate50}}>
                      {['Test','Code','Method','Requirement','Result','Unit','Status'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',fontWeight:700,color:S.slate500,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {grpTests.map(t=>(
                        <tr key={t.i} style={{borderBottom:`1px solid ${S.slate200}`,background:sBg(t.status)}}>
                          <td style={{padding:'7px 10px',fontWeight:600,color:S.slate800,minWidth:180}}>{t.name}</td>
                          <td style={{padding:'7px 8px',fontFamily:'monospace',color:'#2E7D32',fontSize:11}}>{t.code}</td>
                          <td style={{padding:'7px 8px',color:S.slate400,fontSize:11,fontStyle:'italic',minWidth:130}}>{t.method}</td>
                          <td style={{padding:'7px 8px'}}>
                            <input value={t.req} onChange={e=>setTest(t.i,'req',e.target.value)}
                              style={{width:90,padding:'4px 7px',borderRadius:5,border:`1px solid ${S.slate200}`,fontSize:11}}/>
                          </td>
                          <td style={{padding:'7px 8px'}}>
                            <input value={t.result} onChange={e=>setTest(t.i,'result',e.target.value)} placeholder="Result..."
                              style={{width:80,padding:'4px 7px',borderRadius:5,fontSize:12,fontWeight:t.result?700:400,
                                border:`1px solid ${t.result?'#2E7D32':S.slate200}`,
                                background:t.result?'#E8F5E9':S.white,color:t.result?'#065F46':S.slate800}}/>
                          </td>
                          <td style={{padding:'7px 8px',color:S.slate500,fontSize:11,whiteSpace:'nowrap'}}>{t.unit}</td>
                          <td style={{padding:'7px 8px'}}>
                            <div style={{display:'flex',gap:3}}>
                              {['PASS','FAIL','—'].map(s=>(
                                <button key={s} onClick={()=>setTest(t.i,'status',s==='—'?'PENDING':s)}
                                  style={{padding:'3px 8px',borderRadius:5,fontSize:10,fontWeight:700,cursor:'pointer',
                                    border:`1px solid ${t.status===(s==='—'?'PENDING':s)?sColor(t.status):S.slate200}`,
                                    background:t.status===(s==='—'?'PENDING':s)?sBg(t.status):S.white,
                                    color:t.status===(s==='—'?'PENDING':s)?sColor(t.status):S.slate400}}>{s}</button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </_MCard>
            );
          })}
          <_MCard>
            <label style={{fontSize:11,fontWeight:700,color:S.slate500,textTransform:'uppercase'}}>Remarks</label>
            <textarea value={F.overallRemarks} onChange={e=>set('overallRemarks',e.target.value)} rows={3}
              placeholder="Lab comments, failed test actions, follow-up required..."
              style={{width:'100%',marginTop:6,padding:'9px 11px',borderRadius:8,border:`1px solid ${S.slate200}`,fontSize:12,resize:'vertical',fontFamily:'inherit'}}/>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:12}}>
              <_MRes result={autoRes()}/>
              <_MBtn onClick={handleSave} disabled={saving} color="#2E7D32">{saved?'✓ Saved':saving?'Saving…':'💾 Save GPT Report'}</_MBtn>
            </div>
          </_MCard>
        </>
      )}
    </div>
  );
}


function RiskReport({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('risk');
  const [view,setView]=React.useState('form');

  const fresh=()=>({
    id:_uid(),monType:'risk',date:_TODAY(),
    style:filters?.style||'',po:filters?.po||'',
    buyer:filters?.buyer||'',colour:filters?.colour||'',
    exFtyDate:'',preparedBy:'',approvedBy:'',
    risks:RISK_CATEGORIES.map(cat=>({
      category:cat.cat,
      items:cat.items.map(l=>({label:l,level:'LOW',action:'',responsible:''})),
    })),
    overallRisk:'LOW',overallRemarks:'',
  });
  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const setRisk=(ci,ii,field,v)=>setF(f=>({
    ...f,risks:f.risks.map((cat,cIdx)=>cIdx!==ci?cat:{
      ...cat,items:cat.items.map((item,iIdx)=>iIdx!==ii?item:{...item,[field]:v}),
    }),
  }));

  const recs=(db.monitoring||[]).filter(r=>r.monType==='risk');
  const allItems=F.risks.flatMap(c=>c.items);
  const highCount=allItems.filter(i=>i.level==='HIGH').length;
  const medCount =allItems.filter(i=>i.level==='MEDIUM').length;
  const overallAutoRisk=highCount>0?'HIGH':medCount>2?'MEDIUM':'LOW';
  const rColor=r=>r==='HIGH'?S.red:r==='MEDIUM'?S.amber:S.green;
  const rBg   =r=>r==='HIGH'?S.redBg:r==='MEDIUM'?S.amberBg:S.greenBg;

  const handleSave=async()=>{
    await doSave({...F,overallRisk:overallAutoRisk,savedAt:new Date().toISOString()},db,setDb);
    setF(fresh());
  };

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <h2 style={{fontSize:16,fontWeight:800,color:S.slate800}}>Risk Assessment Report</h2>
          <p style={{fontSize:12,color:S.slate500}}>Identify & plan mitigation for style-specific risks before production</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <_MBtn sm outline onClick={()=>setView('form')} color={S.red}>＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color={S.red}>📋 History ({recs.length})</_MBtn>
        </div>
      </div>

      {view==='history'?(
        <_MCard>
          <_MSH title="Risk Assessment Records" color={S.red}/>
          <_MTbl headers={['Date','Style','PO','Buyer','High Risks','Med Risks','Overall Risk']}
            rows={recs.map(r=>{
              const items=(r.risks||[]).flatMap(c=>c.items||[]);
              const hi=items.filter(i=>i.level==='HIGH').length;
              const med=items.filter(i=>i.level==='MEDIUM').length;
              return[r.date,r.style||'—',r.po||'—',r.buyer||'—',
                <span style={{fontWeight:700,color:S.red}}>{hi}</span>,
                <span style={{fontWeight:700,color:S.amber}}>{med}</span>,
                <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,
                  color:rColor(r.overallRisk),background:rBg(r.overallRisk)}}>{r.overallRisk||'—'}</span>];
            })} emptyMsg="No risk assessments yet."/>
        </_MCard>
      ):(
        <>
          <_MCard>
            <_MSH title="Order Details" color={S.red}/>
            <_MGrid gap={14}>
              <_MFld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
              <_MFld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} placeholder="Style..."/>
              <_MFld label="PO No." value={F.po} onChange={e=>set('po',e.target.value)} placeholder="PO#..."/>
              <_MFld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={_BUYERS}/>
              <_MFld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} placeholder="Colour..."/>
              <_MFld label="Ex-Factory Date" type="date" value={F.exFtyDate} onChange={e=>set('exFtyDate',e.target.value)}/>
              <_MFld label="Prepared By" value={F.preparedBy} onChange={e=>set('preparedBy',e.target.value)} placeholder="Name..."/>
              <_MFld label="Approved By" value={F.approvedBy} onChange={e=>set('approvedBy',e.target.value)} placeholder="Name..."/>
            </_MGrid>
          </_MCard>

          <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
            {[['HIGH',highCount,S.red,S.redBg],['MEDIUM',medCount,S.amber,S.amberBg],
              ['LOW',allItems.filter(i=>i.level==='LOW').length,S.green,S.greenBg]
            ].map(([l,v,c,b])=>(
              <div key={l} style={{padding:'8px 16px',background:b,borderRadius:8,textAlign:'center',border:`1px solid ${c}44`}}>
                <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
                <div style={{fontSize:10,fontWeight:700,color:c,textTransform:'uppercase'}}>{l} Risk</div>
              </div>
            ))}
            <div style={{padding:'8px 18px',background:rBg(overallAutoRisk),
              borderRadius:8,border:`2px solid ${rColor(overallAutoRisk)}`,
              fontSize:14,fontWeight:800,color:rColor(overallAutoRisk)}}>
              Overall: {overallAutoRisk}
            </div>
          </div>

          {F.risks.map((cat,ci)=>(
            <_MCard key={cat.category}>
              <_MSH title={cat.category} color={S.red}/>
              {cat.items.map((item,ii)=>(
                <div key={ii} style={{display:'flex',gap:10,alignItems:'flex-start',
                  padding:'9px 0',borderBottom:`1px solid ${S.slate200}`,flexWrap:'wrap'}}>
                  <div style={{flex:2,minWidth:180,fontSize:12,color:S.slate800,paddingTop:6}}>{item.label}</div>
                  <div style={{display:'flex',gap:5}}>
                    {['LOW','MEDIUM','HIGH'].map(l=>(
                      <button key={l} onClick={()=>setRisk(ci,ii,'level',l)}
                        style={{padding:'5px 12px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',
                          border:`1px solid ${item.level===l?rColor(l):S.slate200}`,
                          background:item.level===l?rBg(l):S.white,
                          color:item.level===l?rColor(l):S.slate400}}>{l}</button>
                    ))}
                  </div>
                  <input value={item.action||''} onChange={e=>setRisk(ci,ii,'action',e.target.value)}
                    placeholder="Mitigation action..."
                    style={{flex:2,minWidth:160,padding:'6px 10px',borderRadius:6,
                      border:`1px solid ${S.slate200}`,fontSize:11}}/>
                  <input value={item.responsible||''} onChange={e=>setRisk(ci,ii,'responsible',e.target.value)}
                    placeholder="Responsible..."
                    style={{flex:1,minWidth:100,padding:'6px 10px',borderRadius:6,
                      border:`1px solid ${S.slate200}`,fontSize:11}}/>
                </div>
              ))}
            </_MCard>
          ))}

          <_MCard>
            <_MFld label="Overall Remarks" type="textarea" rows={3} value={F.overallRemarks}
              onChange={e=>set('overallRemarks',e.target.value)} placeholder="Overall risk comments..."/>
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:14}}>
              <_MBtn onClick={handleSave} disabled={saving} color={S.red}>
                {saved?'✓ Saved':saving?'Saving…':'💾 Save Risk Assessment'}
              </_MBtn>
            </div>
          </_MCard>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  PILOT RUN REPORT
// ══════════════════════════════════════════════════════════════
function PilotReport({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('pilot');
  const [view,setView]=React.useState('form');

  const fresh=()=>({
    id:_uid(),monType:'pilot',date:_TODAY(),
    style:filters?.style||'',po:filters?.po||'',
    buyer:filters?.buyer||'',colour:filters?.colour||'',
    lineNo:'',pilotQty:0,targetTime:'',actualTime:'',efficiency:0,
    issues:[{id:_uid(),issue:'',action:'',responsible:'',status:'OPEN'}],
    criticalPath:'',bottleneckOp:'',recommendedTarget:0,
    qcManager:'',productionManager:'',result:'PENDING',overallRemarks:'',
  });
  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const updIssue=(i,k,v)=>setF(f=>({...f,issues:f.issues.map((is,ii)=>ii===i?{...is,[k]:v}:is)}));

  const recs=(db.monitoring||[]).filter(r=>r.monType==='pilot');
  const autoEff=()=>{
    const t=parseInt(F.targetTime),a=parseInt(F.actualTime);
    if(t>0&&a>0) return ((t/a)*100).toFixed(1);
    return F.efficiency||0;
  };

  const handleSave=async()=>{
    await doSave({...F,efficiency:autoEff(),savedAt:new Date().toISOString()},db,setDb);
    setF(fresh());
  };

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <h2 style={{fontSize:16,fontWeight:800,color:S.slate800}}>Pilot Run Report</h2>
          <p style={{fontSize:12,color:S.slate500}}>Pre-production pilot — identify bottlenecks before bulk run</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <_MBtn sm outline onClick={()=>setView('form')} color="#6A1B9A">＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color="#6A1B9A">📋 History ({recs.length})</_MBtn>
        </div>
      </div>

      {view==='history'?(
        <_MCard>
          <_MSH title="Pilot Run Records" color="#6A1B9A"/>
          <_MTbl headers={['Date','Style','PO','Line','Pilot Qty','Efficiency %','Open Issues','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.po||'—',r.lineNo||'—',r.pilotQty||'—',
              <span style={{fontWeight:700,color:parseFloat(r.efficiency)<70?S.red:S.green}}>{r.efficiency||0}%</span>,
              (r.issues||[]).filter(i=>i.status==='OPEN').length,
              <_MRes result={r.result}/>])}
            emptyMsg="No pilot run records yet."/>
        </_MCard>
      ):(
        <>
          <_MCard>
            <_MSH title="Pilot Run Details" color="#6A1B9A"/>
            <_MGrid gap={14}>
              <_MFld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
              <_MFld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} placeholder="Style..."/>
              <_MFld label="PO No." value={F.po} onChange={e=>set('po',e.target.value)} placeholder="PO#..."/>
              <_MFld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={_BUYERS}/>
              <_MFld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} placeholder="Colour..."/>
              <_MFld label="Line No." value={F.lineNo} onChange={e=>set('lineNo',e.target.value)} placeholder="Line..."/>
              <_MFld label="Pilot Qty" type="number" value={F.pilotQty} onChange={e=>set('pilotQty',e.target.value)} placeholder="0"/>
              <_MFld label="Target Time (min/pcs)" type="number" value={F.targetTime} onChange={e=>set('targetTime',e.target.value)} placeholder="0"/>
              <_MFld label="Actual Time (min/pcs)" type="number" value={F.actualTime} onChange={e=>set('actualTime',e.target.value)} placeholder="0"
                note={F.targetTime&&F.actualTime?`Efficiency: ${autoEff()}%`:''}/>
              <_MFld label="Bottleneck Operation" value={F.bottleneckOp} onChange={e=>set('bottleneckOp',e.target.value)} placeholder="Operation name..."/>
              <_MFld label="Critical Path" value={F.criticalPath} onChange={e=>set('criticalPath',e.target.value)} placeholder="Key path..."/>
              <_MFld label="Recommended Target (pcs/hr)" type="number" value={F.recommendedTarget} onChange={e=>set('recommendedTarget',e.target.value)} placeholder="0"/>
              <_MFld label="QC Manager" value={F.qcManager} onChange={e=>set('qcManager',e.target.value)} placeholder="Name..."/>
              <_MFld label="Production Manager" value={F.productionManager} onChange={e=>set('productionManager',e.target.value)} placeholder="Name..."/>
              <_MFld label="Result" value={F.result} onChange={e=>set('result',e.target.value)} options={['PENDING','PASS','FAIL','HOLD']}/>
            </_MGrid>
          </_MCard>

          <_MCard>
            <_MSH title="Issues Found During Pilot" color="#6A1B9A"
              action={<_MBtn sm onClick={()=>setF(f=>({...f,issues:[...f.issues,{id:_uid(),issue:'',action:'',responsible:'',status:'OPEN'}]}))} color="#6A1B9A">＋ Add Issue</_MBtn>}/>
            {F.issues.map((iss,i)=>(
              <div key={iss.id} style={{display:'grid',gridTemplateColumns:'1fr 1fr 120px 100px 28px',
                gap:8,marginBottom:8,alignItems:'center'}}>
                <input value={iss.issue} onChange={e=>updIssue(i,'issue',e.target.value)} placeholder="Issue found..."
                  style={{padding:'7px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:12}}/>
                <input value={iss.action} onChange={e=>updIssue(i,'action',e.target.value)} placeholder="Action taken..."
                  style={{padding:'7px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:12}}/>
                <input value={iss.responsible} onChange={e=>updIssue(i,'responsible',e.target.value)} placeholder="Person..."
                  style={{padding:'7px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:12}}/>
                <select value={iss.status} onChange={e=>updIssue(i,'status',e.target.value)}
                  style={{padding:'7px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:12,fontWeight:700,fontFamily:'inherit',
                    background:iss.status==='CLOSED'?S.greenBg:iss.status==='OPEN'?S.redBg:S.amberBg,
                    color:iss.status==='CLOSED'?S.green:iss.status==='OPEN'?S.red:S.amber}}>
                  {['OPEN','IN PROGRESS','CLOSED'].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={()=>setF(f=>({...f,issues:f.issues.filter((_,ii)=>ii!==i)}))}
                  style={{background:S.redBg,border:'none',borderRadius:6,cursor:'pointer',color:S.red,fontWeight:700,padding:'6px'}}>✕</button>
              </div>
            ))}
          </_MCard>

          <_MCard>
            <_MFld label="Remarks" type="textarea" rows={3} value={F.overallRemarks}
              onChange={e=>set('overallRemarks',e.target.value)} placeholder="Pilot run observations..."/>
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:14}}>
              <_MBtn onClick={handleSave} disabled={saving} color="#6A1B9A">
                {saved?'✓ Saved':saving?'Saving…':'💾 Save Pilot Report'}
              </_MBtn>
            </div>
          </_MCard>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  INLINE INSPECTION REPORT
// ══════════════════════════════════════════════════════════════
function InlineReport({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('inline');
  const [view,setView]=React.useState('form');

  const fresh=()=>({
    id:_uid(),monType:'inline',date:_TODAY(),
    style:filters?.style||'',po:filters?.po||'',
    buyer:filters?.buyer||'',colour:filters?.colour||'',
    process:'',lineNo:'',inspectorName:'',shift:'DAY',
    inspected:0,passed:0,failed:0, rejected:0,
    defects:INLINE_DEFECTS.map(d=>({label:d,qty:0})),
    remarks:'',result:'PENDING',
  });
  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const setDef=(i,v)=>setF(f=>({...f,defects:f.defects.map((d,di)=>di===i?{...d,qty:parseInt(v)||0}:d)}));

  const recs=(db.monitoring||[]).filter(r=>r.monType==='inline');
  const totalDef=F.defects.reduce((s,d)=>s+(d.qty||0),0);
  const dhu=F.inspected>0?((totalDef/F.inspected)*100).toFixed(2):'0.00';
  const autoRes=()=>parseFloat(dhu)<=5?'PASS':'FAIL';

  const handleSave=async()=>{
    await doSave({...F,totalDefects:totalDef,dhu:parseFloat(dhu),
      result:autoRes(),savedAt:new Date().toISOString()},db,setDb);
    setF(fresh());
  };

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <h2 style={{fontSize:16,fontWeight:800,color:S.slate800}}>Inline Inspection Report</h2>
          <p style={{fontSize:12,color:S.slate500}}>Process-wise in-line quality check with DHU tracking</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <_MBtn sm outline onClick={()=>setView('form')} color={S.amber}>＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color={S.amber}>📋 History ({recs.length})</_MBtn>
        </div>
      </div>

      {view==='history'?(
        <_MCard>
          <_MSH title="Inline Inspection Records" color={S.amber}/>
          <_MTbl headers={['Date','Style','PO','Process','Line','Inspected','Defects','DHU%','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.po||'—',r.process||'—',r.lineNo||'—',
              r.inspected||'—',r.totalDefects||0,`${r.dhu||0}%`,<_MRes result={r.result}/>])}
            emptyMsg="No inline inspection records yet."/>
        </_MCard>
      ):(
        <>
          <_MCard>
            <_MSH title="Inspection Header" color={S.amber}/>
            <_MGrid gap={14}>
              <_MFld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
              <_MFld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} placeholder="Style..."/>
              <_MFld label="PO No." value={F.po} onChange={e=>set('po',e.target.value)} placeholder="PO#..."/>
              <_MFld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={_BUYERS}/>
              <_MFld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} placeholder="Colour..."/>
              <_MFld label="Process" value={F.process} onChange={e=>set('process',e.target.value)} placeholder="e.g. Sewing Inline..."/>
              <_MFld label="Line No." value={F.lineNo} onChange={e=>set('lineNo',e.target.value)} placeholder="Line..."/>
              <_MFld label="Inspector" value={F.inspectorName} onChange={e=>set('inspectorName',e.target.value)} placeholder="Name..."/>
              <_MFld label="Shift" value={F.shift} onChange={e=>set('shift',e.target.value)} options={['DAY','NIGHT','GENERAL']}/>
              <_MFld label="Inspected Qty" type="number" value={F.inspected} onChange={e=>set('inspected',parseInt(e.target.value)||0)} placeholder="0"/>
              <_MFld label="Passed Qty" type="number" value={F.passed} onChange={e=>set('passed',parseInt(e.target.value)||0)} placeholder="0"/>
              <_MFld label="Failed Qty" type="number" value={F.failed} onChange={e=>set('failed',parseInt(e.target.value)||0)} placeholder="0"/>
              <_MFld label="Rejected Qty" type="number" value={F.rejected} onChange={e=>set('rejected',parseInt(e.target.value)||0)} placeholder="0"/>
            </_MGrid>
          </_MCard>

          <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
            {[['Inspected',F.inspected,'#00897B','#EFF6FF'],
              ['Passed',F.passed,S.green,S.greenBg],
              ['Defects',totalDef,S.red,S.redBg],
              ['DHU',`${dhu}%`,parseFloat(dhu)>5?S.red:S.green,parseFloat(dhu)>5?S.redBg:S.greenBg],
            ].map(([l,v,c,b])=>(
              <div key={l} style={{padding:'10px 16px',background:b,borderRadius:8,
                border:`1px solid ${c}22`,textAlign:'center',minWidth:100}}>
                <div style={{fontSize:22,fontWeight:800,color:c}}>{v}</div>
                <div style={{fontSize:10,fontWeight:700,color:S.slate500,textTransform:'uppercase',marginTop:4}}>{l}</div>
              </div>
            ))}
          </div>

          <_MCard>
            <_MSH title="Defect Breakdown" color={S.amber}/>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:7}}>
              {F.defects.map((d,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',
                  borderRadius:6,background:d.qty>0?S.redBg:S.slate50,
                  border:`1px solid ${d.qty>0?S.redBd:S.slate200}`}}>
                  <div style={{flex:1,fontSize:12,color:S.slate800,fontWeight:d.qty>0?600:400}}>{d.label}</div>
                  <input type="number" value={d.qty||''} onChange={e=>setDef(i,e.target.value)}
                    style={{width:55,padding:'4px',borderRadius:6,border:`1px solid ${S.slate200}`,
                      fontSize:13,textAlign:'center',fontWeight:700,color:d.qty>0?S.red:S.slate800}}/>
                </div>
              ))}
            </div>
          </_MCard>

          <_MCard>
            <_MFld label="Remarks" type="textarea" rows={3} value={F.remarks}
              onChange={e=>set('remarks',e.target.value)} placeholder="Inline inspection observations..."/>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14}}>
              <_MRes result={autoRes()}/>
              <_MBtn onClick={handleSave} disabled={saving} color={S.amber}>
                {saved?'✓ Saved':saving?'Saving…':'💾 Save Inline Report'}
              </_MBtn>
            </div>
          </_MCard>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  FINAL INSPECTION REPORT  (AQL-based)
// ══════════════════════════════════════════════════════════════
function FinalReport({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('final');
  const [view,setView]=React.useState('form');

  const fresh=()=>({
    id:_uid(),monType:'final',date:_TODAY(),
    style:filters?.style||'',po:filters?.po||'',
    buyer:filters?.buyer||'',colour:filters?.colour||'',
    orderQty:0,lotSize:0,inspType:'FINAL',aqlLevel:'2.5',
    criticalFound:0,majorFound:0,minorFound:0,
    defects:INLINE_DEFECTS.map(d=>({label:d,critical:0,major:0,minor:0})),
    auditor:'',qcManager:'',remarks:'',
  });
  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const setDef=(i,k,v)=>setF(f=>{
    const defects=[...f.defects];defects[i]={...defects[i],[k]:parseInt(v)||0};
    return {...f,defects,
      criticalFound:defects.reduce((s,d)=>s+(d.critical||0),0),
      majorFound:defects.reduce((s,d)=>s+(d.major||0),0),
      minorFound:defects.reduce((s,d)=>s+(d.minor||0),0)};
  });

  const recs=(db.monitoring||[]).filter(r=>r.monType==='final');
  const aql=getAQL(F.lotSize||F.orderQty);
  const result=F.criticalFound>0?'FAIL':F.majorFound>=aql.re?'FAIL':F.majorFound>aql.ac?'HOLD':'PASS';

  const handleSave=async()=>{
    await doSave({...F,result,sampleSize:aql.n,savedAt:new Date().toISOString()},db,setDb);
    setF(fresh());
  };

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <h2 style={{fontSize:16,fontWeight:800,color:S.slate800}}>Final Inspection Report</h2>
          <p style={{fontSize:12,color:S.slate500}}>AQL-based final / pre-final garment inspection</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <_MBtn sm outline onClick={()=>setView('form')} color={S.green}>＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color={S.green}>📋 History ({recs.length})</_MBtn>
        </div>
      </div>

      {view==='history'?(
        <_MCard>
          <_MSH title="Final Inspection Records" color={S.green}/>
          <_MTbl headers={['Date','Style','PO','Buyer','Order Qty','Sample','Critical','Major','Minor','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.po||'—',r.buyer||'—',r.orderQty||'—',r.sampleSize||'—',
              <span style={{color:'#6A1B9A',fontWeight:700}}>{r.criticalFound||0}</span>,
              <span style={{color:S.red,fontWeight:700}}>{r.majorFound||0}</span>,
              <span style={{color:S.amber,fontWeight:700}}>{r.minorFound||0}</span>,
              <_MRes result={r.result}/>])}
            emptyMsg="No final inspections yet."/>
        </_MCard>
      ):(
        <>
          <_MCard>
            <_MSH title="Inspection Header" color={S.green}/>
            <_MGrid gap={14}>
              <_MFld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
              <_MFld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} placeholder="Style..."/>
              <_MFld label="PO No." value={F.po} onChange={e=>set('po',e.target.value)} placeholder="PO#..."/>
              <_MFld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={_BUYERS}/>
              <_MFld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} placeholder="Colour..."/>
              <_MFld label="Order Qty" type="number" value={F.orderQty} onChange={e=>set('orderQty',e.target.value)} placeholder="0"/>
              <_MFld label="Lot Size" type="number" value={F.lotSize} onChange={e=>set('lotSize',e.target.value)} placeholder="0"
                note="Leave 0 to use Order Qty for AQL"/>
              <_MFld label="Inspection Type" value={F.inspType} onChange={e=>set('inspType',e.target.value)} options={['INLINE','PRE-FINAL','FINAL','PRE-SHIPMENT']}/>
              <_MFld label="AQL Level" value={F.aqlLevel} onChange={e=>set('aqlLevel',e.target.value)} options={['1.0','1.5','2.5','4.0']}/>
              <_MFld label="Auditor" value={F.auditor} onChange={e=>set('auditor',e.target.value)} placeholder="Name..."/>
              <_MFld label="QC Manager" value={F.qcManager} onChange={e=>set('qcManager',e.target.value)} placeholder="Name..."/>
            </_MGrid>
          </_MCard>

          {/* AQL calculator */}
          <div style={{padding:'14px 18px',background:'#EFF6FF',
            border:`1px solid ${S.teal}44`,borderRadius:12}}>
            <div style={{fontSize:12,fontWeight:700,color:S.teal,marginBottom:10}}>
              AQL {F.aqlLevel} · Lot: {F.lotSize||F.orderQty||'(enter qty)'}
            </div>
            <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
              {[['Sample Size',aql.n,'#00897B'],['Accept (Ac)',aql.ac,S.green],['Reject (Re)',aql.re,S.red],
                ['Critical',F.criticalFound,'#6A1B9A'],['Major',F.majorFound,S.red],['Minor',F.minorFound,S.amber]
              ].map(([l,v,c])=>(
                <div key={l} style={{textAlign:'center',padding:'8px 14px',
                  background:S.white,borderRadius:8,border:`1px solid ${c}22`}}>
                  <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
                  <div style={{fontSize:10,color:S.slate500,textTransform:'uppercase'}}>{l}</div>
                </div>
              ))}
              <div style={{padding:'8px 20px',borderRadius:8,display:'flex',alignItems:'center',
                background:result==='PASS'?S.greenBg:result==='FAIL'?S.redBg:S.amberBg,
                border:`2px solid ${result==='PASS'?S.greenBd:result==='FAIL'?S.redBd:S.amberBd}`}}>
                <div style={{fontSize:18,fontWeight:800,
                  color:result==='PASS'?S.green:result==='FAIL'?S.red:S.amber}}>
                  {result==='PASS'?'✅ ACCEPTED':result==='FAIL'?'❌ REJECTED':'⚠️ ON HOLD'}
                </div>
              </div>
            </div>
          </div>

          <_MCard>
            <_MSH title="Defect Classification" color={S.green}/>
            <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 80px',gap:6,marginBottom:8}}>
              {['Defect Name','Critical','Major','Minor'].map(h=>(
                <div key={h} style={{fontSize:10,fontWeight:700,color:S.slate500,textTransform:'uppercase'}}>{h}</div>
              ))}
            </div>
            {F.defects.map((d,i)=>(
              <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 80px',
                gap:6,marginBottom:5,alignItems:'center',
                padding:'5px 8px',borderRadius:6,
                background:(d.critical||d.major||d.minor)>0?S.redBg:S.slate50}}>
                <div style={{fontSize:12,color:S.slate800}}>{d.label}</div>
                {['critical','major','minor'].map(k=>(
                  <input key={k} type="number" value={d[k]||''} onChange={e=>setDef(i,k,e.target.value)}
                    placeholder="0"
                    style={{padding:'5px',borderRadius:6,border:`1px solid ${S.slate200}`,
                      fontSize:12,textAlign:'center',
                      color:k==='critical'?'#6A1B9A':k==='major'?S.red:S.amber}}/>
                ))}
              </div>
            ))}
          </_MCard>

          <_MCard>
            <_MFld label="Remarks" type="textarea" rows={3} value={F.remarks}
              onChange={e=>set('remarks',e.target.value)} placeholder="Final inspection observations..."/>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14}}>
              <_MRes result={result}/>
              <_MBtn onClick={handleSave} disabled={saving} color={S.green}>
                {saved?'✓ Saved':saving?'Saving…':'💾 Save Final Inspection'}
              </_MBtn>
            </div>
          </_MCard>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  CARTON AUDIT REPORT
// ══════════════════════════════════════════════════════════════
function CartonReport({db,setDb,filters}){
  const {saving,saved,doSave}=_useSave('carton');
  const [view,setView]=React.useState('form');

  const newCtn=()=>({id:_uid(),ctnNo:'',pcsPerCtn:0,sizeRatio:'',
    shadeOk:true,labelOk:true,packingOk:true,measOk:true,
    errors:0,result:'PASS',remarks:''});

  const fresh=()=>({
    id:_uid(),monType:'carton',date:_TODAY(),
    style:filters?.style||'',po:filters?.po||'',
    buyer:filters?.buyer||'',colour:filters?.colour||'',
    totalCtns:0,ctnsAudited:0,
    ctns:[newCtn()],
    auditor:'',qcManager:'',
  });
  const [F,setF]=React.useState(fresh());
  const set=(k,v)=>setF(f=>({...f,[k]:v}));
  const updCtn=(i,k,v)=>setF(f=>{
    const ctns=[...f.ctns];ctns[i]={...ctns[i],[k]:v};
    if(['shadeOk','labelOk','packingOk','measOk'].includes(k)){
      const ctn=ctns[i];
      const errs=[!ctn.shadeOk,!ctn.labelOk,!ctn.packingOk,!ctn.measOk].filter(Boolean).length;
      ctns[i].errors=errs;
      ctns[i].result=errs===0?'PASS':'FAIL';
    }
    return {...f,ctns};
  });

  const recs=(db.monitoring||[]).filter(r=>r.monType==='carton');
  const passCtns=F.ctns.filter(c=>c.result==='PASS').length;
  const failCtns=F.ctns.filter(c=>c.result==='FAIL').length;
  const overallRes=failCtns===0&&passCtns>0?'PASS':failCtns>0?'FAIL':'PENDING';

  const handleSave=async()=>{
    await doSave({...F,passCtns,failCtns,result:overallRes,savedAt:new Date().toISOString()},db,setDb);
    setF(fresh());
  };

  return(
    <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
        <div>
          <h2 style={{fontSize:16,fontWeight:800,color:S.slate800}}>Carton Audit Report</h2>
          <p style={{fontSize:12,color:S.slate500}}>Check packing, label, shade & measurement accuracy per carton</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <_MBtn sm outline onClick={()=>setView('form')} color="#0F766E">＋ New</_MBtn>
          <_MBtn sm outline={view!=='history'} onClick={()=>setView('history')} color="#0F766E">📋 History ({recs.length})</_MBtn>
        </div>
      </div>

      {view==='history'?(
        <_MCard>
          <_MSH title="Carton Audit Records" color="#0F766E"/>
          <_MTbl headers={['Date','Style','PO','Buyer','CTN Audited','Pass','Fail','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.po||'—',r.buyer||'—',r.ctnsAudited||'—',
              <span style={{color:S.green,fontWeight:700}}>{r.passCtns||0}</span>,
              <span style={{color:S.red,fontWeight:700}}>{r.failCtns||0}</span>,
              <_MRes result={r.result}/>])}
            emptyMsg="No carton audit records yet."/>
        </_MCard>
      ):(
        <>
          <_MCard>
            <_MSH title="Audit Header" color="#0F766E"/>
            <_MGrid gap={14}>
              <_MFld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
              <_MFld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} placeholder="Style..."/>
              <_MFld label="PO No." value={F.po} onChange={e=>set('po',e.target.value)} placeholder="PO#..."/>
              <_MFld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={_BUYERS}/>
              <_MFld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} placeholder="Colour..."/>
              <_MFld label="Total CTNs" type="number" value={F.totalCtns} onChange={e=>set('totalCtns',e.target.value)} placeholder="0"/>
              <_MFld label="CTNs Audited" type="number" value={F.ctnsAudited} onChange={e=>set('ctnsAudited',e.target.value)} placeholder="0"/>
              <_MFld label="Auditor" value={F.auditor} onChange={e=>set('auditor',e.target.value)} placeholder="Name..."/>
              <_MFld label="QC Manager" value={F.qcManager} onChange={e=>set('qcManager',e.target.value)} placeholder="Name..."/>
            </_MGrid>
          </_MCard>

          <div style={{display:'flex',gap:10}}>
            <span style={{padding:'6px 14px',borderRadius:20,background:S.greenBg,color:S.green,fontWeight:700,fontSize:12}}>✓ {passCtns} Pass</span>
            <span style={{padding:'6px 14px',borderRadius:20,background:S.redBg,color:S.red,fontWeight:700,fontSize:12}}>✗ {failCtns} Fail</span>
            <_MRes result={overallRes}/>
          </div>

          <_MCard>
            <_MSH title="Carton-wise Audit" color="#0F766E"
              action={<_MBtn sm onClick={()=>setF(f=>({...f,ctns:[...f.ctns,newCtn()]}))} color="#0F766E">＋ Add CTN</_MBtn>}/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:S.slate50}}>
                  {['CTN No.','Pcs/CTN','Size Ratio','Shade ✓','Label ✓','Packing ✓','Meas ✓','Errors','Result','Remarks',''].map(h=>(
                    <th key={h} style={{padding:'8px 10px',fontWeight:700,color:S.slate500,fontSize:10,
                      textTransform:'uppercase',borderBottom:`2px solid ${S.slate200}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {F.ctns.map((c,i)=>(
                    <tr key={c.id} style={{borderBottom:`1px solid ${S.slate200}`,
                      background:c.result==='FAIL'?S.redBg:S.white}}>
                      <td style={{padding:'5px 6px'}}><input value={c.ctnNo} onChange={e=>updCtn(i,'ctnNo',e.target.value)} placeholder="CTN#"
                        style={{width:70,padding:'5px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:11}}/></td>
                      <td style={{padding:'5px 6px'}}><input type="number" value={c.pcsPerCtn||''} onChange={e=>updCtn(i,'pcsPerCtn',e.target.value)} placeholder="0"
                        style={{width:60,padding:'5px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:11,textAlign:'center'}}/></td>
                      <td style={{padding:'5px 6px'}}><input value={c.sizeRatio||''} onChange={e=>updCtn(i,'sizeRatio',e.target.value)} placeholder="S2 M3..."
                        style={{width:80,padding:'5px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:11}}/></td>
                      {['shadeOk','labelOk','packingOk','measOk'].map(k=>(
                        <td key={k} style={{padding:'5px 8px',textAlign:'center'}}>
                          <button onClick={()=>updCtn(i,k,!c[k])}
                            style={{padding:'4px 12px',borderRadius:6,fontSize:12,fontWeight:700,
                              cursor:'pointer',border:'none',
                              background:c[k]?S.greenBg:S.redBg,color:c[k]?S.green:S.red}}>
                            {c[k]?'✓':'✗'}
                          </button>
                        </td>
                      ))}
                      <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,
                        color:c.errors>0?S.red:S.slate500}}>{c.errors}</td>
                      <td style={{padding:'5px 8px',textAlign:'center'}}>
                        <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,
                          color:c.result==='PASS'?S.green:S.red,
                          background:c.result==='PASS'?S.greenBg:S.redBg}}>{c.result}</span>
                      </td>
                      <td style={{padding:'5px 6px'}}><input value={c.remarks||''} onChange={e=>updCtn(i,'remarks',e.target.value)} placeholder="Remarks..."
                        style={{width:'100%',minWidth:90,padding:'5px',borderRadius:6,border:`1px solid ${S.slate200}`,fontSize:11}}/></td>
                      <td style={{padding:'5px 6px'}}>
                        <button onClick={()=>setF(f=>({...f,ctns:f.ctns.filter((_,ci)=>ci!==i)}))}
                          style={{background:S.redBg,border:'none',borderRadius:6,cursor:'pointer',color:S.red,fontWeight:700,padding:'5px 7px'}}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:14}}>
              <_MBtn onClick={handleSave} disabled={saving} color="#0F766E">
                {saved?'✓ Saved':saving?'Saving…':'💾 Save Carton Audit'}
              </_MBtn>
            </div>
          </_MCard>
        </>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
const NAV = [
  { id:'dashboard',  label:'Dashboard',        icon:'📊', color:S.teal    },
  { id:'monitoring', label:'Monitoring',       icon:'🔬', color:'#00897B' },
  { id:'sampling',   label:'Sampling',          icon:'🎯', color:S.dSampling,   annex:'Ann-01 → 02',  forms:2  },
  { id:'warehouse',  label:'Warehouse / RM',    icon:'🧵', color:S.dWarehouse,  annex:'Ann-03 → 13',  forms:11 },
  { id:'cutting',    label:'Cutting',           icon:'✂️', color:S.dCutting,    annex:'Ann-14 → 28',  forms:15 },
  { id:'sewing',     label:'Sewing',            icon:'🪡', color:S.dSewing,     annex:'Ann-29 → 38',  forms:10 },
  { id:'washing',    label:'Washing',           icon:'💧', color:S.dWashing,    annex:'Ann-39 → 44',  forms:6  },
  { id:'wf',         label:'Wrinkle Free',      icon:'🌀', color:S.dWF,         annex:'Ann-45 → 52',  forms:8  },
  { id:'finishing',  label:'Finishing',         icon:'✨', color:S.dFinishing,  annex:'Ann-53 → 71',  forms:19 },
  { id:'mending',    label:'Mending / Spotting',icon:'🔧', color:S.dMending,    annex:'Ann-72 → 74',  forms:3  },
  { id:'killed',     label:'Killed Garments',   icon:'☠️', color:S.dKilled,     annex:'Ann-75 → 79',  forms:5  },
  { id:'process',    label:'Process Control',   icon:'⚙️', color:S.dProcess,    annex:'Ann-80 → 82',  forms:3  },
];

// Stub for departments not yet built
function DeptStub({ dept }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:16, color:S.slate500 }}>
      <div style={{ fontSize:56 }}>{dept.icon}</div>
      <div style={{ fontSize:18, fontWeight:700, color:S.slate700 }}>{dept.label}</div>
      <QBadge color={dept.color}>{dept.annex} · {dept.forms} forms</QBadge>
      <div style={{ fontSize:13, color:S.slate400, textAlign:'center', maxWidth:300 }}>
        This module will be delivered in the next step.<br/>Paste the department code below the previous block.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────
export default function QualityModule({ db: extDb, setDb: extSetDb } = {}) {
  // Inject CSS once
  useEffect(() => {
    const el = document.createElement('style');
    el.id = '__qm_css';
    if (!document.getElementById('__qm_css')) { el.textContent = _css; document.head.appendChild(el); }
    return () => { const e = document.getElementById('__qm_css'); e && e.remove(); };
  }, []);

  // Auth
  const [user, setUser] = useState(null);

  // Local DB (used if no external db passed)
  const [localDb, setLocalDb] = useState({
    sampling:[], warehouse:[], cutting:[], sewing:[], washing:[], wf:[], finishing:[], mending:[], killed:[], process:[], monitoring:[],
  });
  const db    = extDb    || localDb;
  const setDb = extSetDb || setLocalDb;

  // Sync master data cache whenever db changes so all dropdowns get live data
  useEffect(() => { syncDbCache(db); }, [db]);

  // Filters (global context)
  const [filters, setFilters] = useState({});

  // Navigation
  const [activeDept,  setActiveDept]  = useState('dashboard');
  const [activeForm,  setActiveForm]  = useState(null);   // sub-form within dept
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Responsive
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  if (!user) return <LoginScreen onLogin={setUser}/>;

  const activeDeptCfg = NAV.find(n => n.id === activeDept) || NAV[0];

  // Render department — use DEPT_REGISTRY if populated, else show stub
  const renderDept = () => {
    if (activeDept === 'dashboard')  return <DashboardView db={db} setDb={setDb} filters={filters}/>;
    if (activeDept === 'monitoring') return <MonitoringView db={db} setDb={setDb} filters={filters}/>;
    const DeptComp = DEPT_REGISTRY[activeDept];
    if (!DeptComp) return <DeptStub dept={activeDeptCfg}/>;
    return <DeptComp db={db} setDb={setDb} filters={filters} activeForm={activeForm} setActiveForm={setActiveForm}/>;
  };

  // ── SIDEBAR ─────────────────────────────────────
  const Sidebar = () => (
    <div style={{
      width:230, background:S.navy, display:'flex', flexDirection:'column',
      borderRight:`1px solid #1E3A5F`, flexShrink:0,
      position: isMobile?'fixed':'relative', top:0, left:0, height:'100%', zIndex:200,
      transform: isMobile?(sidebarOpen?'translateX(0)':'translateX(-100%)'):'none',
      transition:'transform .25s ease',
    }}>
      {/* Logo */}
      <div style={{ padding:'18px 16px', borderBottom:`1px solid #1E3A5F` }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36,height:36,borderRadius:r.lg,background:`linear-gradient(135deg,${S.blue},${S.teal})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0 }}>🔍</div>
          <div>
            <div style={{ color:'white',fontWeight:800,fontSize:14,letterSpacing:'-.3px' }}>QMS Pro</div>
            <div style={{ color:S.slate400,fontSize:10 }}>10 Depts · 60 Forms</div>
          </div>
        </div>
      </div>

      {/* User */}
      <div style={{ padding:'10px 16px', borderBottom:`1px solid #1E3A5F` }}>
        <div style={{ fontSize:11, color:S.slate400, marginBottom:3 }}>Signed in as</div>
        <div style={{ color:'white', fontWeight:600, fontSize:13 }}>{user.name}</div>
        <QBadge color={S.teal}>{user.role}</QBadge>
      </div>

      {/* Nav items */}
      <nav style={{ flex:1, overflowY:'auto', padding:'6px 0' }}>
        {NAV.map(n => {
          const isActive = activeDept===n.id;
          const isBuilt  = n.id==='dashboard' || !!DEPT_REGISTRY[n.id];
          const recCount = n.id!=='dashboard' ? (db[n.id]||[]).length : null;
          return (
            <button key={n.id} onClick={() => { setActiveDept(n.id); setActiveForm(null); setSidebarOpen(false); }}
              style={{
                width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 16px',
                background: isActive ? n.color+'22' : 'transparent', border:'none', cursor:'pointer',
                borderLeft: isActive ? `3px solid ${n.color}` : '3px solid transparent',
                transition:'all .15s',
              }}>
              <span style={{ fontSize:16, flexShrink:0 }}>{n.icon}</span>
              <div style={{ flex:1, textAlign:'left' }}>
                <div style={{ fontSize:12, fontWeight: isActive?700:400, color: isActive?'white':S.slate400 }}>{n.label}</div>
                {n.annex && <div style={{ fontSize:9, color: isBuilt?n.color:S.slate500 }}>{n.annex}</div>}
              </div>
              {recCount !== null && recCount > 0 && (
                <span style={{ fontSize:10, background:n.color+'33', color:n.color, padding:'1px 7px', borderRadius:10, fontWeight:700 }}>{recCount}</span>
              )}
              {!isBuilt && n.id!=='dashboard' && (
                <span style={{ fontSize:9, background:S.slate700, color:S.slate400, padding:'1px 5px', borderRadius:4 }}>soon</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Sign out */}
      <div style={{ padding:12, borderTop:`1px solid #1E3A5F` }}>
        <button onClick={() => setUser(null)}
          style={{ width:'100%', padding:9, background:'transparent', border:`1px solid #1E3A5F`, borderRadius:r.md, color:S.slate400, cursor:'pointer', fontSize:12, fontWeight:600 }}>
          Sign Out
        </button>
      </div>
    </div>
  );

  // ── BOTTOM NAV (mobile) ──────────────────────────
  const BottomNav = () => (
    <div style={{ position:'fixed', bottom:0, left:0, right:0, background:S.navy, borderTop:`1px solid #1E3A5F`, display:'flex', zIndex:100, height:56 }}>
      {NAV.slice(0,5).map(n => (
        <button key={n.id} onClick={() => setActiveDept(n.id)}
          style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:2, background:'transparent', border:'none', cursor:'pointer' }}>
          <span style={{ fontSize:18 }}>{n.icon}</span>
          <span style={{ fontSize:8, color: activeDept===n.id?n.color:S.slate500, fontWeight: activeDept===n.id?700:400 }}>{n.label.split(' ')[0]}</span>
        </button>
      ))}
      <button onClick={() => setSidebarOpen(!sidebarOpen)}
        style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:2, background:'transparent', border:'none', cursor:'pointer' }}>
        <span style={{ fontSize:18 }}>⋯</span>
        <span style={{ fontSize:8, color:S.slate500 }}>More</span>
      </button>
    </div>
  );

  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', fontFamily:"'Inter','Inter',system-ui,sans-serif", background:S.slate100 }}>
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:199 }}/>
      )}

      <Sidebar/>

      {/* Main */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        {/* Top bar */}
        <div style={{ background:S.white, borderBottom:`1px solid ${S.slate200}`, padding:'10px 16px', display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
          {isMobile && (
            <button onClick={() => setSidebarOpen(true)}
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:S.slate700, padding:4 }}>☰</button>
          )}
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:18 }}>{activeDeptCfg.icon}</span>
              <h2 style={{ fontSize:15, fontWeight:700, color:S.slate800 }}>{activeDeptCfg.label}</h2>
              {activeDeptCfg.annex && <QBadge color={activeDeptCfg.color||S.blue}>{activeDeptCfg.annex}</QBadge>}
            </div>
            {(filters.style||filters.po||filters.brand) && (
              <div style={{ display:'flex', gap:6, marginTop:3, flexWrap:'wrap' }}>
                {filters.style  && <span style={{ fontSize:11, color:S.teal,  fontWeight:600 }}>Style: {filters.style}</span>}
                {filters.po     && <span style={{ fontSize:11, color:S.amber, fontWeight:600 }}>PO: {filters.po}</span>}
                {filters.brand  && <span style={{ fontSize:11, color:S.dSampling, fontWeight:600 }}>{filters.brand}</span>}
                {filters.colour && <span style={{ fontSize:11, color:S.purple }}>Colour: {filters.colour}</span>}
                {filters.exFac  && <span style={{ fontSize:11, color:S.red, fontWeight:600 }}>Ex-Fac: {filters.exFac}</span>}
              </div>
            )}
          </div>
          <div style={{ fontSize:11, color:S.slate400 }}>{new Date().toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short',year:'numeric'})}</div>
        </div>

        {/* Filter bar */}
        <GlobalFilterBar filters={filters} setFilters={setFilters} db={db}/>

        {/* Content */}
        <div style={{ flex:1, overflowY:'auto', paddingBottom: isMobile?60:0 }}>
          {renderDept()}
        </div>
      </div>


  {/* PO Number datalist for inline inputs */}
  <datalist id="po-datalist">
    {dbGet('poNumbers',[]).map((p,i) => <option key={i} value={p}/>)}
  </datalist>
      {isMobile && <BottomNav/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
//  ↓ PASTE DEPARTMENT MODULES BELOW THIS LINE — one block at a time ↓
// ══════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════
//  DEPT-01 · SAMPLING · Ann-01 → Ann-02
//  Paste this entire block below the base file (after the last comment line)
//  Forms:
//    Ann-01 · Sample Audit Report       (Nir/Sam/01)
//    Ann-02 · Sample Comment Form       (Nir/Sam/02)
// ══════════════════════════════════════════════════════════════════════

(function registerSampling() {

  // ── CONSTANTS ────────────────────────────────────────────────────────
  const SAMPLE_TYPES_ALL = [
    'PROTO','FIT SAMPLE','SIZE SET','SEALING SAMPLE',
    'PP SAMPLE','TOP OF PRODUCTION','SHIPMENT SAMPLE','AD SAMPLE','OTHER',
  ];

  const FABRIC_QUALITIES    = ['APPROVED','PENDING','REJECTED','NA'];
  const LINING_QUALITIES    = ['APPROVED','PENDING','REJECTED','NA'];
  const ACCESSORIES_QUALITY = ['APPROVED','PENDING','REJECTED','NA'];
  const WASH_STATUSES       = ['APPROVED','PENDING','REJECTED','NA','NOT APPLICABLE'];
  const SUBMISSION_STATUSES = ['1ST SUBMISSION','2ND SUBMISSION','3RD SUBMISSION','REVISION'];

  const APPROVAL_OPTIONS = [
    'APPROVED','APPROVED WITH COMMENTS','REJECTED','PENDING','NOT CHECKED','NA',
  ];

  const TESTING_STANDARDS = [
    { name:'Colorfastness to Crocking',             std:'AATCC-08'           },
    { name:'Fabrication / Weight',                  std:'ASTM D-3776'        },
    { name:'Colorfastness to Laundering',           std:'AATCC-61 (2A)'      },
    { name:'Dimensional Stability – Laundering',    std:'AATCC-135'          },
    { name:'Garment Appearance after Cleaning',     std:'LS&CO-23A / AATCC-135'},
    { name:'Tear Strength',                         std:'ASTM D-1424'        },
    { name:'Seam Strength',                         std:'ASTM D1683 Modified' },
    { name:'Tensile Strength',                      std:'ASTM D-5034'        },
    { name:'Seam Twist',                            std:'LS&CO-23A'          },
    { name:'Weight',                                std:'ASTM D-3776'        },
    { name:'Seam Slippage',                         std:'ASTM D-434'         },
    { name:'Skew Movement (Torque)',                std:'AATCC-179'          },
    { name:'Print Durability',                      std:'LS&CO-23A'          },
    { name:'Absorbency',                            std:'AATCC-79'           },
    { name:'Colorfastness to Extended Home Launder',std:'LS&CO-23A'          },
    { name:'Crease Retention',                      std:'AATCC-88C'          },
    { name:'Smoothness Retention',                  std:'AATCC-143'          },
    { name:'Wrinkle Recovery',                      std:'AATCC-128'          },
    { name:'Seam Smoothness',                       std:'AATCC-88B'          },
    { name:'Soil & Stain Release',                  std:'CTL / AATCC-130'    },
    { name:'Water Repellency',                      std:'AATCC-193'          },
    { name:'Waistband Stretch',                     std:'LS&CO-48'           },
    { name:'Flex Abrasion Test',                    std:'ASTM D-3885'        },
    { name:'Button Pull Test',                      std:'ASTM D-4846B'       },
  ];

  const SAR_SECTIONS = [
    {
      category: 'A. FABRIC',
      items: [
        { id:'fab_width',    label:'1. Width'         },
        { id:'fab_shrink',   label:'2. Shrinkage'     },
        { id:'fab_shade',    label:'3. Shade Blanket' },
        { id:'fab_quality',  label:'4. Quality'       },
      ],
    },
    {
      category: 'B. TRIMS',
      items: [
        { id:'trim_quality', label:'1. Quality' },
        { id:'trim_colour',  label:'2. Color'   },
      ],
    },
    {
      category: 'C. SAMPLING',
      items: [
        { id:'samp_sizeset', label:'1. Size Set'   },
        { id:'samp_pilot',   label:'2. Pilot Run'  },
      ],
    },
    {
      category: 'D. CUTTING',
      items: [
        { id:'cut_result', label:'Cutting Result' },
      ],
    },
    {
      category: 'E. STITCHING',
      items: [
        { id:'stitch_const', label:'1. Construction' },
        { id:'stitch_qual',  label:'2. Quality'      },
      ],
    },
    {
      category: 'F. WASHING / WRINKLE FREE',
      items: [
        { id:'wash_handfeel', label:'1. Handfeel'     },
        { id:'wash_colour',   label:'2. Color Loss'   },
        { id:'wash_grinding', label:'3. Grinding'     },
        { id:'wash_blast',    label:'4. Blasting'     },
      ],
    },
    {
      category: 'G. FINISHING',
      items: [
        { id:'fin_fold',    label:'1. Folding'            },
        { id:'fin_ticket',  label:'2. Price Ticket Place' },
        { id:'fin_polybag', label:'3. Polybag'            },
        { id:'fin_carton',  label:'4. Carton'             },
        { id:'fin_ship',    label:'5. Shipping Mark'      },
      ],
    },
  ];

  const CONSTR_ITEMS = [
    { id:'main_label',      label:'Main Label'         },
    { id:'size_label',      label:'Size Label'         },
    { id:'care_label',      label:'Care Label'         },
    { id:'hang_tag',        label:'Hang Tag'           },
    { id:'price_ticket',    label:'Price Ticket'       },
    { id:'interlining',     label:'Interlining'        },
    { id:'zipper',          label:'Zipper'             },
    { id:'polybag',         label:'Poly Bag'           },
    { id:'hanger',          label:'Hanger'             },
    { id:'sewing_thread',   label:'Sewing Thread'      },
    { id:'button_hole',     label:'Button Hole'        },
    { id:'country_origin',  label:'Country of Origin'  },
    { id:'fiber_content',   label:'Content of Fiber'   },
    { id:'care_instruction',label:'Care Instruction'   },
    { id:'print',           label:'Print'              },
    { id:'embroidery',      label:'Embroidery'         },
    { id:'packing',         label:'Packing'            },
    { id:'pack_method',     label:'Pack Method'        },
  ];

  // ── HELPERS ──────────────────────────────────────────────────────────
  const _S = window.__QM_S || {
    white:'#fff', slate50:'#F8F9FA', slate100:'#F4F6F8', slate200:'#DEE2E6',
    slate300:'#CED4DA', slate400:'#ADB5BD', slate500:'#6C757D', slate600:'#495057',
    slate700:'#2C3E50', slate800:'#1A2A3A', slate900:'#0D1B2A',
    blue:'#1565C0', teal:'#00897B', green:'#2E7D32', greenBg:'#E8F5E9',
    red:'#C62828', redBg:'#FFEBEE', redBd:'#FFCDD2',
    amber:'#E65100', amberBg:'#FFF8E1', purple:'#6A1B9A', purpleBg:'#EDE7F6',
    dSampling:'#7B1FA2',
  };
  const _r = { sm:'6px', md:'8px', lg:'12px', xl:'16px' };
  const _today = new Date().toISOString().split('T')[0];

  // expose design tokens to sub-components via window (avoids re-declaration)
  window.__QM_S = _S;

  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2,7); }

  function calcResult(sections, constrItems) {
    // REJECTED if any section is REJECTED
    const allApprovals = [...sections, ...constrItems];
    if (allApprovals.some(a => a.value === 'REJECTED')) return 'REJECTED';
    if (allApprovals.some(a => a.value === 'APPROVED WITH COMMENTS')) return 'APPROVED WITH COMMENTS';
    if (allApprovals.every(a => a.value === 'APPROVED' || a.value === 'NA' || a.value === 'NOT CHECKED')) return 'APPROVED';
    return 'PENDING';
  }

  function resultColor(r) {
    if (r === 'APPROVED')                  return { color:'#2E7D32', bg:'#E8F5E9' };
    if (r === 'APPROVED WITH COMMENTS')    return { color:'#E65100', bg:'#FFF8E1' };
    if (r === 'REJECTED')                  return { color:'#C62828', bg:'#FFEBEE' };
    return { color:'#6C757D', bg:'#F4F6F8' };
  }

  // ── ANN-01 · SAMPLE AUDIT REPORT ─────────────────────────────────────
  function Ann01_SampleAuditReport({ db, setDb, filters }) {
    const [tab, setTab]         = React.useState('form');   // 'form' | 'history'
    const [saving, setSaving]   = React.useState(false);
    const [saved,  setSaved]    = React.useState(false);
    const [editing, setEditing] = React.useState(null);     // record id being edited

    // ── form state ──
    const blankForm = () => ({
      id:          uid(),
      date:        _today,
      submStatus:  SUBMISSION_STATUSES[0],
      sampleType:  SAMPLE_TYPES_ALL[0],
      buyer:       filters.buyer   || '',
      brand:       filters.brand   || '',
      style:       filters.style   || '',
      po:          filters.po      || '',
      colour:      filters.colour  || '',
      product:     filters.product || '',
      qty:         filters.qty     || '',
      exFac:       filters.exFac   || '',
      merchant:    '',
      checkedBy:   '',
      checkedDate: _today,

      // Fabric quality
      fabQuality: 'PENDING',
      fabStdWt:   '',
      fabActWt:   '',
      linQuality: 'PENDING',

      // SAR section approvals
      sarRows: SAR_SECTIONS.flatMap(s => s.items.map(it => ({ id: it.id, label: it.label, category: s.category, value:'PENDING', date:'', comments:'' }))),

      // Construction / workmanship checklist
      constrRows: CONSTR_ITEMS.map(it => ({ id: it.id, label: it.label, value:'PENDING', comments:'' })),

      // Measurement comments
      measComments: '',
      constrComments: '',
      otherComments: '',

      // Final
      result: 'PENDING',
      specAttached: false,
      condSample: '',
    });

    const [form, setForm] = React.useState(blankForm());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = form.style || form.styleNo || '';
      const poKey    = form.po    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setForm(prev => ({
        ...prev,
        ...(fill.qty && !prev.qty      ? { qty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac  ? { exFac:  fill.exFac        } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0] } : {}),
      }));
    }, [form.style, form.styleNo, form.po]); // eslint-disable-line

    // sync filters → form
    React.useEffect(() => {
      setForm(f => ({
        ...f,
        buyer:  filters.buyer  || f.buyer,
        brand:  filters.brand  || f.brand,
        style:  filters.style  || f.style,
        po:     filters.po     || f.po,
        colour: filters.colour || f.colour,
      }));
    }, [filters]);

    const records = (db.sampling || []).filter(r => r.formType === 'SAR');

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    const setSarRow = (id, field, val) => setForm(f => ({
      ...f,
      sarRows: f.sarRows.map(r => r.id === id ? { ...r, [field]: val } : r),
    }));

    const setConstrRow = (id, field, val) => setForm(f => ({
      ...f,
      constrRows: f.constrRows.map(r => r.id === id ? { ...r, [field]: val } : r),
    }));

    const computeResult = (f) => {
      const allVals = [...f.sarRows, ...f.constrRows].map(r => r.value);
      if (allVals.some(v => v === 'REJECTED'))               return 'REJECTED';
      if (allVals.some(v => v === 'APPROVED WITH COMMENTS')) return 'APPROVED WITH COMMENTS';
      if (allVals.every(v => v === 'APPROVED' || v === 'NA' || v === 'NOT CHECKED' || v === 'PENDING')) {
        if (allVals.filter(v => v === 'APPROVED').length > 0) return 'APPROVED';
      }
      return 'PENDING';
    };

    const handleSave = async () => {
      setSaving(true);
      const record = { ...form, formType: 'SAR', result: computeResult(form), savedAt: new Date().toISOString() };
      const id = await apiSave('sampling_sar', record);
      record.serverId = id;
      setDb(prev => {
        const list = prev.sampling || [];
        const exists = list.findIndex(r => r.id === form.id);
        return {
          ...prev,
          sampling: exists >= 0 ? list.map(r => r.id === form.id ? record : r) : [...list, record],
        };
      });
      setSaving(false); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditing(null);
      setForm(blankForm());
    };

    const loadEdit = (rec) => {
      setForm({ ...rec });
      setEditing(rec.id);
      setTab('form');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const rc = resultColor(computeResult(form));

    // group sarRows by category
    const sarByCategory = SAR_SECTIONS.map(s => ({
      ...s,
      rows: form.sarRows.filter(r => r.category === s.category),
    }));

    // ── RENDER ─────────────────────────────────────────────────────────
    return (
      <div className="q-fadein" style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>

        {/* Header row */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
          <div>
            <h2 style={{ fontSize:16, fontWeight:800, color:_S.slate800 }}>Ann-01 · Sample Audit Report</h2>
            <div style={{ fontSize:11, color:_S.slate400 }}>Ref: Nir/Sam/01 · Prepared by: Sample QA → Merchant / QA Head</div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <QPrint/>
            {['form','history'].map(t => (
              <QBtn key={t} sm onClick={() => setTab(t)} color={_S.dSampling} outline={tab !== t}>
                {t === 'form' ? (editing ? '✏️ Editing' : '＋ New') : `📋 History (${records.length})`}
              </QBtn>
            ))}
          </div>
        </div>

        {tab === 'history' ? (
          // ── HISTORY TAB ──────────────────────────────────────────────
          <QCard>
            <QSecHead title="Submitted Records" color={_S.dSampling}
              action={<QBadge color={_S.dSampling}>{records.length} records</QBadge>}/>
            <QTable
              headers={['Date','Style','PO','Buyer','Type','Submission','Result','Actions']}
              rows={records.map(rec => [
                rec.date,
                <strong>{rec.style||'—'}</strong>,
                rec.po||'—',
                rec.buyer||'—',
                <QBadge color={_S.purple}>{rec.sampleType}</QBadge>,
                <QBadge color={_S.teal}>{rec.submStatus}</QBadge>,
                <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, color:resultColor(rec.result).color, background:resultColor(rec.result).bg }}>{rec.result}</span>,
                <QBtn sm outline color={_S.blue} onClick={() => loadEdit(rec)}>Edit</QBtn>,
              ])}
              emptyMsg="No sample audit records yet. Submit the form to create one."
            />
          </QCard>
        ) : (
          // ── FORM TAB ─────────────────────────────────────────────────
          <>
            {/* Result banner */}
            <div style={{ padding:'12px 18px', background:rc.bg, border:`2px solid ${rc.color}33`, borderRadius:_r.lg, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:_S.slate500, textTransform:'uppercase' }}>Auto-computed Result</div>
                <div style={{ fontSize:22, fontWeight:800, color:rc.color, letterSpacing:'-.5px' }}>{computeResult(form)}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:11, color:_S.slate400 }}>SAR Ref: Nir/Sam/01</div>
                <div style={{ fontSize:11, color:_S.slate400 }}>{form.date}</div>
              </div>
            </div>

            {/* SECTION 1: Order Header */}
            <QCard>
              <QSecHead title="Order & Sample Header" color={_S.dSampling}/>
              <QGrid cols={4} gap={14}>
                <QField label="Date" type="date" value={form.date} onChange={e => set('date', e.target.value)}/>
                <QField label="Submission Status" value={form.submStatus} onChange={e => set('submStatus', e.target.value)} options={SUBMISSION_STATUSES}/>
                <QField label="Sample Type" value={form.sampleType} onChange={e => set('sampleType', e.target.value)} options={SAMPLE_TYPES_ALL}/>
                <QField label="Buyer" value={form.buyer} onChange={e => set('buyer', e.target.value)} options={['', ...dbGet('buyers',_FB_BUYERS)]}/>
                <QField label="Brand" value={form.brand} onChange={e => set('brand', e.target.value)} options={['', ...dbGet('brands',_FB_BRANDS)]}/>
                <QField label="Style No." value={form.style} onChange={e => set('style', e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style number..."/>
                <QField label="PO No." value={form.po} onChange={e => set('po', e.target.value)} options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <QField label="Colour" value={form.colour} onChange={e => set('colour', e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <QField label="Product" value={form.product} onChange={e => set('product', e.target.value)} placeholder="Product type..."/>
                <QField label="Order Qty" type="number" value={form.qty} onChange={e => set('qty', e.target.value)} placeholder="0"/>
                <QField label="Ex-Factory Date" type="date" value={form.exFac} onChange={e => set('exFac', e.target.value)} note="Auto-fills from PO data when style is selected"/>
                <QField label="Checked Date" type="date" value={form.checkedDate} onChange={e => set('checkedDate', e.target.value)}/>
                <QField label="Line QC Name" value={form.checkedBy} onChange={e => set('checkedBy', e.target.value)} placeholder="Inspector name..."/>
                <QField label="Merchant / Tech" value={form.merchant} onChange={e => set('merchant', e.target.value)} placeholder="Merchant name..."/>
                <QField label="Condition of Sample" value={form.condSample} onChange={e => set('condSample', e.target.value)} placeholder="Good / Worn / Other..."/>
                <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:18 }}>
                  <input type="checkbox" id="spec" checked={form.specAttached} onChange={e => set('specAttached', e.target.checked)} style={{ width:16,height:16,accentColor:_S.dSampling }}/>
                  <label htmlFor="spec" style={{ fontSize:12, fontWeight:600, color:_S.slate600, cursor:'pointer' }}>Spec Sheet Attached</label>
                </div>
              </QGrid>
            </QCard>

            {/* SECTION 2: Fabric & Lining Quality */}
            <QCard>
              <QSecHead title="Fabric, Lining & Accessories Quality" color={_S.teal}/>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14 }}>
                <QField label="Fabric Quality" value={form.fabQuality} onChange={e => set('fabQuality', e.target.value)} options={FABRIC_QUALITIES}/>
                <QField label="Lining Quality" value={form.linQuality} onChange={e => set('linQuality', e.target.value)} options={LINING_QUALITIES}/>
                <QField label="Accessories Quality" value={form.accQuality||'PENDING'} onChange={e => set('accQuality', e.target.value)} options={ACCESSORIES_QUALITY}/>
                <QField label="Wash Status" value={form.washStatus||'NA'} onChange={e => set('washStatus', e.target.value)} options={WASH_STATUSES}/>
                <QField label="Fabric Std Weight (gsm)" type="number" value={form.fabStdWt} onChange={e => set('fabStdWt', e.target.value)} placeholder="0"/>
                <QField label="Fabric Act Weight (gsm)" type="number" value={form.fabActWt} onChange={e => set('fabActWt', e.target.value)} placeholder="0"
                  note={form.fabStdWt && form.fabActWt ? `Variance: ${(form.fabActWt - form.fabStdWt).toFixed(1)} gsm` : ''}/>
                <QField label="Sizes Checked" value={form.sizes||''} onChange={e => set('sizes', e.target.value)} placeholder="e.g. S,M,L,XL"/>
                <QField label="Quantity Checked" type="number" value={form.sampQty||''} onChange={e => set('sampQty', e.target.value)} placeholder="0"/>
              </div>
            </QCard>

            {/* SECTION 3: SAR Checklist by Category */}
            <QCard>
              <QSecHead title="SAR Approval Checklist (Nir/Sam/01)" color={_S.dSampling}/>
              {sarByCategory.map(cat => (
                <div key={cat.category} style={{ marginBottom:16 }}>
                  <div style={{ fontSize:11, fontWeight:800, color:_S.dSampling, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8, paddingBottom:4, borderBottom:`2px solid ${_S.dSampling}22` }}>
                    {cat.category}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:10 }}>
                    {cat.rows.map(row => (
                      <div key={row.id} style={{ background:_S.slate50, borderRadius:_r.md, padding:'10px 12px', border:`1px solid ${_S.slate200}` }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                          <span style={{ fontSize:12, fontWeight:600, color:_S.slate700 }}>{row.label}</span>
                          <span style={{ padding:'2px 9px', borderRadius:10, fontSize:10, fontWeight:700, color:resultColor(row.value).color, background:resultColor(row.value).bg }}>
                            {row.value}
                          </span>
                        </div>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom: row.value !== 'PENDING' && row.value !== 'APPROVED' && row.value !== 'NA' ? 6 : 0 }}>
                          {APPROVAL_OPTIONS.map(opt => (
                            <button key={opt} onClick={() => setSarRow(row.id, 'value', opt)}
                              style={{
                                padding:'4px 9px', fontSize:10, fontWeight:600, borderRadius:10, cursor:'pointer',
                                background: row.value === opt ? resultColor(opt).bg : _S.slate100,
                                color:      row.value === opt ? resultColor(opt).color : _S.slate400,
                                border:     row.value === opt ? `1.5px solid ${resultColor(opt).color}55` : `1px solid ${_S.slate200}`,
                                transition:'all .12s',
                              }}>
                              {opt === 'APPROVED' ? '✓' : opt === 'REJECTED' ? '✗' : opt === 'APPROVED WITH COMMENTS' ? '⚠' : opt === 'NA' ? 'NA' : opt === 'NOT CHECKED' ? '—' : '⏳'}
                              {' '}{opt}
                            </button>
                          ))}
                        </div>
                        {(row.value === 'APPROVED WITH COMMENTS' || row.value === 'REJECTED' || row.value !== 'PENDING') && row.value !== 'APPROVED' && row.value !== 'NA' && row.value !== 'NOT CHECKED' && (
                          <input placeholder="Comment / action required..."
                            value={row.comments} onChange={e => setSarRow(row.id, 'comments', e.target.value)}
                            style={{ width:'100%', padding:'5px 8px', fontSize:11, borderRadius:_r.sm, border:`1px solid ${_S.slate300}`, marginTop:4 }}/>
                        )}
                        <div style={{ display:'flex', gap:8, marginTop:6, alignItems:'center' }}>
                          <span style={{ fontSize:10, color:_S.slate400 }}>Date Approved:</span>
                          <input type="date" value={row.date} onChange={e => setSarRow(row.id, 'date', e.target.value)}
                            style={{ fontSize:10, padding:'3px 6px', border:`1px solid ${_S.slate200}`, borderRadius:_r.sm, color:_S.slate600 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </QCard>

            {/* SECTION 4: Construction / Workmanship Checklist */}
            <QCard>
              <QSecHead title="Construction & Trims Checklist" color={_S.teal}/>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:8 }}>
                {form.constrRows.map(row => (
                  <div key={row.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background:_S.slate50, borderRadius:_r.sm, border:`1px solid ${_S.slate200}` }}>
                    <div style={{ flex:1, fontSize:12, color:_S.slate700, fontWeight:500 }}>{row.label}</div>
                    <select value={row.value} onChange={e => setConstrRow(row.id, 'value', e.target.value)}
                      style={{
                        padding:'4px 8px', fontSize:11, borderRadius:_r.sm, border:`1px solid ${_S.slate300}`,
                        background: resultColor(row.value).bg, color: resultColor(row.value).color, fontWeight:700, cursor:'pointer',
                      }}>
                      {APPROVAL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </QCard>

            {/* SECTION 5: Comments */}
            <QCard>
              <QSecHead title="Comments & Remarks" color={_S.slate500}/>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
                <QField label="Measurement Comments" type="textarea" rows={4} value={form.measComments} onChange={e => set('measComments', e.target.value)} placeholder="Measurement deviations, tolerances..."/>
                <QField label="Construction / Stitching" type="textarea" rows={4} value={form.constrComments} onChange={e => set('constrComments', e.target.value)} placeholder="Stitching issues, workmanship..."/>
                <QField label="Other Comments" type="textarea" rows={4} value={form.otherComments} onChange={e => set('otherComments', e.target.value)} placeholder="Any other observations..."/>
              </div>
            </QCard>

            {/* SECTION 6: Type of Sample Details */}
            <QCard>
              <QSecHead title="Type of Sample — Details" color={_S.purple}/>
              <QGrid cols={4} gap={14}>
                <QField label="Surface / Overall Appearance" value={form.appearance||''} onChange={e => set('appearance', e.target.value)} options={['','OK','NOT OK','PENDING']}/>
                <QField label="Placement" value={form.placement||''} onChange={e => set('placement', e.target.value)} options={['','OK','NOT OK','PENDING']}/>
                <QField label="Type / Technique" value={form.printTech||''} onChange={e => set('printTech', e.target.value)} options={['','OK','NOT OK','PENDING']}/>
                <QField label="Abrasion" value={form.abrasion||''} onChange={e => set('abrasion', e.target.value)} options={['','OK','NOT OK','PENDING']}/>
                <QField label="EXT / Marketing" value={form.extMarketing||''} onChange={e => set('extMarketing', e.target.value)} options={['','OK','NOT OK','PENDING']}/>
                <QField label="Wet Processing" value={form.wetProc||''} onChange={e => set('wetProc', e.target.value)} options={['','OK','NOT OK','PENDING']}/>
                <QField label="Color Shade in Shadeband" value={form.shadeband||''} onChange={e => set('shadeband', e.target.value)} options={['','OK','NOT OK','PENDING']}/>
                <QField label="Quantity Submitted" type="number" value={form.submQty||''} onChange={e => set('submQty', e.target.value)} placeholder="0"/>
              </QGrid>
            </QCard>

            {/* SAVE */}
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', alignItems:'center', flexWrap:'wrap' }}>
              {editing && (
                <QBtn sm outline color={_S.slate500} onClick={() => { setEditing(null); setForm(blankForm()); }}>
                  Cancel Edit
                </QBtn>
              )}
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ padding:'8px 16px', background:rc.bg, borderRadius:_r.md, fontSize:12, fontWeight:700, color:rc.color }}>
                  Result: {computeResult(form)}
                </div>
                <QBtn onClick={handleSave} disabled={saving} color={_S.dSampling} icon={saved ? '✓' : saving ? '…' : '💾'}>
                  {saved ? 'Saved!' : saving ? 'Saving...' : editing ? 'Update Record' : 'Save SAR'}
                </QBtn>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── ANN-02 · SAMPLE COMMENT FORM ─────────────────────────────────────
  function Ann02_SampleCommentForm({ db, setDb, filters }) {
    const [tab, setTab]       = React.useState('form');
    const [saving, setSaving] = React.useState(false);
    const [saved,  setSaved]  = React.useState(false);

    const blankForm = () => ({
      id:          uid(),
      date:        _today,
      dayOfWeek:   new Date().toLocaleDateString('en-GB',{ weekday:'long' }),
      buyer:       filters.buyer  || '',
      styleNo:     filters.style  || '',
      line:        '',
      lineQC:      '',
      checkedDate: _today,
      sampleType:  SAMPLE_TYPES_ALL[0],
      // Comments
      measComments:  '',
      constrComments:'',
      otherComments: '',
      merchantSign:  '',
      sampledBy:     '',
    });

    const [form, setForm] = React.useState(blankForm());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = form.style || form.styleNo || '';
      const poKey    = form.po    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setForm(prev => ({
        ...prev,
        ...(fill.qty && !prev.qty      ? { qty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac  ? { exFac:  fill.exFac        } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0] } : {}),
      }));
    }, [form.style, form.styleNo, form.po]); // eslint-disable-line
    const records = (db.sampling || []).filter(r => r.formType === 'COMMENT');

    React.useEffect(() => {
      setForm(f => ({
        ...f,
        buyer:  filters.buyer  || f.buyer,
        styleNo:filters.style  || f.styleNo,
      }));
    }, [filters]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // Measurement rows — dynamic add/remove
    const [measRows, setMeasRows] = React.useState([
      { id:uid(), point:'Chest',             spec:'', actual:'', tol:'', ok:null },
      { id:uid(), point:'Body Length',       spec:'', actual:'', tol:'', ok:null },
      { id:uid(), point:'Sleeve Length',     spec:'', actual:'', tol:'', ok:null },
      { id:uid(), point:'Across Shoulder',   spec:'', actual:'', tol:'', ok:null },
      { id:uid(), point:'Waist',             spec:'', actual:'', tol:'', ok:null },
      { id:uid(), point:'Hip / Seat',        spec:'', actual:'', tol:'', ok:null },
    ]);

    const addMeasRow = () => setMeasRows(r => [...r, { id:uid(), point:'', spec:'', actual:'', tol:'', ok:null }]);
    const updMeasRow = (id, field, val) => {
      setMeasRows(rows => rows.map(r => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: val };
        // auto ok/fail
        if ((field === 'actual' || field === 'spec' || field === 'tol') && updated.spec && updated.actual && updated.tol) {
          const diff = Math.abs(parseFloat(updated.actual) - parseFloat(updated.spec));
          updated.ok = diff <= parseFloat(updated.tol);
        }
        return updated;
      }));
    };
    const delMeasRow = (id) => setMeasRows(r => r.filter(x => x.id !== id));

    const handleSave = async () => {
      setSaving(true);
      const record = { ...form, formType:'COMMENT', measRows, savedAt: new Date().toISOString() };
      const id = await apiSave('sampling_comment', record);
      record.serverId = id;
      setDb(prev => ({ ...prev, sampling: [...(prev.sampling||[]), record] }));
      setSaving(false); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setMeasRows([
        { id:uid(), point:'Chest', spec:'', actual:'', tol:'', ok:null },
        { id:uid(), point:'Body Length', spec:'', actual:'', tol:'', ok:null },
        { id:uid(), point:'Sleeve Length', spec:'', actual:'', tol:'', ok:null },
        { id:uid(), point:'Across Shoulder', spec:'', actual:'', tol:'', ok:null },
      ]);
      setForm(blankForm());
    };

    const measPass = measRows.filter(r => r.ok === true).length;
    const measFail = measRows.filter(r => r.ok === false).length;

    return (
      <div className="q-fadein" style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
          <div>
            <h2 style={{ fontSize:16, fontWeight:800, color:_S.slate800 }}>Ann-02 · Sample Comment Form</h2>
            <div style={{ fontSize:11, color:_S.slate400 }}>Ref: Nir/Sam/02 · Prepared by: Sample QC → Merchant</div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <QPrint/>
            {['form','history'].map(t => (
              <QBtn key={t} sm onClick={() => setTab(t)} color={_S.dSampling} outline={tab !== t}>
                {t === 'form' ? '＋ New Comment' : `📋 History (${records.length})`}
              </QBtn>
            ))}
          </div>
        </div>

        {tab === 'history' ? (
          <QCard>
            <QSecHead title="Sample Comment Records" color={_S.dSampling}
              action={<QBadge color={_S.dSampling}>{records.length} records</QBadge>}/>
            <QTable
              headers={['Date','Style','Buyer','Sample Type','Line QC','Meas (P/F)','Saved By']}
              rows={records.map(rec => [
                rec.date,
                <strong>{rec.styleNo||'—'}</strong>,
                rec.buyer||'—',
                <QBadge color={_S.purple}>{rec.sampleType}</QBadge>,
                rec.lineQC||'—',
                <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11 }}>
                  <span style={{ color:_S.green }}>{(rec.measRows||[]).filter(r=>r.ok===true).length}P</span>
                  {' / '}
                  <span style={{ color:_S.red }}>{(rec.measRows||[]).filter(r=>r.ok===false).length}F</span>
                </span>,
                rec.sampledBy||'—',
              ])}
              emptyMsg="No comment forms yet."
            />
          </QCard>
        ) : (
          <>
            {/* SECTION 1: Header */}
            <QCard>
              <QSecHead title="Order Header" color={_S.dSampling}/>
              <QGrid cols={4} gap={14}>
                <QField label="Date" type="date" value={form.date} onChange={e => set('date', e.target.value)}/>
                <QField label="Day" value={new Date(form.date+'T12:00').toLocaleDateString('en-GB',{weekday:'long'})} readOnly/>
                <QField label="Buyer" value={form.buyer} onChange={e => set('buyer', e.target.value)} options={['','UK BUYER','EU BUYER','US BUYER','AUSTRALIA',"DILLARD'S",'OTHER']}/>
                <QField label="Style No." value={form.styleNo} onChange={e => set('styleNo', e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style number..."/>
                <QField label="Line No." value={form.line} onChange={e => set('line', e.target.value)} options={['', ...dbGet('lines',_FB_LINES)]}/>
                <QField label="Line QC Name" value={form.lineQC} onChange={e => set('lineQC', e.target.value)} placeholder="Inspector name..."/>
                <QField label="Checked Date" type="date" value={form.checkedDate} onChange={e => set('checkedDate', e.target.value)}/>
                <QField label="Sample Type" value={form.sampleType} onChange={e => set('sampleType', e.target.value)} options={SAMPLE_TYPES_ALL}/>
              </QGrid>
            </QCard>

            {/* SECTION 2: Measurement Comments Table */}
            <QCard>
              <QSecHead title="Measurement Comments"
                color={measFail > 0 ? _S.red : measPass > 0 ? _S.green : _S.teal}
                action={
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <QBadge color={_S.green} bg={_S.greenBg}>{measPass} Pass</QBadge>
                    <QBadge color={_S.red}   bg={_S.redBg}>  {measFail} Fail</QBadge>
                    <QBtn sm onClick={addMeasRow} color={_S.teal}>＋ Add Point</QBtn>
                  </div>
                }/>

              {/* Table header */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 80px 80px 80px 80px 36px', gap:6, marginBottom:6 }}>
                {['Measurement Point','Spec','Actual','Tol ±','Result',''].map((h,i) => (
                  <div key={i} style={{ fontSize:10, fontWeight:700, color:_S.slate400, textTransform:'uppercase', textAlign: i>0?'center':'left' }}>{h}</div>
                ))}
              </div>

              {measRows.map((row, idx) => (
                <div key={row.id} style={{ display:'grid', gridTemplateColumns:'1fr 80px 80px 80px 80px 36px', gap:6, marginBottom:5, alignItems:'center' }}>
                  <input value={row.point} onChange={e => updMeasRow(row.id,'point',e.target.value)}
                    placeholder={`Point ${idx+1}...`}
                    style={{ padding:'7px 9px', borderRadius:_r.sm, border:`1px solid ${_S.slate200}`, fontSize:12, color:_S.slate700 }}/>
                  {['spec','actual','tol'].map(field => (
                    <input key={field} type="number" value={row[field]} onChange={e => updMeasRow(row.id,field,e.target.value)}
                      placeholder="0.0"
                      style={{ padding:'7px 6px', borderRadius:_r.sm, border:`1px solid ${_S.slate200}`, fontSize:12, textAlign:'center', color:_S.slate700 }}/>
                  ))}
                  <div style={{ textAlign:'center' }}>
                    {row.ok === null ? <span style={{ fontSize:12, color:_S.slate300 }}>—</span>
                      : row.ok
                        ? <span style={{ fontSize:13, color:_S.green, fontWeight:800 }}>✓</span>
                        : <span style={{ fontSize:13, color:_S.red,   fontWeight:800 }}>✗</span>}
                  </div>
                  <button onClick={() => delMeasRow(row.id)}
                    style={{ background:_S.redBg, border:`1px solid ${_S.redBd}`, borderRadius:_r.sm, cursor:'pointer', padding:'4px 8px', color:_S.red, fontSize:11, fontWeight:700 }}>
                    ✕
                  </button>
                </div>
              ))}

              <div style={{ marginTop:10, padding:'8px 12px', background:_S.slate50, borderRadius:_r.sm, display:'flex', gap:20 }}>
                <span style={{ fontSize:12, color:_S.slate500 }}>
                  Total Points: <strong style={{ color:_S.slate700 }}>{measRows.length}</strong>
                </span>
                <span style={{ fontSize:12, color:_S.green }}>Passed: <strong>{measPass}</strong></span>
                <span style={{ fontSize:12, color:_S.red   }}>Failed: <strong>{measFail}</strong></span>
                <span style={{ fontSize:12, color:measFail>0?_S.red:_S.green, fontWeight:700 }}>
                  Meas Result: {measFail>0?'FAIL (Action Required)':'PASS'}
                </span>
              </div>
            </QCard>

            {/* SECTION 3: Quality Comments */}
            <QCard>
              <QSecHead title="Quality Comments" color={_S.slate600}/>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
                <QField label="Measurement Comments" type="textarea" rows={5} value={form.measComments}
                  onChange={e => set('measComments', e.target.value)} placeholder="Measurement deviations found, corrective action needed..."/>
                <QField label="Construction / Stitching" type="textarea" rows={5} value={form.constrComments}
                  onChange={e => set('constrComments', e.target.value)} placeholder="Stitching quality, workmanship issues..."/>
                <QField label="Other Comments" type="textarea" rows={5} value={form.otherComments}
                  onChange={e => set('otherComments', e.target.value)} placeholder="Accessories, washing, packaging, other..."/>
              </div>
              <QDiv label="Sign-off"/>
              <QGrid cols={4} gap={14}>
                <QField label="Sample Checked By" value={form.sampledBy} onChange={e => set('sampledBy', e.target.value)} placeholder="Name..."/>
                <QField label="Merchant Sign" value={form.merchantSign} onChange={e => set('merchantSign', e.target.value)} placeholder="Name..."/>
              </QGrid>
            </QCard>

            {/* SAVE */}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
              <QBtn onClick={handleSave} disabled={saving} color={_S.dSampling} icon={saved?'✓':saving?'…':'💾'}>
                {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Comment Form'}
              </QBtn>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── SAMPLING DEPT SHELL ──────────────────────────────────────────────
  function SamplingDept({ db, setDb, filters, activeForm, setActiveForm }) {
    const subForms = [
      { id:'ann01', label:'Ann-01 · Sample Audit Report',  icon:'📋', ref:'Nir/Sam/01', formType:'SAR'    },
      { id:'ann02', label:'Ann-02 · Sample Comment Form',  icon:'💬', ref:'Nir/Sam/02', formType:'COMMENT' },
    ];

    const [active, setActive] = React.useState('ann01');

    const counts = {
      ann01: (db.sampling||[]).filter(r=>r.formType==='SAR').length,
      ann02: (db.sampling||[]).filter(r=>r.formType==='COMMENT').length,
    };

    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
        {/* Sub-nav tabs */}
        <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${_S.slate200}`, background:_S.white, paddingTop:4, paddingLeft:16, overflowX:'auto', flexShrink:0 }}>
          {subForms.map(f => (
            <button key={f.id} onClick={() => setActive(f.id)}
              style={{
                padding:'10px 18px', fontSize:12, fontWeight: active===f.id ? 700 : 400,
                color: active===f.id ? _S.dSampling : _S.slate500,
                background: 'transparent', border:'none',
                borderBottom: active===f.id ? `2.5px solid ${_S.dSampling}` : '2.5px solid transparent',
                cursor:'pointer', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap',
              }}>
              <span>{f.icon}</span>{f.label}
              {counts[f.id] > 0 && (
                <span style={{ background:_S.dSampling+'22', color:_S.dSampling, padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>{counts[f.id]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Active form */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {active === 'ann01' && <Ann01_SampleAuditReport  db={db} setDb={setDb} filters={filters}/>}
          {active === 'ann02' && <Ann02_SampleCommentForm  db={db} setDb={setDb} filters={filters}/>}
        </div>
      </div>
    );
  }

  // ── REGISTER ─────────────────────────────────────────────────────────
  DEPT_REGISTRY['sampling'] = SamplingDept;
  console.log('[QMS] Dept-01 Sampling registered ✓ (Ann-01, Ann-02)');

})();

// ════════════════════════════════════════════════════════════════════════════
//  DEPT-02 · WAREHOUSE / RAW MATERIAL · Ann-03 → Ann-13  (11 forms)
//  HOW TO USE: Paste this entire block at the very bottom of your file,
//  below the Sampling block (below the last line: })(); )
//
//  Ann-03  Trims Inspection Report        Nir/Wh/01  → warehouse_trims_insp
//  Ann-04  Trims Card                     Nir/Wh/02  → warehouse_trims_card
//  Ann-05  Fabric Inspection 4-Point      Nir/Wh/03  → warehouse_fabric_insp
//  Ann-06  Fabric Claim Register          Nir/Wh/04  → warehouse_fabric_claim
//  Ann-07  Trims Approval Status          Nir/Wh/05  → warehouse_trims_approval
//  Ann-08  Shade Segregation Summary      Nir/Wh/06  → warehouse_shade_seg
//  Ann-09  Roll Wise All Report                Nir/Wh/07  → warehouse_roll_wise_all
//  Ann-10  Width Inspection Report        Nir/Wh/08  → warehouse_width_insp
//  Ann-11  Fabric Swatch Card             Nir/Wh/09  → warehouse_swatch_card
//  Ann-12  Sample Submission – Wash       Nir/Wh/10  → warehouse_wash_sub
//  Ann-13  Sample Submission – Testing    Nir/Wh/11  → warehouse_test_sub
// ════════════════════════════════════════════════════════════════════════════

(function registerWarehouse() {

  // ─── colours (match existing QualityControl.js palette exactly) ──────────
  const C = {
    bg:'#0D1B2A', bgCard:'#162032', white:'#FFFFFF',
    slate50:'#F8F9FA', slate100:'#F4F6F8', slate200:'#DEE2E6',
    border:'#DEE2E6', text:'#1A2A3A', textSub:'#6C757D', textMuted:'#ADB5BD',
    accent:'#00B4D8',
    wh:'#00897B',           // warehouse dept accent
    success:'#2E7D32', successBg:'#E8F5E9', successBd:'#A5D6A7',
    danger :'#C62828', dangerBg :'#FFEBEE', dangerBd :'#FFCDD2',
    warning:'#E65100', warningBg:'#FFF8E1', warningBd:'#FFE082',
  };
  const R = { sm:'6px', md:'8px', lg:'12px' };
  const TODAY = new Date().toISOString().split('T')[0];
  const API   = 'https://nirvikalp.integrashion.com/api/save_universal.php';

  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }

  // ─── master data ─────────────────────────────────────────────────────────
  const BRANDS = dbGet('brands', _FB_BRANDS);
  const BUYERS = dbGet('buyers', _FB_BUYERS);
  const FABRICS = dbGet('fabrics', _FB_FABRICS);
  const TRIM_TYPES = [
    'Button','Zipper','Snap Button','Hook & Bar','Velcro','Elastic Band',
    'Interlining','Fusible Tape','Drawstring','Main Label','Size Label',
    'Care Label','Hang Tag','Price Ticket','Polybag','Carton','Thread','Rivets','Sticker','Other',
  ];
  const FABRIC_DEFECTS = [
    'Hole','Weaving Defect','Slub','Miss Pick / End Out','Knot','Stain',
    'Shade Bar','Crease Mark','Thick Yarn','Thin Yarn','Missing Yarn',
    'Fly Yarn','Stop Mark','Long Streak Mark','Joints','Running Shade',
  ];
  const TRIM_CHECKS = [
    'Damage / Physical Condition','Weaving / Knit Defect','Letter Clarity',
    'Print Detail & Code Correct','Made In / Country of Origin',
    'Shade within Range','Color Fastness','Pull / Breaking Strength',
    'Metal Detector Passed','Fraying / Edge Condition','Sealing Quality',
    'Adhesiveness / Bond Strength',
  ];
  const TESTING_STDS = [
    { name:'Colorfastness to Crocking',             std:'AATCC-08'              },
    { name:'Fabrication / Weight',                  std:'ASTM D-3776'           },
    { name:'Colorfastness to Laundering',           std:'AATCC-61 (2A)'         },
    { name:'Dimensional Stability – Laundering',    std:'AATCC-135'             },
    { name:'Garment Appearance after Cleaning',     std:'LS&CO-23A / AATCC-135' },
    { name:'Tear Strength',                         std:'ASTM D-1424'           },
    { name:'Seam Strength',                         std:'ASTM D1683 Modified'   },
    { name:'Tensile Strength',                      std:'ASTM D-5034'           },
    { name:'Seam Twist',                            std:'LS&CO-23A'             },
    { name:'Weight',                                std:'ASTM D-3776'           },
    { name:'Seam Slippage',                         std:'ASTM D-434'            },
    { name:'Skew Movement (Torque)',                std:'AATCC-179'             },
    { name:'Print Durability',                      std:'LS&CO-23A'             },
    { name:'Absorbency',                            std:'AATCC-79'              },
    { name:'Colorfastness – Extended Home Launder', std:'LS&CO-23A'             },
    { name:'Crease Retention',                      std:'AATCC-88C'             },
    { name:'Smoothness Retention',                  std:'AATCC-143'             },
    { name:'Wrinkle Recovery',                      std:'AATCC-128'             },
    { name:'Seam Smoothness',                       std:'AATCC-88B'             },
    { name:'Soil & Stain Release',                  std:'CTL / AATCC-130'       },
    { name:'Water Repellency',                      std:'AATCC-193'             },
    { name:'Waistband Stretch',                     std:'LS&CO-48'              },
    { name:'Flex Abrasion Test',                    std:'ASTM D-3885'           },
    { name:'Button Pull Test',                      std:'ASTM D-4846B'          },
  ];

  // ─── shared API save (writes to db.warehouse array) ──────────────────────
  async function saveToServer(category, record, db, setDb) {
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, data: record }),
      });
      const j = await res.json();
      record.serverId = j.id || Date.now();
    } catch { record.serverId = Date.now(); }

    setDb(prev => {
      const deptKey = record._dept || 'warehouse';
      const list = prev[deptKey] || [];
      const idx  = list.findIndex(r => r.id === record.id);
      return {
        ...prev,
        [deptKey]: idx >= 0 ? list.map(r => r.id === record.id ? record : r) : [...list, record],
      };
    });
  }

  // ─── mini UI components (self-contained, no dependency on base exports) ──

  function Fld({ label, value, onChange, type='text', readOnly=false,
    placeholder='', options=null, required=false, note='', rows=3 }) {
    const base = {
      width:'100%', padding:'9px 11px', fontSize:13, borderRadius:R.md,
      border:`1px solid ${C.border}`, background:readOnly?C.slate50:C.white,
      color:readOnly?C.textSub:C.text, outline:'none', fontFamily:'inherit',
    };
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        <label style={{ fontSize:11, fontWeight:600, color:C.textSub, textTransform:'uppercase', letterSpacing:'.4px' }}>
          {label}{required && <span style={{ color:C.danger }}> *</span>}
        </label>
        {options
          ? <select value={value} onChange={onChange} disabled={readOnly}
              style={{ ...base, cursor: readOnly?'default':'pointer' }}>
              {options.map((o,i) => <option key={i} value={o.value??o}>{o.label??o}</option>)}
            </select>
          : type === 'textarea'
          ? <textarea value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} rows={rows} style={{ ...base, resize:'vertical' }}/>
          : <input type={type} value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} style={base}/>}
        {note && <div style={{ fontSize:11, color:C.textMuted }}>{note}</div>}
      </div>
    );
  }

  function Btn({ children, onClick, color=C.wh, outline=false, sm=false, disabled=false, icon=null }) {
    return (
      <button onClick={onClick} disabled={disabled} style={{
        display:'flex', alignItems:'center', gap:5,
        padding: sm ? '7px 13px' : '10px 20px', borderRadius:R.md,
        border: outline ? `2px solid ${color}` : 'none',
        background: disabled ? C.slate100 : outline ? 'transparent' : color,
        color: disabled ? C.textMuted : outline ? color : C.white,
        fontWeight:700, fontSize: sm ? 12 : 13,
        cursor: disabled ? 'default' : 'pointer', fontFamily:'inherit', transition:'all .15s',
      }}>{icon && <span>{icon}</span>}{children}</button>
    );
  }

  function Bdg({ children, color=C.wh, bg }) {
    return (
      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
        color, background: bg || color+'22', display:'inline-block', whiteSpace:'nowrap' }}>
        {children}
      </span>
    );
  }

  function Res({ result }) {
    const m = {
      PASS:    { c:C.success, b:C.successBg },
      FAIL:    { c:C.danger,  b:C.dangerBg  },
      HOLD:    { c:C.warning, b:C.warningBg },
      PENDING: { c:C.textMuted, b:C.slate100 },
    };
    const x = m[result?.toUpperCase()] || m.PENDING;
    return <Bdg color={x.c} bg={x.b}>{result || 'PENDING'}</Bdg>;
  }

  function Card({ children, style={} }) {
    return (
      <div style={{ background:C.white, border:`1px solid ${C.border}`,
        borderRadius:R.lg, padding:20, ...style }}>
        {children}
      </div>
    );
  }

  function SH({ title, color=C.wh, action=null }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, paddingBottom:12, borderBottom:`2px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:3, height:18, background:color, borderRadius:2 }}/>
          <h3 style={{ fontSize:13, fontWeight:700, color:C.text,
            textTransform:'uppercase', letterSpacing:'.4px' }}>{title}</h3>
        </div>
        {action}
      </div>
    );
  }

  function Grid({ children, gap=14 }) {
    return (
      <div style={{ display:'grid',
        gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap }}>
        {children}
      </div>
    );
  }

  function Tbl({ headers=[], rows=[], emptyMsg='No records found.' }) {
    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:C.slate50 }}>
              {headers.map((h,i) => (
                <th key={i} style={{ padding:'9px 11px', textAlign:'left', fontWeight:700,
                  color:C.textSub, fontSize:11, textTransform:'uppercase',
                  borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0
              ? <tr><td colSpan={headers.length}
                  style={{ padding:24, textAlign:'center', color:C.textMuted, fontStyle:'italic' }}>
                  {emptyMsg}</td></tr>
              : rows.map((row,i) => (
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}`,
                  background: i%2===0 ? C.white : C.slate50 }}>
                  {row.map((cell,j) => (
                    <td key={j} style={{ padding:'9px 11px', color:C.text, verticalAlign:'middle' }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  // checklist row — OK / NG radio + remark on NG
  function ChkRow({ label, ok, onOk, remark, onRemark }) {
    return (
      <div style={{ display:'flex', gap:10, alignItems:'flex-start',
        padding:'9px 0', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', gap:6, flexShrink:0, paddingTop:2 }}>
          {[['OK', true, C.success], ['NG', false, C.danger]].map(([lbl, val, col]) => (
            <label key={lbl} style={{ display:'flex', alignItems:'center', gap:4, cursor:'pointer' }}>
              <input type="radio" checked={ok === val} onChange={() => onOk(val)}
                style={{ accentColor:col, width:14, height:14 }}/>
              <span style={{ fontSize:11, fontWeight:700, color: ok===val ? col : C.textMuted }}>{lbl}</span>
            </label>
          ))}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, color:C.text }}>{label}</div>
          {ok === false && (
            <input placeholder="Corrective action / remark..." value={remark || ''}
              onChange={e => onRemark(e.target.value)}
              style={{ width:'100%', padding:'5px 8px', fontSize:11, borderRadius:R.sm,
                border:`1px solid ${C.dangerBd}`, background:C.dangerBg, marginTop:4 }}/>
          )}
        </div>
      </div>
    );
  }

  // save hook
  function useSave() {
    const [saving, setSaving] = React.useState(false);
    const [saved,  setSaved]  = React.useState(false);
    async function doSave(category, record, db, setDb, extra) {
      setSaving(true);
      await saveToServer(category, record, db, setDb);
      if (extra) extra(record);
      setSaving(false); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    return { saving, saved, doSave };
  }

  // tab bar (used inside each form)
  function TabBar({ tabs, active, setActive }) {
    return (
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`,
        background:C.white, overflowX:'auto', flexShrink:0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActive(t.id)} style={{
            padding:'10px 16px', fontSize:12,
            fontWeight: active===t.id ? 700 : 400,
            color: active===t.id ? C.wh : C.textSub,
            background:'transparent', border:'none', cursor:'pointer',
            borderBottom: active===t.id ? `2.5px solid ${C.wh}` : '2.5px solid transparent',
            display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
          }}>
            {t.icon && <span>{t.icon}</span>}
            {t.label}
            {t.count > 0 && (
              <span style={{ background:C.wh+'22', color:C.wh,
                padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  // page header (title + subtitle + right buttons)
  function PgHead({ title, ref_, action }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:18 }}>
        <div>
          <h2 style={{ fontSize:16, fontWeight:800, color:C.text }}>{title}</h2>
          <div style={{ fontSize:11, color:C.textMuted }}>Ref: {ref_}</div>
        </div>
        {action}
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-03 · TRIMS INSPECTION REPORT  (Nir/Wh/01)
  // ═════════════════════════════════════════════════════════════════
  function Ann03({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'TRIMS_INSP', date:TODAY, clientRef:'', inv:'',
      supp:'', recvDate:TODAY, inspDate:TODAY, item:'',
      buyer: filters.buyer||'', brand: filters.brand||'', style: filters.style||'',
      recvQty:'', inspQty:'', rejectQty:'',
      checks: TRIM_CHECKS.map(l => ({ label:l, ok:null, remark:'' })),
      lengthBw:'', lengthAw:'', shrinkPct:'', widthShrink:'',
      remarks:'', whMgr:'', qcMgr:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const setChk = (i, ok) => setF(f => ({ ...f, checks: f.checks.map((c,ci) => ci===i ? { ...c, ok } : c) }));
    const setRmk = (i, r)  => setF(f => ({ ...f, checks: f.checks.map((c,ci) => ci===i ? { ...c, remark:r } : c) }));

    const rejectPct = () => {
      const r = parseInt(F.rejectQty)||0, i = Math.max(parseInt(F.inspQty)||1, 1);
      return ((r/i)*100).toFixed(1);
    };
    const autoResult = () => {
      const ng = F.checks.filter(c => c.ok===false).length;
      const ok = F.checks.filter(c => c.ok===true).length;
      if (ng === 0 && ok > 0) return 'PASS';
      if (ng <= 2)            return 'HOLD';
      return 'FAIL';
    };

    const recs = (db.warehouse||[]).filter(r => r.formType==='TRIMS_INSP');

    const handleSave = async () => {
      const rec = { ...F, rejectPct:rejectPct(), result:autoResult(), savedAt:new Date().toISOString() };
      await doSave('warehouse_trims_insp', rec, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-03 · Trims Inspection Report" ref_="Nir/Wh/01 · Inspector → Merchant / Warehouse"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={() => setView('form')} color={C.wh}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={() => setView('history')} color={C.wh}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view === 'history' ? (
          <Card>
            <SH title="Inspection Records"/>
            <Tbl headers={['Date','Item','Client Ref#','Supplier','Recv','Inspected','Reject %','Result']}
              rows={recs.map(r => [
                r.date, <strong>{r.item||'—'}</strong>, r.clientRef||'—', r.supp||'—',
                r.recvQty||'—', r.inspQty||'—', `${r.rejectPct||0}%`, <Res result={r.result}/>,
              ])} emptyMsg="No trims inspections yet."/>
          </Card>
        ) : (
          <>
            {/* header */}
            <Card>
              <SH title="Inspection Header" color={C.wh}/>
              <Grid gap={14}>
                <Fld label="Insp. Date"  type="date" value={F.inspDate} onChange={e=>set('inspDate',e.target.value)}/>
                <Fld label="Recv. Date"  type="date" value={F.recvDate} onChange={e=>set('recvDate',e.target.value)}/>
                <Fld label="Client Ref #"  value={F.clientRef}  onChange={e=>set('clientRef',e.target.value)}  placeholder="Client Ref#..."/>
                <Fld label="Invoice #"   value={F.inv}   onChange={e=>set('inv',e.target.value)}   placeholder="INV#..."/>
                <Fld label="Supplier"    value={F.supp}  onChange={e=>set('supp',e.target.value)}  placeholder="Supplier name..."/>
                <Fld label="Item / Trim" value={F.item}  onChange={e=>set('item',e.target.value)}  options={['',...TRIM_TYPES]}/>
                <Fld label="Buyer"       value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Brand"       value={F.brand} onChange={e=>set('brand',e.target.value)} options={['',...BRANDS]}/>
                <Fld label="Style No."   value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
              </Grid>
            </Card>

            {/* AQL */}
            <Card>
              <SH title="AQL Audit Details" color={C.wh}/>
              <Grid gap={14}>
                <Fld label="Received Qty"  type="number" value={F.recvQty}   onChange={e=>set('recvQty',e.target.value)}   placeholder="0"/>
                <Fld label="Inspected Qty" type="number" value={F.inspQty}   onChange={e=>set('inspQty',e.target.value)}   placeholder="0"/>
                <Fld label="Reject Qty"    type="number" value={F.rejectQty} onChange={e=>set('rejectQty',e.target.value)} placeholder="0"/>
                <div style={{ padding:'12px 14px', background:C.slate50, borderRadius:R.md, display:'flex', flexDirection:'column', gap:4 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>Reject %</div>
                  <div style={{ fontSize:22, fontWeight:800,
                    color: parseFloat(rejectPct())>5 ? C.danger : C.success }}>
                    {rejectPct()}%
                  </div>
                  <div style={{ fontSize:10, color:C.textMuted }}>AQL Level 2.5</div>
                </div>
              </Grid>
            </Card>

            {/* checklist */}
            <Card>
              <SH title="Quality Checklist" color={C.wh}
                action={
                  <div style={{ display:'flex', gap:6 }}>
                    <Bdg color={C.success} bg={C.successBg}>{F.checks.filter(c=>c.ok===true).length} OK</Bdg>
                    <Bdg color={C.danger}  bg={C.dangerBg }>{F.checks.filter(c=>c.ok===false).length} NG</Bdg>
                  </div>
                }/>
              <div style={{ columns: 2, columnGap:20 }}>
                {F.checks.map((c,i) => (
                  <div key={i} style={{ breakInside:'avoid', marginBottom:2 }}>
                    <ChkRow label={c.label} ok={c.ok} onOk={v=>setChk(i,v)}
                      remark={c.remark} onRemark={r=>setRmk(i,r)}/>
                  </div>
                ))}
              </div>
            </Card>

            {/* measurements + sign-off */}
            <Card>
              <SH title="Measurements & Sign-off" color={C.wh}/>
              <Grid gap={14}>
                <Fld label="Length B/Wash (cm)" type="number" value={F.lengthBw}   onChange={e=>set('lengthBw',e.target.value)}   placeholder="0"/>
                <Fld label="Length A/Wash (cm)" type="number" value={F.lengthAw}   onChange={e=>set('lengthAw',e.target.value)}   placeholder="0"
                  note={F.lengthBw && F.lengthAw ? `Shrink: ${(((F.lengthBw-F.lengthAw)/F.lengthBw)*100).toFixed(1)}%` : ''}/>
                <Fld label="Length Shrink %"    type="number" value={F.shrinkPct}  onChange={e=>set('shrinkPct',e.target.value)}  placeholder="0"/>
                <Fld label="Width Shrink %"     type="number" value={F.widthShrink} onChange={e=>set('widthShrink',e.target.value)} placeholder="0"/>
                <Fld label="Remarks"  type="textarea" rows={2} value={F.remarks} onChange={e=>set('remarks',e.target.value)} placeholder="Overall remarks..."/>
                <Fld label="W/House Mgr" value={F.whMgr}  onChange={e=>set('whMgr',e.target.value)}  placeholder="Name..."/>
                <Fld label="QC Manager"  value={F.qcMgr}  onChange={e=>set('qcMgr',e.target.value)}  placeholder="Name..."/>
              </Grid>
              <div style={{ marginTop:16, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ fontSize:11, color:C.textSub }}>Auto Result</div>
                  <Res result={autoResult()}/>
                </div>
                <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
                  {saved?'Saved!':saving?'Saving…':'Save Report'}
                </Btn>
              </div>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-04 · TRIMS CARD  (Nir/Wh/02)
  // ═════════════════════════════════════════════════════════════════
  function Ann04({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const newItem = () => ({ id:uid(), type:'', spec:'', actual:'', colour:'', supplier:'', status:'PENDING' });
    const fresh   = () => ({
      id:uid(), formType:'TRIMS_CARD', date:TODAY,
      style: filters.style||'', colour: filters.colour||'',
      brand: filters.brand||'', buyer: filters.buyer||'',
      po: filters.po||'', orderQty: filters.qty||'',
      season:'', preparedBy:'', approvedBy:'',
      items: [newItem()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const addItem = () => setF(f => ({ ...f, items:[...f.items, newItem()] }));
    const updItem = (i,k,v) => setF(f => ({ ...f, items: f.items.map((it,ii) => ii===i ? { ...it,[k]:v } : it) }));
    const delItem = (i) => setF(f => ({ ...f, items: f.items.filter((_,ii) => ii!==i) }));

    const recs = (db.warehouse||[]).filter(r => r.formType==='TRIMS_CARD');
    const sBg  = s => s==='APPROVED'?C.successBg:s==='REJECTED'?C.dangerBg:s==='HOLD'?C.warningBg:C.slate50;
    const sTxt = s => s==='APPROVED'?C.success:s==='REJECTED'?C.danger:s==='HOLD'?C.warning:C.textMuted;

    const handleSave = async () => {
      await doSave('warehouse_trims_card', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-04 · Trims Card" ref_="Nir/Wh/02 · Warehouse → All Departments"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={() => setView('form')} color={C.wh}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={() => setView('history')} color={C.wh}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view === 'history' ? (
          <Card>
            <SH title="Trim Cards"/>
            <Tbl headers={['Date','Style','Colour','Brand','Buyer','PO','Items','Prepared By']}
              rows={recs.map(r=>[r.date,<strong>{r.style||'—'}</strong>,r.colour||'—',r.brand||'—',r.buyer||'—',r.po||'—',r.items?.length||0,r.preparedBy||'—'])}
              emptyMsg="No trim cards yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Order Header" color={C.wh}/>
              <Grid gap={14}>
                <Fld label="Date"        type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."   value={F.style}       onChange={e=>set('style',e.target.value)}       options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Colour"      value={F.colour}      onChange={e=>set('colour',e.target.value)}      options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Brand"       value={F.brand}       onChange={e=>set('brand',e.target.value)}       options={['',...BRANDS]}/>
                <Fld label="Buyer"       value={F.buyer}       onChange={e=>set('buyer',e.target.value)}       options={['',...BUYERS]}/>
                <Fld label="PO No."      value={F.po}          onChange={e=>set('po',e.target.value)}          options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Order Qty"   type="number" value={F.orderQty} onChange={e=>set('orderQty',e.target.value)} placeholder="Auto from PO"/>
                <Fld label="Season"      value={F.season}      onChange={e=>set('season',e.target.value)}      placeholder="SS25 / AW25..."/>
                <Fld label="Prepared By" value={F.preparedBy}  onChange={e=>set('preparedBy',e.target.value)}  placeholder="Name..."/>
                <Fld label="Approved By" value={F.approvedBy}  onChange={e=>set('approvedBy',e.target.value)}  placeholder="Name..."/>
              </Grid>
            </Card>

            <Card>
              <SH title="Trim Items" color={C.wh} action={<Btn sm onClick={addItem} color={C.wh}>＋ Add Trim</Btn>}/>
              {/* column headers */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 90px 110px 120px 32px', gap:6, marginBottom:8 }}>
                {['Trim Type','Spec / Description','Actual / Supplied','Colour','Supplier','Status',''].map((h,i) => (
                  <div key={i} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
                ))}
              </div>
              {F.items.map((it,i) => (
                <div key={it.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 90px 110px 120px 32px', gap:6, marginBottom:6, alignItems:'center' }}>
                  <select value={it.type} onChange={e=>updItem(i,'type',e.target.value)}
                    style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, fontFamily:'inherit' }}>
                    <option value="">Select...</option>
                    {TRIM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input value={it.spec}     onChange={e=>updItem(i,'spec',e.target.value)}     placeholder="Spec..."
                    style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
                  <input value={it.actual}   onChange={e=>updItem(i,'actual',e.target.value)}   placeholder="Actual..."
                    style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, background:sBg(it.status) }}/>
                  <input value={it.colour}   onChange={e=>updItem(i,'colour',e.target.value)}   placeholder="Colour"
                    style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
                  <input value={it.supplier} onChange={e=>updItem(i,'supplier',e.target.value)} placeholder="Supplier"
                    style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
                  <select value={it.status} onChange={e=>updItem(i,'status',e.target.value)}
                    style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12,
                      fontWeight:700, fontFamily:'inherit', background:sBg(it.status), color:sTxt(it.status) }}>
                    {['PENDING','APPROVED','REJECTED','HOLD'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={()=>delItem(i)} style={{ background:C.dangerBg, border:`1px solid ${C.dangerBd}`,
                    borderRadius:R.sm, cursor:'pointer', padding:'6px', color:C.danger, fontSize:12, fontWeight:700 }}>✕</button>
                </div>
              ))}
              <div style={{ marginTop:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', gap:8 }}>
                  <Bdg color={C.success} bg={C.successBg}>{F.items.filter(it=>it.status==='APPROVED').length} Approved</Bdg>
                  <Bdg color={C.danger}  bg={C.dangerBg }>{F.items.filter(it=>it.status==='REJECTED').length} Rejected</Bdg>
                  <Bdg color={C.warning} bg={C.warningBg}>{F.items.filter(it=>it.status==='HOLD').length} Hold</Bdg>
                </div>
                <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
                  {saved?'Saved!':saving?'Saving…':'Save Trim Card'}
                </Btn>
              </div>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-05 · FABRIC INSPECTION REPORT – 4-POINT  (Nir/Wh/03)
  // ═════════════════════════════════════════════════════════════════
  function Ann05({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const newRoll = () => ({ id:uid(), rollNo:'', supShade:'', markedYds:'', actualYds:'',
      actualMtr:'', ordWidth:'', actWidth:'', defects:[], totalPts:0, defPts100:0, result:'PENDING' });
    const fresh   = () => ({
      id:uid(), formType:'FABRIC_INSP', date:TODAY, inspDate:TODAY, recvDate:TODAY,
      supplier:'', invoiceNo:'', clientRef: filters.style||'', fabricType:'',
      colour: filters.colour||'', buyer: filters.buyer||'',
      recvRolls:'', inspRolls:'', recvYds:'', cutableWidth:'56',
      rolls:[newRoll()], inspector:'', qcIncharge:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));

    const addRoll = () => setF(f => ({ ...f, rolls:[...f.rolls, newRoll()] }));
    const delRoll = (i) => setF(f => ({ ...f, rolls: f.rolls.filter((_,ri) => ri!==i) }));
    const setRoll = (i,k,v) => setF(f => {
      const rolls=[...f.rolls]; rolls[i]={...rolls[i],[k]:v}; return {...f,rolls};
    });

    const addDefect = (ri, name) => setF(f => {
      const rolls=[...f.rolls];
      rolls[ri].defects=[...rolls[ri].defects, {id:uid(),name,len:'',pts:0}];
      return {...f,rolls};
    });
    const updDefect = (ri,di,k,v) => setF(f => {
      const rolls=[...f.rolls]; const defs=[...rolls[ri].defects];
      defs[di]={...defs[di],[k]:v};
      if (k==='len') {
        const inch=parseFloat(v)||0;
        defs[di].pts = inch<=3?1:inch<=6?2:inch<=9?3:4;
      }
      const totalPts=defs.reduce((s,d)=>s+(d.pts||0),0);
      const yds=parseFloat(rolls[ri].actualYds)||1;
      const cw=parseFloat(f.cutableWidth)||56;
      const defPts100=((totalPts*36*100)/(yds*cw)).toFixed(2);
      rolls[ri]={...rolls[ri],defects:defs,totalPts,defPts100,
        result:parseFloat(defPts100)<=20?'PASS':'FAIL'};
      return {...f,rolls};
    });
    const delDefect = (ri,di) => setF(f => {
      const rolls=[...f.rolls];
      rolls[ri].defects=rolls[ri].defects.filter((_,i)=>i!==di);
      const totalPts=rolls[ri].defects.reduce((s,d)=>s+(d.pts||0),0);
      const yds=parseFloat(rolls[ri].actualYds)||1;
      const cw=parseFloat(f.cutableWidth)||56;
      const defPts100=((totalPts*36*100)/(yds*cw)).toFixed(2);
      rolls[ri]={...rolls[ri],totalPts,defPts100,result:parseFloat(defPts100)<=20?'PASS':'FAIL'};
      return {...f,rolls};
    });

    const overall   = () => {
      if (F.rolls.every(r=>r.result==='PASS')) return 'PASS';
      if (F.rolls.some(r=>r.result==='FAIL'))  return 'FAIL';
      return 'PENDING';
    };
    const totalPts  = F.rolls.reduce((s,r)=>s+(r.totalPts||0),0);
    const avgPts    = F.rolls.length>0
      ? (F.rolls.reduce((s,r)=>s+(parseFloat(r.defPts100)||0),0)/F.rolls.length).toFixed(2)
      : '0.00';

    const recs = (db.warehouse||[]).filter(r => r.formType==='FABRIC_INSP');

    const handleSave = async () => {
      const rec={...F, result:overall(), totalDefPts:totalPts, avgDefPts:avgPts, savedAt:new Date().toISOString()};
      await doSave('warehouse_fabric_insp', rec, db, setDb, r => {
        // backward-compat: also write to db.fabricInsp for existing Dashboard
        setDb(prev => ({ ...prev, fabricInsp:[...(prev.fabricInsp||[]),r] }));
      });
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-05 · Fabric Inspection Report (4-Point System)"
          ref_="Nir/Wh/03 · Pass ≤ 20 Def.Pts / 100 Sq.Yds"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.wh}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.wh}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Fabric Inspection Records"/>
            <Tbl headers={['Date','Supplier','Invoice','Client Ref','Recv Rolls','Avg Pts/100','Result']}
              rows={recs.map(r=>[r.date,r.supplier||'—',r.invoiceNo||'—',r.clientRef||'—',r.recvRolls||'—',r.avgDefPts||'—',<Res result={r.result}/>])}
              emptyMsg="No fabric inspections yet."/>
          </Card>
        ) : (
          <>
            {/* header */}
            <Card>
              <SH title="Inspection Header" color={C.wh}/>
              <Grid gap={14}>
                <Fld label="Recv. Date"    type="date" value={F.recvDate}     onChange={e=>set('recvDate',e.target.value)}/>
                <Fld label="Insp. Date"    type="date" value={F.inspDate}     onChange={e=>set('inspDate',e.target.value)}/>
                <Fld label="Supplier/Mill" value={F.supplier}    onChange={e=>set('supplier',e.target.value)}    placeholder="Supplier name..."/>
                <Fld label="Invoice No."   value={F.invoiceNo}   onChange={e=>set('invoiceNo',e.target.value)}   placeholder="Invoice#..."/>
                <Fld label="Client Ref #"  value={F.clientRef}        onChange={e=>set('clientRef',e.target.value)}        placeholder="Client Ref#..."/>
                <Fld label="Fabric Type"   value={F.fabricType}  onChange={e=>set('fabricType',e.target.value)}  options={['',...FABRICS]}/>
                <Fld label="Colour"        value={F.colour}      onChange={e=>set('colour',e.target.value)}      options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Buyer"         value={F.buyer}       onChange={e=>set('buyer',e.target.value)}       options={['',...BUYERS]}/>
                <Fld label="Recv. Rolls"   type="number" value={F.recvRolls} onChange={e=>set('recvRolls',e.target.value)} placeholder="0"/>
                <Fld label="Insp. Rolls"   type="number" value={F.inspRolls} onChange={e=>set('inspRolls',e.target.value)} placeholder="0"/>
                <Fld label="Recv. Yards"   type="number" value={F.recvYds}   onChange={e=>set('recvYds',e.target.value)}   placeholder="0"/>
                <Fld label='Cutable Width"' type="number" value={F.cutableWidth} onChange={e=>set('cutableWidth',e.target.value)} placeholder='56"' note="Used in 4-pt formula"/>
              </Grid>
            </Card>

            {/* formula banner */}
            <div style={{ background:'#EFF6FF', border:`1px solid ${C.wh}44`,
              borderRadius:R.lg, padding:'12px 18px', fontSize:12, color:C.textSub }}>
              <strong style={{ color:C.wh }}>4-Point Formula: </strong>
              Total Def.Pts × 36 × 100 ÷ (Inspected Yds × Cutable Width) &nbsp;|&nbsp;
              <strong style={{ color:C.success }}>PASS ≤ 20</strong> &nbsp;|&nbsp;
              <strong style={{ color:C.danger }}>FAIL &gt; 20 pts/100 sq.yds</strong>
            </div>

            {/* summary bar */}
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              {[
                ['Rolls',    F.rolls.length,                                    C.text,    C.slate50    ],
                ['Pass',     F.rolls.filter(r=>r.result==='PASS').length,       C.success, C.successBg  ],
                ['Fail',     F.rolls.filter(r=>r.result==='FAIL').length,       C.danger,  C.dangerBg   ],
                ['Total Pts',totalPts,                                          C.danger,  '#EFF6FF'    ],
                ['Avg/100',  avgPts,  parseFloat(avgPts)>20?C.danger:C.success, parseFloat(avgPts)>20?C.dangerBg:C.successBg],
              ].map(([l,v,c,b]) => (
                <div key={l} style={{ padding:'10px 16px', background:b, borderRadius:R.md,
                  textAlign:'center', border:`1px solid ${c}22` }}>
                  <div style={{ fontSize:20, fontWeight:800, color:c }}>{v}</div>
                  <div style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase' }}>{l}</div>
                </div>
              ))}
              <Btn sm onClick={addRoll} color={C.wh} style={{ alignSelf:'center' }}>＋ Add Roll</Btn>
            </div>

            {/* rolls */}
            {F.rolls.map((roll,ri) => (
              <Card key={roll.id} style={{
                border:`1px solid ${roll.result==='FAIL'?C.dangerBd:roll.result==='PASS'?C.successBd:C.border}`,
              }}>
                {/* roll header */}
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14,
                  paddingBottom:12, borderBottom:`1px solid ${C.border}`, flexWrap:'wrap' }}>
                  <span style={{ fontWeight:700, fontSize:14 }}>Roll #{ri+1}</span>
                  {roll.actualYds && <Res result={roll.result}/>}
                  {roll.defPts100>0 && (
                    <span style={{ fontSize:11, color:C.textSub }}>{roll.defPts100} pts / 100 sq.yds</span>
                  )}
                  <button onClick={()=>delRoll(ri)} style={{ marginLeft:'auto', background:'none',
                    border:'none', color:C.danger, cursor:'pointer', fontSize:18 }}>✕</button>
                </div>

                <Grid gap={10}>
                  {[
                    ['Roll No.','rollNo','text'],['Sup. Shade','supShade','text'],
                    ['Marked Yds','markedYds','number'],['Actual Yds','actualYds','number'],
                    ['Actual Mtr','actualMtr','number'],['Ord. Width"','ordWidth','number'],
                    ['Act. Width"','actWidth','number'],
                  ].map(([lbl,k,t]) => (
                    <Fld key={k} label={lbl} type={t} value={roll[k]||''}
                      onChange={e=>setRoll(ri,k,e.target.value)} placeholder={t==='number'?'0':'...'}/>
                  ))}
                </Grid>

                {/* defect buttons */}
                <div style={{ marginTop:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.textSub,
                    textTransform:'uppercase', marginBottom:8 }}>
                    Click defect to log (enter length in inches → points auto-calculated)
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                    {FABRIC_DEFECTS.map(name => (
                      <button key={name} onClick={() => addDefect(ri,name)}
                        style={{ padding:'4px 9px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, background:C.white,
                          cursor:'pointer', fontSize:11, fontWeight:600, color:C.textSub }}>
                        {name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* defect rows */}
                {roll.defects.length > 0 && (
                  <div style={{ marginTop:12 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 100px 60px 32px',
                      gap:6, marginBottom:4 }}>
                      {['Defect Name','Length (in)','Points',''].map(h => (
                        <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
                      ))}
                    </div>
                    {roll.defects.map((d,di) => (
                      <div key={d.id} style={{ display:'grid', gridTemplateColumns:'1fr 100px 60px 32px',
                        gap:6, marginBottom:5, alignItems:'center' }}>
                        <div style={{ padding:'5px 8px', background:C.dangerBg, borderRadius:R.sm,
                          color:C.danger, fontSize:12, fontWeight:600 }}>{d.name}</div>
                        <input type="number" value={d.len} placeholder="inches"
                          onChange={e=>updDefect(ri,di,'len',e.target.value)}
                          style={{ padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                            fontSize:12, textAlign:'center' }}/>
                        <div style={{ textAlign:'center', fontWeight:800, color:C.danger, fontSize:14 }}>
                          {d.pts || '—'}
                        </div>
                        <button onClick={()=>delDefect(ri,di)}
                          style={{ background:C.dangerBg, border:'none', borderRadius:R.sm,
                            cursor:'pointer', color:C.danger, fontWeight:700, fontSize:11, padding:'4px' }}>
                          ✕
                        </button>
                      </div>
                    ))}
                    <div style={{ padding:'8px 12px', background:C.slate50, borderRadius:R.sm,
                      display:'flex', gap:20, fontSize:12, marginTop:6, flexWrap:'wrap' }}>
                      <span>Defects: <strong>{roll.defects.length}</strong></span>
                      <span>Total Pts: <strong style={{ color:C.danger }}>{roll.totalPts}</strong></span>
                      <span>Pts/100 sq.yds: <strong style={{ color:parseFloat(roll.defPts100)>20?C.danger:C.success }}>{roll.defPts100}</strong></span>
                    </div>
                  </div>
                )}
              </Card>
            ))}

            {/* sign-off */}
            <Card>
              <SH title="Inspector Sign-off" color={C.wh}/>
              <Grid gap={14}>
                <Fld label="Inspector"   value={F.inspector}   onChange={e=>set('inspector',e.target.value)}   placeholder="Name..."/>
                <Fld label="QC Incharge" value={F.qcIncharge}  onChange={e=>set('qcIncharge',e.target.value)}  placeholder="Name..."/>
              </Grid>
              <div style={{ marginTop:14, display:'flex', justifyContent:'space-between',
                alignItems:'center', flexWrap:'wrap', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <Res result={overall()}/>
                  <span style={{ fontSize:11, color:C.textMuted }}>
                    Avg: {avgPts} pts/100 sq.yds (target ≤ 20)
                  </span>
                </div>
                <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
                  {saved?'Saved!':saving?'Saving…':'Save Inspection'}
                </Btn>
              </div>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-06 · FABRIC CLAIM REGISTER  (Nir/Wh/04)
  // ═════════════════════════════════════════════════════════════════
  function Ann06({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const fresh = () => ({
      id:uid(), formType:'FABRIC_CLAIM',
      claimNo: uid().slice(-6).toUpperCase(), claimDate:TODAY,
      supplier:'', buyer: filters.buyer||'', colour: filters.colour||'',
      invoice:'', recvDate:TODAY, recvRolls:'', recvYds:'',
      issue:'', seriousness:'MODERATE', reqYds:'', unitUSD:'',
      status:'OPEN', replaceDate:'', replaceQty:'', replaceVal:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const recs = (db.warehouse||[]).filter(r => r.formType==='FABRIC_CLAIM');
    const totalVal = (parseInt(F.reqYds)||0) * (parseFloat(F.unitUSD)||0);

    const handleSave = async () => {
      await doSave('warehouse_fabric_claim', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    const sCol = s => s==='OPEN'?C.danger:s==='CLOSED'?C.success:C.warning;

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-06 · Fabric Claim Register" ref_="Nir/Wh/04 · WH QA Incharge → Record"
          action={<Bdg color={C.wh}>{recs.filter(r=>r.status==='OPEN').length} Open · {recs.length} Total</Bdg>}/>

        <Card>
          <SH title="New Claim Entry" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Claim No."        value={F.claimNo}    onChange={e=>set('claimNo',e.target.value)}    placeholder="Auto..."/>
            <Fld label="Claim Date"       type="date" value={F.claimDate}  onChange={e=>set('claimDate',e.target.value)}/>
            <Fld label="Supplier"         value={F.supplier}   onChange={e=>set('supplier',e.target.value)}   placeholder="Supplier..."/>
            <Fld label="Buyer"            value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
            <Fld label="Colour"           value={F.colour}     onChange={e=>set('colour',e.target.value)}     options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Invoice No."      value={F.invoice}    onChange={e=>set('invoice',e.target.value)}    placeholder="Invoice#..."/>
            <Fld label="Recv. Date"       type="date" value={F.recvDate}   onChange={e=>set('recvDate',e.target.value)}/>
            <Fld label="Recv. Rolls"      type="number" value={F.recvRolls} onChange={e=>set('recvRolls',e.target.value)} placeholder="0"/>
            <Fld label="Recv. Yards"      type="number" value={F.recvYds}   onChange={e=>set('recvYds',e.target.value)}   placeholder="0"/>
            <Fld label="Seriousness"      value={F.seriousness} onChange={e=>set('seriousness',e.target.value)}
              options={['MINOR','MODERATE','SERIOUS','CRITICAL']}/>
            <Fld label="Req. Replace Yds" type="number" value={F.reqYds}   onChange={e=>set('reqYds',e.target.value)}    placeholder="0"/>
            <Fld label="Unit Value (USD)" type="number" value={F.unitUSD}  onChange={e=>set('unitUSD',e.target.value)}   placeholder="0.00"
              note={totalVal>0?`Total: $${totalVal.toFixed(2)}`:''}/>
            <Fld label="Claim Status"     value={F.status}     onChange={e=>set('status',e.target.value)}
              options={['OPEN','REPLACE PENDING','REPLACED','CLOSED','REJECTED']}/>
          </Grid>
          <div style={{ marginTop:10 }}>
            <Fld label="Issue / Problem Description" type="textarea" rows={3}
              value={F.issue} onChange={e=>set('issue',e.target.value)} placeholder="Describe the fabric defect or issue..."/>
          </div>

          <div style={{ marginTop:14, marginBottom:8, fontSize:12, fontWeight:700, color:C.textSub, textTransform:'uppercase' }}>
            Replacement Details
          </div>
          <Grid gap={14}>
            <Fld label="Replace Recv. Date" type="date" value={F.replaceDate} onChange={e=>set('replaceDate',e.target.value)}/>
            <Fld label="Replace Qty (Yds)"  type="number" value={F.replaceQty} onChange={e=>set('replaceQty',e.target.value)} placeholder="0"/>
            <Fld label="Replace Value (USD)" type="number" value={F.replaceVal} onChange={e=>set('replaceVal',e.target.value)} placeholder="0.00"/>
          </Grid>
          <div style={{ marginTop:14, display:'flex', justifyContent:'flex-end' }}>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Register Claim'}
            </Btn>
          </div>
        </Card>

        <Card>
          <SH title="Claim Register" color={C.wh}/>
          <Tbl
            headers={['Claim#','Date','Supplier','Colour','Recv Yds','Seriousness','Req Yds','Total $','Status']}
            rows={recs.map(r => [
              <strong style={{ fontFamily:'monospace' }}>{r.claimNo}</strong>,
              r.claimDate, r.supplier||'—', r.colour||'—', r.recvYds||'—',
              <Bdg color={r.seriousness==='CRITICAL'?C.danger:r.seriousness==='SERIOUS'?C.warning:C.textMuted}>{r.seriousness}</Bdg>,
              r.reqYds||'—',
              r.unitUSD&&r.reqYds ? `$${((parseInt(r.reqYds))*(parseFloat(r.unitUSD))).toFixed(2)}` : '—',
              <Bdg color={sCol(r.status)}>{r.status}</Bdg>,
            ])}
            emptyMsg="No fabric claims registered."/>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-07 · TRIMS APPROVAL STATUS  (Nir/Wh/05)
  // ═════════════════════════════════════════════════════════════════
  function Ann07({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const OPTS = ['APPROVED','PENDING','HOLD','REJECTED','NA'];
    const sC = s => s==='APPROVED'?C.success:s==='REJECTED'?C.danger:s==='HOLD'?C.warning:C.textMuted;
    const sB = s => s==='APPROVED'?C.successBg:s==='REJECTED'?C.dangerBg:s==='HOLD'?C.warningBg:C.slate50;
    const ov = it => {
      const v=[it.shade,it.shrinkage,it.strength];
      if (v.some(x=>x==='REJECTED')) return 'REJECTED';
      if (v.some(x=>x==='HOLD'))     return 'HOLD';
      if (v.every(x=>x==='APPROVED'))return 'APPROVED';
      return 'PENDING';
    };
    const fresh = () => ({
      id:uid(), formType:'TRIMS_APPROVAL', date:TODAY,
      style: filters.style||'', colour: filters.colour||'',
      buyer: filters.buyer||'', brand: filters.brand||'',
      preparedBy:'', approvedBy:'',
      items: TRIM_TYPES.slice(0,12).map(t => ({
        type:t, shade:'PENDING', shrinkage:'PENDING', strength:'PENDING', overall:'PENDING', remarks:'',
      })),
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updItem = (i,k,v) => setF(f => ({
      ...f,
      items: f.items.map((it,ii) => ii===i ? { ...it,[k]:v, overall:ov({...it,[k]:v}) } : it),
    }));
    const recs = (db.warehouse||[]).filter(r => r.formType==='TRIMS_APPROVAL');

    const handleSave = async () => {
      await doSave('warehouse_trims_approval', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-07 · Trims Approval Status" ref_="Nir/Wh/05 · Shade, Shrinkage & Strength"
          action={<Bdg color={C.wh}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Order Details" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Date"        type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."   value={F.style}       onChange={e=>set('style',e.target.value)}       options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Colour"      value={F.colour}      onChange={e=>set('colour',e.target.value)}      options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Brand"       value={F.brand}       onChange={e=>set('brand',e.target.value)}       options={['',...BRANDS]}/>
            <Fld label="Buyer"       value={F.buyer}       onChange={e=>set('buyer',e.target.value)}       options={['',...BUYERS]}/>
            <Fld label="Prepared By" value={F.preparedBy}  onChange={e=>set('preparedBy',e.target.value)}  placeholder="Name..."/>
            <Fld label="Approved By" value={F.approvedBy}  onChange={e=>set('approvedBy',e.target.value)}  placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Trim-wise Approval Matrix" color={C.wh}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Trim Item','Shade / Colour','Shrinkage','Strength','Overall','Remarks'].map(h => (
                    <th key={h} style={{ padding:'9px 11px', fontWeight:700, color:C.textSub,
                      fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.items.map((it,i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.slate50 }}>
                    <td style={{ padding:'8px 11px', fontWeight:600, color:C.text }}>{it.type}</td>
                    {['shade','shrinkage','strength'].map(k => (
                      <td key={k} style={{ padding:'6px 8px' }}>
                        <select value={it[k]} onChange={e=>updItem(i,k,e.target.value)}
                          style={{ padding:'5px 8px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                            fontSize:11, fontWeight:700, fontFamily:'inherit',
                            background:sB(it[k]), color:sC(it[k]), cursor:'pointer' }}>
                          {OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    ))}
                    <td style={{ padding:'8px 11px' }}>
                      <Bdg color={sC(it.overall)} bg={sB(it.overall)}>{it.overall}</Bdg>
                    </td>
                    <td style={{ padding:'6px 8px' }}>
                      <input value={it.remarks} onChange={e=>updItem(i,'remarks',e.target.value)}
                        placeholder="Remark..."
                        style={{ width:'100%', minWidth:120, padding:'5px 8px',
                          borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:14, display:'flex', justifyContent:'space-between',
            alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <div style={{ display:'flex', gap:8 }}>
              <Bdg color={C.success} bg={C.successBg}>{F.items.filter(it=>it.overall==='APPROVED').length} Approved</Bdg>
              <Bdg color={C.warning} bg={C.warningBg}>{F.items.filter(it=>it.overall==='HOLD').length} Hold</Bdg>
              <Bdg color={C.danger}  bg={C.dangerBg }>{F.items.filter(it=>it.overall==='REJECTED').length} Rejected</Bdg>
            </div>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Save Approval Status'}
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-08 · SHADE SEGREGATION SUMMARY  (Nir/Wh/06)
  // ═════════════════════════════════════════════════════════════════
  function Ann08({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const SHADES = ['A','B','C','D','E','F','REJECT'];
    const newRoll = () => ({ id:uid(), rollNo:'', rollId:'', actualYds:'', shade:'A', remarks:'' });
    const fresh   = () => ({
      id:uid(), formType:'SHADE_SEG', date:TODAY,
      clientRef: filters.style||'', colour: filters.colour||'', buyer: filters.buyer||'',
      fabricType:'', totalRolls:'', inspectedBy:'',
      rolls:[newRoll(), newRoll(), newRoll()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const upd = (i,k,v) => setF(f => ({ ...f, rolls: f.rolls.map((r,ri) => ri===i ? { ...r,[k]:v } : r) }));
    const recs = (db.warehouse||[]).filter(r => r.formType==='SHADE_SEG');
    const gc   = SHADES.reduce((acc,g) => ({ ...acc, [g]: F.rolls.filter(r=>r.shade===g).length }), {});

    const handleSave = async () => {
      await doSave('warehouse_shade_seg', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-08 · Shade Segregation Summary" ref_="Nir/Wh/06 · Controls shade-wise lay in Cutting"
          action={<Bdg color={C.wh}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Lot Header" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Date"         type="date" value={F.date}         onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref / Style" value={F.clientRef}         onChange={e=>set('clientRef',e.target.value)}         placeholder="Client Ref#..."/>
            <Fld label="Colour"       value={F.colour}       onChange={e=>set('colour',e.target.value)}       options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Fabric Type"  value={F.fabricType}   onChange={e=>set('fabricType',e.target.value)}   options={['',...FABRICS]}/>
            <Fld label="Buyer"        value={F.buyer}        onChange={e=>set('buyer',e.target.value)}        options={['',...BUYERS]}/>
            <Fld label="Total Rolls"  type="number" value={F.totalRolls} onChange={e=>set('totalRolls',e.target.value)} placeholder="0"/>
            <Fld label="Inspected By" value={F.inspectedBy}  onChange={e=>set('inspectedBy',e.target.value)}  placeholder="Name..."/>
          </Grid>
        </Card>

        {/* shade group tiles */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {SHADES.map(g => (
            <div key={g} style={{ padding:'10px 18px', borderRadius:R.md, textAlign:'center',
              background: g==='REJECT' ? C.dangerBg : C.successBg,
              border:`1px solid ${g==='REJECT' ? C.dangerBd : C.successBd}` }}>
              <div style={{ fontSize:22, fontWeight:800, color: g==='REJECT' ? C.danger : C.success }}>{gc[g]}</div>
              <div style={{ fontSize:10, fontWeight:700, color:C.textSub }}>Shade {g}</div>
            </div>
          ))}
        </div>

        <Card>
          <SH title="Roll-wise Shade Assignment" color={C.wh}
            action={<Btn sm onClick={()=>setF(f=>({...f,rolls:[...f.rolls,newRoll()]}))} color={C.wh}>＋ Add Roll</Btn>}/>
          {/* col headers */}
          <div style={{ display:'grid', gridTemplateColumns:'70px 1fr 90px 80px 1fr 30px', gap:6, marginBottom:8 }}>
            {['Roll #','Roll ID','Act. Yds','Shade','Remarks',''].map(h => (
              <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>
          {F.rolls.map((r,i) => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'70px 1fr 90px 80px 1fr 30px',
              gap:6, marginBottom:5, alignItems:'center' }}>
              <input value={r.rollNo} onChange={e=>upd(i,'rollNo',e.target.value)} placeholder={`${i+1}`}
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              <input value={r.rollId} onChange={e=>upd(i,'rollId',e.target.value)} placeholder="Roll ID..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <input type="number" value={r.actualYds} onChange={e=>upd(i,'actualYds',e.target.value)} placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              <select value={r.shade} onChange={e=>upd(i,'shade',e.target.value)}
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12,
                  fontWeight:700, fontFamily:'inherit',
                  background: r.shade==='REJECT' ? C.dangerBg : C.successBg,
                  color: r.shade==='REJECT' ? C.danger : C.success }}>
                {SHADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <input value={r.remarks} onChange={e=>upd(i,'remarks',e.target.value)} placeholder="Note..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <button onClick={()=>setF(f=>({...f,rolls:f.rolls.filter((_,ri)=>ri!==i)}))}
                style={{ background:C.dangerBg, border:'none', borderRadius:R.sm,
                  cursor:'pointer', color:C.danger, fontWeight:700, fontSize:12, padding:'6px' }}>✕</button>
            </div>
          ))}
          <div style={{ marginTop:12, display:'flex', justifyContent:'flex-end' }}>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Save Segregation'}
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-09 · ROLL WISE ALL REPORT  (Nir/Wh/07)
  //  Roll-wise all-in-one: Shade + Shrinkage + Width
  // ═════════════════════════════════════════════════════════════════
  function Ann09({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const COLS  = ['rollNo','supShade','actShade','actualYds','actualMtr','ordWidth','actWidth','shrinkL','shrinkW','defPts'];
    const HDRS  = ['Roll #','Sup.Shade','Act.Shade','Act.Yds','Act.Mtr','Ord.W"','Act.W"','Shrink L%','Shrink W%','Def.Pts/100'];
    const newR  = () => ({ id:uid(), rollNo:'', supShade:'', actShade:'', actualYds:'', actualMtr:'',
      ordWidth:'', actWidth:'', shrinkL:'', shrinkW:'', defPts:'', result:'PASS', remarks:'' });
    const fresh = () => ({
      id:uid(), formType:'ROLL WISE ALL', date:TODAY,
      clientRef: filters.style||'', colour: filters.colour||'', buyer: filters.buyer||'',
      supplier:'', invoiceNo:'', preparedBy:'',
      rolls: Array.from({ length:5 }, newR),
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const upd = (i,k,v) => setF(f => ({ ...f, rolls: f.rolls.map((r,ri) => ri===i ? { ...r,[k]:v } : r) }));
    const recs = (db.warehouse||[]).filter(r => r.formType==='ROLL WISE ALL');

    const handleSave = async () => {
      await doSave('warehouse_roll_wise_all', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-09 · Roll Wise All Report" ref_="Nir/Wh/07 · Roll-wise Shade, Shrinkage & Width"
          action={<Bdg color={C.wh}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Date"        type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref #"      value={F.clientRef}       onChange={e=>set('clientRef',e.target.value)}       placeholder="Client Ref / Style..."/>
            <Fld label="Supplier"    value={F.supplier}   onChange={e=>set('supplier',e.target.value)}   placeholder="Mill / Supplier..."/>
            <Fld label="Invoice No." value={F.invoiceNo}  onChange={e=>set('invoiceNo',e.target.value)}  placeholder="Invoice#..."/>
            <Fld label="Colour"      value={F.colour}     onChange={e=>set('colour',e.target.value)}     options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Buyer"       value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
            <Fld label="Prepared By" value={F.preparedBy} onChange={e=>set('preparedBy',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Roll Data" color={C.wh}
            action={<Btn sm onClick={()=>setF(f=>({...f,rolls:[...f.rolls,newR()]}))} color={C.wh}>＋ Add Roll</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:900 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {[...HDRS,'Result','Remark'].map(h => (
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub, fontSize:10,
                      textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rolls.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.slate50 }}>
                    {COLS.map(k => (
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>upd(i,k,e.target.value)} placeholder="—"
                          style={{ width:'100%', minWidth:55, padding:'5px', borderRadius:R.sm,
                            border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 8px', textAlign:'center' }}>
                      <select value={r.result} onChange={e=>upd(i,'result',e.target.value)}
                        style={{ padding:'4px 6px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                          fontSize:11, fontWeight:700, fontFamily:'inherit',
                          background: r.result==='PASS'?C.successBg:r.result==='FAIL'?C.dangerBg:C.warningBg,
                          color: r.result==='PASS'?C.success:r.result==='FAIL'?C.danger:C.warning }}>
                        {['PASS','FAIL','HOLD'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.remarks||''} onChange={e=>upd(i,'remarks',e.target.value)} placeholder="Note..."
                        style={{ width:'100%', minWidth:80, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <div style={{ display:'flex', gap:10 }}>
              <Bdg color={C.success} bg={C.successBg}>{F.rolls.filter(r=>r.result==='PASS').length} Pass</Bdg>
              <Bdg color={C.danger}  bg={C.dangerBg }>{F.rolls.filter(r=>r.result==='FAIL').length} Fail</Bdg>
            </div>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Save Roll Wise All Report'}
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-10 · WIDTH INSPECTION REPORT  (Nir/Wh/08)
  // ═════════════════════════════════════════════════════════════════
  function Ann10({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newR  = () => ({ id:uid(), rollNo:'', startW:'', midW:'', endW:'', avgW:'', variance:'', ok:null });
    const fresh = () => ({
      id:uid(), formType:'WIDTH_INSP', date:TODAY,
      clientRef: filters.style||'', colour: filters.colour||'', buyer: filters.buyer||'',
      supplier:'', orderedWidth:'56', fabricType:'', inspectedBy:'',
      rolls: Array.from({ length:5 }, newR),
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const upd = (i,k,v) => setF(f => {
      const rolls=[...f.rolls]; rolls[i]={...rolls[i],[k]:v};
      const s=parseFloat(rolls[i].startW)||0, m=parseFloat(rolls[i].midW)||0, e=parseFloat(rolls[i].endW)||0;
      const cnt=[s,m,e].filter(x=>x>0).length;
      if (cnt>0) {
        const avg=((s+m+e)/cnt).toFixed(1);
        const ord=parseFloat(f.orderedWidth)||56;
        const variance=(parseFloat(avg)-ord).toFixed(1);
        rolls[i]={...rolls[i], avgW:avg, variance, ok:Math.abs(parseFloat(variance))<=1};
      }
      return {...f,rolls};
    });
    const recs = (db.warehouse||[]).filter(r => r.formType==='WIDTH_INSP');

    const handleSave = async () => {
      await doSave('warehouse_width_insp', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-10 · Width Inspection Report" ref_="Nir/Wh/08 · Tolerance ±1 inch from ordered width"
          action={<Bdg color={C.wh}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Date"           type="date" value={F.date}         onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref / Style"   value={F.clientRef}         onChange={e=>set('clientRef',e.target.value)}         placeholder="Client Ref#..."/>
            <Fld label="Supplier"       value={F.supplier}     onChange={e=>set('supplier',e.target.value)}     placeholder="Supplier..."/>
            <Fld label="Colour"         value={F.colour}       onChange={e=>set('colour',e.target.value)}       options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Fabric Type"    value={F.fabricType}   onChange={e=>set('fabricType',e.target.value)}   options={['',...FABRICS]}/>
            <Fld label='Ordered Width"' type="number" value={F.orderedWidth} onChange={e=>set('orderedWidth',e.target.value)} placeholder='56"'/>
            <Fld label="Buyer"          value={F.buyer}        onChange={e=>set('buyer',e.target.value)}        options={['',...BUYERS]}/>
            <Fld label="Inspected By"   value={F.inspectedBy}  onChange={e=>set('inspectedBy',e.target.value)}  placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title='Roll-wise Width Measurements (inches)' color={C.wh}
            action={<Btn sm onClick={()=>setF(f=>({...f,rolls:[...f.rolls,newR()]}))} color={C.wh}>＋ Add Roll</Btn>}/>
          <div style={{ display:'grid', gridTemplateColumns:'70px 80px 80px 80px 80px 90px 60px',
            gap:6, marginBottom:8 }}>
            {['Roll #','Start W"','Mid W"','End W"','Avg W"','Variance','OK?'].map(h => (
              <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted,
                textTransform:'uppercase', textAlign:'center' }}>{h}</div>
            ))}
          </div>
          {F.rolls.map((r,i) => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'70px 80px 80px 80px 80px 90px 60px',
              gap:6, marginBottom:5, alignItems:'center' }}>
              <input value={r.rollNo} onChange={e=>upd(i,'rollNo',e.target.value)} placeholder={`${i+1}`}
                style={{ padding:'6px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              {['startW','midW','endW'].map(k => (
                <input key={k} type="number" value={r[k]} onChange={e=>upd(i,k,e.target.value)} placeholder="0"
                  style={{ padding:'6px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              ))}
              <div style={{ textAlign:'center', fontWeight:700, color:C.text, fontSize:13 }}>{r.avgW||'—'}"</div>
              <div style={{ textAlign:'center', fontWeight:700, fontSize:12,
                color: r.ok===null?C.textMuted : r.ok?C.success:C.danger }}>
                {r.variance ? `${parseFloat(r.variance)>0?'+':''}${r.variance}"` : '—'}
              </div>
              <div style={{ textAlign:'center', fontSize:18 }}>
                {r.ok===null ? '—' : r.ok
                  ? <span style={{ color:C.success }}>✓</span>
                  : <span style={{ color:C.danger  }}>✗</span>}
              </div>
            </div>
          ))}
          <div style={{ marginTop:14, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <div style={{ display:'flex', gap:10 }}>
              <Bdg color={C.success} bg={C.successBg}>{F.rolls.filter(r=>r.ok===true).length} Within Tol.</Bdg>
              <Bdg color={C.danger}  bg={C.dangerBg }>{F.rolls.filter(r=>r.ok===false).length} Out of Tol.</Bdg>
            </div>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Save Width Report'}
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-11 · FABRIC SWATCH CARD  (Nir/Wh/09)
  // ═════════════════════════════════════════════════════════════════
  function Ann11({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newR  = () => ({ id:uid(), rollNo:'', shade:'', width:'', shrinkL:'', shrinkW:'', gsm:'', status:'PASS', note:'' });
    const fresh = () => ({
      id:uid(), formType:'SWATCH_CARD', date:TODAY,
      clientRef: filters.style||'', colour: filters.colour||'',
      buyer: filters.buyer||'', brand: filters.brand||'',
      supplier:'', fabricType:'', approvedShade:'', stdGSM:'', inspectedBy:'',
      rows: Array.from({ length:6 }, newR),
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const upd = (i,k,v) => setF(f => ({ ...f, rows: f.rows.map((r,ri) => ri===i ? { ...r,[k]:v } : r) }));
    const recs = (db.warehouse||[]).filter(r => r.formType==='SWATCH_CARD');

    const handleSave = async () => {
      await doSave('warehouse_swatch_card', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-11 · Fabric Swatch Card" ref_="Nir/Wh/09 · Roll-wise shade record after segregation"
          action={<Bdg color={C.wh}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Date"              type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref / Style"      value={F.clientRef}          onChange={e=>set('clientRef',e.target.value)}          placeholder="Client Ref#..."/>
            <Fld label="Brand"             value={F.brand}         onChange={e=>set('brand',e.target.value)}         options={['',...BRANDS]}/>
            <Fld label="Buyer"             value={F.buyer}         onChange={e=>set('buyer',e.target.value)}         options={['',...BUYERS]}/>
            <Fld label="Colour"            value={F.colour}        onChange={e=>set('colour',e.target.value)}        options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Supplier"          value={F.supplier}      onChange={e=>set('supplier',e.target.value)}      placeholder="Supplier..."/>
            <Fld label="Fabric Type"       value={F.fabricType}    onChange={e=>set('fabricType',e.target.value)}    options={['',...FABRICS]}/>
            <Fld label="Approved Shade Ref" value={F.approvedShade} onChange={e=>set('approvedShade',e.target.value)} placeholder="Shade ref..."/>
            <Fld label="Standard GSM"      type="number" value={F.stdGSM} onChange={e=>set('stdGSM',e.target.value)} placeholder="0"/>
            <Fld label="Inspected By"      value={F.inspectedBy}   onChange={e=>set('inspectedBy',e.target.value)}   placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Roll Swatch Data" color={C.wh}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newR()]}))} color={C.wh}>＋ Add Row</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:700 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Roll #','Shade','Width"','Shrink L%','Shrink W%','GSM','Status','Note'].map(h => (
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub, fontSize:11,
                      textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.slate50 }}>
                    {['rollNo','shade','width','shrinkL','shrinkW','gsm'].map(k => (
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>upd(i,k,e.target.value)} placeholder="—"
                          style={{ width:'100%', minWidth:50, padding:'5px', borderRadius:R.sm,
                            border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.status} onChange={e=>upd(i,'status',e.target.value)}
                        style={{ padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                          fontSize:11, fontWeight:700, fontFamily:'inherit',
                          background: r.status==='PASS'?C.successBg:r.status==='FAIL'?C.dangerBg:C.warningBg,
                          color:      r.status==='PASS'?C.success:r.status==='FAIL'?C.danger:C.warning }}>
                        {['PASS','FAIL','HOLD'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.note||''} onChange={e=>upd(i,'note',e.target.value)} placeholder="Note..."
                        style={{ width:'100%', minWidth:80, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:12, display:'flex', justifyContent:'flex-end' }}>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Save Swatch Card'}
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-12 · SAMPLE SUBMISSION – WASH  (Nir/Wh/10)
  // ═════════════════════════════════════════════════════════════════
  function Ann12({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const WASH = ['Normal Wash','Stone Wash','Enzyme Wash','Acid Wash','Sand Blast','Bleach Wash','Silicon Wash','Pigment Dye','Wrinkle Free'];
    const fresh = () => ({
      id:uid(), formType:'WASH_SUB', date:TODAY,
      clientRef: filters.style||'', colour: filters.colour||'',
      buyer: filters.buyer||'', brand: filters.brand||'', po: filters.po||'',
      washType:'', recipeRef:'', lotNo:'', noOfSamples:'', fabricWeight:'',
      submittedBy:'', receivedBy:'', targetShade:'', targetHand:'', remarks:'',
      status:'PENDING',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const recs = (db.warehouse||[]).filter(r => r.formType==='WASH_SUB');

    const handleSave = async () => {
      await doSave('warehouse_wash_sub', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-12 · Sample Submission Form – Wash" ref_="Nir/Wh/10 · Blanket wash before shade segregation"
          action={<Bdg color={C.wh}>{recs.length} submissions</Bdg>}/>

        <Card>
          <SH title="Submission Details" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}      onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref / Style"    value={F.clientRef}      onChange={e=>set('clientRef',e.target.value)}      placeholder="Client Ref#..."/>
            <Fld label="Brand"           value={F.brand}     onChange={e=>set('brand',e.target.value)}     options={['',...BRANDS]}/>
            <Fld label="Buyer"           value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
            <Fld label="Colour"          value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="PO No."          value={F.po}        onChange={e=>set('po',e.target.value)}        options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
            <Fld label="Wash Type"       value={F.washType}  onChange={e=>set('washType',e.target.value)}  options={['',...WASH]}/>
            <Fld label="Recipe Ref."     value={F.recipeRef} onChange={e=>set('recipeRef',e.target.value)} placeholder="Recipe#..."/>
            <Fld label="Lot No."         value={F.lotNo}     onChange={e=>set('lotNo',e.target.value)}     placeholder="Lot#..."/>
            <Fld label="No. of Samples"  type="number" value={F.noOfSamples}   onChange={e=>set('noOfSamples',e.target.value)}   placeholder="0"/>
            <Fld label="Fabric Wt. (gsm)" type="number" value={F.fabricWeight} onChange={e=>set('fabricWeight',e.target.value)} placeholder="0"/>
            <Fld label="Target Shade"    value={F.targetShade} onChange={e=>set('targetShade',e.target.value)} placeholder="Target shade description..."/>
            <Fld label="Target Hand Feel" value={F.targetHand} onChange={e=>set('targetHand',e.target.value)}
              options={['','SOFT','MEDIUM','STIFF','SILKY','NATURAL']}/>
            <Fld label="Submitted By"    value={F.submittedBy} onChange={e=>set('submittedBy',e.target.value)} placeholder="Name..."/>
            <Fld label="Received By"     value={F.receivedBy}  onChange={e=>set('receivedBy',e.target.value)}  placeholder="Name..."/>
            <Fld label="Status"          value={F.status}    onChange={e=>set('status',e.target.value)}
              options={['PENDING','SUBMITTED','APPROVED','REJECTED']}/>
          </Grid>
          <div style={{ marginTop:10 }}>
            <Fld label="Remarks / Special Instructions" type="textarea" rows={3}
              value={F.remarks} onChange={e=>set('remarks',e.target.value)} placeholder="Any special wash instructions..."/>
          </div>
          <div style={{ marginTop:14, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <Res result={F.status==='APPROVED'?'PASS':F.status==='REJECTED'?'FAIL':null}/>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Submit Form'}
            </Btn>
          </div>
        </Card>

        <Card>
          <SH title="Submission History" color={C.wh}/>
          <Tbl headers={['Date','Style','Colour','Wash Type','Lot #','Samples','Status','By']}
            rows={recs.map(r => [
              r.date, r.clientRef||'—', r.colour||'—', r.washType||'—', r.lotNo||'—', r.noOfSamples||'—',
              <Bdg color={r.status==='APPROVED'?C.success:r.status==='REJECTED'?C.danger:C.warning}>{r.status}</Bdg>,
              r.submittedBy||'—',
            ])} emptyMsg="No wash submissions yet."/>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  ANN-13 · SAMPLE SUBMISSION – LAB TESTING  (Nir/Wh/11)
  // ═════════════════════════════════════════════════════════════════
  function Ann13({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const fresh = () => ({
      id:uid(), formType:'TEST_SUB', submissionDate:TODAY,
      buyer: filters.buyer||'', brand: filters.brand||'',
      clientRef: filters.style||'', colour: filters.colour||'', po: filters.po||'',
      dept:'', orderNo:'', styleNo: filters.style||'',
      submittedBy:'', contactNo:'', labRefNo:'',
      submissionStatus:'1ST SUBMISSION', noOfSamples:'', testType:'FABRIC',
      tests: TESTING_STDS.map((t,i) => ({ ...t, selected:i<6, result:'', requirement:'' })),
      careInstruction:'', remarks:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set    = (k,v) => setF(f => ({ ...f, [k]:v }));
    const toggle = (i)   => setF(f => ({ ...f, tests: f.tests.map((t,ti) => ti===i ? { ...t, selected:!t.selected } : t) }));
    const updT   = (i,k,v) => setF(f => ({ ...f, tests: f.tests.map((t,ti) => ti===i ? { ...t,[k]:v } : t) }));
    const recs   = (db.warehouse||[]).filter(r => r.formType==='TEST_SUB');
    const sel    = F.tests.filter(t => t.selected);
    const passed = sel.filter(t => t.result==='PASS').length;
    const failed = sel.filter(t => t.result==='FAIL').length;

    const sC = s => s==='PASS'?C.success:s==='FAIL'?C.danger:s==='HOLD'?C.warning:C.textMuted;
    const sB = s => s==='PASS'?C.successBg:s==='FAIL'?C.dangerBg:s==='HOLD'?C.warningBg:C.slate50;

    const handleSave = async () => {
      await doSave('warehouse_test_sub', { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-13 · Sample Submission Form – Lab Testing"
          ref_="Lab Testing Submission · Contact Lab for details"
          action={<Bdg color={C.wh}>{recs.length} submissions</Bdg>}/>

        <Card>
          <SH title="Submission Details" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Submission Date"   type="date" value={F.submissionDate} onChange={e=>set('submissionDate',e.target.value)}/>
            <Fld label="Buyer"             value={F.buyer}    onChange={e=>set('buyer',e.target.value)}    options={['',...BUYERS]}/>
            <Fld label="Brand"             value={F.brand}    onChange={e=>set('brand',e.target.value)}    options={['',...BRANDS]}/>
            <Fld label="Department"        value={F.dept}     onChange={e=>set('dept',e.target.value)}     placeholder="Dept..."/>
            <Fld label="Order No."         value={F.orderNo}  onChange={e=>set('orderNo',e.target.value)}  options={["", ...dbGet("poNumbers",[])]} placeholder="Order#..."/>
            <Fld label="Style No."         value={F.styleNo}  onChange={e=>set('styleNo',e.target.value)}  options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Colour"            value={F.colour}   onChange={e=>set('colour',e.target.value)}   options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Submission Status" value={F.submissionStatus} onChange={e=>set('submissionStatus',e.target.value)}
              options={['1ST SUBMISSION','2ND SUBMISSION','3RD SUBMISSION','REVISION']}/>
            <Fld label="No. of Samples"    type="number" value={F.noOfSamples} onChange={e=>set('noOfSamples',e.target.value)} placeholder="0"/>
            <Fld label="Test Type"         value={F.testType} onChange={e=>set('testType',e.target.value)} options={['FABRIC','GARMENT','BOTH']}/>
            <Fld label="Submitted By"      value={F.submittedBy} onChange={e=>set('submittedBy',e.target.value)} placeholder="Name..."/>
            <Fld label="Contact No."       value={F.contactNo}   onChange={e=>set('contactNo',e.target.value)}   placeholder="Phone..."/>
            <Fld label="Lab Ref No. (Lab fills)" value={F.labRefNo} onChange={e=>set('labRefNo',e.target.value)} placeholder="Filled by Lab..."/>
          </Grid>
        </Card>

        <Card>
          <SH title={`Testing Programme  —  ${sel.length} tests selected`} color={C.wh}
            action={
              <div style={{ display:'flex', gap:8 }}>
                <Bdg color={C.success} bg={C.successBg}>{passed} Pass</Bdg>
                <Bdg color={C.danger}  bg={C.dangerBg }>{failed} Fail</Bdg>
              </div>
            }/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['✓','Testing Name','Standard','Requirement','Result'].map(h => (
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                      fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.tests.map((t,i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${C.border}`,
                    background: t.selected ? (i%2===0?C.white:C.slate50) : '#fafafa',
                    opacity: t.selected ? 1 : 0.5 }}>
                    <td style={{ padding:'7px 10px' }}>
                      <input type="checkbox" checked={t.selected} onChange={()=>toggle(i)}
                        style={{ width:15, height:15, accentColor:C.wh, cursor:'pointer' }}/>
                    </td>
                    <td style={{ padding:'7px 10px', fontWeight: t.selected?600:400, color:C.text }}>{t.name}</td>
                    <td style={{ padding:'7px 10px', fontFamily:'monospace', fontSize:11, color:C.wh }}>{t.std}</td>
                    <td style={{ padding:'6px 8px' }}>
                      <input value={t.requirement} onChange={e=>updT(i,'requirement',e.target.value)}
                        placeholder="Min. requirement..."
                        style={{ width:'100%', minWidth:120, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'6px 8px' }}>
                      <select value={t.result} onChange={e=>updT(i,'result',e.target.value)}
                        style={{ padding:'5px 8px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                          fontSize:11, fontWeight:700, fontFamily:'inherit',
                          background:sB(t.result), color:sC(t.result) }}>
                        {['','PASS','FAIL','HOLD','PENDING'].map(s => (
                          <option key={s} value={s}>{s||'Not Tested'}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <SH title="Additional Info & Sign-off" color={C.wh}/>
          <Grid gap={14}>
            <Fld label="Care Instruction" type="textarea" rows={3}
              value={F.careInstruction} onChange={e=>set('careInstruction',e.target.value)}
              placeholder="Care symbols / wash instructions..."/>
            <Fld label="Remarks / Special Requirements" type="textarea" rows={3}
              value={F.remarks} onChange={e=>set('remarks',e.target.value)}
              placeholder="Additional test requirements or notes..."/>
          </Grid>
          <div style={{ marginTop:14, display:'flex', justifyContent:'space-between',
            alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              {failed>0 && <Res result="FAIL"/>}
              {passed>0 && failed===0 && <Res result="PASS"/>}
              <span style={{ fontSize:12, color:C.textMuted }}>
                {sel.length} tests selected · {passed} pass · {failed} fail
              </span>
            </div>
            <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
              {saved?'Saved!':saving?'Saving…':'Submit Test Request'}
            </Btn>
          </div>
        </Card>

        <Card>
          <SH title="Submission History" color={C.wh}/>
          <Tbl headers={['Date','Style','Colour','Test Type','Tests','Pass','Fail','By']}
            rows={recs.map(r => [
              r.submissionDate, r.styleNo||'—', r.colour||'—',
              <Bdg color={C.wh}>{r.testType}</Bdg>,
              (r.tests||[]).filter(t=>t.selected).length,
              <Bdg color={C.success} bg={C.successBg}>{(r.tests||[]).filter(t=>t.result==='PASS').length}</Bdg>,
              <Bdg color={C.danger}  bg={C.dangerBg }>{(r.tests||[]).filter(t=>t.result==='FAIL').length}</Bdg>,
              r.submittedBy||'—',
            ])} emptyMsg="No test submissions yet."/>
        </Card>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  WAREHOUSE DEPT SHELL  — sub-nav + routing
  // ═════════════════════════════════════════════════════════════════
  const SUB = [
    { id:'ann03', label:'Ann-03 Trims Insp.',     icon:'🔍', Comp:Ann03, ft:'TRIMS_INSP'      },
    { id:'ann04', label:'Ann-04 Trims Card',       icon:'🃏', Comp:Ann04, ft:'TRIMS_CARD'      },
    { id:'ann05', label:'Ann-05 Fabric Insp.',     icon:'🧵', Comp:Ann05, ft:'FABRIC_INSP'     },
    { id:'ann06', label:'Ann-06 Fabric Claims',    icon:'📋', Comp:Ann06, ft:'FABRIC_CLAIM'    },
    { id:'ann07', label:'Ann-07 Trims Approval',   icon:'✅', Comp:Ann07, ft:'TRIMS_APPROVAL'  },
    { id:'ann08', label:'Ann-08 Shade Seg.',       icon:'🎨', Comp:Ann08, ft:'SHADE_SEG'       },
    { id:'ann09', label:'Ann-09 Roll Wise All',         icon:'📊', Comp:Ann09, ft:'ROLL WISE ALL'        },
    { id:'ann10', label:'Ann-10 Width Insp.',      icon:'📏', Comp:Ann10, ft:'WIDTH_INSP'      },
    { id:'ann11', label:'Ann-11 Swatch Card',      icon:'🎴', Comp:Ann11, ft:'SWATCH_CARD'     },
    { id:'ann12', label:'Ann-12 Wash Sub.',        icon:'💧', Comp:Ann12, ft:'WASH_SUB'        },
    { id:'ann13', label:'Ann-13 Test Sub.',        icon:'🔬', Comp:Ann13, ft:'TEST_SUB'        },
  ];

  function WarehouseDept({ db, setDb, filters }) {
    const [active, setActive] = React.useState('ann05');

    const counts = SUB.reduce((acc,f) => ({
      ...acc,
      [f.id]: (db.warehouse||[]).filter(r => r.formType===f.ft).length,
    }), {});

    const ActiveComp = SUB.find(f => f.id===active)?.Comp;

    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
        {/* scrollable sub-nav */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
          overflowX:'auto', flexShrink:0 }}>
          <div style={{ display:'flex', paddingTop:4, paddingLeft:8, minWidth:'max-content' }}>
            {SUB.map(f => (
              <button key={f.id} onClick={() => setActive(f.id)} style={{
                padding:'10px 14px', fontSize:12,
                fontWeight: active===f.id ? 700 : 400,
                color: active===f.id ? C.wh : C.textSub,
                background:'transparent', border:'none', cursor:'pointer',
                borderBottom: active===f.id ? `2.5px solid ${C.wh}` : '2.5px solid transparent',
                display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
              }}>
                <span>{f.icon}</span>{f.label}
                {counts[f.id] > 0 && (
                  <span style={{ background:C.wh+'22', color:C.wh,
                    padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>
                    {counts[f.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* active form */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {ActiveComp && <ActiveComp db={db} setDb={setDb} filters={filters}/>}
        </div>
      </div>
    );
  }

  // ── REGISTER into the shell's DEPT_REGISTRY ────────────────────
  DEPT_REGISTRY['warehouse'] = WarehouseDept;
  console.log('[QMS] Dept-02 Warehouse registered ✓  (Ann-03 → Ann-13, 11 forms)');

})();

// ════════════════════════════════════════════════════════════════════════════
//  DEPT-03 · CUTTING · Ann-14 → Ann-28  (15 forms)
//  HOW TO USE: Paste this entire block at the very bottom of your file,
//  below the Warehouse block (below the last line: })(); )
//
//  Ann-14  Marker Inspection Report               Nir/Cut/01 → cutting_marker_insp
//  Ann-15  Daily Marker Inspection Summary        Nir/Cut/02 → cutting_marker_summary
//  Ann-16  Spreading Quality Control Report       Nir/Cut/03 → cutting_spreading
//  Ann-17  Daily After Cutting Inspection Summary Nir/Cut/04 → cutting_after_cut
//  Ann-18  Daily Cut Parts Inspection Summary     Nir/Cut/05 → cutting_parts_summary
//  Ann-19  Cut Part Inspection Report             Nir/Cut/06 → cutting_parts_insp
//  Ann-20  Cutting QC Report (Auto / Manual)      Nir/Cut/07 → cutting_qc_report
//  Ann-21  Daily Bundle Audit Report              Nir/Cut/08 → cutting_bundle_audit
//  Ann-22  Daily Bundle & Number Audit Summary    Nir/Cut/09 → cutting_bundle_summary
//  Ann-23  Embroidery Inspection Report           Nir/Cut/10 → cutting_embroidery
//  Ann-24  Cutting Process Control Report         Nir/Cut/11 → cutting_process_ctrl
//  Ann-25  Hourly Fusing Machine Inspection       Nir/Cut/12 → cutting_fusing_hourly
//  Ann-26  Daily Fusing Machine Inspection        Nir/Cut/13 → cutting_fusing_daily
//  Ann-27  Button Pull Test Report                Nir/Cut/14 → cutting_button_pull
//  Ann-28  Fusing Machine Process Control         Nir/Cut/15 → cutting_fusing_process
// ════════════════════════════════════════════════════════════════════════════

(function registerCutting() {

  // ── palette (matches existing QualityControl.js exactly) ─────────────────
  const C = {
    bg:'#0D1B2A', white:'#FFFFFF',
    slate50:'#F8F9FA', slate100:'#F4F6F8',
    border:'#DEE2E6', text:'#1A2A3A', textSub:'#6C757D', textMuted:'#ADB5BD',
    cut:'#C62828',   // dept accent — cutting red
    success:'#2E7D32', successBg:'#E8F5E9', successBd:'#A5D6A7',
    danger :'#C62828', dangerBg :'#FFEBEE', dangerBd :'#FFCDD2',
    warning:'#E65100', warningBg:'#FFF8E1', warningBd:'#FFE082',
  };
  const R   = { sm:'6px', md:'8px', lg:'12px' };
  const API = 'https://nirvikalp.integrashion.com/api/save_universal.php';
  const TODAY = new Date().toISOString().split('T')[0];

  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }

  // ── master data ───────────────────────────────────────────────────────────
  const BUYERS = dbGet('buyers', _FB_BUYERS);
  const BRANDS = dbGet('brands', _FB_BRANDS);
  const FABRICS = dbGet('fabrics', _FB_FABRICS);
  const SIZES = dbGet('sizes', ['XS','S','M','L','XL','XXL','3XL','28','30','32','34','36','38','40']);

  const MARKER_DEFECTS = [
    'Pattern size incorrect','Grainline not aligned','Plotter problem / skip',
    'Overlapping pieces','Missing pattern piece','Incorrect ratio',
    'Notch missing','Drill hole missing','Seam allowance wrong',
    'Part label missing','Marker width exceeds fabric','Other',
  ];

  const SPREADING_CHECKS = [
    'Ply height within limit (max 4")','Table marking correct',
    'Marker placed correctly','Splices minimised',
    'Narrow goods / width shortage handled','Leaning controlled (< 1/8")',
    'Fabric tension even — no stretching','Ply count matches cut order',
    'Remnants recorded','Fabric flaws marked & avoided',
    'Shade lot consistency maintained','End bits within allowance',
  ];

  const CUTTING_DEFECTS = [
    'Mis-cut','Ragged cutting','Notch missing / incorrect',
    'Drill hole missing','Pattern check failed','Wrong ply count',
    'Shade mixing','Bundle numbering error','Fusing defect',
    'Measurement out of tolerance','Fabric flaw in cut panel','Other',
  ];

  const BUNDLE_DEFECTS = [
    'Wrong bundle no.','Wrong size','Wrong colour','Shade mixing',
    'Missing piece','Extra piece','Wrong numbering sequence',
    'Bundle ticket missing','Fusing missing','Other',
  ];

  const EMBROIDER_DEFECTS = [
    'Placement wrong','Design incomplete','Thread break','Puckering',
    'Wrong colour thread','Registration off','Stitch count wrong',
    'Fabric damage','Density uneven','Other',
  ];

  const FUSING_HOURS = [
    '08:00-09:00','09:00-10:00','10:00-11:00','11:00-12:00',
    '12:00-01:00','01:00-02:00','02:00-03:00','03:00-04:00',
    '04:00-05:00','05:00-06:00','06:00-07:00',
  ];

  const MARKER_PARTS_BOTTOM = [
    'Front Part (Lt)','Front Part (Rt)','Back Part Lt','Back Part Rt',
    'Waist Band Top/In','Front WB Lt Top/In','Front W/B Rt Top/In',
    'S/Fly','D/Fly','Ft Pkt Facing Top L/R','Ft Pkt Facing In L/R',
    'Bk Welt Pkt L/R','Bk Welt Facing L/R','Back Pocket L/R',
    'Front Pocket L/R','Pocket Flap Top/In','Middle Loop',
  ];

  // ── shared API save ───────────────────────────────────────────────────────
  async function saveToServer(category, record, db, setDb) {
    try {
      const res = await fetch(API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ category, data:record }),
      });
      const j = await res.json();
      record.serverId = j.id || Date.now();
    } catch { record.serverId = Date.now(); }
    setDb(prev => {
      const deptKey = record._dept || 'cutting';
      const list = prev[deptKey] || [];
      const idx  = list.findIndex(r => r.id === record.id);
      return {
        ...prev,
        [deptKey]: idx >= 0 ? list.map(r => r.id===record.id ? record : r) : [...list, record],
      };
    });
  }

  // ── mini UI (self-contained) ──────────────────────────────────────────────
  function Fld({ label, value, onChange, type='text', readOnly=false,
    placeholder='', options=null, required=false, note='', rows=3 }) {
    const base = {
      width:'100%', padding:'9px 11px', fontSize:13, borderRadius:R.md,
      border:`1px solid ${C.border}`, background:readOnly?C.slate50:C.white,
      color:readOnly?C.textSub:C.text, outline:'none', fontFamily:'inherit',
    };
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        <label style={{ fontSize:11, fontWeight:600, color:C.textSub,
          textTransform:'uppercase', letterSpacing:'.4px' }}>
          {label}{required && <span style={{ color:C.danger }}> *</span>}
        </label>
        {options
          ? <select value={value} onChange={onChange} disabled={readOnly}
              style={{ ...base, cursor:readOnly?'default':'pointer' }}>
              {options.map((o,i) => <option key={i} value={o.value??o}>{o.label??o}</option>)}
            </select>
          : type==='textarea'
          ? <textarea value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} rows={rows} style={{ ...base, resize:'vertical' }}/>
          : <input type={type} value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} style={base}/>}
        {note && <div style={{ fontSize:11, color:C.textMuted }}>{note}</div>}
      </div>
    );
  }

  function Btn({ children, onClick, color=C.cut, outline=false, sm=false, disabled=false, icon=null }) {
    return (
      <button onClick={onClick} disabled={disabled} style={{
        display:'flex', alignItems:'center', gap:5,
        padding:sm?'7px 13px':'10px 20px', borderRadius:R.md,
        border:outline?`2px solid ${color}`:'none',
        background:disabled?C.slate100:outline?'transparent':color,
        color:disabled?C.textMuted:outline?color:C.white,
        fontWeight:700, fontSize:sm?12:13, cursor:disabled?'default':'pointer',
        fontFamily:'inherit', transition:'all .15s',
      }}>{icon && <span>{icon}</span>}{children}</button>
    );
  }

  function Bdg({ children, color=C.cut, bg }) {
    return (
      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
        color, background:bg||color+'22', display:'inline-block', whiteSpace:'nowrap' }}>
        {children}
      </span>
    );
  }

  function Res({ result }) {
    const m = {
      PASS:{c:C.success,b:C.successBg}, FAIL:{c:C.danger,b:C.dangerBg},
      HOLD:{c:C.warning,b:C.warningBg}, PENDING:{c:C.textMuted,b:C.slate100},
    };
    const x = m[result?.toUpperCase()] || m.PENDING;
    return <Bdg color={x.c} bg={x.b}>{result||'PENDING'}</Bdg>;
  }

  function Card({ children, style={} }) {
    return (
      <div style={{ background:C.white, border:`1px solid ${C.border}`,
        borderRadius:R.lg, padding:20, ...style }}>
        {children}
      </div>
    );
  }

  function SH({ title, color=C.cut, action=null }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, paddingBottom:12, borderBottom:`2px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:3, height:18, background:color, borderRadius:2 }}/>
          <h3 style={{ fontSize:13, fontWeight:700, color:C.text,
            textTransform:'uppercase', letterSpacing:'.4px' }}>{title}</h3>
        </div>
        {action}
      </div>
    );
  }

  function Grid({ children, gap=14, min=180 }) {
    return (
      <div style={{ display:'grid',
        gridTemplateColumns:`repeat(auto-fit,minmax(${min}px,1fr))`, gap }}>
        {children}
      </div>
    );
  }

  function Tbl({ headers=[], rows=[], emptyMsg='No records found.' }) {
    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:C.slate50 }}>
              {headers.map((h,i) => (
                <th key={i} style={{ padding:'9px 11px', textAlign:'left', fontWeight:700,
                  color:C.textSub, fontSize:11, textTransform:'uppercase',
                  borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length===0
              ? <tr><td colSpan={headers.length}
                  style={{ padding:24, textAlign:'center', color:C.textMuted, fontStyle:'italic' }}>
                  {emptyMsg}</td></tr>
              : rows.map((row,i) => (
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}`,
                  background:i%2===0?C.white:C.slate50 }}>
                  {row.map((cell,j) => (
                    <td key={j} style={{ padding:'9px 11px', color:C.text, verticalAlign:'middle' }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  function ChkRow({ label, ok, onOk, remark, onRemark }) {
    return (
      <div style={{ display:'flex', gap:10, alignItems:'flex-start',
        padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', gap:6, flexShrink:0, paddingTop:2 }}>
          {[['OK',true,C.success],['NG',false,C.danger]].map(([lbl,val,col]) => (
            <label key={lbl} style={{ display:'flex', alignItems:'center', gap:4, cursor:'pointer' }}>
              <input type="radio" checked={ok===val} onChange={()=>onOk(val)}
                style={{ accentColor:col, width:14, height:14 }}/>
              <span style={{ fontSize:11, fontWeight:700, color:ok===val?col:C.textMuted }}>{lbl}</span>
            </label>
          ))}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, color:C.text }}>{label}</div>
          {ok===false && (
            <input placeholder="Corrective action..." value={remark||''}
              onChange={e=>onRemark(e.target.value)}
              style={{ width:'100%', padding:'5px 8px', fontSize:11, borderRadius:R.sm,
                border:`1px solid ${C.dangerBd}`, background:C.dangerBg, marginTop:4 }}/>
          )}
        </div>
      </div>
    );
  }

  function PgHead({ title, ref_, action }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:18 }}>
        <div>
          <h2 style={{ fontSize:16, fontWeight:800, color:C.text }}>{title}</h2>
          <div style={{ fontSize:11, color:C.textMuted }}>Ref: {ref_}</div>
        </div>
        {action}
      </div>
    );
  }

  function useSave() {
    const [saving, setSaving] = React.useState(false);
    const [saved,  setSaved]  = React.useState(false);
    async function doSave(category, record, db, setDb, extra) {
      setSaving(true);
      await saveToServer(category, record, db, setDb);
      if (extra) extra(record);
      setSaving(false); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    return { saving, saved, doSave };
  }

  function SaveBar({ result, saving, saved, onSave }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        flexWrap:'wrap', gap:10, marginTop:16 }}>
        {result && <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ fontSize:11, color:C.textSub }}>Auto Result</div>
          <Res result={result}/>
        </div>}
        <Btn onClick={onSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
          {saved?'Saved!':saving?'Saving…':'Save Report'}
        </Btn>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-14 · MARKER INSPECTION REPORT  (Nir/Cut/01)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann14({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const newRow = () => ({ id:uid(), partName:'', size:'', qty:0, defect:'', remarks:'' });
    const fresh  = () => ({
      id:uid(), formType:'MARKER_INSP', date:TODAY,
      nir:filters.style||'', po:filters.po||'', buyer:filters.buyer||'',
      brand:filters.brand||'', colour:filters.colour||'',
      markerRef:'', markerRatio:'', fabricColour:'', style:filters.style||'',
      inspectedBy:'', qcSupervisor:'',
      parts: MARKER_PARTS_BOTTOM.map(p => ({ id:uid(), part:p, ok:null, remark:'' })),
      totalMarkers:'', incorrectMarkers:'',
      problemDetails:'', approvedBy:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f=>({...f,[k]:v}));
    const setPart = (i,k,v) => setF(f=>({...f, parts:f.parts.map((p,pi)=>pi===i?{...p,[k]:v}:p)}));

    const recs = (db.cutting||[]).filter(r=>r.formType==='MARKER_INSP');
    const okCount  = F.parts.filter(p=>p.ok===true).length;
    const ngCount  = F.parts.filter(p=>p.ok===false).length;
    const autoRes  = () => ngCount===0&&okCount>0?'PASS':ngCount<=2?'HOLD':'FAIL';

    const handleSave = async () => {
      await doSave('cutting_marker_insp',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-14 · Marker Inspection Report" ref_="Nir/Cut/01 · 100% Marker Check"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.cut}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.cut}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Marker Inspection Records"/>
            <Tbl headers={['Date','Style','Marker Ref','Buyer','Colour','OK Parts','NG Parts','Result']}
              rows={recs.map(r=>[r.date, r.style||'—', r.markerRef||'—', r.buyer||'—', r.colour||'—',
                <Bdg color={C.success} bg={C.successBg}>{(r.parts||[]).filter(p=>p.ok===true).length}</Bdg>,
                <Bdg color={C.danger}  bg={C.dangerBg }>{(r.parts||[]).filter(p=>p.ok===false).length}</Bdg>,
                <Res result={r.result}/>])}
              emptyMsg="No marker inspections yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Marker Header" color={C.cut}/>
              <Grid gap={14}>
                <Fld label="Date"         type="date" value={F.date}        onChange={e=>set('date',e.target.value)}/>
                <Fld label="Client Ref / Style" value={F.style}       onChange={e=>set('style',e.target.value)}       placeholder="Style..."/>
                <Fld label="PO No."       value={F.po}          onChange={e=>set('po',e.target.value)}          options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer"        value={F.buyer}       onChange={e=>set('buyer',e.target.value)}       options={['',...BUYERS]}/>
                <Fld label="Brand"        value={F.brand}       onChange={e=>set('brand',e.target.value)}       options={['',...BRANDS]}/>
                <Fld label="Colour"       value={F.colour}      onChange={e=>set('colour',e.target.value)}      options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Marker Ref"   value={F.markerRef}   onChange={e=>set('markerRef',e.target.value)}   placeholder="Marker ref..."/>
                <Fld label="Marker Ratio" value={F.markerRatio} onChange={e=>set('markerRatio',e.target.value)} placeholder="e.g. S2 M3 L2..."/>
                <Fld label="Fabric Colour" value={F.fabricColour} onChange={e=>set('fabricColour',e.target.value)} placeholder="Fabric colour..."/>
                <Fld label="Total Markers Inspected" type="number" value={F.totalMarkers}
                  onChange={e=>set('totalMarkers',e.target.value)} placeholder="0"/>
                <Fld label="Incorrect Markers Found" type="number" value={F.incorrectMarkers}
                  onChange={e=>set('incorrectMarkers',e.target.value)} placeholder="0"/>
                <Fld label="Inspected By"  value={F.inspectedBy}  onChange={e=>set('inspectedBy',e.target.value)}  placeholder="Name..."/>
                <Fld label="QC Supervisor" value={F.qcSupervisor} onChange={e=>set('qcSupervisor',e.target.value)} placeholder="Name..."/>
                <Fld label="Approved By"   value={F.approvedBy}   onChange={e=>set('approvedBy',e.target.value)}   placeholder="Name..."/>
              </Grid>
            </Card>

            <Card>
              <SH title="Parts / Pieces Check (Bottom Marker)" color={C.cut}
                action={
                  <div style={{ display:'flex', gap:6 }}>
                    <Bdg color={C.success} bg={C.successBg}>{okCount} OK</Bdg>
                    <Bdg color={C.danger}  bg={C.dangerBg }>{ngCount} NG</Bdg>
                  </div>
                }/>
              <div style={{ columns:2, columnGap:20 }}>
                {F.parts.map((p,i) => (
                  <div key={p.id} style={{ breakInside:'avoid', marginBottom:2 }}>
                    <ChkRow label={p.part} ok={p.ok} onOk={v=>setPart(i,'ok',v)}
                      remark={p.remark} onRemark={r=>setPart(i,'remark',r)}/>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <SH title="Problem Details" color={C.cut}/>
              <Fld label="Details of Problem Found" type="textarea" rows={4}
                value={F.problemDetails} onChange={e=>set('problemDetails',e.target.value)}
                placeholder="Describe any incorrect markers found..."/>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-15 · DAILY MARKER INSPECTION SUMMARY  (Nir/Cut/02)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann15({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newRow = () => ({ id:uid(), buyer:'', po:'', sizes:'', problem:'', qty:0 });
    const fresh  = () => ({
      id:uid(), formType:'MARKER_SUMMARY', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      totalInspected:'', totalIncorrect:'',
      qcManager:'', cuttingManager:'', cadIncharge:'',
      incorrectRows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f, incorrectRows:f.incorrectRows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.cutting||[]).filter(r=>r.formType==='MARKER_SUMMARY');

    const handleSave = async () => {
      await doSave('cutting_marker_summary',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-15 · Daily Marker Inspection Summary" ref_="Nir/Cut/02"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Summary Header" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"                type="date" value={F.date}            onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."           value={F.style}           onChange={e=>set('style',e.target.value)}           options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"               value={F.buyer}           onChange={e=>set('buyer',e.target.value)}           options={['',...BUYERS]}/>
            <Fld label="Total Markers Insp." type="number" value={F.totalInspected}  onChange={e=>set('totalInspected',e.target.value)}  placeholder="0"/>
            <Fld label="Incorrect Markers"   type="number" value={F.totalIncorrect}  onChange={e=>set('totalIncorrect',e.target.value)}  placeholder="0"/>
            <Fld label="QC Manager"          value={F.qcManager}       onChange={e=>set('qcManager',e.target.value)}       placeholder="Name..."/>
            <Fld label="Cutting Manager"     value={F.cuttingManager}  onChange={e=>set('cuttingManager',e.target.value)}  placeholder="Name..."/>
            <Fld label="CAD Incharge"        value={F.cadIncharge}     onChange={e=>set('cadIncharge',e.target.value)}     placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Details of Incorrect Markers" color={C.cut}
            action={<Btn sm onClick={()=>setF(f=>({...f,incorrectRows:[...f.incorrectRows,newRow()]}))} color={C.cut}>＋ Add Row</Btn>}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 80px 30px', gap:6, marginBottom:8 }}>
            {['Buyer','PO No.','No. of Sizes','Problem Found','Qty',''].map(h=>(
              <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>
          {F.incorrectRows.map((r,i) => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 80px 30px', gap:6, marginBottom:5, alignItems:'center' }}>
              <select value={r.buyer} onChange={e=>updR(i,'buyer',e.target.value)}
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, fontFamily:'inherit' }}>
                <option value="">Buyer...</option>
                {BUYERS.map(b=><option key={b} value={b}>{b}</option>)}
              </select>
              <input value={r.po}      onChange={e=>updR(i,'po',e.target.value)}      placeholder="PO#..." list="po-datalist"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <input value={r.sizes}   onChange={e=>updR(i,'sizes',e.target.value)}   placeholder="e.g. S,M,L"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <input value={r.problem} onChange={e=>updR(i,'problem',e.target.value)} placeholder="Problem..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <input type="number" value={r.qty} onChange={e=>updR(i,'qty',e.target.value)} placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              <button onClick={()=>setF(f=>({...f,incorrectRows:f.incorrectRows.filter((_,ri)=>ri!==i)}))}
                style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer', color:C.danger, fontWeight:700, fontSize:12, padding:'6px' }}>✕</button>
            </div>
          ))}
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-16 · SPREADING QUALITY CONTROL REPORT  (Nir/Cut/03)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann16({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'SPREADING', date:TODAY,
      style:filters.style||'', po:filters.po||'', buyer:filters.buyer||'', colour:filters.colour||'',
      cutNo:'', plyHeight:'', layLength:'', markerLength:'', fabricWidth:'',
      spreadedBy:'', qcName:'',
      checks: SPREADING_CHECKS.map(l=>({ label:l, ok:null, remark:'' })),
      remarks:'',
    });
    const [F, setF]   = React.useState(fresh());
    const set         = (k,v) => setF(f=>({...f,[k]:v}));
    const setChk      = (i,ok) => setF(f=>({...f,checks:f.checks.map((c,ci)=>ci===i?{...c,ok}:c)}));
    const setRmk      = (i,r)  => setF(f=>({...f,checks:f.checks.map((c,ci)=>ci===i?{...c,remark:r}:c)}));
    const recs        = (db.cutting||[]).filter(r=>r.formType==='SPREADING');
    const ngCount     = F.checks.filter(c=>c.ok===false).length;
    const okCount     = F.checks.filter(c=>c.ok===true).length;
    const autoRes     = () => ngCount===0&&okCount>0?'PASS':ngCount<=2?'HOLD':'FAIL';

    const handleSave = async () => {
      await doSave('cutting_spreading',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-16 · Spreading Quality Control Report" ref_="Nir/Cut/03"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.cut}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.cut}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Spreading Records"/>
            <Tbl headers={['Date','Style','Cut No.','Buyer','Ply Height','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.cutNo||'—',r.buyer||'—',r.plyHeight||'—',<Res result={r.result}/>])}
              emptyMsg="No spreading records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Spreading Header" color={C.cut}/>
              <Grid gap={14}>
                <Fld label="Date"          type="date" value={F.date}        onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."     value={F.style}      onChange={e=>set('style',e.target.value)}      options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="PO No."        value={F.po}         onChange={e=>set('po',e.target.value)}         options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer"         value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
                <Fld label="Colour"        value={F.colour}     onChange={e=>set('colour',e.target.value)}     options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Cut No."       value={F.cutNo}      onChange={e=>set('cutNo',e.target.value)}      placeholder="Cut#..."/>
                <Fld label='Ply Height"'   value={F.plyHeight}  onChange={e=>set('plyHeight',e.target.value)}  placeholder='max 4"'/>
                <Fld label="Lay Length"    value={F.layLength}  onChange={e=>set('layLength',e.target.value)}  placeholder="yards..."/>
                <Fld label="Marker Length" value={F.markerLength} onChange={e=>set('markerLength',e.target.value)} placeholder="yards..."/>
                <Fld label='Fabric Width"' value={F.fabricWidth}  onChange={e=>set('fabricWidth',e.target.value)}  placeholder='56"'/>
                <Fld label="Spreaded By"   value={F.spreadedBy} onChange={e=>set('spreadedBy',e.target.value)} placeholder="Name..."/>
                <Fld label="QC Name"       value={F.qcName}     onChange={e=>set('qcName',e.target.value)}     placeholder="Name..."/>
              </Grid>
            </Card>

            <Card>
              <SH title="Spreading Quality Checklist" color={C.cut}
                action={
                  <div style={{ display:'flex', gap:6 }}>
                    <Bdg color={C.success} bg={C.successBg}>{okCount} OK</Bdg>
                    <Bdg color={C.danger}  bg={C.dangerBg }>{ngCount} NG</Bdg>
                  </div>
                }/>
              <div style={{ columns:2, columnGap:20 }}>
                {F.checks.map((c,i) => (
                  <div key={i} style={{ breakInside:'avoid', marginBottom:2 }}>
                    <ChkRow label={c.label} ok={c.ok} onOk={v=>setChk(i,v)}
                      remark={c.remark} onRemark={r=>setRmk(i,r)}/>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
                onChange={e=>set('remarks',e.target.value)} placeholder="Any observations..."/>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-17 · DAILY AFTER CUTTING INSPECTION SUMMARY  (Nir/Cut/04)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann17({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newRow = () => ({ id:uid(), partName:'', defectType:'', auditedBy:'', qty:0, defectQty:0 });
    const fresh  = () => ({
      id:uid(), formType:'AFTER_CUT', date:TODAY,
      orderNo:filters.style||'', cutNo:'', buyer:filters.buyer||'', colour:filters.colour||'',
      cuttingManager:'', qcManager:'',
      rows:[newRow(), newRow(), newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.cutting||[]).filter(r=>r.formType==='AFTER_CUT');

    const totalQty     = F.rows.reduce((s,r)=>s+(parseInt(r.qty)||0),0);
    const totalDefect  = F.rows.reduce((s,r)=>s+(parseInt(r.defectQty)||0),0);
    const defPct       = totalQty>0?((totalDefect/totalQty)*100).toFixed(1):0;

    const handleSave = async () => {
      await doSave('cutting_after_cut',
        { ...F, totalQty, totalDefect, defPct, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-17 · Daily After Cutting Inspection Summary" ref_="Nir/Cut/04 · Top / Middle / Bottom"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}           onChange={e=>set('date',e.target.value)}/>
            <Fld label="Order No."       value={F.orderNo}        onChange={e=>set('orderNo',e.target.value)}        options={["", ...dbGet("poNumbers",[])]} placeholder="Order / Style..."/>
            <Fld label="Cut No."         value={F.cutNo}          onChange={e=>set('cutNo',e.target.value)}          placeholder="Cut#..."/>
            <Fld label="Buyer"           value={F.buyer}          onChange={e=>set('buyer',e.target.value)}          options={['',...BUYERS]}/>
            <Fld label="Colour"          value={F.colour}         onChange={e=>set('colour',e.target.value)}         options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Cutting Manager" value={F.cuttingManager} onChange={e=>set('cuttingManager',e.target.value)} placeholder="Name..."/>
            <Fld label="QC Manager"      value={F.qcManager}      onChange={e=>set('qcManager',e.target.value)}      placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Inspection Detail — Top / Middle / Bottom Lay" color={C.cut}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.cut}>＋ Add Row</Btn>}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 80px 80px 30px', gap:6, marginBottom:8 }}>
            {['Part Name','Type of Defect','Audited By','Qty','Defect Qty',''].map(h=>(
              <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>
          {F.rows.map((r,i) => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 80px 80px 30px', gap:6, marginBottom:5, alignItems:'center' }}>
              <input value={r.partName}   onChange={e=>updR(i,'partName',e.target.value)}   placeholder="Part name..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <select value={r.defectType} onChange={e=>updR(i,'defectType',e.target.value)}
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, fontFamily:'inherit' }}>
                <option value="">Defect type...</option>
                {CUTTING_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
              <input value={r.auditedBy}  onChange={e=>updR(i,'auditedBy',e.target.value)}  placeholder="Name..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <input type="number" value={r.qty}       onChange={e=>updR(i,'qty',e.target.value)}       placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              <input type="number" value={r.defectQty} onChange={e=>updR(i,'defectQty',e.target.value)} placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                  background:parseInt(r.defectQty)>0?C.dangerBg:C.white }}/>
              <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer', color:C.danger, fontWeight:700, fontSize:12, padding:'6px' }}>✕</button>
            </div>
          ))}
          <div style={{ marginTop:10, padding:'10px 14px', background:C.slate50, borderRadius:R.md, display:'flex', gap:24 }}>
            <span style={{ fontSize:12 }}>Total Qty: <strong>{totalQty}</strong></span>
            <span style={{ fontSize:12 }}>Total Defect: <strong style={{ color:C.danger }}>{totalDefect}</strong></span>
            <span style={{ fontSize:12 }}>Defect %: <strong style={{ color:parseFloat(defPct)>5?C.danger:C.success }}>{defPct}%</strong></span>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-18 · DAILY CUT PARTS INSPECTION SUMMARY  (Nir/Cut/05)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann18({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newRow = () => ({ id:uid(), qcName:'', inspectedQty:0, defectQty:0, defPct:0 });
    const fresh  = () => ({
      id:uid(), formType:'PARTS_SUMMARY', date:TODAY,
      orderNo:filters.style||'', buyer:filters.buyer||'',
      supervisor:'', cuttingManager:'', qcManager:'', qualityGM:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>{
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      const insp=parseInt(rows[i].inspectedQty)||0;
      const def =parseInt(rows[i].defectQty)||0;
      rows[i].defPct=insp>0?((def/insp)*100).toFixed(1):0;
      return {...f,rows};
    });
    const recs   = (db.cutting||[]).filter(r=>r.formType==='PARTS_SUMMARY');
    const totInsp= F.rows.reduce((s,r)=>s+(parseInt(r.inspectedQty)||0),0);
    const totDef = F.rows.reduce((s,r)=>s+(parseInt(r.defectQty)||0),0);
    const totPct = totInsp>0?((totDef/totInsp)*100).toFixed(1):0;

    const handleSave = async () => {
      await doSave('cutting_parts_summary',
        { ...F, totInsp, totDef, totPct, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-18 · Daily Cut Parts Inspection Summary" ref_="Nir/Cut/05"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}           onChange={e=>set('date',e.target.value)}/>
            <Fld label="Order No."       value={F.orderNo}        onChange={e=>set('orderNo',e.target.value)}        options={["", ...dbGet("poNumbers",[])]} placeholder="Order / Style..."/>
            <Fld label="Buyer"           value={F.buyer}          onChange={e=>set('buyer',e.target.value)}          options={['',...BUYERS]}/>
            <Fld label="Supervisor"      value={F.supervisor}     onChange={e=>set('supervisor',e.target.value)}     placeholder="Name..."/>
            <Fld label="Cutting Manager" value={F.cuttingManager} onChange={e=>set('cuttingManager',e.target.value)} placeholder="Name..."/>
            <Fld label="QC Manager"      value={F.qcManager}      onChange={e=>set('qcManager',e.target.value)}      placeholder="Name..."/>
            <Fld label="Quality GM"      value={F.qualityGM}      onChange={e=>set('qualityGM',e.target.value)}      placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="QC-wise Inspection Data" color={C.cut}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.cut}>＋ Add QC</Btn>}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px 80px 30px', gap:6, marginBottom:8 }}>
            {['Name of QC','Inspected Qty','Defect Qty','Def %',''].map(h=>(
              <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>
          {F.rows.map((r,i) => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px 80px 30px', gap:6, marginBottom:5, alignItems:'center' }}>
              <input value={r.qcName}      onChange={e=>updR(i,'qcName',e.target.value)}      placeholder="QC Name..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <input type="number" value={r.inspectedQty} onChange={e=>updR(i,'inspectedQty',e.target.value)} placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              <input type="number" value={r.defectQty}    onChange={e=>updR(i,'defectQty',e.target.value)}    placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                  background:parseInt(r.defectQty)>0?C.dangerBg:C.white }}/>
              <div style={{ textAlign:'center', fontWeight:700,
                color:parseFloat(r.defPct)>5?C.danger:C.success }}>{r.defPct}%</div>
              <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer', color:C.danger, fontWeight:700, fontSize:12, padding:'6px' }}>✕</button>
            </div>
          ))}
          <div style={{ marginTop:10, padding:'10px 14px', background:C.slate50, borderRadius:R.md,
            display:'flex', gap:24, borderTop:`2px solid ${C.border}` }}>
            <span style={{ fontSize:12 }}>Total Inspected: <strong>{totInsp}</strong></span>
            <span style={{ fontSize:12 }}>Total Defect: <strong style={{ color:C.danger }}>{totDef}</strong></span>
            <span style={{ fontSize:12 }}>Overall %: <strong style={{ color:parseFloat(totPct)>5?C.danger:C.success }}>{totPct}%</strong></span>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-19 · CUT PART INSPECTION REPORT  (Nir/Cut/06)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann19({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newRow = () => ({ id:uid(), cutNo:'', partDesc:'', inspPcs:0, defFound:0, nature:'', bundleQty:0, okQty:0 });
    const fresh  = () => ({
      id:uid(), formType:'PARTS_INSP', date:TODAY,
      styleNo:filters.style||'', buyer:filters.buyer||'',
      colour:filters.colour||'', inspectorName:'',
      rows:[newRow(), newRow()],
      qcSupervisor:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.cutting||[]).filter(r=>r.formType==='PARTS_INSP');

    const totChk = F.rows.reduce((s,r)=>s+(parseInt(r.inspPcs)||0),0);
    const totDef = F.rows.reduce((s,r)=>s+(parseInt(r.defFound)||0),0);
    const defPct = totChk>0?((totDef/totChk)*100).toFixed(1):0;

    const handleSave = async () => {
      await doSave('cutting_parts_insp',
        { ...F, totalCheckQty:totChk, totalDefectQty:totDef, defectPct:defPct, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-19 · Cut Part Inspection Report" ref_="Nir/Cut/06 · 100% Cut Panel Check"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"           type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."      value={F.styleNo}       onChange={e=>set('styleNo',e.target.value)}       options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"          value={F.buyer}         onChange={e=>set('buyer',e.target.value)}         options={['',...BUYERS]}/>
            <Fld label="Colour"         value={F.colour}        onChange={e=>set('colour',e.target.value)}        options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Inspector Name" value={F.inspectorName} onChange={e=>set('inspectorName',e.target.value)} placeholder="Name..."/>
            <Fld label="QC Supervisor"  value={F.qcSupervisor}  onChange={e=>set('qcSupervisor',e.target.value)}  placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Cut Part Inspection Details" color={C.cut}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.cut}>＋ Add Row</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:800 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Cut No.','Part Description','Inspected Pcs','Def Found','Nature of Defect','Bundle Qty','OK Qty',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub, fontSize:11,
                      textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'5px 6px' }}><input value={r.cutNo} onChange={e=>updR(i,'cutNo',e.target.value)} placeholder="Cut#"
                      style={{ width:'100%', padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/></td>
                    <td style={{ padding:'5px 6px' }}><input value={r.partDesc} onChange={e=>updR(i,'partDesc',e.target.value)} placeholder="Part..."
                      style={{ width:'100%', padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/></td>
                    <td style={{ padding:'5px 6px' }}><input type="number" value={r.inspPcs} onChange={e=>updR(i,'inspPcs',e.target.value)} placeholder="0"
                      style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/></td>
                    <td style={{ padding:'5px 6px' }}><input type="number" value={r.defFound} onChange={e=>updR(i,'defFound',e.target.value)} placeholder="0"
                      style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center',
                        background:parseInt(r.defFound)>0?C.dangerBg:C.white }}/></td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.nature} onChange={e=>updR(i,'nature',e.target.value)}
                        style={{ width:'100%', minWidth:140, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">Select defect...</option>
                        {CUTTING_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}><input type="number" value={r.bundleQty} onChange={e=>updR(i,'bundleQty',e.target.value)} placeholder="0"
                      style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/></td>
                    <td style={{ padding:'5px 6px' }}><input type="number" value={r.okQty} onChange={e=>updR(i,'okQty',e.target.value)} placeholder="0"
                      style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center',
                        background:C.successBg }}/></td>
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer', color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:10, padding:'10px 14px', background:C.slate50, borderRadius:R.md, display:'flex', gap:24 }}>
            <span style={{ fontSize:12 }}>Total Check Qty: <strong>{totChk}</strong></span>
            <span style={{ fontSize:12 }}>Total Defect Qty: <strong style={{ color:C.danger }}>{totDef}</strong></span>
            <span style={{ fontSize:12 }}>Defect %: <strong style={{ color:parseFloat(defPct)>5?C.danger:C.success }}>{defPct}%</strong></span>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-20 · CUTTING QC REPORT — AUTO / MANUAL  (Nir/Cut/07)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann20({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'CUTTING_QC', date:TODAY,
      inspectorName:'', style:filters.style||'', cutNumber:'',
      noOfLay:'', bundleInspected:'',
      buyer:filters.buyer||'', colour:filters.colour||'',
      cuttingType:'MANUAL', // AUTO or MANUAL
      panelParts:'', misCut:'', matchingPlies:'', raggedCutting:'',
      notches:'', patternCheck:'',
      cutterName:'', cuttingSup:'', qcManager:'',
      remarks:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const recs = (db.cutting||[]).filter(r=>r.formType==='CUTTING_QC');

    const FIELDS_CHECK = [
      { k:'misCut',       label:'Mis-Cut' },
      { k:'matchingPlies',label:'Matching Plies' },
      { k:'raggedCutting',label:'Ragged Cutting' },
      { k:'notches',      label:'Notches Correct' },
      { k:'patternCheck', label:'Pattern Check' },
    ];
    const autoRes = () => {
      const fails = FIELDS_CHECK.filter(fc => F[fc.k]==='NG').length;
      if (fails===0 && FIELDS_CHECK.some(fc=>F[fc.k]==='OK')) return 'PASS';
      if (fails<=1) return 'HOLD'; return 'FAIL';
    };

    const handleSave = async () => {
      await doSave('cutting_qc_report',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-20 · Cutting Quality Control Report (Auto / Manual)" ref_="Nir/Cut/07"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.cut}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.cut}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Cutting QC Records"/>
            <Tbl headers={['Date','Style','Cut No.','Type','Bundles','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.cutNumber||'—',
                <Bdg color={C.cut}>{r.cuttingType}</Bdg>,
                r.bundleInspected||'—',<Res result={r.result}/>])}
              emptyMsg="No records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Header" color={C.cut}/>
              <Grid gap={14}>
                <Fld label="Date"              type="date" value={F.date}           onChange={e=>set('date',e.target.value)}/>
                <Fld label="Inspector Name"    value={F.inspectorName}  onChange={e=>set('inspectorName',e.target.value)}  placeholder="Name..."/>
                <Fld label="Style No."         value={F.style}          onChange={e=>set('style',e.target.value)}          options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Cut Number"        value={F.cutNumber}      onChange={e=>set('cutNumber',e.target.value)}      placeholder="Cut#..."/>
                <Fld label="No. of Lay"        value={F.noOfLay}        onChange={e=>set('noOfLay',e.target.value)}        placeholder="0"/>
                <Fld label="Bundle Inspected"  value={F.bundleInspected} onChange={e=>set('bundleInspected',e.target.value)} placeholder="0"/>
                <Fld label="Buyer"             value={F.buyer}          onChange={e=>set('buyer',e.target.value)}          options={['',...BUYERS]}/>
                <Fld label="Colour"            value={F.colour}         onChange={e=>set('colour',e.target.value)}         options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Cutting Type"      value={F.cuttingType}    onChange={e=>set('cuttingType',e.target.value)}    options={['MANUAL','AUTO']}/>
                <Fld label="Panel / Parts"     value={F.panelParts}     onChange={e=>set('panelParts',e.target.value)}     placeholder="Parts description..."/>
                <Fld label="Cutter Man"        value={F.cutterName}     onChange={e=>set('cutterName',e.target.value)}     placeholder="Name..."/>
                <Fld label="Cutting Supervisor" value={F.cuttingSup}   onChange={e=>set('cuttingSup',e.target.value)}     placeholder="Name..."/>
                <Fld label="QC Manager"        value={F.qcManager}      onChange={e=>set('qcManager',e.target.value)}      placeholder="Name..."/>
              </Grid>
            </Card>

            <Card>
              <SH title="Quality Check Points" color={C.cut}/>
              {FIELDS_CHECK.map(fc => (
                <div key={fc.k} style={{ display:'flex', gap:12, alignItems:'center',
                  padding:'9px 0', borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ flex:1, fontSize:12, fontWeight:600, color:C.text }}>{fc.label}</div>
                  <div style={{ display:'flex', gap:8 }}>
                    {['OK','NG','NA'].map(v => (
                      <label key={v} style={{ display:'flex', alignItems:'center', gap:4, cursor:'pointer' }}>
                        <input type="radio" checked={F[fc.k]===v} onChange={()=>set(fc.k,v)}
                          style={{ accentColor:v==='OK'?C.success:v==='NG'?C.danger:C.textMuted, width:14, height:14 }}/>
                        <span style={{ fontSize:11, fontWeight:700,
                          color:F[fc.k]===v?(v==='OK'?C.success:v==='NG'?C.danger:C.textMuted):C.textMuted }}>{v}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </Card>

            <Card>
              <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
                onChange={e=>set('remarks',e.target.value)} placeholder="Any observations..."/>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-21 · DAILY BUNDLE AUDIT REPORT  (Nir/Cut/08)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann21({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newRow = () => ({ id:uid(), orderNo:'', totalBundle:0, inspectedBundle:0,
      auditResult:'PASS', reAuditResult:'', defectType:'', supervisorSign:'' });
    const fresh  = () => ({
      id:uid(), formType:'BUNDLE_AUDIT', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'', colour:filters.colour||'',
      auditedBy:'', qcManager:'', cuttingManager:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.cutting||[]).filter(r=>r.formType==='BUNDLE_AUDIT');

    const handleSave = async () => {
      await doSave('cutting_bundle_audit',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-21 · Daily Cutting Wise Ready Input Bundle Audit" ref_="Nir/Cut/08 · 100% Bundle Inspection"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}           onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."       value={F.style}          onChange={e=>set('style',e.target.value)}          options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"           value={F.buyer}          onChange={e=>set('buyer',e.target.value)}          options={['',...BUYERS]}/>
            <Fld label="Colour"          value={F.colour}         onChange={e=>set('colour',e.target.value)}         options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Audited By"      value={F.auditedBy}      onChange={e=>set('auditedBy',e.target.value)}      placeholder="Name..."/>
            <Fld label="QC Manager"      value={F.qcManager}      onChange={e=>set('qcManager',e.target.value)}      placeholder="Name..."/>
            <Fld label="Cutting Manager" value={F.cuttingManager} onChange={e=>set('cuttingManager',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Bundle Audit Detail" color={C.cut}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.cut}>＋ Add Row</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:700 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Order No.','Total Bundle','Inspected','Audit Result','Re-Audit','Type of Defect',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub, fontSize:11,
                      textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'5px 6px' }}><input value={r.orderNo} onChange={e=>updR(i,'orderNo',e.target.value)} placeholder="Order#" list="po-datalist"
                      style={{ width:90, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/></td>
                    <td style={{ padding:'5px 6px' }}><input type="number" value={r.totalBundle} onChange={e=>updR(i,'totalBundle',e.target.value)} placeholder="0"
                      style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/></td>
                    <td style={{ padding:'5px 6px' }}><input type="number" value={r.inspectedBundle} onChange={e=>updR(i,'inspectedBundle',e.target.value)} placeholder="0"
                      style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/></td>
                    <td style={{ padding:'5px 8px' }}>
                      <select value={r.auditResult} onChange={e=>updR(i,'auditResult',e.target.value)}
                        style={{ padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontWeight:700, fontFamily:'inherit',
                          background:r.auditResult==='PASS'?C.successBg:r.auditResult==='FAIL'?C.dangerBg:C.warningBg,
                          color:r.auditResult==='PASS'?C.success:r.auditResult==='FAIL'?C.danger:C.warning }}>
                        {['PASS','FAIL','HOLD'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 8px' }}>
                      <select value={r.reAuditResult} onChange={e=>updR(i,'reAuditResult',e.target.value)}
                        style={{ padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">—</option>
                        {['PASS','FAIL'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.defectType} onChange={e=>updR(i,'defectType',e.target.value)}
                        style={{ width:'100%', minWidth:130, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">Defect type...</option>
                        {BUNDLE_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer', color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-22 · DAILY BUNDLE & NUMBER AUDIT SUMMARY  (Nir/Cut/09)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann22({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newRow = () => ({ id:uid(), bundleName:'', totalChecked:0, totalDefects:0, defPct:0, preparedBy:'' });
    const fresh  = () => ({
      id:uid(), formType:'BUNDLE_SUMMARY', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'', colour:filters.colour||'',
      totalCheck:'', totalDefects:'',
      supervisor:'', cuttingManager:'', qcManager:'', qualityGM:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>{
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      const c=parseInt(rows[i].totalChecked)||0; const d=parseInt(rows[i].totalDefects)||0;
      rows[i].defPct=c>0?((d/c)*100).toFixed(1):0;
      return {...f,rows};
    });
    const recs = (db.cutting||[]).filter(r=>r.formType==='BUNDLE_SUMMARY');

    const handleSave = async () => {
      await doSave('cutting_bundle_summary',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-22 · Daily Bundle & Number Audit Summary" ref_="Nir/Cut/09 · 20% Audit to check shade mixing"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}           onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."       value={F.style}          onChange={e=>set('style',e.target.value)}          options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"           value={F.buyer}          onChange={e=>set('buyer',e.target.value)}          options={['',...BUYERS]}/>
            <Fld label="Colour"          value={F.colour}         onChange={e=>set('colour',e.target.value)}         options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Total Check"     type="number" value={F.totalCheck}   onChange={e=>set('totalCheck',e.target.value)}   placeholder="0"/>
            <Fld label="Total Defects"   type="number" value={F.totalDefects} onChange={e=>set('totalDefects',e.target.value)} placeholder="0"/>
            <Fld label="Supervisor"      value={F.supervisor}     onChange={e=>set('supervisor',e.target.value)}     placeholder="Name..."/>
            <Fld label="Cutting Manager" value={F.cuttingManager} onChange={e=>set('cuttingManager',e.target.value)} placeholder="Name..."/>
            <Fld label="QC Manager"      value={F.qcManager}      onChange={e=>set('qcManager',e.target.value)}      placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Bundle-wise Summary" color={C.cut}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.cut}>＋ Add Bundle</Btn>}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px 80px 1fr 30px', gap:6, marginBottom:8 }}>
            {['Bundle Name','Total Checked','Total Defects','Def %','Prepared By',''].map(h=>(
              <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>
          {F.rows.map((r,i) => (
            <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px 80px 1fr 30px', gap:6, marginBottom:5, alignItems:'center' }}>
              <input value={r.bundleName}    onChange={e=>updR(i,'bundleName',e.target.value)}    placeholder="Bundle name..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <input type="number" value={r.totalChecked}  onChange={e=>updR(i,'totalChecked',e.target.value)}  placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
              <input type="number" value={r.totalDefects}  onChange={e=>updR(i,'totalDefects',e.target.value)}  placeholder="0"
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                  background:parseInt(r.totalDefects)>0?C.dangerBg:C.white }}/>
              <div style={{ textAlign:'center', fontWeight:700, padding:'7px',
                color:parseFloat(r.defPct)>5?C.danger:C.success }}>{r.defPct}%</div>
              <input value={r.preparedBy} onChange={e=>updR(i,'preparedBy',e.target.value)} placeholder="Name..."
                style={{ padding:'7px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
              <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer', color:C.danger, fontWeight:700, fontSize:12, padding:'6px' }}>✕</button>
            </div>
          ))}
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-23 · EMBROIDERY INSPECTION REPORT  (Nir/Cut/10)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann23({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'EMBROIDERY', date:TODAY,
      factory:'', challanNo:'', style:filters.style||'',
      buyer:filters.buyer||'', colour:filters.colour||'',
      todayRcvd:'', totalRcvd:'', plantRej:'', nirRej:'',
      plantRejPct:'', nirRejPct:'',
      qcInspector:'',
      defects: EMBROIDER_DEFECTS.map(d=>({ label:d, qty:0 })),
      remarks:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set    = (k,v) => setF(f=>({...f,[k]:v}));
    const setDef = (i,v) => setF(f=>({...f, defects:f.defects.map((d,di)=>di===i?{...d,qty:parseInt(v)||0}:d)}));
    const recs   = (db.cutting||[]).filter(r=>r.formType==='EMBROIDERY');

    const totalDef = F.defects.reduce((s,d)=>s+(d.qty||0),0);
    const nirPct  = () => {
      const total=parseInt(F.todayRcvd)||0;
      const rej=parseInt(F.clientRefRej)||0;
      return total>0?((rej/total)*100).toFixed(1):0;
    };
    const autoRes = () => {
      const pct=parseFloat(nirPct());
      if (pct===0) return 'PASS';
      if (pct<=5)  return 'HOLD';
      return 'FAIL';
    };

    const handleSave = async () => {
      await doSave('cutting_embroidery',
        { ...F, nirRejPct:nirPct(), result:autoRes(), totalDefects:totalDef, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-23 · Embroidery Inspection Report" ref_="Nir/Cut/10 · Outsourcing Quality Check"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.cut}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.cut}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Embroidery Inspection Records"/>
            <Tbl headers={['Date','Factory','Challan No.','Style','Rcvd Today','Co. Rej','Result']}
              rows={recs.map(r=>[r.date,r.factory||'—',r.challanNo||'—',r.style||'—',
                r.todayRcvd||'—',r.clientRefRej||'—',<Res result={r.result}/>])}
              emptyMsg="No embroidery inspection records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Embroidery Receipt Header" color={C.cut}/>
              <Grid gap={14}>
                <Fld label="Date"          type="date" value={F.date}      onChange={e=>set('date',e.target.value)}/>
                <Fld label="Factory"       value={F.factory}    onChange={e=>set('factory',e.target.value)}    placeholder="Factory name..."/>
                <Fld label="Challan No."   value={F.challanNo}  onChange={e=>set('challanNo',e.target.value)}  placeholder="Challan#..."/>
                <Fld label="Style No."     value={F.style}      onChange={e=>set('style',e.target.value)}      options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Buyer"         value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
                <Fld label="Colour"        value={F.colour}     onChange={e=>set('colour',e.target.value)}     options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Today Rcvd"    type="number" value={F.todayRcvd} onChange={e=>set('todayRcvd',e.target.value)} placeholder="0"/>
                <Fld label="Total Rcvd"    type="number" value={F.totalRcvd} onChange={e=>set('totalRcvd',e.target.value)} placeholder="0"/>
                <Fld label="Plant Rej"     type="number" value={F.plantRej}  onChange={e=>set('plantRej',e.target.value)}  placeholder="0"/>
                <Fld label="Co. Rej"       type="number" value={F.clientRefRej}   onChange={e=>set('nirRej',e.target.value)}   placeholder="0"
                  note={`Co. Rej %: ${nirPct()}%`}/>
                <Fld label="QC Inspector"  value={F.qcInspector} onChange={e=>set('qcInspector',e.target.value)} placeholder="Name..."/>
              </Grid>
            </Card>

            <Card>
              <SH title="Defect Breakdown" color={C.cut}
                action={<Bdg color={C.danger} bg={C.dangerBg}>Total: {totalDef}</Bdg>}/>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:8 }}>
                {F.defects.map((d,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10,
                    padding:'8px 12px', background:d.qty>0?C.dangerBg:C.slate50,
                    borderRadius:R.md, border:`1px solid ${d.qty>0?C.dangerBd:C.border}` }}>
                    <div style={{ flex:1, fontSize:12, color:C.text }}>{d.label}</div>
                    <input type="number" value={d.qty} onChange={e=>setDef(i,e.target.value)}
                      style={{ width:60, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                        fontSize:12, textAlign:'center', fontWeight:700,
                        color:d.qty>0?C.danger:C.text }}/>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
                onChange={e=>set('remarks',e.target.value)} placeholder="Any observations on embroidery quality..."/>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-24 · CUTTING PROCESS CONTROL REPORT  (Nir/Cut/11)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann24({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();

    const CHECKS = [
      { key:'cutProjVsExcel',   label:'Cut projection length matches Excel chart length' },
      { key:'excelVsMarker',    label:'Excel chart length matches Marker length' },
      { key:'markerWrittenVsAct', label:'Marker length written matches actual measured' },
      { key:'markerWidthVsFab', label:'Marker width within fabric width' },
      { key:'layLengthAllow',   label:'Lay length allowance correct' },
      { key:'actualFabVsSticker', label:'Actual fabric length matches sticker length' },
      { key:'shadeWiseLaying',  label:'Shade-wise laying per lay plan' },
      { key:'cuttingShadeVsSewing', label:'Cutting shade matches sewing programme shade' },
    ];

    const fresh = () => ({
      id:uid(), formType:'CUTTING_PROCESS', date:TODAY,
      nir:filters.style||'', colour:filters.colour||'', buyer:filters.buyer||'',
      markerLength:'', actualMeasured:'', markerWidth:'', fabricWidth:'',
      layAllowance:'', stickerLength:'', fabricRollLength:'',
      layPlanShade:'', actualLayShade:'', cuttingNumber:'', cuttingShade:'', sewingLotLabel:'',
      cutProjLength:'', excelLength:'',
      checks: CHECKS.reduce((acc,c)=>({...acc,[c.key]:{ ok:null, remark:'' }}),{}),
      qualityIncharge:'', cuttingManager:'', qualityManager:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set      = (k,v) => setF(f=>({...f,[k]:v}));
    const setChk   = (key,field,v) => setF(f=>({...f,checks:{...f.checks,[key]:{...f.checks[key],[field]:v}}}));
    const recs     = (db.cutting||[]).filter(r=>r.formType==='CUTTING_PROCESS');

    const ngCount = CHECKS.filter(c=>F.checks[c.key]?.ok===false).length;
    const autoRes = () => ngCount===0&&CHECKS.some(c=>F.checks[c.key]?.ok===true)?'PASS':ngCount>0?'FAIL':'PENDING';

    const handleSave = async () => {
      await doSave('cutting_process_ctrl',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-24 · Cutting Process Control Report" ref_="Nir/Cut/11 · Process Controller QC"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Order Details" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref #"          value={F.clientRef}      onChange={e=>set('clientRef',e.target.value)}      placeholder="Client Ref / Style..."/>
            <Fld label="Colour"          value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Buyer"           value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
            <Fld label="Cut Proj. Length" value={F.cutProjLength} onChange={e=>set('cutProjLength',e.target.value)} placeholder="yards..."/>
            <Fld label="Excel Length"    value={F.excelLength}   onChange={e=>set('excelLength',e.target.value)}   placeholder="yards..."/>
            <Fld label="Marker Length (Written)"  value={F.markerLength}   onChange={e=>set('markerLength',e.target.value)}   placeholder="yards..."/>
            <Fld label="Marker Length (Actual)"   value={F.actualMeasured} onChange={e=>set('actualMeasured',e.target.value)} placeholder="yards..."/>
            <Fld label='Marker Width"'   value={F.markerWidth}   onChange={e=>set('markerWidth',e.target.value)}   placeholder='inches'/>
            <Fld label='Fabric Width"'   value={F.fabricWidth}   onChange={e=>set('fabricWidth',e.target.value)}   placeholder='inches'/>
            <Fld label="Lay Allowance"   value={F.layAllowance}  onChange={e=>set('layAllowance',e.target.value)}  placeholder="inches/yards..."/>
            <Fld label="Sticker Length"  value={F.stickerLength} onChange={e=>set('stickerLength',e.target.value)} placeholder="yards..."/>
            <Fld label="Lay Plan Shade"  value={F.layPlanShade}  onChange={e=>set('layPlanShade',e.target.value)}  placeholder="Shade..."/>
            <Fld label="Actual Lay Shade" value={F.actualLayShade} onChange={e=>set('actualLayShade',e.target.value)} placeholder="Shade..."/>
            <Fld label="Cutting Number"  value={F.cuttingNumber} onChange={e=>set('cuttingNumber',e.target.value)} placeholder="Cut#..."/>
            <Fld label="Cutting Shade"   value={F.cuttingShade}  onChange={e=>set('cuttingShade',e.target.value)}  placeholder="Shade..."/>
            <Fld label="Sewing Lot Label" value={F.sewingLotLabel} onChange={e=>set('sewingLotLabel',e.target.value)} placeholder="Lot label..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Process Control Checks" color={C.cut}/>
          {CHECKS.map(c => (
            <ChkRow key={c.key} label={c.label}
              ok={F.checks[c.key]?.ok}    onOk={v=>setChk(c.key,'ok',v)}
              remark={F.checks[c.key]?.remark} onRemark={r=>setChk(c.key,'remark',r)}/>
          ))}
        </Card>

        <Card>
          <SH title="Sign-off" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Quality Incharge"  value={F.qualityIncharge}  onChange={e=>set('qualityIncharge',e.target.value)}  placeholder="Name..."/>
            <Fld label="Cutting Manager"   value={F.cuttingManager}   onChange={e=>set('cuttingManager',e.target.value)}   placeholder="Name..."/>
            <Fld label="Quality Manager"   value={F.qualityManager}   onChange={e=>set('qualityManager',e.target.value)}   placeholder="Name..."/>
          </Grid>
          <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-25 · HOURLY FUSING MACHINE INSPECTION  (Nir/Cut/12)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann25({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const fresh = () => ({
      id:uid(), formType:'FUSING_HOURLY', date:TODAY,
      machineNo:'', style:filters.style||'', buyer:filters.buyer||'',
      stdTemp:'', stdSpeed:'', stdPressure:'',
      rows: FUSING_HOURS.map(h=>({ hour:h, temp:'', speed:'', pressure:'', ok:null, remark:'' })),
      maintenanceManager:'', qualityManager:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>{
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      // auto OK/NG if all three values entered
      if (rows[i].temp && rows[i].speed && rows[i].pressure && F.stdTemp && F.stdSpeed && F.stdPressure) {
        const tOk=Math.abs(parseFloat(rows[i].temp)-parseFloat(F.stdTemp))<=5;
        const sOk=Math.abs(parseFloat(rows[i].speed)-parseFloat(F.stdSpeed))<=0.5;
        const pOk=Math.abs(parseFloat(rows[i].pressure)-parseFloat(F.stdPressure))<=0.5;
        rows[i].ok=tOk&&sOk&&pOk;
      }
      return {...f,rows};
    });
    const recs = (db.cutting||[]).filter(r=>r.formType==='FUSING_HOURLY');
    const ngRows = F.rows.filter(r=>r.ok===false).length;
    const autoRes = () => ngRows===0&&F.rows.some(r=>r.ok===true)?'PASS':ngRows>0?'FAIL':'PENDING';

    const handleSave = async () => {
      await doSave('cutting_fusing_hourly',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-25 · Hourly Fusing Machine Inspection" ref_="Nir/Cut/12 · Ensure correct temperature"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Machine & Standards" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"       type="date" value={F.date}      onChange={e=>set('date',e.target.value)}/>
            <Fld label="Machine No" value={F.machineNo}  onChange={e=>set('machineNo',e.target.value)}  placeholder="M/C No..."/>
            <Fld label="Style No."  value={F.style}      onChange={e=>set('style',e.target.value)}      options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"      value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
            <Fld label="Std. Temp (°C)"     type="number" value={F.stdTemp}     onChange={e=>set('stdTemp',e.target.value)}     placeholder="0"/>
            <Fld label="Std. Speed (m/min)" type="number" value={F.stdSpeed}    onChange={e=>set('stdSpeed',e.target.value)}    placeholder="0"/>
            <Fld label="Std. Pressure (bar)" type="number" value={F.stdPressure} onChange={e=>set('stdPressure',e.target.value)} placeholder="0"/>
          </Grid>
        </Card>

        <Card>
          <SH title="Hourly Readings" color={C.cut}
            action={
              <div style={{ display:'flex', gap:8 }}>
                <Bdg color={C.success} bg={C.successBg}>{F.rows.filter(r=>r.ok===true).length} OK</Bdg>
                <Bdg color={C.danger}  bg={C.dangerBg }>{ngRows} NG</Bdg>
              </div>
            }/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Hour','Temp (°C)','Speed (m/min)','Pressure (bar)','Status','Remark'].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub, fontSize:11,
                      textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.hour} style={{ borderBottom:`1px solid ${C.border}`,
                    background:r.ok===false?C.dangerBg:r.ok===true?C.successBg:C.white }}>
                    <td style={{ padding:'6px 10px', textAlign:'center', fontWeight:700, fontSize:12 }}>{r.hour}</td>
                    {['temp','speed','pressure'].map(k => (
                      <td key={k} style={{ padding:'5px 8px', textAlign:'center' }}>
                        <input type="number" value={r[k]} onChange={e=>updR(i,k,e.target.value)} placeholder="—"
                          style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                            fontSize:12, textAlign:'center' }}/>
                      </td>
                    ))}
                    <td style={{ padding:'6px 10px', textAlign:'center', fontSize:16 }}>
                      {r.ok===null?'—':r.ok
                        ?<span style={{ color:C.success }}>✓</span>
                        :<span style={{ color:C.danger  }}>✗</span>}
                    </td>
                    <td style={{ padding:'5px 8px' }}>
                      <input value={r.remark} onChange={e=>updR(i,'remark',e.target.value)} placeholder="Remark..."
                        style={{ width:'100%', minWidth:100, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <div style={{ display:'flex', gap:10 }}>
              <Fld label="Maintenance Manager" value={F.maintenanceManager}
                onChange={e=>set('maintenanceManager',e.target.value)} placeholder="Name..."/>
              <div style={{ width:14 }}/>
              <Fld label="Quality Manager" value={F.qualityManager}
                onChange={e=>set('qualityManager',e.target.value)} placeholder="Name..."/>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:14 }}>
              <Res result={autoRes()}/>
              <Btn onClick={handleSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
                {saved?'Saved!':saving?'Saving…':'Save Report'}
              </Btn>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-26 · DAILY FUSING MACHINE INSPECTION  (Nir/Cut/13)
  //  Heating check schedule — 3 times per day
  // ═══════════════════════════════════════════════════════════════════════
  function Ann26({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const newRow = () => ({ id:uid(), pcEdp:'', contract:'', pressure:'', time:'', mcTemp:'',
      thermoPaperTemp:'', fuseStrength:'', colourChange:'None', nameWfQa:'', partOfStrip:'', ok:null });
    const fresh  = () => ({
      id:uid(), formType:'FUSING_DAILY', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      stdTemp:'', stdPressure:'', stdTime:'',
      rows:[newRow(), newRow(), newRow()], // 3 times per day
      maintenanceManager:'', merchantManager:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>{
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      if (rows[i].mcTemp && rows[i].thermoPaperTemp) {
        const diff=Math.abs(parseFloat(rows[i].mcTemp)-parseFloat(rows[i].thermoPaperTemp));
        rows[i].ok=diff<=5&&rows[i].fuseStrength==='GOOD';
      }
      return {...f,rows};
    });
    const recs = (db.cutting||[]).filter(r=>r.formType==='FUSING_DAILY');

    const handleSave = async () => {
      await doSave('cutting_fusing_daily',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-26 · Daily Fusing Machine Heating Check Schedule"
          ref_="Nir/Cut/13 · 3 times per day required"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Machine Standards" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}        onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style / P/C EDP" value={F.style}       onChange={e=>set('style',e.target.value)}       placeholder="Style / EDP..."/>
            <Fld label="Buyer"           value={F.buyer}       onChange={e=>set('buyer',e.target.value)}       options={['',...BUYERS]}/>
            <Fld label="Std. Temp (°C)"  type="number" value={F.stdTemp}     onChange={e=>set('stdTemp',e.target.value)}     placeholder="0"/>
            <Fld label="Std. Pressure"   type="number" value={F.stdPressure} onChange={e=>set('stdPressure',e.target.value)} placeholder="0"/>
            <Fld label="Std. Time (sec)" type="number" value={F.stdTime}     onChange={e=>set('stdTime',e.target.value)}     placeholder="0"/>
          </Grid>
        </Card>

        <div style={{ padding:'10px 14px', background:'#FFF7ED', border:`1px solid ${C.warningBd}`,
          borderRadius:R.md, fontSize:12, color:C.warning, fontWeight:600 }}>
          ⚠️ NOTE: Per day 3 time checks need to be completed (Morning / Afternoon / Evening)
        </div>

        <Card>
          <SH title="3-Time Daily Checks" color={C.cut}/>
          {F.rows.map((r,i) => (
            <div key={r.id} style={{ border:`1px solid ${r.ok===false?C.dangerBd:r.ok===true?C.successBd:C.border}`,
              borderRadius:R.md, padding:14, marginBottom:12,
              background:r.ok===false?C.dangerBg:r.ok===true?C.successBg:C.white }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.text, marginBottom:10 }}>
                Check {i+1}: {['Morning','Afternoon','Evening'][i]}
              </div>
              <Grid gap={10}>
                <Fld label="P/C EDP"          value={r.pcEdp}           onChange={e=>updR(i,'pcEdp',e.target.value)}           placeholder="EDP..."/>
                <Fld label="Contract/Cut"      value={r.contract}        onChange={e=>updR(i,'contract',e.target.value)}        placeholder="Contract..."/>
                <Fld label="Pressure (lbs)"    type="number" value={r.pressure}  onChange={e=>updR(i,'pressure',e.target.value)}  placeholder="0"/>
                <Fld label="Time (sec)"        type="number" value={r.time}      onChange={e=>updR(i,'time',e.target.value)}      placeholder="0"/>
                <Fld label="M/C Temp (°C)"     type="number" value={r.mcTemp}    onChange={e=>updR(i,'mcTemp',e.target.value)}    placeholder="0"/>
                <Fld label="Thermo Paper Temp" type="number" value={r.thermoPaperTemp} onChange={e=>updR(i,'thermoPaperTemp',e.target.value)} placeholder="0"
                  note={r.mcTemp&&r.thermoPaperTemp?`Diff: ${Math.abs(parseFloat(r.mcTemp)-parseFloat(r.thermoPaperTemp)).toFixed(1)}°C`:''}/>
                <Fld label="Fuse Strength"     value={r.fuseStrength} onChange={e=>updR(i,'fuseStrength',e.target.value)} options={['','GOOD','ACCEPTABLE','POOR','FAIL']}/>
                <Fld label="Colour Change"     value={r.colourChange} onChange={e=>updR(i,'colourChange',e.target.value)} options={['None','Slight','Severe']}/>
                <Fld label="Name WF/QA"        value={r.nameWfQa}    onChange={e=>updR(i,'nameWfQa',e.target.value)}    placeholder="Name..."/>
                <Fld label="Part of Strip"     value={r.partOfStrip} onChange={e=>updR(i,'partOfStrip',e.target.value)} options={['','TOP','MIDDLE','BOTTOM']}/>
              </Grid>
            </div>
          ))}
          <Grid gap={14}>
            <Fld label="Maintenance Manager" value={F.maintenanceManager} onChange={e=>set('maintenanceManager',e.target.value)} placeholder="Name..."/>
            <Fld label="Merchandising Manager" value={F.merchantManager}  onChange={e=>set('merchantManager',e.target.value)}  placeholder="Name..."/>
          </Grid>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-27 · BUTTON PULL TEST REPORT  (Nir/Cut/14)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann27({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();
    const [view, setView] = React.useState('form');

    const newRow = () => ({ id:uid(), sampleNo:'', garmentPart:'', buttonType:'',
      testLoad:'', result:'PASS', remarks:'' });
    const fresh  = () => ({
      id:uid(), formType:'BUTTON_PULL', date:TODAY,
      style:filters.style||'', po:filters.po||'', buyer:filters.buyer||'',
      colour:filters.colour||'',
      stdLoad:'17', // lbs — ASTM D-4846 standard 10 sec hold at 17 lbs
      testStd:'ASTM D-4846', testDuration:'10',
      testedBy:'', qcManager:'',
      rows:[newRow(), newRow(), newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>{
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      // auto pass/fail based on test load vs std load
      if (k==='testLoad') {
        rows[i].result=parseFloat(v)>=parseFloat(F.stdLoad)?'PASS':'FAIL';
      }
      return {...f,rows};
    });
    const recs   = (db.cutting||[]).filter(r=>r.formType==='BUTTON_PULL');
    const passCount = F.rows.filter(r=>r.result==='PASS').length;
    const failCount = F.rows.filter(r=>r.result==='FAIL').length;
    const autoRes   = () => failCount===0&&passCount>0?'PASS':failCount>0?'FAIL':'PENDING';

    const handleSave = async () => {
      await doSave('cutting_button_pull',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-27 · Button Pull Test Report" ref_="Nir/Cut/14 · Safety Control · ASTM D-4846"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.cut}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.cut}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Button Pull Test Records"/>
            <Tbl headers={['Date','Style','PO','Buyer','Colour','Pass','Fail','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.po||'—',r.buyer||'—',r.colour||'—',
                <Bdg color={C.success} bg={C.successBg}>{(r.rows||[]).filter(x=>x.result==='PASS').length}</Bdg>,
                <Bdg color={C.danger}  bg={C.dangerBg }>{(r.rows||[]).filter(x=>x.result==='FAIL').length}</Bdg>,
                <Res result={r.result}/>])}
              emptyMsg="No button pull test records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Test Header" color={C.cut}/>
              <Grid gap={14}>
                <Fld label="Date"             type="date" value={F.date}      onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."        value={F.style}     onChange={e=>set('style',e.target.value)}     options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="PO No."           value={F.po}        onChange={e=>set('po',e.target.value)}        options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer"            value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
                <Fld label="Colour"           value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Test Standard"    value={F.testStd}   onChange={e=>set('testStd',e.target.value)}   options={['ASTM D-4846','BS EN ISO 7012','Other']}/>
                <Fld label="Std. Load (lbs)"  type="number" value={F.stdLoad}   onChange={e=>set('stdLoad',e.target.value)}   placeholder="17"/>
                <Fld label="Hold Duration (sec)" type="number" value={F.testDuration} onChange={e=>set('testDuration',e.target.value)} placeholder="10"/>
                <Fld label="Tested By"        value={F.testedBy}  onChange={e=>set('testedBy',e.target.value)}  placeholder="Name..."/>
                <Fld label="QC Manager"       value={F.qcManager} onChange={e=>set('qcManager',e.target.value)} placeholder="Name..."/>
              </Grid>
            </Card>

            <div style={{ padding:'10px 14px', background:'#EFF6FF', borderRadius:R.md, fontSize:12, color:'#1D4ED8' }}>
              <strong>Standard:</strong> {F.testStd} · 10 sec hold @ {F.stdLoad} lbs · Button must NOT detach. Auto FAIL if test load &lt; {F.stdLoad} lbs.
            </div>

            <Card>
              <SH title="Test Samples" color={C.cut}
                action={
                  <div style={{ display:'flex', gap:8 }}>
                    <Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.cut}>＋ Add Sample</Btn>
                    <Bdg color={C.success} bg={C.successBg}>{passCount} Pass</Bdg>
                    <Bdg color={C.danger}  bg={C.dangerBg }>{failCount} Fail</Bdg>
                  </div>
                }/>
              <div style={{ display:'grid', gridTemplateColumns:'70px 1fr 1fr 100px 90px 1fr 30px', gap:6, marginBottom:8 }}>
                {['Sample#','Garment Part','Button Type','Test Load (lbs)','Result','Remarks',''].map(h=>(
                  <div key={h} style={{ fontSize:10, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>
                ))}
              </div>
              {F.rows.map((r,i) => (
                <div key={r.id} style={{ display:'grid', gridTemplateColumns:'70px 1fr 1fr 100px 90px 1fr 30px',
                  gap:6, marginBottom:5, alignItems:'center' }}>
                  <input value={r.sampleNo}  onChange={e=>updR(i,'sampleNo',e.target.value)}  placeholder={`${i+1}`}
                    style={{ padding:'6px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center' }}/>
                  <input value={r.garmentPart} onChange={e=>updR(i,'garmentPart',e.target.value)} placeholder="e.g. Front, Waist BTN..."
                    style={{ padding:'6px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
                  <input value={r.buttonType}  onChange={e=>updR(i,'buttonType',e.target.value)} placeholder="e.g. 28L, Snap..."
                    style={{ padding:'6px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
                  <input type="number" value={r.testLoad} onChange={e=>updR(i,'testLoad',e.target.value)} placeholder="0"
                    style={{ padding:'6px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                      background: parseFloat(r.testLoad)<parseFloat(F.stdLoad)&&r.testLoad?C.dangerBg:C.white }}/>
                  <div style={{ textAlign:'center' }}><Res result={r.result}/></div>
                  <input value={r.remarks} onChange={e=>updR(i,'remarks',e.target.value)} placeholder="Remarks..."
                    style={{ padding:'6px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12 }}/>
                  <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                    style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                      color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                </div>
              ))}
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-28 · FUSING MACHINE PROCESS CONTROL  (Nir/Cut/15)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann28({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave();

    const PROCESS_CHECKS = [
      { key:'approvedSpec',     label:'01. Approved specification from interlining supplier available' },
      { key:'swatchDisplayed',  label:'02. Authorized final colour-wise swatch displayed' },
      { key:'hourlyReport',     label:'03. Hourly fusing control report being followed' },
      { key:'thermoStripTest',  label:'04. Thermo strip test conducted for every machine' },
      { key:'interPlacementBoard', label:'05. Interlining placement board available' },
      { key:'trimChartAvail',   label:'06. Cutting trim chart with interlining quality available' },
      { key:'cleaningChecklist',label:'07. Fusing machine cleaning check list available' },
      { key:'cleaningSchedule', label:'08. As per schedule cleaning is happening' },
      { key:'bondStrength',     label:'09. Bond strength / pull test conducted' },
      { key:'calibrationDone',  label:'10. Calibration of fusing machine is done' },
    ];

    const fresh = () => ({
      id:uid(), formType:'FUSING_PROCESS', date:TODAY,
      unit:'', style:filters.style||'', buyer:filters.buyer||'',
      machineId:'', stitchPerInch:'', needleSize:'',
      checks: PROCESS_CHECKS.reduce((acc,c)=>({...acc,[c.key]:{ ok:null, remark:'' }}),{}),
      productionHead:'', qualityGM:'',
      remarks:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set    = (k,v) => setF(f=>({...f,[k]:v}));
    const setChk = (key,field,v) => setF(f=>({...f,checks:{...f.checks,[key]:{...f.checks[key],[field]:v}}}));
    const recs   = (db.cutting||[]).filter(r=>r.formType==='FUSING_PROCESS');
    const ngCount= PROCESS_CHECKS.filter(c=>F.checks[c.key]?.ok===false).length;
    const okCount= PROCESS_CHECKS.filter(c=>F.checks[c.key]?.ok===true).length;
    const autoRes= () => ngCount===0&&okCount>0?'PASS':ngCount<=2?'HOLD':ngCount>2?'FAIL':'PENDING';

    const handleSave = async () => {
      await doSave('cutting_fusing_process',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-28 · Fusing Machine Process Control Register" ref_="Nir/Cut/15"
          action={<Bdg color={C.cut}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Machine & Order Details" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Date"           type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
            <Fld label="Unit No."       value={F.unit}       onChange={e=>set('unit',e.target.value)}       placeholder="Unit..."/>
            <Fld label="Style No."      value={F.style}      onChange={e=>set('style',e.target.value)}      options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"          value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
            <Fld label="Machine ID"     value={F.machineId}  onChange={e=>set('machineId',e.target.value)}  placeholder="M/C ID..."/>
            <Fld label="Stitch Per Inch (SPI)" value={F.stitchPerInch} onChange={e=>set('stitchPerInch',e.target.value)} placeholder="e.g. 12"/>
            <Fld label="Needle Size/Type"      value={F.needleSize}    onChange={e=>set('needleSize',e.target.value)}    placeholder="e.g. 14/90..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Process Control Checklist" color={C.cut}
            action={
              <div style={{ display:'flex', gap:8 }}>
                <Bdg color={C.success} bg={C.successBg}>{okCount} OK</Bdg>
                <Bdg color={C.danger}  bg={C.dangerBg }>{ngCount} NG</Bdg>
              </div>
            }/>
          {PROCESS_CHECKS.map(c => (
            <ChkRow key={c.key} label={c.label}
              ok={F.checks[c.key]?.ok}    onOk={v=>setChk(c.key,'ok',v)}
              remark={F.checks[c.key]?.remark} onRemark={r=>setChk(c.key,'remark',r)}/>
          ))}
        </Card>

        <Card>
          <SH title="Sign-off & Remarks" color={C.cut}/>
          <Grid gap={14}>
            <Fld label="Production Head" value={F.productionHead} onChange={e=>set('productionHead',e.target.value)} placeholder="Name..."/>
            <Fld label="Quality GM"      value={F.qualityGM}      onChange={e=>set('qualityGM',e.target.value)}      placeholder="Name..."/>
          </Grid>
          <div style={{ marginTop:10 }}>
            <Fld label="Overall Remarks" type="textarea" rows={3} value={F.remarks}
              onChange={e=>set('remarks',e.target.value)} placeholder="Any process control observations..."/>
          </div>
          <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  CUTTING DEPT SHELL — sub-nav + routing
  // ═══════════════════════════════════════════════════════════════════════
  const SUB = [
    { id:'ann14', label:'Ann-14 Marker Insp.',        icon:'📐', Comp:Ann14, ft:'MARKER_INSP'      },
    { id:'ann15', label:'Ann-15 Marker Summary',      icon:'📋', Comp:Ann15, ft:'MARKER_SUMMARY'   },
    { id:'ann16', label:'Ann-16 Spreading QC',        icon:'🔀', Comp:Ann16, ft:'SPREADING'        },
    { id:'ann17', label:'Ann-17 After Cut Summary',   icon:'✂️', Comp:Ann17, ft:'AFTER_CUT'        },
    { id:'ann18', label:'Ann-18 Cut Parts Summary',   icon:'📊', Comp:Ann18, ft:'PARTS_SUMMARY'    },
    { id:'ann19', label:'Ann-19 Cut Part Insp.',      icon:'🔍', Comp:Ann19, ft:'PARTS_INSP'       },
    { id:'ann20', label:'Ann-20 Cutting QC',          icon:'⚙️', Comp:Ann20, ft:'CUTTING_QC'       },
    { id:'ann21', label:'Ann-21 Bundle Audit',        icon:'📦', Comp:Ann21, ft:'BUNDLE_AUDIT'     },
    { id:'ann22', label:'Ann-22 Bundle Summary',      icon:'🗂️', Comp:Ann22, ft:'BUNDLE_SUMMARY'   },
    { id:'ann23', label:'Ann-23 Embroidery Insp.',    icon:'🧶', Comp:Ann23, ft:'EMBROIDERY'       },
    { id:'ann24', label:'Ann-24 Process Control',     icon:'🎛️', Comp:Ann24, ft:'CUTTING_PROCESS'  },
    { id:'ann25', label:'Ann-25 Fusing Hourly',       icon:'🔥', Comp:Ann25, ft:'FUSING_HOURLY'    },
    { id:'ann26', label:'Ann-26 Fusing Daily',        icon:'📅', Comp:Ann26, ft:'FUSING_DAILY'     },
    { id:'ann27', label:'Ann-27 Button Pull Test',    icon:'🔘', Comp:Ann27, ft:'BUTTON_PULL'      },
    { id:'ann28', label:'Ann-28 Fusing Process Ctrl', icon:'🗃️', Comp:Ann28, ft:'FUSING_PROCESS'   },
  ];

  function CuttingDept({ db, setDb, filters }) {
    const [active, setActive] = React.useState('ann14');

    const counts = SUB.reduce((acc,f) => ({
      ...acc,
      [f.id]: (db.cutting||[]).filter(r => r.formType===f.ft).length,
    }), {});

    const ActiveComp = SUB.find(f => f.id===active)?.Comp;

    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
        {/* scrollable sub-nav */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
          overflowX:'auto', flexShrink:0 }}>
          <div style={{ display:'flex', paddingTop:4, paddingLeft:8, minWidth:'max-content' }}>
            {SUB.map(f => (
              <button key={f.id} onClick={() => setActive(f.id)} style={{
                padding:'10px 14px', fontSize:12,
                fontWeight: active===f.id ? 700 : 400,
                color: active===f.id ? C.cut : C.textSub,
                background:'transparent', border:'none', cursor:'pointer',
                borderBottom: active===f.id ? `2.5px solid ${C.cut}` : '2.5px solid transparent',
                display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
              }}>
                <span>{f.icon}</span>{f.label}
                {counts[f.id] > 0 && (
                  <span style={{ background:C.cut+'22', color:C.cut,
                    padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>
                    {counts[f.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* active form */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {ActiveComp && <ActiveComp db={db} setDb={setDb} filters={filters}/>}
        </div>
      </div>
    );
  }

  // ── REGISTER ─────────────────────────────────────────────────────────────
  DEPT_REGISTRY['cutting'] = CuttingDept;
  console.log('[QMS] Dept-03 Cutting registered ✓  (Ann-14 → Ann-28, 15 forms)');

})();

// ════════════════════════════════════════════════════════════════════════════
//  DEPT-04 · SEWING · Ann-29 → Ann-38  (10 forms)
//  HOW TO USE: Paste this entire block at the very bottom of your file,
//  below the Cutting block (below the last line: })(); )
//
//  Ann-29  Daily Sewing Quality Report          Nir/Sew-Qc/01 → sewing_daily_quality
//  Ann-30  Sewing Quality Inspection Report     Nir/Sew-Qc/02 → sewing_inspection
//  Ann-31  Supervisor Audit Report              Nir/Sew-Qc/03 → sewing_supervisor_audit
//  Ann-32  Major 3 Defects of the Day           Nir/Sew-Qc/04 → sewing_major3_defects
//  Ann-33  Killed Status Report                 Nir/Sew-Qc/06 → sewing_killed_status
//  Ann-34  Hourly Alteration Report             Nir/Sew-Qc/07 → sewing_hourly_alteration
//  Ann-35  Summary of Final Audit – Sewing      Nir/Sew-Qa/01 → sewing_audit_summary
//  Ann-36  Hourly Audit Report for Sewing       Nir/Sew-Qa/02 → sewing_hourly_audit
//  Ann-37  Checklist for FTA                    Nir/Sew-Qa/04 → sewing_fta_checklist
//  Ann-38  Audit Check List                     Nir/Sew-Qa/05 → sewing_audit_checklist
// ════════════════════════════════════════════════════════════════════════════



// ════════════════════════════════════════════════════════════════════════════
//  DEPT-04 · SEWING DATAPAD  (Live punch-in quality tracking)
//  3 sections: FRONT · BACK · ASSEMBLY
//  Each punch logged with timestamp → hourly auto-summary
// ════════════════════════════════════════════════════════════════════════════


(function registerSewing() {

  const _C = {
    bg:'#0D1B2A', white:'#FFFFFF', sl50:'#F8F9FA', sl100:'#F4F6F8', sl200:'#DEE2E6',
    text:'#1A2A3A', sub:'#6C757D', muted:'#ADB5BD',
    green:'#2E7D32', greenBg:'#E8F5E9', greenBd:'#A5D6A7',
    red:'#C62828',   redBg:'#FFEBEE',   redBd:'#FFCDD2',
    amb:'#E65100',   ambBg:'#FFF8E1',   ambBd:'#FFE082',
    blue:'#1565C0',  blueBg:'#EFF6FF',
    purple:'#6A1B9A',purpleBg:'#EDE7F6',
  };

  const API = 'https://nirvikalpa.integrashion.com/api/save_universal.php';

  function uid() { return Date.now().toString(36)+Math.random().toString(36).slice(2,6); }

  // ── Defect codes from Excel ───────────────────────────────────────────────
  const DEFECT_CODES = [
    {code:'109',   name:'OPEN SEAM'},       {code:'140.B',name:'BROKEN STITCH'},
    {code:'140.S', name:'SKIP STITCH'},      {code:'112.D',name:'DOWN STITCH'},
    {code:'112.U', name:'UNEVEN'},           {code:'112.P',name:'PLEATED'},
    {code:'203',   name:'DROP STITCH'},      {code:'406',  name:'RAW EDGES'},
    {code:'125',   name:'PUCKERING'},        {code:'139',  name:'TENSION LOOSE'},
    {code:'110',   name:'JOIN STITCH'},      {code:'121',  name:'BARTACK MISSING'},
    {code:'121.D', name:'BARTACK DISPLACE'}, {code:'172',  name:'PKT OPENING TIGHT/LOOSE'},
    {code:'172.B', name:'BACK PKT GAP'},     {code:'166',  name:'LOOP SLANTED'},
    {code:'165',   name:'LOOP MISSING'},     {code:'126',  name:'UNEVEN HEM FOLD'},
    {code:'499',   name:'MOUTH CLOSE UP/DOWN'},{code:'416',name:'STAIN / OIL SPOT'},
    {code:'303',   name:'SHADING'},          {code:'207',  name:'FABRIC DEFECT'},
    {code:'136',   name:'HOLES / DAMAGE'},   {code:'402',  name:'UNCUT TRIM'},
    {code:'404',   name:'IRON DEFECT'},      {code:'115',  name:'BUTTON DISPLACE'},
    {code:'155',   name:'WRONG SIZE/PATTERN'},{code:'602', name:'WRONG ZIPPER/NO'},
    {code:'624',   name:'MISSING PART'},     {code:'701',  name:'WRONG LABEL'},
    {code:'107',   name:'SNAP PROBLEM'},     {code:'109.B',name:'STITCH ATTACH W/B'},
    {code:'999',   name:'OTHER'},
  ];

  const FRONT_OPS=['MK COIN PKT HEM D FLY TACK','MK COIN PKT HEM & ATT','ATT PKT FAC TO PKT',
    'ATT ZIPPER TO S. FLY','O/L FLY FAC & F. FLY','O/L FRONT RISE','ATT. FLY FAC & EDGE S/T',
    'FLY J BT. & ATT D. FLY','ATT FLY BY 1/16 TOP ST.','JOIN & TOP ST. 1/16 ON F. RISE',
    'ATT. FRONT PKT 1/4','TOP ST. ON FRONT PKT','FAC ATT. TO FRONT PKT',
    'TACK ON TOP OF FRONT PKT','TACK ON SIDE OF FRONT PKT','CLS FRONT PKT BAG',
    'TOP ST. ON FRONT PKT BAG','COIN PKT. BARTACK','ATT. FRONT PKT.','FRONT PKT O/L','COIN PKT O/L','J. STITCH'];
  const BACK_OPS=['BK BELT TACK','FACING JOIN WITH PKT BAG','BK. DART','MK. WELT PKT.',
    'BK. RISE PIPING','BK. RISE JOIN','BK. FACING TOP ST.','BK. BONE TACK','BK. BONE TOP ST.',
    'BK. PKT KEY HOLE','BK. PKT BAG CLS','BONE COMPLETE','BK. PKT. BAG 1/4 T/S','BAG PKT. TACK','BK PKT BARTACK'];
  const ASSY_OPS=['JOIN S/S BY O/L','TOP ST. ON SIDE SEAM','JOIN INSEAM','JOIN S/S & INSEAM',
    'BTM OF HEM','ATT. LOOP AT WAIST','ATT. W/B BY KANSAI','MOUTH CLOSE AT. W/B',
    'FR. PKT. FLY BARTACK','FR. PKT BARTACK + LOOP BTK','LOOP BARTACK','CLOSE W/B BY DEEP ST.','A.P.W'];

  const HOUR_SLOTS=['08:00-09:00','09:00-10:00','10:00-11:00','11:00-12:00',
    '12:00-01:00','01:00-02:00','02:00-03:00','03:00-04:00',
    '04:00-05:00','05:00-06:00','06:00-07:00'];
  const LUNCH='01:00-02:00';

  function getSlot(ts){
    const d=new Date(ts); const h=d.getHours(),m=d.getMinutes(); const t=h*60+m;
    const ranges=[[480,540,'08:00-09:00'],[540,600,'09:00-10:00'],[600,660,'10:00-11:00'],
      [660,720,'11:00-12:00'],[720,780,'12:00-01:00'],[780,840,'01:00-02:00'],
      [840,900,'02:00-03:00'],[900,960,'03:00-04:00'],[960,1020,'04:00-05:00'],
      [1020,1080,'05:00-06:00'],[1080,1140,'06:00-07:00']];
    const f=ranges.find(([s,e])=>t>=s&&t<e);
    return f?f[2]:HOUR_SLOTS[0];
  }

  // ── Core summary calculation ──────────────────────────────────────────────
  // DHU = (Total Defects / Total Pieces Checked) × 100   [industry standard]
  function buildSummary(defectPunches, prodTaps) {
    const byHour={};
    HOUR_SLOTS.forEach(h=>{
      byHour[h]={checked:0,passed:0,reject:0,repair:0,defects:0,defectMap:{}};
    });

    // Production taps → hourly
    prodTaps.forEach(tap=>{
      const slot=tap.slot||getSlot(tap.ts);
      if(!byHour[slot]) return;
      byHour[slot][tap.type]=(byHour[slot][tap.type]||0)+1;
    });

    // Defect punches → hourly
    defectPunches.forEach(p=>{
      const slot=p.slot||getSlot(p.ts);
      if(!byHour[slot]) return;
      byHour[slot].defects+=1;
      byHour[slot].defectMap[p.defectCode]=(byHour[slot].defectMap[p.defectCode]||0)+1;
    });

    // Totals
    const totChecked = prodTaps.filter(t=>t.type==='checked').length;
    const totPassed  = prodTaps.filter(t=>t.type==='passed').length;
    const totReject  = prodTaps.filter(t=>t.type==='reject').length;
    const totRepair  = prodTaps.filter(t=>t.type==='repair').length;
    const totDefects = defectPunches.length;

    // DHU = defects / checked × 100  (industry standard formula)
    const dhu = totChecked>0 ? ((totDefects/totChecked)*100).toFixed(2) : '0.00';

    // Defect totals map
    const defTotals={};
    defectPunches.forEach(p=>{ defTotals[p.defectCode]=(defTotals[p.defectCode]||0)+1; });

    // Operation totals
    const opTotals={};
    defectPunches.forEach(p=>{ if(p.operation) opTotals[p.operation]=(opTotals[p.operation]||0)+1; });

    // Per-hour DHU
    Object.keys(byHour).forEach(h=>{
      const hd=byHour[h];
      hd.dhu=hd.checked>0?((hd.defects/hd.checked)*100).toFixed(1):'0.0';
    });

    return {byHour,defTotals,opTotals,totChecked,totPassed,totReject,totRepair,totDefects,dhu};
  }

  // ── Prod Counter Button ───────────────────────────────────────────────────
  function CounterBtn({label,count,color,bg,bd,onTap,onUndo}){
    return(
      <div style={{flex:1,minWidth:120,display:'flex',flexDirection:'column',
        background:bg,border:`2px solid ${bd}`,borderRadius:12,overflow:'hidden',
        userSelect:'none'}}>
        <button onClick={onTap}
          style={{flex:1,padding:'14px 8px',background:'transparent',border:'none',
            cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
          <div style={{fontSize:32,fontWeight:900,color,fontFamily:"'JetBrains Mono',monospace",
            lineHeight:1}}>{count}</div>
          <div style={{fontSize:11,fontWeight:700,color,textTransform:'uppercase',
            letterSpacing:'.5px'}}>{label}</div>
        </button>
        {count>0&&(
          <button onClick={onUndo}
            style={{padding:'4px',background:color+'22',border:'none',
              borderTop:`1px solid ${bd}`,cursor:'pointer',
              fontSize:10,color,fontWeight:700}}>−1 undo</button>
        )}
      </div>
    );
  }

  // ── Sewing Datapad ────────────────────────────────────────────────────────
  function SewingDatapad({section,defOps,db,setDb,session,sectionColor}){
    const [defPunches,  setDefPunches]  = React.useState([]);
    const [prodTaps,    setProdTaps]    = React.useState([]);
    const [selOp,       setSelOp]       = React.useState('');
    const [tab,         setTab]         = React.useState('pad');
    const [saving,      setSaving]      = React.useState(false);
    const [saved,       setSaved]       = React.useState(false);

    const summary = React.useMemo(()=>buildSummary(defPunches,prodTaps),[defPunches,prodTaps]);

    // Production counter tap
    function tap(type){
      const now=Date.now();
      setProdTaps(prev=>[...prev,{id:uid(),ts:now,slot:getSlot(now),type}]);
    }
    function undoTap(type){
      setProdTaps(prev=>{
        const idx=[...prev].map((t,i)=>({t,i})).reverse().find(({t})=>t.type===type)?.i;
        return idx!=null ? prev.filter((_,i)=>i!==idx) : prev;
      });
    }

    // Defect punch
    function addDefect(code,name){
      const now=Date.now();
      setDefPunches(prev=>[...prev,{id:uid(),ts:now,slot:getSlot(now),
        defectCode:code,defectName:name,operation:selOp,section}]);
    }
    function undoDefect(code){
      setDefPunches(prev=>{
        const idx=[...prev].map((p,i)=>({p,i})).reverse().find(({p})=>p.defectCode===code)?.i;
        return idx!=null ? prev.filter((_,i)=>i!==idx) : prev;
      });
    }

    // Save session
    async function saveSession(){
      setSaving(true);
      const rec={id:uid(),section,date:new Date().toISOString().split('T')[0],
        ...session, defPunches, prodTaps,
        summary:{dhu:parseFloat(summary.dhu),totChecked:summary.totChecked,
          totDefects:summary.totDefects,totPassed:summary.totPassed,
          totReject:summary.totReject,totRepair:summary.totRepair},
        savedAt:new Date().toISOString()};
      try{
        await fetch(API,{method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({category:`sewing_datapad_${section.toLowerCase()}`,data:rec})});
      }catch{}
      setDb(prev=>({...prev,sewing:[...(prev.sewing||[]),
        {...rec,totalChecked:summary.totChecked,totalDefects:summary.totDefects,
          dhu:parseFloat(summary.dhu),totalPassed:summary.totPassed}]}));
      setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2500);
    }

    // Top 3 defects for analysis
    const top3=Object.entries(summary.defTotals).sort((a,b)=>b[1]-a[1]).slice(0,3)
      .map(([code,cnt])=>({code,cnt,name:DEFECT_CODES.find(d=>d.code===code)?.name||code}));

    // Chart data
    const hourlyChartData=HOUR_SLOTS.filter(h=>h!==LUNCH).map(h=>{
      const hd=summary.byHour[h]||{};
      return{hour:h.slice(0,5),checked:hd.checked||0,passed:hd.passed||0,
        defects:hd.defects||0,reject:hd.reject||0,repair:hd.repair||0,
        dhu:parseFloat(hd.dhu||0)};
    });

    const defectChartData=Object.entries(summary.defTotals)
      .sort((a,b)=>b[1]-a[1]).slice(0,10)
      .map(([code,cnt])=>({
        name:DEFECT_CODES.find(d=>d.code===code)?.name?.slice(0,14)||code,
        code,cnt,
        pct:summary.totDefects>0?((cnt/summary.totDefects)*100).toFixed(1):0
      }));

    return(
      <div style={{display:'flex',flexDirection:'column',height:'100%'}}>

        {/* Sub-tab bar */}
        <div style={{display:'flex',borderBottom:`1px solid ${_C.sl200}`,background:_C.white,flexShrink:0}}>
          {[['pad','🎯 Punch Pad'],['summary','📋 Hourly Log'],['analysis','📊 Analysis']].map(([id,lbl])=>(
            <button key={id} onClick={()=>setTab(id)} style={{
              padding:'9px 18px',fontSize:12,fontWeight:tab===id?700:400,
              color:tab===id?sectionColor:_C.sub,background:'transparent',border:'none',
              cursor:'pointer',borderBottom:tab===id?`2.5px solid ${sectionColor}`:'2.5px solid transparent',
            }}>{lbl}</button>
          ))}
          <div style={{flex:1}}/>
          <button onClick={saveSession} disabled={saving} style={{
            margin:'6px 10px',padding:'5px 16px',borderRadius:6,
            background:saved?_C.green:sectionColor,color:_C.white,
            border:'none',fontWeight:700,fontSize:12,cursor:'pointer',
          }}>{saved?'✓ Saved':saving?'Saving…':'💾 Save'}</button>
        </div>

        {/* Live KPI bar */}
        <div style={{display:'flex',background:'#0D1B2A',flexShrink:0}}>
          {[
            ['CHECKED', summary.totChecked, '#38BDF8','#0C4A6E'],
            ['PASSED',  summary.totPassed,  _C.green, '#14532D'],
            ['DEFECTS', summary.totDefects, _C.red,   '#450A0A'],
            ['REPAIR',  summary.totRepair,  _C.amb,   '#451A03'],
            ['REJECT',  summary.totReject,  '#A855F7','#2E1065'],
            ['DHU %',   `${summary.dhu}%`,
              parseFloat(summary.dhu)>5?_C.red:parseFloat(summary.dhu)>2?_C.amb:_C.green,
              parseFloat(summary.dhu)>5?'#450A0A':parseFloat(summary.dhu)>2?'#451A03':'#14532D'],
          ].map(([l,v,c,bg])=>(
            <div key={l} style={{flex:1,padding:'8px 4px',background:bg,
              textAlign:'center',borderRight:'1px solid #1A2A3A'}}>
              <div style={{fontSize:22,fontWeight:900,color:c,
                fontFamily:"'JetBrains Mono',monospace",lineHeight:1}}>{v}</div>
              <div style={{fontSize:9,fontWeight:700,color:c+'99',
                textTransform:'uppercase',letterSpacing:'.5px',marginTop:3}}>{l}</div>
            </div>
          ))}
        </div>

        <div style={{flex:1,overflowY:'auto'}}>

          {/* ── PUNCH PAD ── */}
          {tab==='pad'&&(
            <div style={{padding:14,display:'flex',flexDirection:'column',gap:14}}>

              {/* Production counters */}
              <div>
                <div style={{fontSize:11,fontWeight:700,color:_C.muted,
                  textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>
                  Production Counters — tap to record each piece
                </div>
                <div style={{display:'flex',gap:10}}>
                  <CounterBtn label="CHECKED"  count={summary.totChecked} color='#38BDF8'
                    bg='#0C4A6E' bd='#0369A1'
                    onTap={()=>tap('checked')} onUndo={()=>undoTap('checked')}/>
                  <CounterBtn label="PASSED"   count={summary.totPassed}  color={_C.green}
                    bg={_C.greenBg} bd={_C.greenBd}
                    onTap={()=>tap('passed')}  onUndo={()=>undoTap('passed')}/>
                  <CounterBtn label="REPAIR"   count={summary.totRepair}  color={_C.amb}
                    bg={_C.ambBg}   bd={_C.ambBd}
                    onTap={()=>tap('repair')}  onUndo={()=>undoTap('repair')}/>
                  <CounterBtn label="REJECT"   count={summary.totReject}  color='#A855F7'
                    bg='#EDE7F6'    bd='#D8B4FE'
                    onTap={()=>tap('reject')}  onUndo={()=>undoTap('reject')}/>
                </div>
              </div>

              {/* DHU formula display */}
              <div style={{padding:'8px 14px',background:'#0D1B2A',borderRadius:8,
                display:'flex',gap:16,alignItems:'center',flexWrap:'wrap'}}>
                <div style={{fontSize:11,color:'#6C757D',fontFamily:'monospace'}}>
                  DHU = Defects({summary.totDefects}) ÷ Checked({summary.totChecked}) × 100 =
                </div>
                <div style={{fontSize:18,fontWeight:800,fontFamily:'monospace',
                  color:parseFloat(summary.dhu)>5?_C.red:parseFloat(summary.dhu)>2?_C.amb:_C.green}}>
                  {summary.dhu}%
                </div>
              </div>

              {/* Operation selector */}
              <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                <span style={{fontSize:11,fontWeight:700,color:_C.sub,flexShrink:0}}>Operation:</span>
                <select value={selOp} onChange={e=>setSelOp(e.target.value)}
                  style={{flex:1,minWidth:200,padding:'8px 10px',borderRadius:6,
                    border:`1px solid ${_C.sl200}`,fontSize:12,background:_C.white}}>
                  <option value="">— Select operation (optional) —</option>
                  {defOps.map(op=><option key={op} value={op}>{op}</option>)}
                </select>
              </div>

              {/* Defect grid */}
              <div>
                <div style={{fontSize:11,fontWeight:700,color:_C.muted,
                  textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>
                  Defect Codes — tap to log a defect
                </div>
                <div style={{display:'grid',
                  gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:7}}>
                  {DEFECT_CODES.map(d=>{
                    const cnt=summary.defTotals[d.code]||0;
                    return(
                      <div key={d.code}
                        onClick={()=>addDefect(d.code,d.name)}
                        style={{background:cnt>0?_C.redBg:_C.sl50,
                          border:`2px solid ${cnt>0?_C.redBd:_C.sl200}`,
                          borderRadius:10,padding:'8px 10px',cursor:'pointer',
                          position:'relative',transition:'all .1s',
                          boxShadow:cnt>0?`0 2px 8px ${_C.red}33`:'none'}}>
                        <div style={{display:'flex',justifyContent:'space-between',
                          alignItems:'flex-start'}}>
                          <span style={{fontFamily:'monospace',fontSize:10,
                            fontWeight:700,color:sectionColor,
                            background:sectionColor+'18',padding:'1px 5px',borderRadius:4}}>
                            {d.code}
                          </span>
                          {cnt>0&&(
                            <span style={{fontSize:20,fontWeight:900,color:_C.red,
                              lineHeight:1,fontFamily:'monospace'}}>{cnt}</span>
                          )}
                        </div>
                        <div style={{fontSize:11,fontWeight:600,color:_C.text,
                          marginTop:5,lineHeight:1.3}}>{d.name}</div>
                        {cnt>0&&(
                          <button onClick={e=>{e.stopPropagation();undoDefect(d.code);}}
                            style={{position:'absolute',bottom:3,right:4,
                              background:_C.red+'22',border:'none',
                              borderRadius:4,padding:'1px 6px',fontSize:9,
                              color:_C.red,cursor:'pointer',fontWeight:700}}>−1</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── HOURLY LOG ── */}
          {tab==='summary'&&(
            <div style={{padding:14}}>
              <div style={{marginBottom:10,fontSize:12,color:_C.sub}}>
                All data auto-recorded from punch pad with timestamps.
                <strong style={{color:sectionColor}}> DHU = Defects ÷ Checked × 100</strong>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:_C.sl50}}>
                      {['Hour','Checked','Passed','Defects','Repair','Reject','Pass Rate','DHU %'].map(h=>(
                        <th key={h} style={{padding:'9px 11px',fontWeight:700,color:_C.sub,
                          fontSize:10,textTransform:'uppercase',
                          borderBottom:`2px solid ${_C.sl200}`,textAlign:'center'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {HOUR_SLOTS.map(slot=>{
                      const hd=summary.byHour[slot]||{};
                      const isLunch=slot===LUNCH;
                      const passRate=hd.checked>0?((hd.passed/hd.checked)*100).toFixed(1):null;
                      const dhuN=parseFloat(hd.dhu||0);
                      return(
                        <tr key={slot} style={{borderBottom:`1px solid ${_C.sl200}`,
                          background:isLunch?_C.sl100:hd.defects>0?'#FFF1F2':
                            hd.checked>0?'#F0FDF4':_C.white}}>
                          <td style={{padding:'7px 11px',textAlign:'center',
                            fontWeight:700,color:sectionColor,fontSize:12}}>{slot}</td>
                          {isLunch
                            ?<td colSpan={7} style={{padding:'7px',textAlign:'center',
                                color:_C.muted,fontStyle:'italic',fontSize:11}}>LUNCH HOUR</td>
                            :<>
                              <td style={{padding:'7px 11px',textAlign:'center',
                                fontWeight:700,color:'#0369A1',fontSize:14}}>{hd.checked||0}</td>
                              <td style={{padding:'7px 11px',textAlign:'center',
                                fontWeight:700,color:_C.green,fontSize:14}}>{hd.passed||0}</td>
                              <td style={{padding:'7px 11px',textAlign:'center',
                                fontWeight:hd.defects>0?800:400,
                                color:hd.defects>0?_C.red:_C.muted,fontSize:14}}>{hd.defects||0}</td>
                              <td style={{padding:'7px 11px',textAlign:'center',
                                color:_C.amb,fontWeight:hd.repair>0?700:400}}>{hd.repair||0}</td>
                              <td style={{padding:'7px 11px',textAlign:'center',
                                color:'#A855F7',fontWeight:hd.reject>0?700:400}}>{hd.reject||0}</td>
                              <td style={{padding:'7px 11px',textAlign:'center',
                                color:passRate&&parseFloat(passRate)>=95?_C.green:_C.amb,
                                fontWeight:700}}>{passRate?`${passRate}%`:'—'}</td>
                              <td style={{padding:'7px 11px',textAlign:'center',fontWeight:800,
                                color:dhuN>5?_C.red:dhuN>2?_C.amb:dhuN>0?_C.green:_C.muted,
                                fontSize:14}}>{hd.checked>0?`${hd.dhu}%`:'—'}</td>
                            </>
                          }
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr style={{background:'#0D1B2A',fontWeight:700}}>
                      <td style={{padding:'10px 11px',textAlign:'center',color:'#ADB5BD',fontWeight:700}}>TOTAL</td>
                      <td style={{padding:'10px 11px',textAlign:'center',color:'#38BDF8',fontSize:15}}>{summary.totChecked}</td>
                      <td style={{padding:'10px 11px',textAlign:'center',color:_C.green,fontSize:15}}>{summary.totPassed}</td>
                      <td style={{padding:'10px 11px',textAlign:'center',color:_C.red,fontSize:15}}>{summary.totDefects}</td>
                      <td style={{padding:'10px 11px',textAlign:'center',color:_C.amb}}>{summary.totRepair}</td>
                      <td style={{padding:'10px 11px',textAlign:'center',color:'#A855F7'}}>{summary.totReject}</td>
                      <td style={{padding:'10px 11px',textAlign:'center',
                        color:summary.totChecked>0?_C.green:_C.muted,fontWeight:800}}>
                        {summary.totChecked>0?`${((summary.totPassed/summary.totChecked)*100).toFixed(1)}%`:'—'}
                      </td>
                      <td style={{padding:'10px 11px',textAlign:'center',fontSize:16,fontWeight:900,
                        color:parseFloat(summary.dhu)>5?_C.red:parseFloat(summary.dhu)>2?_C.amb:_C.green}}>
                        {summary.dhu}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Per-hour defect breakdown */}
              {defPunches.length>0&&(
                <div style={{marginTop:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:_C.sub,
                    textTransform:'uppercase',marginBottom:8}}>Defect Log ({defPunches.length} entries)</div>
                  <div style={{display:'flex',flexDirection:'column',gap:3,maxHeight:220,overflowY:'auto'}}>
                    {[...defPunches].reverse().map(p=>(
                      <div key={p.id} style={{display:'flex',gap:10,alignItems:'center',
                        padding:'5px 10px',background:_C.white,borderRadius:6,
                        border:`1px solid ${_C.sl200}`,fontSize:11}}>
                        <span style={{fontFamily:'monospace',color:sectionColor,
                          fontWeight:700,minWidth:45}}>{p.defectCode}</span>
                        <span style={{flex:1,color:_C.text}}>{p.defectName}</span>
                        {p.operation&&<span style={{color:_C.muted,fontSize:10,
                          maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {p.operation}</span>}
                        <span style={{color:_C.muted,flexShrink:0}}>
                          {new Date(p.ts).toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                        </span>
                        <span style={{padding:'2px 6px',background:p.slot?sectionColor+'22':_C.sl100,
                          borderRadius:4,color:sectionColor,fontSize:10,fontWeight:700,flexShrink:0}}>
                          {p.slot}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ANALYSIS ── */}
          {tab==='analysis'&&(
            <div style={{padding:14,display:'flex',flexDirection:'column',gap:16}}>

              {/* Summary cards */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:10}}>
                {[
                  ['Total Checked',  summary.totChecked, '#38BDF8', '#EFF6FF'],
                  ['Total Passed',   summary.totPassed,  _C.green,  _C.greenBg],
                  ['Total Defects',  summary.totDefects, _C.red,    _C.redBg],
                  ['Sent to Repair', summary.totRepair,  _C.amb,    _C.ambBg],
                  ['Rejected',       summary.totReject,  '#A855F7', '#EDE7F6'],
                  ['Overall DHU',    `${summary.dhu}%`,
                    parseFloat(summary.dhu)>5?_C.red:parseFloat(summary.dhu)>2?_C.amb:_C.green,
                    parseFloat(summary.dhu)>5?_C.redBg:parseFloat(summary.dhu)>2?_C.ambBg:_C.greenBg],
                ].map(([l,v,c,bg])=>(
                  <div key={l} style={{padding:'12px 10px',background:bg,borderRadius:10,
                    textAlign:'center',border:`1px solid ${c}33`}}>
                    <div style={{fontSize:26,fontWeight:900,color:c,
                      fontFamily:'monospace',lineHeight:1}}>{v}</div>
                    <div style={{fontSize:10,color:c,textTransform:'uppercase',
                      fontWeight:700,marginTop:4,lineHeight:1.3}}>{l}</div>
                  </div>
                ))}
              </div>

              {/* DHU formula explanation */}
              <div style={{padding:'10px 14px',background:'#0D1B2A',borderRadius:8}}>
                <div style={{fontSize:11,fontWeight:700,color:'#ADB5BD',
                  textTransform:'uppercase',marginBottom:4}}>DHU Calculation</div>
                <div style={{fontFamily:'monospace',fontSize:13,color:'#F4F6F8'}}>
                  DHU = (Total Defects ÷ Total Checked) × 100
                </div>
                <div style={{fontFamily:'monospace',fontSize:15,color:
                  parseFloat(summary.dhu)>5?_C.red:parseFloat(summary.dhu)>2?_C.amb:_C.green,
                  marginTop:4,fontWeight:700}}>
                  = ({summary.totDefects} ÷ {summary.totChecked||1}) × 100
                  = <strong>{summary.dhu}%</strong>
                  {' '}<span style={{fontSize:11,fontWeight:400,color:'#6C757D'}}>
                    {parseFloat(summary.dhu)>5?'(Above 5% — Action Required)':
                      parseFloat(summary.dhu)>2?'(2-5% — Monitor)':'(Below 2% — Good)'}
                  </span>
                </div>
              </div>

              {/* Hourly DHU trend */}
              {summary.totChecked>0&&(
                <div style={{background:_C.white,border:`1px solid ${_C.sl200}`,
                  borderRadius:10,padding:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:_C.sub,
                    textTransform:'uppercase',marginBottom:12}}>
                    Hourly DHU % Trend
                  </div>
                  <div style={{height:180}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={hourlyChartData.filter(d=>d.checked>0)}>
                        <CartesianGrid strokeDasharray="3 3" stroke={_C.sl100}/>
                        <XAxis dataKey="hour" tick={{fontSize:10}} stroke={_C.sl200}/>
                        <YAxis tick={{fontSize:10}} stroke={_C.sl200} unit="%"/>
                        <Tooltip formatter={(v)=>[`${v}%`,'DHU']}
                          contentStyle={{fontSize:11,borderRadius:6}}/>
                        {/* Target line at 5% */}
                        <ReferenceLine y={5} stroke={_C.red} strokeDasharray="4 2"
                          label={{value:'Target 5%',fill:_C.red,fontSize:10,position:'right'}}/>
                        <ReferenceLine y={2} stroke={_C.green} strokeDasharray="4 2"
                          label={{value:'Good 2%',fill:_C.green,fontSize:10,position:'right'}}/>
                        <Line type="monotone" dataKey="dhu" stroke={sectionColor}
                          strokeWidth={2} dot={{fill:sectionColor,r:4}}
                          activeDot={{r:6}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Hourly production stacked bar */}
              {summary.totChecked>0&&(
                <div style={{background:_C.white,border:`1px solid ${_C.sl200}`,
                  borderRadius:10,padding:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:_C.sub,
                    textTransform:'uppercase',marginBottom:12}}>
                    Hourly Production Breakdown
                  </div>
                  <div style={{height:180}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourlyChartData.filter(d=>d.checked>0)}>
                        <CartesianGrid strokeDasharray="3 3" stroke={_C.sl100}/>
                        <XAxis dataKey="hour" tick={{fontSize:10}} stroke={_C.sl200}/>
                        <YAxis tick={{fontSize:10}} stroke={_C.sl200}/>
                        <Tooltip contentStyle={{fontSize:11,borderRadius:6}}/>
                        <Legend wrapperStyle={{fontSize:10}}/>
                        <Bar dataKey="passed"  name="Passed"  fill={_C.green}  stackId="a" radius={[0,0,0,0]}/>
                        <Bar dataKey="repair"  name="Repair"  fill={_C.amb}    stackId="a"/>
                        <Bar dataKey="reject"  name="Reject"  fill="#A855F7"   stackId="a"/>
                        <Bar dataKey="defects" name="Defects" fill={_C.red}    stackId="b" radius={[2,2,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Defect Pareto chart */}
              {defectChartData.length>0&&(
                <div style={{background:_C.white,border:`1px solid ${_C.sl200}`,
                  borderRadius:10,padding:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:_C.sub,
                    textTransform:'uppercase',marginBottom:12}}>
                    Defect Pareto (Top {defectChartData.length})
                  </div>
                  <div style={{height:200}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={defectChartData} layout="vertical"
                        margin={{left:10,right:40}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={_C.sl100}/>
                        <XAxis type="number" tick={{fontSize:10}} stroke={_C.sl200}/>
                        <YAxis dataKey="name" type="category" tick={{fontSize:10}}
                          stroke={_C.sl200} width={110}/>
                        <Tooltip formatter={(v,n,p)=>[`${v} (${p.payload.pct}%)`,'Count']}
                          contentStyle={{fontSize:11,borderRadius:6}}/>
                        <Bar dataKey="cnt" name="Defects" radius={[0,3,3,0]}>
                          {defectChartData.map((_,i)=>(
                            <Cell key={i} fill={
                              i===0?_C.red:i===1?'#F97316':i===2?_C.amb:'#FCA5A5'}/>
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Top 3 defects text summary */}
              {top3.length>0&&(
                <div style={{background:_C.white,border:`1px solid ${_C.sl200}`,
                  borderRadius:10,padding:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:_C.sub,
                    textTransform:'uppercase',marginBottom:12}}>Top Defects This Session</div>
                  {top3.map((d,i)=>{
                    const pct=summary.totDefects>0?((d.cnt/summary.totDefects)*100):0;
                    const colors=[_C.red,'#F97316',_C.amb];
                    return(
                      <div key={d.code} style={{display:'flex',alignItems:'center',
                        gap:12,marginBottom:12}}>
                        <div style={{width:26,height:26,borderRadius:'50%',
                          background:colors[i],color:_C.white,display:'flex',
                          alignItems:'center',justifyContent:'center',
                          fontSize:13,fontWeight:900,flexShrink:0}}>#{i+1}</div>
                        <div style={{flex:1}}>
                          <div style={{display:'flex',justifyContent:'space-between',
                            marginBottom:4}}>
                            <span style={{fontSize:12,fontWeight:600,color:_C.text}}>
                              [{d.code}] {d.name}
                            </span>
                            <span style={{fontSize:13,fontWeight:900,color:colors[i]}}>
                              {d.cnt} ({pct.toFixed(0)}%)
                            </span>
                          </div>
                          <div style={{height:8,background:_C.sl100,borderRadius:4}}>
                            <div style={{height:8,borderRadius:4,
                              background:colors[i],width:`${pct}%`,
                              transition:'width .3s'}}/>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Operation-wise defects */}
              {Object.keys(summary.opTotals).length>0&&(
                <div style={{background:_C.white,border:`1px solid ${_C.sl200}`,
                  borderRadius:10,padding:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:_C.sub,
                    textTransform:'uppercase',marginBottom:10}}>Operation-wise Defects</div>
                  {Object.entries(summary.opTotals).sort((a,b)=>b[1]-a[1]).map(([op,cnt])=>(
                    <div key={op} style={{display:'flex',alignItems:'center',
                      gap:10,marginBottom:7}}>
                      <div style={{flex:1,fontSize:11,color:_C.text,
                        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{op}</div>
                      <div style={{width:120,height:8,background:_C.sl100,
                        borderRadius:4,flexShrink:0}}>
                        <div style={{height:8,borderRadius:4,background:sectionColor,
                          width:`${(cnt/summary.totDefects)*100}%`}}/>
                      </div>
                      <div style={{fontSize:12,fontWeight:700,color:sectionColor,
                        minWidth:24,textAlign:'right'}}>{cnt}</div>
                    </div>
                  ))}
                </div>
              )}

              {summary.totChecked===0&&defPunches.length===0&&(
                <div style={{padding:40,textAlign:'center',color:_C.muted,fontSize:13}}>
                  Start punching on the Punch Pad tab to see analytics here.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Sewing Dept Shell ─────────────────────────────────────────────────────
  function SewingDept({db,setDb,filters}){
    const TODAY_DATE=new Date().toISOString().split('T')[0];
    const [section,  setSection]  = React.useState('FRONT');
    const [mainTab,  setMainTab]  = React.useState('datapad');
    const [session,  setSession]  = React.useState({
      cardNo:'',qiName:'',lineNo:'',date:TODAY_DATE,
      style:filters?.style||'',po:filters?.po||'',
      buyer:filters?.buyer||'',colour:filters?.colour||'',
    });
    const setSess=(k,v)=>setSession(s=>({...s,[k]:v}));

    const SECTIONS=[
      {id:'FRONT',    icon:'👕',color:'#00897B',ops:FRONT_OPS},
      {id:'BACK',     icon:'🔄',color:'#6A1B9A',ops:BACK_OPS },
      {id:'ASSEMBLY', icon:'🪡',color:'#2E7D32',ops:ASSY_OPS },
    ];
    const activeSec=SECTIONS.find(s=>s.id===section);
    const sessions=(db.sewing||[]).filter(r=>r.section===section);

    return(
      <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
        {/* Top nav */}
        <div style={{background:S.navy,padding:'8px 14px',
          display:'flex',alignItems:'center',gap:10,flexShrink:0,flexWrap:'wrap'}}>
          {SECTIONS.map(sec=>(
            <button key={sec.id} onClick={()=>setSection(sec.id)} style={{
              padding:'6px 16px',borderRadius:20,
              background:section===sec.id?sec.color:'transparent',
              color:section===sec.id?S.white:S.slate400,
              border:`1.5px solid ${section===sec.id?sec.color:S.slate600}`,
              fontWeight:700,fontSize:12,cursor:'pointer',
              display:'flex',alignItems:'center',gap:5,
            }}>{sec.icon} {sec.id}</button>
          ))}
          <div style={{flex:1}}/>
          {['datapad','sessions'].map(m=>(
            <button key={m} onClick={()=>setMainTab(m)} style={{
              padding:'5px 12px',borderRadius:6,
              background:mainTab===m?S.teal:'transparent',
              color:mainTab===m?S.white:S.slate400,
              border:`1px solid ${mainTab===m?S.teal:S.slate600}`,
              fontWeight:600,fontSize:11,cursor:'pointer',
            }}>{m==='datapad'?'🎯 Datapad':'📋 Sessions'}</button>
          ))}
        </div>

        {/* Session header */}
        <div style={{background:'#0D1B2A',padding:'6px 14px',
          display:'flex',gap:10,flexWrap:'wrap',flexShrink:0,
          borderBottom:'1px solid #1E3A5F'}}>
          {[['Card No.','cardNo',70],['QI Name','qiName',90],['Line','lineNo',55],
            ['Style','style',85],['PO','po',85],['Buyer','buyer',85],['Colour','colour',85],
          ].map(([lbl,key,w])=>(
            <div key={key} style={{display:'flex',flexDirection:'column',gap:2}}>
              <label style={{fontSize:9,fontWeight:700,color:S.slate500,textTransform:'uppercase'}}>{lbl}</label>
              <input value={session[key]||''} onChange={e=>setSess(key,e.target.value)}
                placeholder={lbl+'...'}
                style={{padding:'4px 7px',borderRadius:5,border:'1px solid #1E3A5F',
                  background:'#1A2A3A',color:S.white,fontSize:11,width:w}}/>
            </div>
          ))}
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            <label style={{fontSize:9,fontWeight:700,color:S.slate500,textTransform:'uppercase'}}>Date</label>
            <input type="date" value={session.date||TODAY_DATE} onChange={e=>setSess('date',e.target.value)}
              style={{padding:'4px 7px',borderRadius:5,border:'1px solid #1E3A5F',
                background:'#1A2A3A',color:S.white,fontSize:11,width:115}}/>
          </div>
        </div>

        <div style={{flex:1,overflowY:'auto'}}>
          {mainTab==='datapad'
            ?<SewingDatapad key={section} section={section} defOps={activeSec.ops}
                db={db} setDb={setDb} session={session} sectionColor={activeSec.color}/>
            :<div style={{padding:16,display:'flex',flexDirection:'column',gap:12}}>
              <div style={{fontSize:13,fontWeight:700,color:S.slate700}}>
                Saved Sessions — {section} ({sessions.length})
              </div>
              {sessions.length===0
                ?<div style={{padding:40,textAlign:'center',color:S.slate400,fontStyle:'italic'}}>
                  No sessions saved for {section} yet.
                </div>
                :sessions.slice().reverse().map(s=>(
                  <div key={s.id} style={{background:S.white,border:`1px solid ${S.slate200}`,
                    borderRadius:10,padding:14}}>
                    <div style={{display:'flex',justifyContent:'space-between',
                      alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:S.slate800}}>
                          {s.date} · Card {s.cardNo||'—'} · {s.qiName||'—'}
                        </div>
                        <div style={{fontSize:11,color:S.slate500}}>
                          Line {s.lineNo||'—'} · {s.style||'—'} · {s.buyer||'—'}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {[['Checked',s.summary?.totChecked||0,'#0369A1','#EFF6FF'],
                          ['Passed',s.summary?.totPassed||0,S.green,S.greenBg],
                          ['Defects',s.summary?.totDefects||0,S.red,S.redBg],
                          ['DHU',`${s.summary?.dhu||0}%`,
                            parseFloat(s.summary?.dhu||0)>5?S.red:S.green,
                            parseFloat(s.summary?.dhu||0)>5?S.redBg:S.greenBg],
                        ].map(([l,v,c,b])=>(
                          <div key={l} style={{textAlign:'center',padding:'6px 12px',
                            background:b,borderRadius:8,border:`1px solid ${c}22`}}>
                            <div style={{fontSize:16,fontWeight:800,color:c}}>{v}</div>
                            <div style={{fontSize:9,color:S.slate500,textTransform:'uppercase'}}>{l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          }
        </div>
      </div>
    );
  }

  DEPT_REGISTRY['sewing'] = SewingDept;
  console.log('[QMS] Sewing Datapad registered ✓');

})();


(function registerWashingAndWF() {

  // ── palette ───────────────────────────────────────────────────────────────
  const C = {
    bg:'#0D1B2A', white:'#FFFFFF',
    slate50:'#F8F9FA', slate100:'#F4F6F8',
    border:'#DEE2E6', text:'#1A2A3A', textSub:'#6C757D', textMuted:'#ADB5BD',
    wash:'#1565C0',   // washing dept — blue
    wf  :'#6A1B9A',   // wrinkle free dept — purple
    success:'#2E7D32', successBg:'#E8F5E9', successBd:'#A5D6A7',
    danger :'#C62828', dangerBg :'#FFEBEE', dangerBd :'#FFCDD2',
    warning:'#E65100', warningBg:'#FFF8E1', warningBd:'#FFE082',
  };
  const R   = { sm:'6px', md:'8px', lg:'12px' };
  const API = 'https://nirvikalp.integrashion.com/api/save_universal.php';
  const TODAY = new Date().toISOString().split('T')[0];
  const HOURS = ['08:00-09:00','09:00-10:00','10:00-11:00','11:00-12:00',
                 '12:00-01:00','01:00-02:00','02:00-03:00','03:00-04:00',
                 '04:00-05:00','05:00-06:00','06:00-07:00'];

  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }

  // ── master data ───────────────────────────────────────────────────────────
  const BUYERS = dbGet('buyers', _FB_BUYERS);
  const BRANDS = dbGet('brands', _FB_BRANDS);
  const COLOURS = dbGet('colours', _FB_COLOURS);
  const WASH_TYPES= ['Normal Wash','Stone Wash','Enzyme Wash','Acid Wash','Sand Blast',
                     'Bleach Wash','Silicon Wash','Pigment Dye','Wrinkle Free','Garment Dye','Other'];
  const SHIFTS    = ['DAY','NIGHT','GENERAL'];

  const WASHING_DEFECTS = [
    'Spot / Bleeding','Off Shade','Sewing Defect','Bleding (Pkt/Waist/Label/Piping)',
    'Stain','Color Bleeding / Color Change','Streak Mark','Patch Mark',
    'Grinding Grain','Sand Blasting','Dirty & Other Spot','Pocketing Issue',
    'Seam Reverse','W/B Puckering','Iron Defects','Holes / Damage','Other',
  ];

  const WF_DEFECTS_PRE = [
    'Seam Overlapping','Seam to Seam Not Matching','Pleat Not Straight',
    'Loop Slanted','Hand Iron Required','Fabric Crease','Inseam Impression',
    'Hem Folded','Double Crease','Damages / Holes',
  ];

  const WF_DEFECTS_POST = [
    'Shade Change','Seam Reverse','W/B Puckering','Sewing Defects',
    'Streak Marks','Water Marks','Blading','Iron Defects','Holes',
  ];

  const WF_PROCESS_CHECKS = [
    'Recipe / programme displayed at machine',
    'Temperature set as per approved standard',
    'Garments not over-loaded in machine',
    'Correct chemical used per recipe',
    'Pre-spray done before oven',
    'Oven temperature calibrated and verified',
    'Post production inspection completed',
    'Dispatch audit done before sending to Finishing',
    'Shade consistency checked per batch',
    'Defect garments isolated and tagged',
  ];

  // ── shared API save ───────────────────────────────────────────────────────
  async function saveRec(category, record, key, db, setDb) {
    try {
      const res = await fetch(API, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ category, data:record }),
      });
      const j = await res.json();
      record.serverId = j.id || Date.now();
    } catch { record.serverId = Date.now(); }
    setDb(prev => {
      const list = prev[key] || [];
      const idx  = list.findIndex(r => r.id === record.id);
      return {
        ...prev,
        [key]: idx >= 0 ? list.map(r => r.id===record.id ? record : r) : [...list, record],
      };
    });
  }

  // ── mini UI ───────────────────────────────────────────────────────────────
  function Fld({ label, value, onChange, type='text', readOnly=false,
    placeholder='', options=null, required=false, note='', rows=3 }) {
    const base = {
      width:'100%', padding:'9px 11px', fontSize:13, borderRadius:R.md,
      border:`1px solid ${C.border}`, background:readOnly?C.slate50:C.white,
      color:readOnly?C.textSub:C.text, outline:'none', fontFamily:'inherit',
    };
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        <label style={{ fontSize:11, fontWeight:600, color:C.textSub,
          textTransform:'uppercase', letterSpacing:'.4px' }}>
          {label}{required && <span style={{ color:C.danger }}> *</span>}
        </label>
        {options
          ? <select value={value} onChange={onChange} disabled={readOnly}
              style={{ ...base, cursor:readOnly?'default':'pointer' }}>
              {options.map((o,i) => <option key={i} value={o.value??o}>{o.label??o}</option>)}
            </select>
          : type==='textarea'
          ? <textarea value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} rows={rows} style={{ ...base, resize:'vertical' }}/>
          : <input type={type} value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} style={base}/>}
        {note && <div style={{ fontSize:11, color:C.textMuted }}>{note}</div>}
      </div>
    );
  }

  function Btn({ children, onClick, color=C.wash, outline=false, sm=false, disabled=false, icon=null }) {
    return (
      <button onClick={onClick} disabled={disabled} style={{
        display:'flex', alignItems:'center', gap:5,
        padding:sm?'7px 13px':'10px 20px', borderRadius:R.md,
        border:outline?`2px solid ${color}`:'none',
        background:disabled?C.slate100:outline?'transparent':color,
        color:disabled?C.textMuted:outline?color:C.white,
        fontWeight:700, fontSize:sm?12:13, cursor:disabled?'default':'pointer',
        fontFamily:'inherit', transition:'all .15s',
      }}>{icon && <span>{icon}</span>}{children}</button>
    );
  }

  function Bdg({ children, color=C.wash, bg }) {
    return (
      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
        color, background:bg||color+'22', display:'inline-block', whiteSpace:'nowrap' }}>
        {children}
      </span>
    );
  }

  function Res({ result }) {
    const m = {
      PASS:{c:C.success,b:C.successBg}, FAIL:{c:C.danger,b:C.dangerBg},
      HOLD:{c:C.warning,b:C.warningBg}, PENDING:{c:C.textMuted,b:C.slate100},
      APPROVED:{c:C.success,b:C.successBg}, REJECTED:{c:C.danger,b:C.dangerBg},
    };
    const x = m[result?.toUpperCase()] || m.PENDING;
    return <Bdg color={x.c} bg={x.b}>{result||'PENDING'}</Bdg>;
  }

  function Card({ children, style={} }) {
    return (
      <div style={{ background:C.white, border:`1px solid ${C.border}`,
        borderRadius:R.lg, padding:20, ...style }}>{children}</div>
    );
  }

  function SH({ title, color=C.wash, action=null }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, paddingBottom:12, borderBottom:`2px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:3, height:18, background:color, borderRadius:2 }}/>
          <h3 style={{ fontSize:13, fontWeight:700, color:C.text,
            textTransform:'uppercase', letterSpacing:'.4px' }}>{title}</h3>
        </div>
        {action}
      </div>
    );
  }

  function Grid({ children, gap=14, min=180 }) {
    return (
      <div style={{ display:'grid',
        gridTemplateColumns:`repeat(auto-fit,minmax(${min}px,1fr))`, gap }}>
        {children}
      </div>
    );
  }

  function Tbl({ headers=[], rows=[], emptyMsg='No records found.' }) {
    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:C.slate50 }}>
              {headers.map((h,i) => (
                <th key={i} style={{ padding:'9px 11px', textAlign:'left', fontWeight:700,
                  color:C.textSub, fontSize:11, textTransform:'uppercase',
                  borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length===0
              ? <tr><td colSpan={headers.length} style={{ padding:24, textAlign:'center',
                  color:C.textMuted, fontStyle:'italic' }}>{emptyMsg}</td></tr>
              : rows.map((row,i) => (
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}`,
                  background:i%2===0?C.white:C.slate50 }}>
                  {row.map((cell,j) => (
                    <td key={j} style={{ padding:'9px 11px', color:C.text,
                      verticalAlign:'middle' }}>{cell}</td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  function ChkRow({ label, ok, onOk, remark, onRemark }) {
    return (
      <div style={{ display:'flex', gap:10, alignItems:'flex-start',
        padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', gap:6, flexShrink:0, paddingTop:2 }}>
          {[['OK',true,C.success],['NG',false,C.danger]].map(([lbl,val,col]) => (
            <label key={lbl} style={{ display:'flex', alignItems:'center', gap:4, cursor:'pointer' }}>
              <input type="radio" checked={ok===val} onChange={()=>onOk(val)}
                style={{ accentColor:col, width:14, height:14 }}/>
              <span style={{ fontSize:11, fontWeight:700,
                color:ok===val?col:C.textMuted }}>{lbl}</span>
            </label>
          ))}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, color:C.text }}>{label}</div>
          {ok===false && (
            <input placeholder="Corrective action..." value={remark||''}
              onChange={e=>onRemark(e.target.value)}
              style={{ width:'100%', padding:'5px 8px', fontSize:11, borderRadius:R.sm,
                border:`1px solid ${C.dangerBd}`, background:C.dangerBg, marginTop:4 }}/>
          )}
        </div>
      </div>
    );
  }

  function PgHead({ title, ref_, color=C.wash, action }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:18 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
            <div style={{ width:4, height:20, background:color, borderRadius:2 }}/>
            <h2 style={{ fontSize:16, fontWeight:800, color:C.text }}>{title}</h2>
          </div>
          <div style={{ fontSize:11, color:C.textMuted, paddingLeft:12 }}>Ref: {ref_}</div>
        </div>
        {action}
      </div>
    );
  }

  function useSave(dbKey) {
    const [saving, setSaving] = React.useState(false);
    const [saved,  setSaved]  = React.useState(false);
    async function doSave(category, record, db, setDb) {
      setSaving(true);
      await saveRec(category, record, dbKey, db, setDb);
      setSaving(false); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    return { saving, saved, doSave };
  }

  function SaveBar({ result, saving, saved, onSave, color }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        flexWrap:'wrap', gap:10, marginTop:16, paddingTop:14,
        borderTop:`1px solid ${C.border}` }}>
        {result
          ? <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ fontSize:11, color:C.textSub }}>Result</div>
              <Res result={result}/>
            </div>
          : <div/>}
        <Btn onClick={onSave} disabled={saving} color={color||C.wash}
          icon={saved?'✓':saving?'…':'💾'}>
          {saved?'Saved!':saving?'Saving…':'Save Report'}
        </Btn>
      </div>
    );
  }

  // Hourly table (shared by several washing forms)
  function HourlyTable({ data, onChange, showDefective=true, color=C.wash }) {
    const totChecked  = data.reduce((s,h)=>s+(parseInt(h.checked)||0),0);
    const totDef      = data.reduce((s,h)=>s+(parseInt(h.defective)||0),0);
    const defPct      = totChecked>0?((totDef/totChecked)*100).toFixed(1):0;

    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:C.slate50 }}>
              {['Hour',showDefective?'Checked Qty':'Checked',
                showDefective?'Defective':'OK',
                'Defect %','Remark'].map(h=>(
                <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub, fontSize:11,
                  textTransform:'uppercase', borderBottom:`2px solid ${C.border}`,
                  textAlign:'center' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((h,i) => {
              const pct = (parseInt(h.checked)||0) > 0
                ? (((parseInt(h.defective)||0)/(parseInt(h.checked)||1))*100).toFixed(1) : '—';
              return (
                <tr key={h.hour} style={{ borderBottom:`1px solid ${C.border}`,
                  background:parseFloat(pct)>5?C.warningBg:C.white }}>
                  <td style={{ padding:'6px 10px', textAlign:'center',
                    fontWeight:700, color }}>
                    {h.hour}
                  </td>
                  {['checked','defective'].map(k=>(
                    <td key={k} style={{ padding:'5px 8px', textAlign:'center' }}>
                      <input type="number" value={h[k]||''} onChange={e=>onChange(i,k,e.target.value)}
                        placeholder="0"
                        style={{ width:80, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                          background:k==='defective'&&parseInt(h[k])>0?C.dangerBg:C.white }}/>
                    </td>
                  ))}
                  <td style={{ padding:'6px 10px', textAlign:'center', fontWeight:700,
                    color:parseFloat(pct)>5?C.danger:pct==='—'?C.textMuted:C.success }}>
                    {pct}{pct!=='—'?'%':''}
                  </td>
                  <td style={{ padding:'5px 8px' }}>
                    <input value={h.remark||''} onChange={e=>onChange(i,'remark',e.target.value)}
                      placeholder="Remark..."
                      style={{ width:'100%', minWidth:100, padding:'5px', borderRadius:R.sm,
                        border:`1px solid ${C.border}`, fontSize:11 }}/>
                  </td>
                </tr>
              );
            })}
            <tr style={{ background:C.slate50, fontWeight:700 }}>
              <td style={{ padding:'8px 10px', textAlign:'center' }}>TOTAL</td>
              <td style={{ padding:'8px 10px', textAlign:'center' }}>{totChecked}</td>
              <td style={{ padding:'8px 10px', textAlign:'center',
                color:totDef>0?C.danger:C.success }}>{totDef}</td>
              <td style={{ padding:'8px 10px', textAlign:'center',
                color:parseFloat(defPct)>5?C.danger:C.success }}>
                {totChecked>0?`${defPct}%`:'—'}
              </td>
              <td/>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Defect tiles (shared by washing + WF)
  function DefectGrid({ defects, onChange, color=C.wash }) {
    const total = defects.reduce((s,d)=>s+(d.qty||0),0);
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textSub, textTransform:'uppercase' }}>
            Defect Breakdown
          </div>
          <Bdg color={C.danger} bg={C.dangerBg}>Total Defects: {total}</Bdg>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:7 }}>
          {defects.map((d,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8,
              padding:'7px 10px', borderRadius:R.sm,
              background:d.qty>0?C.dangerBg:C.slate50,
              border:`1px solid ${d.qty>0?C.dangerBd:C.border}` }}>
              <div style={{ flex:1, fontSize:12, color:C.text,
                fontWeight:d.qty>0?600:400 }}>{d.label}</div>
              <input type="number" value={d.qty||''} onChange={e=>onChange(i,parseInt(e.target.value)||0)}
                style={{ width:55, padding:'4px', borderRadius:R.sm,
                  border:`1px solid ${C.border}`, fontSize:13, textAlign:'center',
                  fontWeight:700, color:d.qty>0?C.danger:C.text }}/>
            </div>
          ))}
        </div>
      </div>
    );
  }


  // ╔══════════════════════════════════════════════════════════════════════╗
  // ║   WASHING FORMS  (Ann-39 → Ann-44)                                  ║
  // ╚══════════════════════════════════════════════════════════════════════╝

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-39 · B/4 WASH AUDIT REPORT  (Nir/Ws/01)
  //  Total observation report after first lot washed
  // ═══════════════════════════════════════════════════════════════════════
  function Ann39({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('washing');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'B4_WASH_AUDIT', date:TODAY,
      style:filters.style||'', po:filters.po||'',
      buyer:filters.buyer||'', colour:filters.colour||'',
      washType:'', lotNo:'', lotSize:0, recipeRef:'',
      // checklist
      shadeApproved:'', dimensionalOk:'', handleFeelOk:'',
      colorFastnessWash:'', colorFastnessRub:'',
      shrinkageL:'', shrinkageW:'',
      // defects
      defects: WASHING_DEFECTS.map(d=>({ label:d, qty:0 })),
      observations:'', actionTaken:'',
      washingQaIncharge:'', washingHead:'',
    });

    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set    = (k,v) => setF(f=>({...f,[k]:v}));
    const setDef = (i,v) => setF(f=>({...f, defects:f.defects.map((d,di)=>di===i?{...d,qty:v}:d)}));
    const recs   = (db.washing||[]).filter(r=>r.formType==='B4_WASH_AUDIT');

    const totalDef = F.defects.reduce((s,d)=>s+(d.qty||0),0);
    const autoRes  = () => totalDef===0?'PASS':totalDef<=5?'HOLD':'FAIL';

    const handleSave = async () => {
      await doSave('washing_b4_wash_audit',
        { ...F, totalDefects:totalDef, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-39 · Before Wash Audit Report" color={C.wash}
          ref_="Nir/Ws/01 · First lot washed — total observation & comments"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.wash}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.wash}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="B/4 Wash Audit Records" color={C.wash}/>
            <Tbl headers={['Date','Style','Wash Type','Lot No.','Lot Size','Total Defects','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.washType||'—',r.lotNo||'—',
                r.lotSize||'—',r.totalDefects||0,<Res result={r.result}/>])}
              emptyMsg="No B/4 wash audit records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Wash Details" color={C.wash}/>
              <Grid gap={14}>
                <Fld label="Date"       type="date" value={F.date}      onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."  value={F.style}     onChange={e=>set('style',e.target.value)}     options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="PO No."     value={F.po}        onChange={e=>set('po',e.target.value)}        options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer"      value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
                <Fld label="Colour"     value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={['',...dbGet('colours',_FB_COLOURS)]}/>
                <Fld label="Wash Type"  value={F.washType}  onChange={e=>set('washType',e.target.value)}  options={['',...WASH_TYPES]}/>
                <Fld label="Lot No."    value={F.lotNo}     onChange={e=>set('lotNo',e.target.value)}     placeholder="Lot#..."/>
                <Fld label="Lot Size"   type="number" value={F.lotSize} onChange={e=>set('lotSize',e.target.value)} placeholder="0"/>
                <Fld label="Recipe Ref" value={F.recipeRef} onChange={e=>set('recipeRef',e.target.value)} placeholder="Recipe#..."/>
              </Grid>
            </Card>

            <Card>
              <SH title="Wash Quality Parameters" color={C.wash}/>
              <Grid gap={14} min={160}>
                <Fld label="Shade Approved"        value={F.shadeApproved}     onChange={e=>set('shadeApproved',e.target.value)}     options={['','YES','NO','HOLD']}/>
                <Fld label="Dimensional Stable"    value={F.dimensionalOk}     onChange={e=>set('dimensionalOk',e.target.value)}     options={['','YES','NO']}/>
                <Fld label="Handle / Feel"         value={F.handleFeelOk}      onChange={e=>set('handleFeelOk',e.target.value)}      options={['','OK','NG','SOFT','HARD']}/>
                <Fld label="Color Fastness (Wash)"  value={F.colorFastnessWash} onChange={e=>set('colorFastnessWash',e.target.value)} placeholder="Grade 1-5"/>
                <Fld label="Color Fastness (Rub)"   value={F.colorFastnessRub}  onChange={e=>set('colorFastnessRub',e.target.value)}  placeholder="Grade 1-5"/>
                <Fld label="Shrinkage L %"          type="number" value={F.shrinkageL} onChange={e=>set('shrinkageL',e.target.value)} placeholder="0"/>
                <Fld label="Shrinkage W %"          type="number" value={F.shrinkageW} onChange={e=>set('shrinkageW',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>

            <Card>
              <DefectGrid defects={F.defects} onChange={setDef} color={C.wash}/>
            </Card>

            <Card>
              <SH title="Observations & Action" color={C.wash}/>
              <Grid gap={14} min={220}>
                <Fld label="Observations" type="textarea" rows={3} value={F.observations}
                  onChange={e=>set('observations',e.target.value)} placeholder="Overall wash quality observations..."/>
                <Fld label="Action Taken" type="textarea" rows={3} value={F.actionTaken}
                  onChange={e=>set('actionTaken',e.target.value)} placeholder="Corrective actions..."/>
              </Grid>
              <Grid gap={14} min={180} style={{ marginTop:12 }}>
                <Fld label="Washing QA Incharge" value={F.washingQaIncharge} onChange={e=>set('washingQaIncharge',e.target.value)} placeholder="Name..."/>
                <Fld label="Washing Head"         value={F.washingHead}      onChange={e=>set('washingHead',e.target.value)}      placeholder="Name..."/>
              </Grid>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wash}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-40 · CORRECTIVE ACTION CHART  (Nir/Ws/02)
  //  Style-wise defect record & action taken on daily audit findings
  // ═══════════════════════════════════════════════════════════════════════
  function Ann40({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('washing');
    const newRow = () => ({ id:uid(), style:'', defect:'', actionTaken:'', defectQty:0,
      responsiblePerson:'', targetDate:TODAY, status:'OPEN' });
    const fresh  = () => ({
      id:uid(), formType:'CORRECTIVE_ACTION', date:TODAY,
      styleNo:filters.style||'', buyer:filters.buyer||'',
      washingHead:'', qaManager:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.washing||[]).filter(r=>r.formType==='CORRECTIVE_ACTION');

    const handleSave = async () => {
      await doSave('washing_corrective_action',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    const sCol = s => s==='CLOSED'?C.success:s==='OPEN'?C.danger:C.warning;
    const sBg  = s => s==='CLOSED'?C.successBg:s==='OPEN'?C.dangerBg:C.warningBg;

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-40 · Corrective Action Chart" color={C.wash}
          ref_="Nir/Ws/02 · Style-wise defect record & action — Washing Head"
          action={<Bdg color={C.wash}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.wash}/>
          <Grid gap={14}>
            <Fld label="Date"         type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."    value={F.styleNo}     onChange={e=>set('styleNo',e.target.value)}     options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"        value={F.buyer}       onChange={e=>set('buyer',e.target.value)}       options={['',...BUYERS]}/>
            <Fld label="Washing Head" value={F.washingHead} onChange={e=>set('washingHead',e.target.value)} placeholder="Name..."/>
            <Fld label="QA Manager"   value={F.qaManager}   onChange={e=>set('qaManager',e.target.value)}   placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Defect & Action Log" color={C.wash}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wash}>＋ Add Row</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:750 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Style','Defect Found','Action Taken','Defect Qty','Responsible','Target Date','Status',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                      fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`,
                    background:r.status==='OPEN'?C.dangerBg:r.status==='CLOSED'?C.successBg:C.warningBg }}>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.style} onChange={e=>updR(i,'style',e.target.value)} placeholder="Style..."
                        style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.defect} onChange={e=>updR(i,'defect',e.target.value)}
                        style={{ width:'100%', minWidth:140, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">Select defect...</option>
                        {WASHING_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.actionTaken} onChange={e=>updR(i,'actionTaken',e.target.value)} placeholder="Action..."
                        style={{ width:'100%', minWidth:130, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input type="number" value={r.defectQty} onChange={e=>updR(i,'defectQty',e.target.value)} placeholder="0"
                        style={{ width:65, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.responsiblePerson} onChange={e=>updR(i,'responsiblePerson',e.target.value)} placeholder="Name..."
                        style={{ width:90, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input type="date" value={r.targetDate} onChange={e=>updR(i,'targetDate',e.target.value)}
                        style={{ padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.status} onChange={e=>updR(i,'status',e.target.value)}
                        style={{ padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11,
                          fontWeight:700, fontFamily:'inherit', background:sBg(r.status), color:sCol(r.status) }}>
                        {['OPEN','IN PROGRESS','CLOSED'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                          color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.wash}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-41 · DEFECT GMT DETAILS  (Nir/Ws/03)
  //  Order-wise garments pending for various washing defects
  // ═══════════════════════════════════════════════════════════════════════
  function Ann41({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('washing');
    const newRow = () => ({
      id:uid(), orderRef:'', colour:'', today:0, total:0,
      defects: WASHING_DEFECTS.slice(0,6).map(d=>({ label:d, qty:0 })),
      killedQty:0, remarks:'',
    });
    const fresh  = () => ({
      id:uid(), formType:'DEFECT_GMT', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      washingQa:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const updDef = (ri,di,v) => setF(f=>({
      ...f,
      rows:f.rows.map((r,rIdx)=>rIdx!==ri?r:{
        ...r, defects:r.defects.map((d,dIdx)=>dIdx!==di?d:{...d,qty:parseInt(v)||0}),
      }),
    }));
    const recs = (db.washing||[]).filter(r=>r.formType==='DEFECT_GMT');

    const handleSave = async () => {
      await doSave('washing_defect_gmt',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-41 · Defect GMT Details" color={C.wash}
          ref_="Nir/Ws/03 · Order-wise GMT pending for washing defects"
          action={<Bdg color={C.wash}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.wash}/>
          <Grid gap={14}>
            <Fld label="Date"       type="date" value={F.date}    onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."  value={F.style}   onChange={e=>set('style',e.target.value)}   options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"      value={F.buyer}   onChange={e=>set('buyer',e.target.value)}   options={['',...BUYERS]}/>
            <Fld label="Washing QA" value={F.washingQa} onChange={e=>set('washingQa',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Defect GMT Tracking" color={C.wash}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wash}>＋ Add Order Row</Btn>}/>
          {F.rows.map((r,ri) => (
            <div key={r.id} style={{ border:`1px solid ${C.border}`, borderRadius:R.md,
              padding:14, marginBottom:12, background:C.slate50 }}>
              <Grid gap={10} min={140}>
                <Fld label="Order Ref" value={r.orderRef} onChange={e=>updR(ri,'orderRef',e.target.value)} options={["", ...dbGet("poNumbers",[])]} placeholder="Order/Ref..."/>
                <Fld label="Colour"    value={r.colour}   onChange={e=>updR(ri,'colour',e.target.value)}   options={['',...dbGet('colours',_FB_COLOURS)]}/>
                <Fld label="Today Qty" type="number" value={r.today} onChange={e=>updR(ri,'today',e.target.value)} placeholder="0"/>
                <Fld label="Total Qty" type="number" value={r.total} onChange={e=>updR(ri,'total',e.target.value)} placeholder="0"/>
                <Fld label="Killed"    type="number" value={r.killedQty} onChange={e=>updR(ri,'killedQty',e.target.value)} placeholder="0"/>
                <Fld label="Remarks"   value={r.remarks}  onChange={e=>updR(ri,'remarks',e.target.value)}  placeholder="Remarks..."/>
              </Grid>
              <div style={{ marginTop:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textSub,
                  textTransform:'uppercase', marginBottom:8 }}>Defect Breakdown</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {r.defects.map((d,di) => (
                    <div key={di} style={{ display:'flex', alignItems:'center', gap:6,
                      padding:'5px 10px', borderRadius:R.sm,
                      background:d.qty>0?C.dangerBg:C.white,
                      border:`1px solid ${d.qty>0?C.dangerBd:C.border}` }}>
                      <span style={{ fontSize:11, color:C.text }}>{d.label}</span>
                      <input type="number" value={d.qty||''} onChange={e=>updDef(ri,di,e.target.value)}
                        style={{ width:48, padding:'3px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                          fontWeight:700, color:d.qty>0?C.danger:C.text }}/>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop:8, display:'flex', justifyContent:'flex-end' }}>
                <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,i)=>i!==ri)}))}
                  style={{ background:C.dangerBg, border:`1px solid ${C.dangerBd}`, borderRadius:R.sm,
                    cursor:'pointer', color:C.danger, fontSize:11, padding:'5px 10px', fontWeight:700 }}>
                  Remove Row
                </button>
              </div>
            </div>
          ))}
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.wash}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-42 · WASHING QC REPORT  (Nir/Ws/04)
  //  Batch card-wise checking — machine & operator identification
  // ═══════════════════════════════════════════════════════════════════════
  function Ann42({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('washing');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'WASH_QC_REPORT', date:TODAY,
      style:filters.style||'', po:filters.po||'',
      buyer:filters.buyer||'', colour:filters.colour||'',
      shift:SHIFTS[0], jobCardNo:'', lotWeight:'', batchQty:0,
      shadeLabel:'', qualityPart:'', ckdQty:0,
      // defect counts
      sewingDefect:0, bledingDefect:0, otherDefect:0,
      okPcs:0, darkPcs:0, lightPcs:0, stricksSpotsQty:0,
      hourlyData: HOURS.map(h=>({ hour:h, checked:0, defective:0, remark:'' })),
      sigWManager:'', sigAqm:'', maintenance:'',
    });

    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updH = (i,k,v) => setF(f=>({...f,
      hourlyData:f.hourlyData.map((h,hi)=>hi===i?{...h,[k]:v}:h)}));
    const recs = (db.washing||[]).filter(r=>r.formType==='WASH_QC_REPORT');

    const totCkd  = F.hourlyData.reduce((s,h)=>s+(parseInt(h.checked)||0),0);
    const totDef  = F.hourlyData.reduce((s,h)=>s+(parseInt(h.defective)||0),0);
    const defPct  = totCkd>0?((totDef/totCkd)*100).toFixed(1):0;
    const autoRes = () => parseFloat(defPct)<=5?'PASS':'FAIL';

    const handleSave = async () => {
      await doSave('washing_qc_report',
        { ...F, totalChecked:totCkd, totalDefective:totDef, defectPct:defPct,
          result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-42 · Washing QC Report" color={C.wash}
          ref_="Nir/Ws/04 · Batch Card-wise — Machine & Operator Identification"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.wash}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.wash}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Washing QC Records" color={C.wash}/>
            <Tbl headers={['Date','Style','Job Card','Batch Qty','Checked','Defective','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.jobCardNo||'—',
                r.batchQty||'—',r.totalChecked||'—',r.totalDefective||'—',<Res result={r.result}/>])}
              emptyMsg="No washing QC records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Batch Card Details" color={C.wash}/>
              <Grid gap={14}>
                <Fld label="Date"         type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."    value={F.style}     onChange={e=>set('style',e.target.value)}     options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="PO No."       value={F.po}        onChange={e=>set('po',e.target.value)}        options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer"        value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
                <Fld label="Colour"       value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={['',...dbGet('colours',_FB_COLOURS)]}/>
                <Fld label="Shift"        value={F.shift}     onChange={e=>set('shift',e.target.value)}     options={SHIFTS}/>
                <Fld label="Job Card No." value={F.jobCardNo} onChange={e=>set('jobCardNo',e.target.value)} placeholder="Job card#..."/>
                <Fld label="Lot Weight"   value={F.lotWeight} onChange={e=>set('lotWeight',e.target.value)} placeholder="kg..."/>
                <Fld label="Batch Qty"    type="number" value={F.batchQty} onChange={e=>set('batchQty',e.target.value)} placeholder="0"/>
                <Fld label="Shade Label"  value={F.shadeLabel} onChange={e=>set('shadeLabel',e.target.value)} placeholder="Shade label..."/>
                <Fld label="Quality Part" value={F.qualityPart} onChange={e=>set('qualityPart',e.target.value)} placeholder="Part..."/>
                <Fld label="Checked Qty"  type="number" value={F.ckdQty} onChange={e=>set('ckdQty',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>

            <Card>
              <SH title="Shade & Defect Summary" color={C.wash}/>
              <Grid gap={14} min={130}>
                <Fld label="OK Pcs"    type="number" value={F.okPcs}   onChange={e=>set('okPcs',e.target.value)}   placeholder="0"/>
                <Fld label="Dark Pcs"  type="number" value={F.darkPcs} onChange={e=>set('darkPcs',e.target.value)} placeholder="0"/>
                <Fld label="Light Pcs" type="number" value={F.lightPcs} onChange={e=>set('lightPcs',e.target.value)} placeholder="0"/>
                <Fld label="Sewing Defect" type="number" value={F.sewingDefect} onChange={e=>set('sewingDefect',e.target.value)} placeholder="0"/>
                <Fld label="Bleeding Defect" type="number" value={F.bledingDefect} onChange={e=>set('bledingDefect',e.target.value)} placeholder="0"/>
                <Fld label="Stricks / Spots" type="number" value={F.stricksSpotsQty} onChange={e=>set('stricksSpotsQty',e.target.value)} placeholder="0"/>
                <Fld label="Other Defect" type="number" value={F.otherDefect} onChange={e=>set('otherDefect',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>

            <Card>
              <SH title="Hourly QC Status" color={C.wash}/>
              <HourlyTable data={F.hourlyData} onChange={updH} color={C.wash}/>
              <Grid gap={14} min={200} style={{ marginTop:12 }}>
                <Fld label="Sig. W.Manager"  value={F.sigWManager}  onChange={e=>set('sigWManager',e.target.value)}  placeholder="Name..."/>
                <Fld label="Sig. AQM"        value={F.sigAqm}       onChange={e=>set('sigAqm',e.target.value)}       placeholder="Name..."/>
                <Fld label="Maintenance"     value={F.maintenance}  onChange={e=>set('maintenance',e.target.value)}  placeholder="Name..."/>
              </Grid>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wash}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-43 · B/4 DRYER GMT INSPECTION  (Nir/Ws/05)
  //  After-wash garments check before dryer
  // ═══════════════════════════════════════════════════════════════════════
  function Ann43({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('washing');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'B4_DRYER', date:TODAY,
      style:filters.style||'', po:filters.po||'',
      buyer:filters.buyer||'', colour:filters.colour||'',
      dryerNo:'', lotCardNo:'', lotWeight:'', lotPcs:0,
      shadeMatchPm:'', okPcs:0, darkPcs:0, lightPcs:0,
      hourlyData: HOURS.map(h=>({ hour:h, checked:0, defective:0, remark:'' })),
      defects: WASHING_DEFECTS.map(d=>({ label:d, qty:0 })),
      remarks:'',
    });

    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updH = (i,k,v) => setF(f=>({...f,
      hourlyData:f.hourlyData.map((h,hi)=>hi===i?{...h,[k]:v}:h)}));
    const setDef = (i,v) => setF(f=>({...f,defects:f.defects.map((d,di)=>di===i?{...d,qty:v}:d)}));
    const recs = (db.washing||[]).filter(r=>r.formType==='B4_DRYER');

    const totChecked  = F.hourlyData.reduce((s,h)=>s+(parseInt(h.checked)||0),0);
    const totDefective= F.hourlyData.reduce((s,h)=>s+(parseInt(h.defective)||0),0);
    const defPct      = totChecked>0?((totDefective/totChecked)*100).toFixed(1):0;
    const autoRes     = () => parseFloat(defPct)<=5?'PASS':'FAIL';

    const handleSave = async () => {
      await doSave('washing_b4_dryer',
        { ...F, totalChecked:totChecked, totalDefective:totDefective, defPct,
          result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-43 · Before Dryer GMT Inspection" color={C.wash}
          ref_="Nir/Ws/05 · After wash garment check before dryer"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.wash}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.wash}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="B/4 Dryer Records" color={C.wash}/>
            <Tbl headers={['Date','Style','Dryer No','Lot Pcs','Checked','Defective','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.dryerNo||'—',r.lotPcs||'—',
                r.totalChecked||'—',r.totalDefective||'—',<Res result={r.result}/>])}
              emptyMsg="No B/4 dryer records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Lot Details" color={C.wash}/>
              <Grid gap={14}>
                <Fld label="Date"          type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."     value={F.style}     onChange={e=>set('style',e.target.value)}     options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="PO No."        value={F.po}        onChange={e=>set('po',e.target.value)}        options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer"         value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
                <Fld label="Colour"        value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={['',...dbGet('colours',_FB_COLOURS)]}/>
                <Fld label="Dryer No."     value={F.dryerNo}   onChange={e=>set('dryerNo',e.target.value)}   placeholder="Dryer#..."/>
                <Fld label="Lot Card No."  value={F.lotCardNo} onChange={e=>set('lotCardNo',e.target.value)} placeholder="Lot card#..."/>
                <Fld label="Lot Weight"    value={F.lotWeight} onChange={e=>set('lotWeight',e.target.value)} placeholder="kg..."/>
                <Fld label="Lot Pcs"       type="number" value={F.lotPcs}    onChange={e=>set('lotPcs',e.target.value)}    placeholder="0"/>
                <Fld label="Shade Match PM" value={F.shadeMatchPm} onChange={e=>set('shadeMatchPm',e.target.value)} options={['','YES','NO','HOLD']}/>
                <Fld label="OK Pcs"        type="number" value={F.okPcs}    onChange={e=>set('okPcs',e.target.value)}    placeholder="0"/>
                <Fld label="Dark Pcs"      type="number" value={F.darkPcs}  onChange={e=>set('darkPcs',e.target.value)}  placeholder="0"/>
                <Fld label="Light Pcs"     type="number" value={F.lightPcs} onChange={e=>set('lightPcs',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>

            <Card>
              <SH title="Hourly Inspection" color={C.wash}/>
              <HourlyTable data={F.hourlyData} onChange={updH} color={C.wash}/>
            </Card>

            <Card>
              <DefectGrid defects={F.defects} onChange={setDef} color={C.wash}/>
              <div style={{ marginTop:10 }}>
                <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
                  onChange={e=>set('remarks',e.target.value)} placeholder="Any observations before dryer..."/>
              </div>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wash}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-44 · DAILY QA AUDIT REPORT  (Nir/Ws/06)
  //  After-wash audit before sending to floor
  // ═══════════════════════════════════════════════════════════════════════
  function Ann44({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('washing');
    const [view, setView] = React.useState('form');

    const newRow = () => ({ id:uid(), batchCardNo:'', checkedQty:0, shade:'',
      handFeel:'', streakMark:'No', colorBleeding:'No', auditPF:'PASS',
      signWfQcSup:'' });
    const fresh  = () => ({
      id:uid(), formType:'WASH_DAILY_QA', date:TODAY,
      style:filters.style||'', po:filters.po||'',
      buyer:filters.buyer||'', colour:filters.colour||'',
      name:'', idNo:'', shift:SHIFTS[0],
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.washing||[]).filter(r=>r.formType==='WASH_DAILY_QA');

    const passCount = F.rows.filter(r=>r.auditPF==='PASS').length;
    const failCount = F.rows.filter(r=>r.auditPF==='FAIL').length;
    const autoRes   = () => failCount===0&&passCount>0?'PASS':failCount>0?'FAIL':'PENDING';

    const handleSave = async () => {
      await doSave('washing_daily_qa',
        { ...F, passCount, failCount, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-44 · Daily QA Audit Report" color={C.wash}
          ref_="Nir/Ws/06 · After wash audit check before sending to floor"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.wash}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.wash}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Daily QA Audit Records" color={C.wash}/>
            <Tbl headers={['Date','Style','Buyer','Colour','Batches','Pass','Fail','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.buyer||'—',r.colour||'—',
                (r.rows||[]).length,
                <Bdg color={C.success} bg={C.successBg}>{r.passCount||0}</Bdg>,
                <Bdg color={C.danger}  bg={C.dangerBg }>{r.failCount||0}</Bdg>,
                <Res result={r.result}/>])}
              emptyMsg="No daily QA records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Auditor Details" color={C.wash}/>
              <Grid gap={14}>
                <Fld label="Date"       type="date" value={F.date}   onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."  value={F.style}  onChange={e=>set('style',e.target.value)}  options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="PO No."     value={F.po}     onChange={e=>set('po',e.target.value)}     options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer"      value={F.buyer}  onChange={e=>set('buyer',e.target.value)}  options={['',...BUYERS]}/>
                <Fld label="Colour"     value={F.colour} onChange={e=>set('colour',e.target.value)} options={['',...dbGet('colours',_FB_COLOURS)]}/>
                <Fld label="Name"       value={F.name}   onChange={e=>set('name',e.target.value)}   placeholder="Auditor name..."/>
                <Fld label="ID No."     value={F.idNo}   onChange={e=>set('idNo',e.target.value)}   placeholder="ID#..."/>
                <Fld label="Shift"      value={F.shift}  onChange={e=>set('shift',e.target.value)}  options={SHIFTS}/>
              </Grid>
            </Card>

            <div style={{ display:'flex', gap:10 }}>
              <Bdg color={C.success} bg={C.successBg}>✓ {passCount} Pass</Bdg>
              <Bdg color={C.danger}  bg={C.dangerBg }>✗ {failCount} Fail</Bdg>
            </div>

            <Card>
              <SH title="Batch Audit Details" color={C.wash}
                action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wash}>＋ Add Batch</Btn>}/>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:750 }}>
                  <thead>
                    <tr style={{ background:C.slate50 }}>
                      {['Batch Card No','Checked Qty','Shade','Hand Feel','Streak Mark','Color Bleeding','Audit P/F','Sign',''].map(h=>(
                        <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                          fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {F.rows.map((r,i) => (
                      <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`,
                        background:r.auditPF==='FAIL'?C.dangerBg:r.auditPF==='PASS'?C.successBg:C.white }}>
                        <td style={{ padding:'5px 6px' }}>
                          <input value={r.batchCardNo} onChange={e=>updR(i,'batchCardNo',e.target.value)} placeholder="Batch#"
                            style={{ width:90, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                        <td style={{ padding:'5px 6px' }}>
                          <input type="number" value={r.checkedQty} onChange={e=>updR(i,'checkedQty',e.target.value)} placeholder="0"
                            style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                        </td>
                        <td style={{ padding:'5px 6px' }}>
                          <input value={r.shade} onChange={e=>updR(i,'shade',e.target.value)} placeholder="Shade"
                            style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                        {['handFeel','streakMark','colorBleeding'].map(k=>(
                          <td key={k} style={{ padding:'5px 6px' }}>
                            <select value={r[k]} onChange={e=>updR(i,k,e.target.value)}
                              style={{ width:80, padding:'5px', borderRadius:R.sm,
                                border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                              {k==='handFeel'
                                ?['OK','NG','SOFT','HARD'].map(s=><option key={s} value={s}>{s}</option>)
                                :['No','Yes'].map(s=><option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                        ))}
                        <td style={{ padding:'5px 8px' }}>
                          <select value={r.auditPF} onChange={e=>updR(i,'auditPF',e.target.value)}
                            style={{ padding:'5px 8px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                              fontSize:11, fontWeight:700, fontFamily:'inherit',
                              background:r.auditPF==='PASS'?C.successBg:C.dangerBg,
                              color:r.auditPF==='PASS'?C.success:C.danger }}>
                            {['PASS','FAIL'].map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:'5px 6px' }}>
                          <input value={r.signWfQcSup} onChange={e=>updR(i,'signWfQcSup',e.target.value)} placeholder="Sign..."
                            style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                        <td style={{ padding:'5px 6px' }}>
                          <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                            style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                              color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wash}/>
            </Card>
          </>
        )}
      </div>
    );
  }


  // ╔══════════════════════════════════════════════════════════════════════╗
  // ║   WRINKLE FREE FORMS  (Ann-45 → Ann-52)                             ║
  // ╚══════════════════════════════════════════════════════════════════════╝

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-45 · WF INTERNAL TRANSFER  (Nir/WF/01)
  //  Send garments from Wrinkle Free to Killed garments room
  // ═══════════════════════════════════════════════════════════════════════
  function Ann45({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const newRow = () => ({ id:uid(), orderRef:'', colour:'', totalReceived:0,
      totalReturned:0, overallRejection:0, receivedBy:'', checkedBy:'', authorizedBy:'' });
    const fresh  = () => ({
      id:uid(), formType:'WF_TRANSFER', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      wrinkleFree:'', remarks:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.wf||[]).filter(r=>r.formType==='WF_TRANSFER');

    const handleSave = async () => {
      await doSave('wf_internal_transfer',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-45 · WF Internal Transfer" color={C.wf}
          ref_="Nir/WF/01 · Send garments from Wrinkle Free to Killed GMT room"
          action={<Bdg color={C.wf}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Transfer Header" color={C.wf}/>
          <Grid gap={14}>
            <Fld label="Date"          type="date" value={F.date}        onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."     value={F.style}      onChange={e=>set('style',e.target.value)}      options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"         value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
            <Fld label="Wrinkle Free"  value={F.wrinkleFree} onChange={e=>set('wrinkleFree',e.target.value)} placeholder="WF section..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Transfer Details" color={C.wf}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wf}>＋ Add Row</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:750 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Order Ref','Colour','Total Received','Total Returned','Overall Rejection','Received By','Checked By','Authorized By',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                      fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.orderRef} onChange={e=>updR(i,'orderRef',e.target.value)} placeholder="Ref..." list="po-datalist"
                        style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.colour} onChange={e=>updR(i,'colour',e.target.value)}
                        style={{ width:90, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">Colour</option>
                        {COLOURS.map(c=><option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    {['totalReceived','totalReturned','overallRejection'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input type="number" value={r[k]} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                            fontSize:11, textAlign:'center',
                            background:k==='overallRejection'&&parseInt(r[k])>0?C.dangerBg:C.white }}/>
                      </td>
                    ))}
                    {['receivedBy','checkedBy','authorizedBy'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="Name..."
                          style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                          color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:10 }}>
            <Fld label="Remarks" type="textarea" rows={2} value={F.remarks}
              onChange={e=>set('remarks',e.target.value)} placeholder="Any transfer remarks..."/>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-46 · WRINKLE FREE REPORT SUMMARY  (Nir/WF/02)
  //  Order-wise defect compilation per colour
  // ═══════════════════════════════════════════════════════════════════════
  function Ann46({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const newRow = () => ({ id:uid(), colour:'', orderRef:'',
      totalRcvd:0, totalRework:0, totalRej:0,
      wfWash:0, other:0, ttlRej:0, todayRejPct:0,
      checkedBy:'', fingMana:'', qgm:'', proGm:'', qaGm:'' });
    const fresh  = () => ({
      id:uid(), formType:'WF_SUMMARY', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      unit:'UNIT-2',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>{
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      // auto totals
      const ttl=(parseInt(rows[i].wfWash)||0)+(parseInt(rows[i].other)||0);
      rows[i].ttlRej=ttl;
      const rcvd=parseInt(rows[i].totalRcvd)||1;
      rows[i].todayRejPct=((ttl/rcvd)*100).toFixed(1);
      return {...f,rows};
    });
    const recs = (db.wf||[]).filter(r=>r.formType==='WF_SUMMARY');

    const handleSave = async () => {
      await doSave('wf_report_summary',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-46 · Wrinkle Free Report Summary" color={C.wf}
          ref_="Nir/WF/02 · Order-wise defect compilation per colour"
          action={<Bdg color={C.wf}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.wf}/>
          <Grid gap={14}>
            <Fld label="Date"      type="date" value={F.date}  onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style}  onChange={e=>set('style',e.target.value)}  options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"     value={F.buyer}  onChange={e=>set('buyer',e.target.value)}  options={['',...BUYERS]}/>
            <Fld label="Unit"      value={F.unit}   onChange={e=>set('unit',e.target.value)}   options={['UNIT-1','UNIT-2','UNIT-3']}/>
          </Grid>
        </Card>

        <Card>
          <SH title="Colour-wise Summary" color={C.wf}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wf}>＋ Add Row</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:850 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Colour','Order Ref','Total Rcvd','Total Rework','Total Rej','W/F & Wash','Other','TTL Rej','Today Rej %','Checked By',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                      fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`,
                    background:parseFloat(r.todayRejPct)>5?C.dangerBg:C.white }}>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.colour} onChange={e=>updR(i,'colour',e.target.value)}
                        style={{ width:90, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">Colour</option>
                        {COLOURS.map(c=><option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.orderRef} onChange={e=>updR(i,'orderRef',e.target.value)} placeholder="Ref..." list="po-datalist"
                        style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    {['totalRcvd','totalRework','totalRej','wfWash','other'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{ width:65, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 8px', textAlign:'center', fontWeight:700, color:C.danger }}>{r.ttlRej||0}</td>
                    <td style={{ padding:'5px 8px', textAlign:'center', fontWeight:700,
                      color:parseFloat(r.todayRejPct)>5?C.danger:C.success }}>{r.todayRejPct||0}%</td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.checkedBy||''} onChange={e=>updR(i,'checkedBy',e.target.value)} placeholder="Name..."
                        style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                          color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-47 · PRE-OVEN INSPECTION REPORT  (Nir/WF/03)
  //  Customer-wise after-press garment checking & defect recording
  // ═══════════════════════════════════════════════════════════════════════
  function Ann47({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'WF_PRE_OVEN', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      colour:filters.colour||'', shift:SHIFTS[0],
      orderRef:'', totalInspected:0,
      // defect counts
      defects: WF_DEFECTS_PRE.map(d=>({ label:d, qty:0 })),
      noOfDefects:0,
      qcSupervisor:'', productionSupervisor:'',
      remarks:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set    = (k,v) => setF(f=>({...f,[k]:v}));
    const setDef = (i,v) => setF(f=>({...f,defects:f.defects.map((d,di)=>di===i?{...d,qty:v}:d)}));
    const recs   = (db.wf||[]).filter(r=>r.formType==='WF_PRE_OVEN');

    const totalDef = F.defects.reduce((s,d)=>s+(d.qty||0),0);
    const defPct   = (parseInt(F.totalInspected)||0)>0
      ? ((totalDef/(parseInt(F.totalInspected)))*100).toFixed(1) : 0;
    const autoRes  = () => totalDef===0?'PASS':parseFloat(defPct)<=5?'HOLD':'FAIL';

    const handleSave = async () => {
      await doSave('wf_pre_oven',
        { ...F, totalDefects:totalDef, defPct, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-47 · Pre-Oven Inspection Report" color={C.wf}
          ref_="Nir/WF/03 · Customer-wise after press garment checking"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.wf}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.wf}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Pre-Oven Records" color={C.wf}/>
            <Tbl headers={['Date','Style','Buyer','Colour','Inspected','Total Defects','Def %','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',r.buyer||'—',r.colour||'—',
                r.totalInspected||'—',r.totalDefects||0,`${r.defPct||0}%`,<Res result={r.result}/>])}
              emptyMsg="No pre-oven records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Inspection Header" color={C.wf}/>
              <Grid gap={14}>
                <Fld label="Date"         type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."    value={F.style}     onChange={e=>set('style',e.target.value)}     options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Buyer"        value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
                <Fld label="Colour"       value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={['',...dbGet('colours',_FB_COLOURS)]}/>
                <Fld label="Shift"        value={F.shift}     onChange={e=>set('shift',e.target.value)}     options={SHIFTS}/>
                <Fld label="Order Ref"    value={F.orderRef}  onChange={e=>set('orderRef',e.target.value)}  options={["", ...dbGet("poNumbers",[])]} placeholder="Order/Ref..."/>
                <Fld label="Total Inspected" type="number" value={F.totalInspected}
                  onChange={e=>set('totalInspected',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>

            <Card>
              <DefectGrid defects={F.defects} onChange={setDef} color={C.wf}/>
              <div style={{ marginTop:10, padding:'10px 14px', background:C.slate50,
                borderRadius:R.md, display:'flex', gap:20 }}>
                <span style={{ fontSize:12 }}>Total Defects: <strong style={{ color:C.danger }}>{totalDef}</strong></span>
                <span style={{ fontSize:12 }}>Defect %: <strong style={{ color:parseFloat(defPct)>5?C.danger:C.success }}>{defPct}%</strong></span>
              </div>
            </Card>

            <Card>
              <Grid gap={14}>
                <Fld label="QC Supervisor"           value={F.qcSupervisor}          onChange={e=>set('qcSupervisor',e.target.value)}          placeholder="Name..."/>
                <Fld label="Production Supervisor"   value={F.productionSupervisor}  onChange={e=>set('productionSupervisor',e.target.value)}  placeholder="Name..."/>
              </Grid>
              <div style={{ marginTop:10 }}>
                <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
                  onChange={e=>set('remarks',e.target.value)} placeholder="Pre-oven observations..."/>
              </div>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-48 · INDIVIDUAL DHU REPORT  (Nir/WF/04)
  //  Machine-wise DHU to analyze operator performance
  // ═══════════════════════════════════════════════════════════════════════
  function Ann48({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const newRow = () => ({
      id:uid(), inspectorName:'', idNo:'', buyer:'', shift:SHIFTS[0],
      legged:0, topper:0, mcNob:'', totalInspected:0, totalDefects:0, dhu:0,
    });
    const fresh  = () => ({
      id:uid(), formType:'WF_INDIVIDUAL_DHU', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      productionIncharge:'',
      rows:[newRow(), newRow(), newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>{
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      const insp=(parseInt(rows[i].totalInspected)||0);
      const def=(parseInt(rows[i].totalDefects)||0);
      rows[i].dhu=insp>0?((def/insp)*100).toFixed(2):0;
      return {...f,rows};
    });
    const recs = (db.wf||[]).filter(r=>r.formType==='WF_INDIVIDUAL_DHU');

    const handleSave = async () => {
      await doSave('wf_individual_dhu',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-48 · Individual DHU Report" color={C.wf}
          ref_="Nir/WF/04 · Machine-wise DHU — Operator Performance Analysis"
          action={<Bdg color={C.wf}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.wf}/>
          <Grid gap={14}>
            <Fld label="Date"                type="date" value={F.date}            onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."           value={F.style}          onChange={e=>set('style',e.target.value)}          options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"               value={F.buyer}          onChange={e=>set('buyer',e.target.value)}          options={['',...BUYERS]}/>
            <Fld label="Production Incharge" value={F.productionIncharge} onChange={e=>set('productionIncharge',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Inspector-wise DHU Data" color={C.wf}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wf}>＋ Add Inspector</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:750 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Inspector Name','ID No.','Buyer','Shift','Legged','Topper','M/C No.','Total Insp.','Total Def.','DHU %',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                      fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'center' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`,
                    background:parseFloat(r.dhu)>5?C.dangerBg:parseFloat(r.dhu)>0?C.successBg:C.white }}>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.inspectorName} onChange={e=>updR(i,'inspectorName',e.target.value)} placeholder="Name..."
                        style={{ width:100, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.idNo} onChange={e=>updR(i,'idNo',e.target.value)} placeholder="ID..."
                        style={{ width:60, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.buyer} onChange={e=>updR(i,'buyer',e.target.value)}
                        style={{ width:90, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">—</option>
                        {BUYERS.map(b=><option key={b} value={b}>{b}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.shift} onChange={e=>updR(i,'shift',e.target.value)}
                        style={{ width:75, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        {SHIFTS.map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    {['legged','topper'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{ width:60, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.mcNob} onChange={e=>updR(i,'mcNob',e.target.value)} placeholder="M/C#"
                        style={{ width:60, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    {['totalInspected','totalDefects'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{ width:65, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 8px', textAlign:'center', fontWeight:800,
                      color:parseFloat(r.dhu)>5?C.danger:C.success }}>{r.dhu}%</td>
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                          color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-49 · PRE/SPRAY/DRY/POST PRODUCTION INSPECTION  (Nir/WF/05)
  //  Buyer-wise defect findings before pressing & post production
  // ═══════════════════════════════════════════════════════════════════════
  function Ann49({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'WF_PRE_SPRAY_POST', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      colour:filters.colour||'', shift:SHIFTS[0],
      stage:'PRE', // PRE | SPRAY | DRY | POST
      orderRef:'', totalInspected:0,
      preDefects:   WF_DEFECTS_PRE.map(d=>({ label:d, qty:0 })),
      postDefects:  WF_DEFECTS_POST.map(d=>({ label:d, qty:0 })),
      totalAlteration:0, supervisor:'', qualityIncharge:'', remarks:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set      = (k,v) => setF(f=>({...f,[k]:v}));
    const setPreD  = (i,v) => setF(f=>({...f,preDefects:f.preDefects.map((d,di)=>di===i?{...d,qty:v}:d)}));
    const setPostD = (i,v) => setF(f=>({...f,postDefects:f.postDefects.map((d,di)=>di===i?{...d,qty:v}:d)}));
    const recs     = (db.wf||[]).filter(r=>r.formType==='WF_PRE_SPRAY_POST');

    const totPre   = F.preDefects.reduce((s,d)=>s+(d.qty||0),0);
    const totPost  = F.postDefects.reduce((s,d)=>s+(d.qty||0),0);
    const autoRes  = () => (totPre+totPost)===0?'PASS':(totPre+totPost)<=5?'HOLD':'FAIL';

    const handleSave = async () => {
      await doSave('wf_pre_spray_post',
        { ...F, totPreDefects:totPre, totPostDefects:totPost,
          result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-49 · Pre/Spray/Dry/Post Production Inspection" color={C.wf}
          ref_="Nir/WF/05 · Buyer-wise defect findings before & after pressing"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.wf}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.wf}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Pre/Post Records" color={C.wf}/>
            <Tbl headers={['Date','Style','Stage','Buyer','Inspected','Pre Def.','Post Def.','Result']}
              rows={recs.map(r=>[r.date,r.style||'—',
                <Bdg color={C.wf}>{r.stage||'PRE'}</Bdg>,
                r.buyer||'—',r.totalInspected||'—',
                r.totPreDefects||0,r.totPostDefects||0,<Res result={r.result}/>])}
              emptyMsg="No records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Inspection Header" color={C.wf}/>
              <Grid gap={14}>
                <Fld label="Date"         type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No."    value={F.style}     onChange={e=>set('style',e.target.value)}     options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Buyer"        value={F.buyer}     onChange={e=>set('buyer',e.target.value)}     options={['',...BUYERS]}/>
                <Fld label="Colour"       value={F.colour}    onChange={e=>set('colour',e.target.value)}    options={['',...dbGet('colours',_FB_COLOURS)]}/>
                <Fld label="Stage"        value={F.stage}     onChange={e=>set('stage',e.target.value)}     options={['PRE','SPRAY','DRY','POST']}/>
                <Fld label="Shift"        value={F.shift}     onChange={e=>set('shift',e.target.value)}     options={SHIFTS}/>
                <Fld label="Order Ref"    value={F.orderRef}  onChange={e=>set('orderRef',e.target.value)}  options={["", ...dbGet("poNumbers",[])]} placeholder="Order/Ref..."/>
                <Fld label="Total Inspected" type="number" value={F.totalInspected} onChange={e=>set('totalInspected',e.target.value)} placeholder="0"/>
                <Fld label="Total Alteration" type="number" value={F.totalAlteration} onChange={e=>set('totalAlteration',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>

            <Card>
              <SH title="Pre-Production Defects" color={C.wf}/>
              <DefectGrid defects={F.preDefects} onChange={setPreD} color={C.wf}/>
            </Card>

            <Card>
              <SH title="Post-Production Defects" color={C.wf}/>
              <DefectGrid defects={F.postDefects} onChange={setPostD} color={C.wf}/>
            </Card>

            <Card>
              <Grid gap={14}>
                <Fld label="Supervisor"        value={F.supervisor}       onChange={e=>set('supervisor',e.target.value)}       placeholder="Name..."/>
                <Fld label="Quality Incharge"  value={F.qualityIncharge}  onChange={e=>set('qualityIncharge',e.target.value)}  placeholder="Name..."/>
              </Grid>
              <div style={{ marginTop:10 }}>
                <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
                  onChange={e=>set('remarks',e.target.value)} placeholder="Observations..."/>
              </div>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-50 · AFTER OVEN AUDIT REPORT  (Nir/WF/06)
  //  After oven findings — signature record for responsible operator
  // ═══════════════════════════════════════════════════════════════════════
  function Ann50({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const newRow = () => ({ id:uid(), legNo:'', topNo:'', problem:'', operator:'',
      responsibleQC:'', auditor:'', qcSup:'', aqm:'' });
    const fresh  = () => ({
      id:uid(), formType:'WF_AFTER_OVEN', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      colour:filters.colour||'', shift:SHIFTS[0], idNo:'', name:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.wf||[]).filter(r=>r.formType==='WF_AFTER_OVEN');

    const handleSave = async () => {
      await doSave('wf_after_oven',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-50 · After Oven Audit Report" color={C.wf}
          ref_="Nir/WF/06 · After oven findings & operator signature record"
          action={<Bdg color={C.wf}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Audit Header" color={C.wf}/>
          <Grid gap={14}>
            <Fld label="Date"    type="date" value={F.date}  onChange={e=>set('date',e.target.value)}/>
            <Fld label="Name"    value={F.name}   onChange={e=>set('name',e.target.value)}   placeholder="Auditor name..."/>
            <Fld label="ID No."  value={F.idNo}   onChange={e=>set('idNo',e.target.value)}   placeholder="ID#..."/>
            <Fld label="Shift"   value={F.shift}  onChange={e=>set('shift',e.target.value)}  options={SHIFTS}/>
            <Fld label="Style"   value={F.style}  onChange={e=>set('style',e.target.value)}  placeholder="Style..."/>
            <Fld label="Buyer"   value={F.buyer}  onChange={e=>set('buyer',e.target.value)}  options={['',...BUYERS]}/>
            <Fld label="Colour"  value={F.colour} onChange={e=>set('colour',e.target.value)} options={['',...dbGet('colours',_FB_COLOURS)]}/>
          </Grid>
        </Card>

        <Card>
          <SH title="Defect & Responsibility Log" color={C.wf}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wf}>＋ Add Row</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:700 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Leg No.','Top No.','Problem Found','Operator','Responsible QC','Auditor','QC Sup','AQM',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                      fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    {['legNo','topNo'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="#..."
                          style={{ width:60, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.problem} onChange={e=>updR(i,'problem',e.target.value)}
                        style={{ width:'100%', minWidth:140, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit',
                          background:r.problem?C.dangerBg:C.white, color:r.problem?C.danger:C.text }}>
                        <option value="">Select problem...</option>
                        {[...WF_DEFECTS_PRE,...WF_DEFECTS_POST].map(d=><option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    {['operator','responsibleQC','auditor','qcSup','aqm'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="Name..."
                          style={{ width:75, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                          color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-51 · DAILY QA DISPATCH AUDIT  (Nir/WF/07)
  //  Audit check report before sending to Finishing
  // ═══════════════════════════════════════════════════════════════════════
  function Ann51({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const newRow = () => ({ id:uid(), batchCardNo:'', checkedQty:0, shade:'',
      handFeel:'OK', streakMark:'No', colorBleeding:'No',
      pocketing:'No', patchMark:'No', auditPF:'PASS', signWfQcSup:'' });
    const fresh  = () => ({
      id:uid(), formType:'WF_DISPATCH_AUDIT', date:TODAY,
      style:filters.style||'', po:filters.po||'',
      buyer:filters.buyer||'', colour:filters.colour||'',
      name:'', idNo:'', shift:SHIFTS[0],
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f=>({...f,[k]:v}));
    const updR = (i,k,v) => setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs = (db.wf||[]).filter(r=>r.formType==='WF_DISPATCH_AUDIT');
    const passCount = F.rows.filter(r=>r.auditPF==='PASS').length;
    const failCount = F.rows.filter(r=>r.auditPF==='FAIL').length;
    const autoRes   = () => failCount===0&&passCount>0?'PASS':failCount>0?'FAIL':'PENDING';

    const handleSave = async () => {
      await doSave('wf_dispatch_audit',
        { ...F, passCount, failCount, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    const TOGGLE_FIELDS = [
      { k:'streakMark',     label:'Streak Mark'      },
      { k:'colorBleeding',  label:'Color Bleeding'   },
      { k:'pocketing',      label:'Pocketing'        },
      { k:'patchMark',      label:'Patch Mark'       },
    ];

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-51 · Daily QA Dispatch Audit — WF" color={C.wf}
          ref_="Nir/WF/07 · Audit before sending to Finishing"
          action={<Bdg color={C.wf}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Auditor Details" color={C.wf}/>
          <Grid gap={14}>
            <Fld label="Date"   type="date" value={F.date}   onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style"  value={F.style}  onChange={e=>set('style',e.target.value)}  placeholder="Style..."/>
            <Fld label="PO No." value={F.po}     onChange={e=>set('po',e.target.value)}     options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
            <Fld label="Buyer"  value={F.buyer}  onChange={e=>set('buyer',e.target.value)}  options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={['',...dbGet('colours',_FB_COLOURS)]}/>
            <Fld label="Name"   value={F.name}   onChange={e=>set('name',e.target.value)}   placeholder="Auditor name..."/>
            <Fld label="ID No." value={F.idNo}   onChange={e=>set('idNo',e.target.value)}   placeholder="ID#..."/>
            <Fld label="Shift"  value={F.shift}  onChange={e=>set('shift',e.target.value)}  options={SHIFTS}/>
          </Grid>
        </Card>

        <div style={{ display:'flex', gap:10 }}>
          <Bdg color={C.success} bg={C.successBg}>✓ {passCount} Pass</Bdg>
          <Bdg color={C.danger}  bg={C.dangerBg }>✗ {failCount} Fail</Bdg>
        </div>

        <Card>
          <SH title="Batch Dispatch Audit" color={C.wf}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.wf}>＋ Add Batch</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:800 }}>
              <thead>
                <tr style={{ background:C.slate50 }}>
                  {['Batch Card#','Checked','Shade','Hand Feel',
                    ...TOGGLE_FIELDS.map(f=>f.label),'Audit P/F','Sign',''].map(h=>(
                    <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                      fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`,
                    background:r.auditPF==='FAIL'?C.dangerBg:r.auditPF==='PASS'?C.successBg:C.white }}>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.batchCardNo} onChange={e=>updR(i,'batchCardNo',e.target.value)} placeholder="Batch#"
                        style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input type="number" value={r.checkedQty} onChange={e=>updR(i,'checkedQty',e.target.value)} placeholder="0"
                        style={{ width:65, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.shade} onChange={e=>updR(i,'shade',e.target.value)} placeholder="Shade"
                        style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={r.handFeel} onChange={e=>updR(i,'handFeel',e.target.value)}
                        style={{ width:75, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        {['OK','NG','SOFT','HARD'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    {TOGGLE_FIELDS.map(tf=>(
                      <td key={tf.k} style={{ padding:'5px 6px', textAlign:'center' }}>
                        <button onClick={()=>updR(i,tf.k,r[tf.k]==='Yes'?'No':'Yes')}
                          style={{ padding:'4px 10px', borderRadius:R.sm, fontSize:11, fontWeight:700,
                            cursor:'pointer', border:'none',
                            background:r[tf.k]==='Yes'?C.dangerBg:C.successBg,
                            color:r[tf.k]==='Yes'?C.danger:C.success }}>
                          {r[tf.k]}
                        </button>
                      </td>
                    ))}
                    <td style={{ padding:'5px 8px' }}>
                      <select value={r.auditPF} onChange={e=>updR(i,'auditPF',e.target.value)}
                        style={{ padding:'5px 8px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                          fontSize:11, fontWeight:700, fontFamily:'inherit',
                          background:r.auditPF==='PASS'?C.successBg:C.dangerBg,
                          color:r.auditPF==='PASS'?C.success:C.danger }}>
                        {['PASS','FAIL'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.signWfQcSup} onChange={e=>updR(i,'signWfQcSup',e.target.value)} placeholder="Sign..."
                        style={{ width:75, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                          color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-52 · WF PROCESS CONTROL REGISTER  (Nir/WF/08)
  //  All-over process control & records keeping for Wrinkle Free dept
  // ═══════════════════════════════════════════════════════════════════════
  function Ann52({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('wf');
    const fresh = () => ({
      id:uid(), formType:'WF_PROCESS_CTRL', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      unit:'', shift:SHIFTS[0],
      checks: WF_PROCESS_CHECKS.map(l=>({ label:l, ok:null, remark:'' })),
      // machine readings
      ovenTemp:'', sprayPressure:'', conveyorSpeed:'',
      // daily summary
      totalReceived:0, totalProcessed:0, totalRejected:0, totalKilled:0,
      productionHead:'', qualityGM:'', remarks:'',
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set    = (k,v) => setF(f=>({...f,[k]:v}));
    const setChk = (i,ok) => setF(f=>({...f,checks:f.checks.map((c,ci)=>ci===i?{...c,ok}:c)}));
    const setRmk = (i,r)  => setF(f=>({...f,checks:f.checks.map((c,ci)=>ci===i?{...c,remark:r}:c)}));
    const recs   = (db.wf||[]).filter(r=>r.formType==='WF_PROCESS_CTRL');
    const ngCount= F.checks.filter(c=>c.ok===false).length;
    const okCount= F.checks.filter(c=>c.ok===true).length;
    const autoRes= () => ngCount===0&&okCount>0?'PASS':ngCount>0?'FAIL':'PENDING';

    const handleSave = async () => {
      await doSave('wf_process_control',
        { ...F, result:autoRes(), savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-52 · WF Process Control Register" color={C.wf}
          ref_="Nir/WF/08 · All-over process control & records for Wrinkle Free"
          action={<Bdg color={C.wf}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Machine & Order Details" color={C.wf}/>
          <Grid gap={14}>
            <Fld label="Date"           type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."      value={F.style}        onChange={e=>set('style',e.target.value)}        options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"          value={F.buyer}        onChange={e=>set('buyer',e.target.value)}        options={['',...BUYERS]}/>
            <Fld label="Unit"           value={F.unit}         onChange={e=>set('unit',e.target.value)}         options={['UNIT-1','UNIT-2','UNIT-3']}/>
            <Fld label="Shift"          value={F.shift}        onChange={e=>set('shift',e.target.value)}        options={SHIFTS}/>
            <Fld label="Oven Temp (°C)" type="number" value={F.ovenTemp}      onChange={e=>set('ovenTemp',e.target.value)}      placeholder="0"/>
            <Fld label="Spray Pressure" type="number" value={F.sprayPressure} onChange={e=>set('sprayPressure',e.target.value)} placeholder="0"/>
            <Fld label="Conveyor Speed" type="number" value={F.conveyorSpeed} onChange={e=>set('conveyorSpeed',e.target.value)} placeholder="0"/>
          </Grid>
        </Card>

        <Card>
          <SH title="Daily Production Summary" color={C.wf}/>
          <Grid gap={14} min={140}>
            <Fld label="Total Received"  type="number" value={F.totalReceived}  onChange={e=>set('totalReceived',e.target.value)}  placeholder="0"/>
            <Fld label="Total Processed" type="number" value={F.totalProcessed} onChange={e=>set('totalProcessed',e.target.value)} placeholder="0"/>
            <Fld label="Total Rejected"  type="number" value={F.totalRejected}  onChange={e=>set('totalRejected',e.target.value)}  placeholder="0"/>
            <Fld label="Total Killed"    type="number" value={F.totalKilled}    onChange={e=>set('totalKilled',e.target.value)}    placeholder="0"/>
          </Grid>
        </Card>

        <Card>
          <SH title="Process Control Checklist" color={C.wf}
            action={
              <div style={{ display:'flex', gap:8 }}>
                <Bdg color={C.success} bg={C.successBg}>{okCount} OK</Bdg>
                <Bdg color={C.danger}  bg={C.dangerBg }>{ngCount} NG</Bdg>
              </div>
            }/>
          {F.checks.map((c,i) => (
            <ChkRow key={i} label={c.label} ok={c.ok} onOk={v=>setChk(i,v)}
              remark={c.remark} onRemark={r=>setRmk(i,r)}/>
          ))}
        </Card>

        <Card>
          <SH title="Sign-off" color={C.wf}/>
          <Grid gap={14}>
            <Fld label="Production Head" value={F.productionHead} onChange={e=>set('productionHead',e.target.value)} placeholder="Name..."/>
            <Fld label="Quality GM"      value={F.qualityGM}      onChange={e=>set('qualityGM',e.target.value)}      placeholder="Name..."/>
          </Grid>
          <div style={{ marginTop:10 }}>
            <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
              onChange={e=>set('remarks',e.target.value)} placeholder="Overall process control observations..."/>
          </div>
          <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave} color={C.wf}/>
        </Card>
      </div>
    );
  }


  // ═══════════════════════════════════════════════════════════════════════
  //  WASHING DEPT SHELL
  // ═══════════════════════════════════════════════════════════════════════
  const WASH_SUB = [
    { id:'ann39', label:'Ann-39 B/4 Wash Audit',    icon:'🧪', Comp:Ann39, ft:'B4_WASH_AUDIT'      },
    { id:'ann40', label:'Ann-40 Corrective Action', icon:'🔧', Comp:Ann40, ft:'CORRECTIVE_ACTION'   },
    { id:'ann41', label:'Ann-41 Defect GMT Details',icon:'📋', Comp:Ann41, ft:'DEFECT_GMT'          },
    { id:'ann42', label:'Ann-42 Washing QC Report', icon:'🔍', Comp:Ann42, ft:'WASH_QC_REPORT'      },
    { id:'ann43', label:'Ann-43 B/4 Dryer Insp.',   icon:'💨', Comp:Ann43, ft:'B4_DRYER'           },
    { id:'ann44', label:'Ann-44 Daily QA Audit',    icon:'📊', Comp:Ann44, ft:'WASH_DAILY_QA'       },
  ];

  function WashingDept({ db, setDb, filters }) {
    const [active, setActive] = React.useState('ann39');
    const counts = WASH_SUB.reduce((acc,f) => ({
      ...acc, [f.id]:(db.washing||[]).filter(r=>r.formType===f.ft).length,
    }), {});
    const ActiveComp = WASH_SUB.find(f=>f.id===active)?.Comp;

    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
          overflowX:'auto', flexShrink:0 }}>
          <div style={{ display:'flex', paddingTop:4, paddingLeft:8, minWidth:'max-content' }}>
            {WASH_SUB.map(f => (
              <button key={f.id} onClick={() => setActive(f.id)} style={{
                padding:'10px 14px', fontSize:12,
                fontWeight: active===f.id ? 700 : 400,
                color: active===f.id ? C.wash : C.textSub,
                background:'transparent', border:'none', cursor:'pointer',
                borderBottom: active===f.id ? `2.5px solid ${C.wash}` : '2.5px solid transparent',
                display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
              }}>
                <span>{f.icon}</span>{f.label}
                {counts[f.id] > 0 && (
                  <span style={{ background:C.wash+'22', color:C.wash,
                    padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>
                    {counts[f.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto' }}>
          {ActiveComp && <ActiveComp db={db} setDb={setDb} filters={filters}/>}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  WRINKLE FREE DEPT SHELL
  // ═══════════════════════════════════════════════════════════════════════
  const WF_SUB = [
    { id:'ann45', label:'Ann-45 Internal Transfer',   icon:'🔄', Comp:Ann45, ft:'WF_TRANSFER'        },
    { id:'ann46', label:'Ann-46 WF Report Summary',   icon:'📊', Comp:Ann46, ft:'WF_SUMMARY'         },
    { id:'ann47', label:'Ann-47 Pre-Oven Inspection', icon:'🔍', Comp:Ann47, ft:'WF_PRE_OVEN'        },
    { id:'ann48', label:'Ann-48 Individual DHU',      icon:'📉', Comp:Ann48, ft:'WF_INDIVIDUAL_DHU'  },
    { id:'ann49', label:'Ann-49 Pre/Spray/Post',      icon:'💨', Comp:Ann49, ft:'WF_PRE_SPRAY_POST'  },
    { id:'ann50', label:'Ann-50 After Oven Audit',    icon:'🔥', Comp:Ann50, ft:'WF_AFTER_OVEN'      },
    { id:'ann51', label:'Ann-51 Dispatch Audit',      icon:'🚚', Comp:Ann51, ft:'WF_DISPATCH_AUDIT'  },
    { id:'ann52', label:'Ann-52 Process Control',     icon:'⚙️', Comp:Ann52, ft:'WF_PROCESS_CTRL'   },
  ];

  function WFDept({ db, setDb, filters }) {
    const [active, setActive] = React.useState('ann47');
    const counts = WF_SUB.reduce((acc,f) => ({
      ...acc, [f.id]:(db.wf||[]).filter(r=>r.formType===f.ft).length,
    }), {});
    const ActiveComp = WF_SUB.find(f=>f.id===active)?.Comp;

    return (
      <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
          overflowX:'auto', flexShrink:0 }}>
          <div style={{ display:'flex', paddingTop:4, paddingLeft:8, minWidth:'max-content' }}>
            {WF_SUB.map(f => (
              <button key={f.id} onClick={() => setActive(f.id)} style={{
                padding:'10px 14px', fontSize:12,
                fontWeight: active===f.id ? 700 : 400,
                color: active===f.id ? C.wf : C.textSub,
                background:'transparent', border:'none', cursor:'pointer',
                borderBottom: active===f.id ? `2.5px solid ${C.wf}` : '2.5px solid transparent',
                display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
              }}>
                <span>{f.icon}</span>{f.label}
                {counts[f.id] > 0 && (
                  <span style={{ background:C.wf+'22', color:C.wf,
                    padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>
                    {counts[f.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto' }}>
          {ActiveComp && <ActiveComp db={db} setDb={setDb} filters={filters}/>}
        </div>
      </div>
    );
  }

  // ── REGISTER BOTH ─────────────────────────────────────────────────────────
  DEPT_REGISTRY['washing'] = WashingDept;
  DEPT_REGISTRY['wf']      = WFDept;
  console.log('[QMS] Dept-05 Washing registered ✓  (Ann-39 → Ann-44, 6 forms)');
  console.log('[QMS] Dept-06 Wrinkle Free registered ✓  (Ann-45 → Ann-52, 8 forms)');

})();

// ════════════════════════════════════════════════════════════════════════════
//  DEPT-07 · FINISHING · Ann-53 → Ann-71  (19 forms)
//  HOW TO USE: Paste this entire block at the bottom of your file,
//  below the Washing/WF block  (below the last line: })(); )
//
//  Ann-53  Daily Finishing Quality Report         Nir/Fin-Qc/01 → finishing_daily_quality
//  Ann-54  Finishing Process Wise DHU             Nir/Fin-Qc/02 → finishing_process_dhu
//  Ann-55  QC Finishing Major 3 Defects           Nir/Fin-Qc/03 → finishing_major3
//  Ann-56  Daily Status Measurement Inspection    Nir/Fin-Qc/04 → finishing_meas_status
//  Ann-57  Finishing Dept Measurement Inspection  Nir/Fin-Qc/06 → finishing_meas_insp
//  Ann-58  Hourly Measurement Inspection          Nir/Fin-Qc/07 → finishing_hourly_meas
//  Ann-59  Top Side / Inside DHU Report           Nir/Fin-Qc/08 → finishing_topside_dhu
//  Ann-60  Finishing Quality Report Supervisor    Nir/Fin-Qc/09 → finishing_qc_supervisor
//  Ann-61  QC Manager Summary                     Nir/Fin-Qc/10 → finishing_qc_manager
//  Ann-62  Hourly Audit Report Finished Goods     Nir/Fin-Qa/01 → finishing_hourly_audit
//  Ann-63  Summary of Final Audit Finishing       Nir/Fin-Qa/02 → finishing_audit_summary
//  Ann-64  Final / Pre-Final Inspection Report    Nir/Fin-Qa/03 → finishing_final_insp
//  Ann-65  CTN Audit Report Summary               Nir/Fin-Qa/04 → finishing_ctn_audit
//  Ann-66  Hourly Audit Status                    Nir/Fin-Qa/05 → finishing_hourly_status
//  Ann-67  Hourly Audit Production Status         Nir/Fin-Qa/06 → finishing_hourly_prod
//  Ann-68  Inline / Pre-Final / Final Inspection  Nir/Fin-Qa/07 → finishing_inline_final
//  Ann-69  Process Control Chart / Fit Test       Nir/Fin-Qa/08 → finishing_fit_test
//  Ann-70  Plant/Field Source Audit — Bottoms     Nir/Fin-Qa/09 → finishing_source_audit
//  Ann-71  Lot Fail % Line/CTN Audit              Nir/Fin-Qa/10 → finishing_lot_fail
// ════════════════════════════════════════════════════════════════════════════

(function registerFinishing() {

  // ── palette ───────────────────────────────────────────────────────────────
  const C = {
    bg:'#0D1B2A', white:'#FFFFFF',
    slate50:'#F8F9FA', slate100:'#F4F6F8',
    border:'#DEE2E6', text:'#1A2A3A', textSub:'#6C757D', textMuted:'#ADB5BD',
    fin:'#E65100',    // finishing amber
    success:'#2E7D32', successBg:'#E8F5E9', successBd:'#A5D6A7',
    danger :'#C62828', dangerBg :'#FFEBEE', dangerBd :'#FFCDD2',
    warning:'#E65100', warningBg:'#FFF8E1', warningBd:'#FFE082',
    accent :'#00897B',
  };
  const R   = { sm:'6px', md:'8px', lg:'12px' };
  const API = 'https://nirvikalp.integrashion.com/api/save_universal.php';
  const TODAY = new Date().toISOString().split('T')[0];
  const HOURS = ['08:00-09:00','09:00-10:00','10:00-11:00','11:00-12:00',
                 '12:00-01:00','01:00-02:00','02:00-03:00','03:00-04:00',
                 '04:00-05:00','05:00-06:00','06:00-07:00'];

  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }

  // ── master data ───────────────────────────────────────────────────────────
  const BUYERS = dbGet('buyers', _FB_BUYERS);
  const BRANDS = dbGet('brands', _FB_BRANDS);
  const SIZES = dbGet('sizes', ['XS','S','M','L','XL','XXL','3XL','28','30','32','34','36','38','40']);
  const LINES = dbGet('lines', _FB_LINES);
  const SHIFTS  = ['DAY','NIGHT','GENERAL'];

  const AQL_TABLE = [
    {min:2,    max:8,     n:2,   ac:0,re:1},  {min:9,    max:15,    n:3,   ac:0,re:1},
    {min:16,   max:25,    n:5,   ac:0,re:1},  {min:26,   max:50,    n:8,   ac:0,re:1},
    {min:51,   max:90,    n:13,  ac:1,re:2},  {min:91,   max:150,   n:20,  ac:1,re:2},
    {min:151,  max:280,   n:32,  ac:2,re:3},  {min:281,  max:500,   n:50,  ac:3,re:4},
    {min:501,  max:1200,  n:80,  ac:5,re:6},  {min:1201, max:3200,  n:125, ac:7,re:8},
    {min:3201, max:10000, n:200, ac:10,re:11}, {min:10001,max:35000, n:315, ac:14,re:15},
    {min:35001,max:999999,n:500, ac:21,re:22},
  ];
  function getAQL(qty) {
    const q=parseInt(qty)||0;
    return AQL_TABLE.find(r=>q>=r.min&&q<=r.max)||AQL_TABLE[AQL_TABLE.length-1];
  }

  const FIN_DEFECTS = [
    'O/L Broken Stitch','Skip/Open Stitch','Bottom Half Stitch','Raw Edge',
    'W/B Loose Puckering','Without Eyelet Barteck','Spot/Stain','Mending Required',
    'Fabric Fault','Oil Mark','Body Hole','Wash Bleeding',
    'Broken Stitch','Uneven Stitch','Thread Untrimmed','Thread Loose',
    'Button Defect','Wrong Label','Wrong Size','Wrong Hang Tag',
    'Elastic Defect','Measurement Problem','Size Mistake','Bubbling',
    'Rejection','Button Alteration','Stain Washing','Washing Damage',
    'Other Washing Defect','Other',
  ];

  const MEAS_POINTS_BTM = [
    'Waist Circumference','Hip Circumference','Thigh','Inseam',
    'Out Seam','Front Rise','Back Rise','Bottom Hem','Zipper Length',
  ];

  const SOURCE_AUDIT_DEFECTS = [
    {code:'01',name:'Skipped/Broken Stitch'}, {code:'02',name:'Raw Edge'},
    {code:'03',name:'Run Off'},               {code:'04',name:'Needle Damage'},
    {code:'05',name:'Puckering'},             {code:'06',name:'Fullness'},
    {code:'07',name:'Open Seam'},             {code:'08',name:'Pleat'},
    {code:'09',name:'Hi-Low'},                {code:'10',name:'Miss-match'},
    {code:'11',name:'Button/Buttonholes'},    {code:'12',name:'Rivets/Bar Tacks'},
    {code:'13',name:'Twisted Leg'},           {code:'14',name:'Loops'},
    {code:'15',name:'Threads Untrimmed'},     {code:'16',name:'Threads Loose'},
    {code:'17',name:'Sew Stain/Oil'},         {code:'18',name:'Other Constr.'},
    {code:'19',name:'Plus Measurement'},      {code:'20',name:'Minus Measurement'},
    {code:'21',name:'Fabric Flaw'},           {code:'22',name:'Fabric Hole'},
    {code:'23',name:'Fabric Twisting'},       {code:'24',name:'Fabric Other'},
    {code:'25',name:'Fin Stain/Oil'},         {code:'26',name:'Proc Splotch'},
    {code:'27',name:'Out of Shade Range'},    {code:'28',name:'Streaks'},
    {code:'29',name:'Holes'},                 {code:'30',name:'Dry Process'},
    {code:'31',name:'Crease Retention'},      {code:'32',name:'Seam Smoothness'},
    {code:'33',name:'Wrong Cast'},            {code:'34',name:'Print/Embroidery'},
    {code:'35',name:'Finishing Others'},      {code:'36',name:'Wrong Sundry'},
    {code:'37',name:'Sundry Position'},       {code:'38',name:'Sundry Functionality'},
    {code:'39',name:'Pack/Fold'},             {code:'40',name:'Pressing'},
  ];

  const ACCESSORY_CHECKS = [
    'Main Label','Sewing Thread','Size Label','Care Label',
    'Button Hole','Hang Tag','Streamer','Price Ticket',
    'Interlining','Size Sticker','Zipper','Poly Bag','Hanger','Folding',
  ];

  // ── shared API save ───────────────────────────────────────────────────────
  async function saveRec(category, record, db, setDb) {
    try {
      const res = await fetch(API,{method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({category,data:record})});
      const j = await res.json();
      record.serverId = j.id || Date.now();
    } catch { record.serverId = Date.now(); }
    setDb(prev => {
      const list = prev.finishing||[];
      const idx  = list.findIndex(r=>r.id===record.id);
      return {...prev, finishing:idx>=0?list.map(r=>r.id===record.id?record:r):[...list,record]};
    });
  }

  // ── mini UI ───────────────────────────────────────────────────────────────
  function Fld({label,value,onChange,type='text',readOnly=false,placeholder='',options=null,required=false,note='',rows=3}){
    const base={width:'100%',padding:'9px 11px',fontSize:13,borderRadius:R.md,
      border:`1px solid ${C.border}`,background:readOnly?C.slate50:C.white,
      color:readOnly?C.textSub:C.text,outline:'none',fontFamily:'inherit'};
    return(
      <div style={{display:'flex',flexDirection:'column',gap:4}}>
        <label style={{fontSize:11,fontWeight:600,color:C.textSub,textTransform:'uppercase',letterSpacing:'.4px'}}>
          {label}{required&&<span style={{color:C.danger}}> *</span>}
        </label>
        {options?<select value={value} onChange={onChange} disabled={readOnly} style={{...base,cursor:readOnly?'default':'pointer'}}>
          {options.map((o,i)=><option key={i} value={o.value??o}>{o.label??o}</option>)}
        </select>:type==='textarea'?<textarea value={value} onChange={onChange} readOnly={readOnly}
          placeholder={placeholder} rows={rows} style={{...base,resize:'vertical'}}/>
        :<input type={type} value={value} onChange={onChange} readOnly={readOnly} placeholder={placeholder} style={base}/>}
        {note&&<div style={{fontSize:11,color:C.textMuted}}>{note}</div>}
      </div>
    );
  }

  function Btn({children,onClick,color=C.fin,outline=false,sm=false,disabled=false,icon=null}){
    return(
      <button onClick={onClick} disabled={disabled} style={{
        display:'flex',alignItems:'center',gap:5,
        padding:sm?'7px 13px':'10px 20px',borderRadius:R.md,
        border:outline?`2px solid ${color}`:'none',
        background:disabled?C.slate100:outline?'transparent':color,
        color:disabled?C.textMuted:outline?color:C.white,
        fontWeight:700,fontSize:sm?12:13,cursor:disabled?'default':'pointer',
        fontFamily:'inherit',transition:'all .15s',
      }}>{icon&&<span>{icon}</span>}{children}</button>
    );
  }

  function Bdg({children,color=C.fin,bg}){
    return <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,
      color,background:bg||color+'22',display:'inline-block',whiteSpace:'nowrap'}}>{children}</span>;
  }

  function Res({result}){
    const m={PASS:{c:C.success,b:C.successBg},FAIL:{c:C.danger,b:C.dangerBg},
      HOLD:{c:C.warning,b:C.warningBg},PENDING:{c:C.textMuted,b:C.slate100}};
    const x=m[result?.toUpperCase()]||m.PENDING;
    return <Bdg color={x.c} bg={x.b}>{result||'PENDING'}</Bdg>;
  }

  function Card({children,style={}}){
    return <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:R.lg,padding:20,...style}}>{children}</div>;
  }

  function SH({title,color=C.fin,action=null}){
    return(
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,paddingBottom:12,borderBottom:`2px solid ${C.border}`}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:3,height:18,background:color,borderRadius:2}}/>
          <h3 style={{fontSize:13,fontWeight:700,color:C.text,textTransform:'uppercase',letterSpacing:'.4px'}}>{title}</h3>
        </div>
        {action}
      </div>
    );
  }

  function Grid({children,gap=14,min=180}){
    return <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fit,minmax(${min}px,1fr))`,gap}}>{children}</div>;
  }

  function Tbl({headers=[],rows=[],emptyMsg='No records found.'}){
    return(
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:C.slate50}}>
            {headers.map((h,i)=><th key={i} style={{padding:'9px 11px',textAlign:'left',fontWeight:700,color:C.textSub,fontSize:11,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,whiteSpace:'nowrap'}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length===0?<tr><td colSpan={headers.length} style={{padding:24,textAlign:'center',color:C.textMuted,fontStyle:'italic'}}>{emptyMsg}</td></tr>
            :rows.map((row,i)=><tr key={i} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.slate50}}>
              {row.map((cell,j)=><td key={j} style={{padding:'9px 11px',color:C.text,verticalAlign:'middle'}}>{cell}</td>)}
            </tr>)}
          </tbody>
        </table>
      </div>
    );
  }

  function PgHead({title,ref_,action}){
    return(
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10,marginBottom:18}}>
        <div>
          <h2 style={{fontSize:16,fontWeight:800,color:C.text}}>{title}</h2>
          <div style={{fontSize:11,color:C.textMuted}}>Ref: {ref_}</div>
        </div>
        {action}
      </div>
    );
  }

  function useSave(){
    const [saving,setSaving]=React.useState(false);
    const [saved,setSaved]=React.useState(false);
    async function doSave(category,record,db,setDb,extra){
      setSaving(true);
      await saveRec(category,record,db,setDb);
      if(extra) extra(record);
      setSaving(false);setSaved(true);
      setTimeout(()=>setSaved(false),2500);
    }
    return {saving,saved,doSave};
  }

  function SaveBar({result,saving,saved,onSave}){
    return(
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10,marginTop:16,paddingTop:14,borderTop:`1px solid ${C.border}`}}>
        {result?<div style={{display:'flex',alignItems:'center',gap:10}}><div style={{fontSize:11,color:C.textSub}}>Result</div><Res result={result}/></div>:<div/>}
        <Btn onClick={onSave} disabled={saving} icon={saved?'✓':saving?'…':'💾'}>
          {saved?'Saved!':saving?'Saving…':'Save Report'}
        </Btn>
      </div>
    );
  }

  function DefectGrid({defects,onChange,color=C.fin}){
    const total=defects.reduce((s,d)=>s+(d.qty||0),0);
    return(
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:700,color:C.textSub,textTransform:'uppercase'}}>Defect Breakdown</div>
          <Bdg color={C.danger} bg={C.dangerBg}>Total: {total}</Bdg>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:7}}>
          {defects.map((d,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:R.sm,
              background:d.qty>0?C.dangerBg:C.slate50,border:`1px solid ${d.qty>0?C.dangerBd:C.border}`}}>
              <div style={{flex:1,fontSize:12,color:C.text,fontWeight:d.qty>0?600:400}}>{d.label}</div>
              <input type="number" value={d.qty||''} onChange={e=>onChange(i,parseInt(e.target.value)||0)}
                style={{width:55,padding:'4px',borderRadius:R.sm,border:`1px solid ${C.border}`,
                  fontSize:13,textAlign:'center',fontWeight:700,color:d.qty>0?C.danger:C.text}}/>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-53 · DAILY FINISHING QUALITY REPORT  (Nir/Fin-Qc/01)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann53({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const newLine=()=>({id:uid(),line:'',orderRef:'',qcCode:'',qcName:'',
      noAudits:0,noFailed:0,successRate:0,noInspected:0,noDefective:0,
      noDefects:0,dhu:0,majorIssues:''});
    const fresh=()=>({
      id:uid(),formType:'FIN_DAILY_QUALITY',date:TODAY,
      buyer:filters.buyer||'',style:filters.style||'',
      po:filters.po||'',colour:filters.colour||'',shift:SHIFTS[0],
      finishingQcSupervisor:'',finishingQcManager:'',
      lines:[newLine()],
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updL=(i,k,v)=>setF(f=>{
      const lines=[...f.lines];lines[i]={...lines[i],[k]:v};
      if(k==='noAudits'||k==='noFailed'){
        const a=parseInt(lines[i].noAudits)||0,fa=parseInt(lines[i].noFailed)||0;
        lines[i].successRate=a>0?(((a-fa)/a)*100).toFixed(1):0;
      }
      if(k==='noInspected'||k==='noDefects'){
        const ins=parseInt(lines[i].noInspected)||0,def=parseInt(lines[i].noDefects)||0;
        lines[i].dhu=ins>0?((def/ins)*100).toFixed(2):0;
      }
      return {...f,lines};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_DAILY_QUALITY');
    const totInsp=F.lines.reduce((s,l)=>s+(parseInt(l.noInspected)||0),0);
    const totDef=F.lines.reduce((s,l)=>s+(parseInt(l.noDefects)||0),0);
    const totDHU=totInsp>0?((totDef/totInsp)*100).toFixed(2):'0.00';

    const handleSave=async()=>{
      const rec={...F,totalInspected:totInsp,totalDefects:totDef,dhu:parseFloat(totDHU),
        result:parseFloat(totDHU)<=5?'PASS':'FAIL',savedAt:new Date().toISOString()};
      await doSave('finishing_daily_quality',rec,db,setDb,r=>{
        setDb(prev=>({...prev,finishingLogs:[...(prev.finishingLogs||[]),r]}));
      });
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-53 · Daily Finishing Quality Report" ref_="Nir/Fin-Qc/01 · QC Supervisor → Quality Manager"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Daily Reports"/><Tbl headers={['Date','Style','Buyer','Lines','Inspected','DHU%','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.buyer||'—',(r.lines||[]).length,
              r.totalInspected||'—',<span style={{fontWeight:700,color:parseFloat(r.dhu)>5?C.danger:C.success}}>{r.dhu||0}%</span>,
              <Res result={r.result}/>])} emptyMsg="No records yet."/></Card>
        ):(
          <>
            <Card><SH title="Header" color={C.fin}/>
              <Grid gap={14}><Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
                <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="PO No." value={F.po} onChange={e=>set('po',e.target.value)} options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Shift" value={F.shift} onChange={e=>set('shift',e.target.value)} options={SHIFTS}/>
                <Fld label="Fin. QC Supervisor" value={F.finishingQcSupervisor} onChange={e=>set('finishingQcSupervisor',e.target.value)} placeholder="Name..."/>
                <Fld label="Fin. QC Manager" value={F.finishingQcManager} onChange={e=>set('finishingQcManager',e.target.value)} placeholder="Name..."/>
              </Grid>
            </Card>
            <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
              {[['Total Inspected',totInsp,C.accent,'#EFF6FF'],['Total Defects',totDef,C.danger,C.dangerBg],
                ['DHU',`${totDHU}%`,parseFloat(totDHU)>5?C.danger:C.success,parseFloat(totDHU)>5?C.dangerBg:C.successBg]
              ].map(([l,v,c,b])=>(
                <div key={l} style={{padding:'10px 16px',background:b,borderRadius:R.md,border:`1px solid ${c}22`,textAlign:'center',minWidth:120}}>
                  <div style={{fontSize:22,fontWeight:800,color:c}}>{v}</div>
                  <div style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase',marginTop:4}}>{l}</div>
                </div>
              ))}
            </div>
            <Card><SH title="Line-wise Data" color={C.fin}
              action={<Btn sm onClick={()=>setF(f=>({...f,lines:[...f.lines,newLine()]}))} color={C.fin}>＋ Add Line</Btn>}/>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:900}}>
                  <thead><tr style={{background:C.slate50}}>
                    {['Line','Order Ref','QC Code','QC Name','Audits','Failed','Success%','Inspected','Defective','Defects','DHU%','Major Issues',''].map(h=>(
                      <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {F.lines.map((l,i)=>(
                      <tr key={l.id} style={{borderBottom:`1px solid ${C.border}`}}>
                        <td style={{padding:'5px 6px'}}>
                          <select value={l.line} onChange={e=>updL(i,'line',e.target.value)}
                            style={{width:90,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                            <option value="">Line...</option>
                            {LINES.map(ln=><option key={ln} value={ln}>{ln}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'5px 6px'}}><input value={l.orderRef} onChange={e=>updL(i,'orderRef',e.target.value)} placeholder="Ref..." list="po-datalist"
                          style={{width:80,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                        <td style={{padding:'5px 6px'}}><input value={l.qcCode} onChange={e=>updL(i,'qcCode',e.target.value)} placeholder="Code"
                          style={{width:55,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                        <td style={{padding:'5px 6px'}}><input value={l.qcName} onChange={e=>updL(i,'qcName',e.target.value)} placeholder="Name"
                          style={{width:80,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                        {['noAudits','noFailed','noInspected','noDefective','noDefects'].map(k=>(
                          <td key={k} style={{padding:'5px 6px'}}>
                            <input type="number" value={l[k]||''} onChange={e=>updL(i,k,e.target.value)} placeholder="0"
                              style={{width:60,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/>
                          </td>
                        ))}
                        <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,
                          color:parseFloat(l.dhu)>5?C.danger:C.success}}>{l.dhu}%</td>
                        <td style={{padding:'5px 6px'}}><input value={l.majorIssues} onChange={e=>updL(i,'majorIssues',e.target.value)} placeholder="Top issues..."
                          style={{width:120,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                        <td style={{padding:'5px 6px'}}>
                          <button onClick={()=>setF(f=>({...f,lines:f.lines.filter((_,li)=>li!==i)}))}
                            style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:11,padding:'5px 7px'}}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <SaveBar result={parseFloat(totDHU)>5?'FAIL':'PASS'} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-54 · FINISHING PROCESS WISE DHU  (Nir/Fin-Qc/02)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann54({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const newRow=()=>({id:uid(),process:'',lineNo:'',checked:0,defects:0,dhu:0,majorDefect:''});
    const fresh=()=>({
      id:uid(),formType:'FIN_PROCESS_DHU',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',colour:filters.colour||'',
      shift:SHIFTS[0],qcManager:'',
      rows:[newRow()],
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updR=(i,k,v)=>setF(f=>{
      const rows=[...f.rows];rows[i]={...rows[i],[k]:v};
      const c=parseInt(rows[i].checked)||0,d=parseInt(rows[i].defects)||0;
      rows[i].dhu=c>0?((d/c)*100).toFixed(2):0;
      return {...f,rows};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_PROCESS_DHU');
    const totChk=F.rows.reduce((s,r)=>s+(parseInt(r.checked)||0),0);
    const totDef=F.rows.reduce((s,r)=>s+(parseInt(r.defects)||0),0);
    const overallDHU=totChk>0?((totDef/totChk)*100).toFixed(2):'0.00';

    const handleSave=async()=>{
      await doSave('finishing_process_dhu',
        {...F,totalChecked:totChk,totalDefects:totDef,overallDHU:parseFloat(overallDHU),
          result:parseFloat(overallDHU)<=5?'PASS':'FAIL',savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-54 · Finishing Process Wise DHU" ref_="Nir/Fin-Qc/02"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}><Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Shift" value={F.shift} onChange={e=>set('shift',e.target.value)} options={SHIFTS}/>
            <Fld label="QC Manager" value={F.qcManager} onChange={e=>set('qcManager',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <div style={{display:'flex',gap:10}}>
          <Bdg color={C.accent} bg="#EFF6FF">Checked: {totChk}</Bdg>
          <Bdg color={C.danger} bg={C.dangerBg}>Defects: {totDef}</Bdg>
          <Bdg color={parseFloat(overallDHU)>5?C.danger:C.success}
            bg={parseFloat(overallDHU)>5?C.dangerBg:C.successBg}>DHU: {overallDHU}%</Bdg>
        </div>
        <Card><SH title="Process-wise DHU Data" color={C.fin}
          action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.fin}>＋ Add Process</Btn>}/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 100px 100px 100px 80px 1fr 30px',gap:6,marginBottom:8}}>
            {['Process Name','Line No.','Checked','Defects','DHU %','Major Defect',''].map(h=>(
              <div key={h} style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase'}}>{h}</div>
            ))}
          </div>
          {F.rows.map((r,i)=>(
            <div key={r.id} style={{display:'grid',gridTemplateColumns:'1fr 100px 100px 100px 80px 1fr 30px',gap:6,marginBottom:5,alignItems:'center'}}>
              <input value={r.process} onChange={e=>updR(i,'process',e.target.value)} placeholder="Process name..."
                style={{padding:'7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12}}/>
              <select value={r.lineNo} onChange={e=>updR(i,'lineNo',e.target.value)}
                style={{padding:'7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,fontFamily:'inherit'}}>
                <option value="">Line</option>
                {LINES.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
              {['checked','defects'].map(k=>(
                <input key={k} type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                  style={{padding:'7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center'}}/>
              ))}
              <div style={{textAlign:'center',fontWeight:700,padding:'7px',
                color:parseFloat(r.dhu)>5?C.danger:C.success}}>{r.dhu}%</div>
              <input value={r.majorDefect} onChange={e=>updR(i,'majorDefect',e.target.value)} placeholder="Major defect..."
                style={{padding:'7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12}}/>
              <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:12,padding:'6px'}}>✕</button>
            </div>
          ))}
          <SaveBar result={parseFloat(overallDHU)>5?'FAIL':'PASS'} saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-55 · QC FINISHING MAJOR 3 DEFECTS  (Nir/Fin-Qc/03)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann55({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const newRow=()=>({id:uid(),line:'',defect1:'',defect2:'',defect3:'',action:'',remarks:''});
    const fresh=()=>({
      id:uid(),formType:'FIN_MAJOR3',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',colour:filters.colour||'',
      finishingQcManager:'',topManagement:'',
      rows:LINES.slice(0,6).map(l=>({id:uid(),line:l,defect1:'',defect2:'',defect3:'',action:'',remarks:''})),
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updR=(i,k,v)=>setF(f=>({...f,rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_MAJOR3');

    const handleSave=async()=>{
      await doSave('finishing_major3',{...F,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-55 · QC Finishing Major 3 Defects" ref_="Nir/Fin-Qc/03 · For Finishing QC Manager"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Fin. QC Manager" value={F.finishingQcManager} onChange={e=>set('finishingQcManager',e.target.value)} placeholder="Name..."/>
            <Fld label="Top Management" value={F.topManagement} onChange={e=>set('topManagement',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <Card><SH title="Line-wise Top 3 Finishing Defects" color={C.fin}
          action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.fin}>＋ Add Line</Btn>}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:700}}>
              <thead><tr style={{background:C.slate50}}>
                {['Line','#1 Defect','#2 Defect','#3 Defect','Corrective Action','Remarks',''].map(h=>(
                  <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:11,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.rows.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:'5px 6px'}}>
                      <select value={r.line} onChange={e=>updR(i,'line',e.target.value)}
                        style={{width:90,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                        {LINES.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                    </td>
                    {['defect1','defect2','defect3'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <select value={r[k]} onChange={e=>updR(i,k,e.target.value)}
                          style={{width:'100%',minWidth:130,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit',
                            background:r[k]?C.dangerBg:C.white,color:r[k]?C.danger:C.textMuted}}>
                          <option value="">Select...</option>
                          {FIN_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
                        </select>
                      </td>
                    ))}
                    <td style={{padding:'5px 6px'}}><input value={r.action} onChange={e=>updR(i,'action',e.target.value)} placeholder="Action..."
                      style={{width:'100%',minWidth:120,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    <td style={{padding:'5px 6px'}}><input value={r.remarks} onChange={e=>updR(i,'remarks',e.target.value)} placeholder="Remarks..."
                      style={{width:'100%',minWidth:100,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    <td style={{padding:'5px 6px'}}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:11,padding:'5px 7px'}}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-56 · DAILY STATUS OF MEASUREMENT INSPECTION  (Nir/Fin-Qc/04)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann56({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const newRow=()=>({id:uid(),lineNo:'',buyerStyleColour:'',inspectorName:'',
      tagOperatorName:'',wrongSizeQty:0,measDeviationQty:0,wrongTagQty:0,
      failedQty:0,failPct:0,supervisorName:''});
    const fresh=()=>({
      id:uid(),formType:'FIN_MEAS_STATUS',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',
      qualityGM:'',rows:[newRow()],
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updR=(i,k,v)=>setF(f=>{
      const rows=[...f.rows];rows[i]={...rows[i],[k]:v};
      const wrong=parseInt(rows[i].wrongSizeQty)||0;
      const meas=parseInt(rows[i].measDeviationQty)||0;
      const tag=parseInt(rows[i].wrongTagQty)||0;
      const total=wrong+meas+tag;
      rows[i].failedQty=total;
      return {...f,rows};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_MEAS_STATUS');

    const handleSave=async()=>{
      await doSave('finishing_meas_status',{...F,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-56 · Daily Status of Measurement Inspection" ref_="Nir/Fin-Qc/04 · Line-wise size tag accuracy"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Quality GM" value={F.qualityGM} onChange={e=>set('qualityGM',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <Card><SH title="Line-wise Measurement Inspection Status" color={C.fin}
          action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.fin}>＋ Add Line</Btn>}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:850}}>
              <thead><tr style={{background:C.slate50}}>
                {['Line No.','Buyer/Style/Colour','Inspector','Tag Operator','Wrong Size','Meas Deviation','Wrong Tag','Total Failed','Supervisor',''].map(h=>(
                  <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.rows.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`,background:r.failedQty>0?C.dangerBg:C.white}}>
                    <td style={{padding:'5px 6px'}}>
                      <select value={r.lineNo} onChange={e=>updR(i,'lineNo',e.target.value)}
                        style={{width:90,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                        <option value="">Line</option>
                        {LINES.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                    </td>
                    <td style={{padding:'5px 6px'}}><input value={r.buyerStyleColour} onChange={e=>updR(i,'buyerStyleColour',e.target.value)} placeholder="Buyer/Style/Colour..."
                      style={{width:130,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    <td style={{padding:'5px 6px'}}><input value={r.inspectorName} onChange={e=>updR(i,'inspectorName',e.target.value)} placeholder="Name..."
                      style={{width:80,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    <td style={{padding:'5px 6px'}}><input value={r.tagOperatorName} onChange={e=>updR(i,'tagOperatorName',e.target.value)} placeholder="Name..."
                      style={{width:80,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    {['wrongSizeQty','measDeviationQty','wrongTagQty'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center',
                            background:parseInt(r[k])>0?C.dangerBg:C.white}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:r.failedQty>0?C.danger:C.success}}>{r.failedQty}</td>
                    <td style={{padding:'5px 6px'}}><input value={r.supervisorName} onChange={e=>updR(i,'supervisorName',e.target.value)} placeholder="Name..."
                      style={{width:80,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    <td style={{padding:'5px 6px'}}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:11,padding:'5px 7px'}}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-57 · FINISHING DEPT MEASUREMENT INSPECTION  (Nir/Fin-Qc/06)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann57({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const newMeas=()=>({id:uid(),point:MEAS_POINTS_BTM[0],spec:'',actual:'',tolerance:'±1"',result:'',deviation:''});
    const fresh=()=>({
      id:uid(),formType:'FIN_MEAS_INSP',date:TODAY,
      style:filters.style||'',po:filters.po||'',buyer:filters.buyer||'',colour:filters.colour||'',
      lineNo:'',inspectorName:'',codeNo:'',shift:SHIFTS[0],
      qtyInspected:0,qtyAlter:0,
      measurements:MEAS_POINTS_BTM.map(p=>({id:uid(),point:p,spec:'',actual:'',tolerance:'±1"',result:'',deviation:''})),
      proSupervisor:'',finishingManager:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updM=(i,k,v)=>setF(f=>{
      const measurements=[...f.measurements];measurements[i]={...measurements[i],[k]:v};
      if((k==='actual'||k==='spec')&&measurements[i].spec&&measurements[i].actual){
        const diff=parseFloat(measurements[i].actual)-parseFloat(measurements[i].spec);
        const tol=parseFloat(measurements[i].tolerance)||1;
        measurements[i].deviation=diff>=0?`+${diff.toFixed(1)}"`:diff.toFixed(1)+'"';
        measurements[i].result=Math.abs(diff)<=tol?'PASS':'FAIL';
      }
      return {...f,measurements};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_MEAS_INSP');
    const failCount=F.measurements.filter(m=>m.result==='FAIL').length;
    const autoRes=()=>failCount===0&&F.measurements.some(m=>m.result==='PASS')?'PASS':failCount>0?'FAIL':'PENDING';
    const alterPct=F.qtyInspected>0?((F.qtyAlter/F.qtyInspected)*100).toFixed(1):0;

    const handleSave=async()=>{
      await doSave('finishing_meas_insp',{...F,result:autoRes(),savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-57 · Finishing Dept Measurement Inspection" ref_="Nir/Fin-Qc/06 · Hourly major points"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Measurement Inspection Records"/><Tbl
            headers={['Date','Style','Line','Buyer','Inspected','Alter','Pass Points','Fail Points','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.lineNo||'—',r.buyer||'—',r.qtyInspected||'—',r.qtyAlter||'—',
              (r.measurements||[]).filter(m=>m.result==='PASS').length,
              (r.measurements||[]).filter(m=>m.result==='FAIL').length,<Res result={r.result}/>])}
            emptyMsg="No records yet."/></Card>
        ):(
          <>
            <Card><SH title="Inspection Header" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
                <Fld label="Code No." value={F.codeNo} onChange={e=>set('codeNo',e.target.value)} placeholder="Code..."/>
                <Fld label="PO No." value={F.po} onChange={e=>set('po',e.target.value)} options={["", ...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Line No." value={F.lineNo} onChange={e=>set('lineNo',e.target.value)} options={['',...LINES]}/>
                <Fld label="Inspector Name" value={F.inspectorName} onChange={e=>set('inspectorName',e.target.value)} placeholder="Name..."/>
                <Fld label="Shift" value={F.shift} onChange={e=>set('shift',e.target.value)} options={SHIFTS}/>
                <Fld label="Qty Inspected" type="number" value={F.qtyInspected} onChange={e=>set('qtyInspected',e.target.value)} placeholder="0"/>
                <Fld label="Qty Alter" type="number" value={F.qtyAlter} onChange={e=>set('qtyAlter',e.target.value)} placeholder="0"
                  note={`Alter %: ${alterPct}%`}/>
              </Grid>
            </Card>
            <Card><SH title="Measurement Points" color={C.fin}
              action={<div style={{display:'flex',gap:8}}>
                <Bdg color={C.success} bg={C.successBg}>{F.measurements.filter(m=>m.result==='PASS').length} Pass</Bdg>
                <Bdg color={C.danger}  bg={C.dangerBg }>{failCount} Fail</Bdg>
              </div>}/>
              <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 80px 70px 80px',gap:6,marginBottom:8}}>
                {['Measurement Point','Spec','Actual','Tolerance','Deviation','Result'].map(h=>(
                  <div key={h} style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase'}}>{h}</div>
                ))}
              </div>
              {F.measurements.map((m,i)=>(
                <div key={m.id} style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 80px 70px 80px',gap:6,marginBottom:5,alignItems:'center'}}>
                  <select value={m.point} onChange={e=>updM(i,'point',e.target.value)}
                    style={{padding:'7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,fontFamily:'inherit'}}>
                    {MEAS_POINTS_BTM.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                  {['spec','actual'].map(k=>(
                    <input key={k} type="number" value={m[k]||''} onChange={e=>updM(i,k,e.target.value)} placeholder="0.0"
                      style={{padding:'6px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center',
                        background:k==='actual'&&m.result==='FAIL'?C.dangerBg:k==='actual'&&m.result==='PASS'?C.successBg:C.white}}/>
                  ))}
                  <input value={m.tolerance} onChange={e=>updM(i,'tolerance',e.target.value)}
                    style={{padding:'6px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center'}}/>
                  <div style={{textAlign:'center',fontSize:12,fontWeight:700,
                    color:m.deviation&&m.deviation.startsWith('+')?C.danger:m.deviation&&!m.deviation.startsWith('+')?C.warning:C.textMuted}}>
                    {m.deviation||'—'}
                  </div>
                  <div style={{textAlign:'center'}}>{m.result?<Res result={m.result}/>:<span style={{color:C.textMuted}}>—</span>}</div>
                </div>
              ))}
              <Grid gap={14} style={{marginTop:12}}>
                <Fld label="Pro Supervisor" value={F.proSupervisor} onChange={e=>set('proSupervisor',e.target.value)} placeholder="Name..."/>
                <Fld label="Finishing Manager" value={F.finishingManager} onChange={e=>set('finishingManager',e.target.value)} placeholder="Name..."/>
              </Grid>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-58 · HOURLY MEASUREMENT INSPECTION  (Nir/Fin-Qc/07)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann58({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const fresh=()=>({
      id:uid(),formType:'FIN_HOURLY_MEAS',date:TODAY,
      style:filters.style||'',po:filters.po||'',buyer:filters.buyer||'',
      colour:filters.colour||'',lineNo:'',unitOfMeasure:'INCHES',
      hourlyData:HOURS.map(h=>({
        hour:h,
        waist:{spec:'',actual:'',ok:null},
        hip:{spec:'',actual:'',ok:null},
        inseam:{spec:'',actual:'',ok:null},
        outseam:{spec:'',actual:'',ok:null},
        inspectedBy:'',lineSupervisor:'',
      })),
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updH=(i,point,field,v)=>setF(f=>{
      const hd=[...f.hourlyData];
      hd[i]={...hd[i],[point]:{...hd[i][point],[field]:v}};
      if(field==='actual'&&hd[i][point].spec){
        const diff=Math.abs(parseFloat(v)-parseFloat(hd[i][point].spec));
        hd[i][point].ok=diff<=1;
      }
      return {...f,hourlyData:hd};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_HOURLY_MEAS');

    const handleSave=async()=>{
      await doSave('finishing_hourly_meas',{...F,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    const POINTS=['waist','hip','inseam','outseam'];

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-58 · Hourly Measurement Inspection Report" ref_="Nir/Fin-Qc/07 · Buyer-wise hourly measurement"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="PO #" value={F.po} onChange={e=>set('po',e.target.value)} options={["", ...dbGet("poNumbers",[])]} placeholder="PO No..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Line No." value={F.lineNo} onChange={e=>set('lineNo',e.target.value)} options={['',...LINES]}/>
            <Fld label="Unit of Measure" value={F.unitOfMeasure} onChange={e=>set('unitOfMeasure',e.target.value)} options={['INCHES','CM']}/>
          </Grid>
        </Card>
        <Card><SH title="Hourly Measurements" color={C.fin}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11,minWidth:900}}>
              <thead>
                <tr style={{background:C.slate50}}>
                  <th rowSpan={2} style={{padding:'8px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,borderRight:`1px solid ${C.border}`,textAlign:'center'}}>Hour</th>
                  {POINTS.map(p=>(
                    <th key={p} colSpan={2} style={{padding:'8px',fontWeight:700,color:C.fin,fontSize:10,textTransform:'uppercase',borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,textAlign:'center'}}>
                      {p.toUpperCase()}
                    </th>
                  ))}
                  <th rowSpan={2} style={{padding:'8px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center'}}>Inspector</th>
                </tr>
                <tr style={{background:C.slate50}}>
                  {POINTS.map(p=>(
                    <React.Fragment key={p}>
                      <th style={{padding:'6px 8px',fontWeight:600,color:C.textSub,fontSize:10,borderBottom:`2px solid ${C.border}`,borderRight:`1px solid ${C.border}44`,textAlign:'center'}}>Spec</th>
                      <th style={{padding:'6px 8px',fontWeight:600,color:C.textSub,fontSize:10,borderBottom:`2px solid ${C.border}`,borderRight:`1px solid ${C.border}`,textAlign:'center'}}>Actual</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.hourlyData.map((h,i)=>(
                  <tr key={h.hour} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:'6px 8px',textAlign:'center',fontWeight:700,color:C.fin,borderRight:`1px solid ${C.border}`}}>{h.hour}</td>
                    {POINTS.map(p=>(
                      <React.Fragment key={p}>
                        <td style={{padding:'4px 5px',borderRight:`1px solid ${C.border}44`}}>
                          <input type="number" value={h[p].spec||''} onChange={e=>updH(i,p,'spec',e.target.value)} placeholder="—"
                            style={{width:60,padding:'4px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/>
                        </td>
                        <td style={{padding:'4px 5px',borderRight:`1px solid ${C.border}`}}>
                          <input type="number" value={h[p].actual||''} onChange={e=>updH(i,p,'actual',e.target.value)} placeholder="—"
                            style={{width:60,padding:'4px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center',
                              background:h[p].ok===false?C.dangerBg:h[p].ok===true?C.successBg:C.white}}/>
                        </td>
                      </React.Fragment>
                    ))}
                    <td style={{padding:'4px 6px'}}>
                      <input value={h.inspectedBy||''} onChange={e=>setF(f=>{const hd=[...f.hourlyData];hd[i]={...hd[i],inspectedBy:e.target.value};return {...f,hourlyData:hd};})} placeholder="Name..."
                        style={{width:80,padding:'4px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-59 · TOP SIDE / INSIDE DHU REPORT  (Nir/Fin-Qc/08)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann59({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const fresh=()=>({
      id:uid(),formType:'FIN_TOPSIDE_DHU',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',colour:filters.colour||'',
      lineNo:'',orderRef:filters.po||'',shift:SHIFTS[0],
      hourlyData:HOURS.map(h=>({
        hour:h,
        insideChecked:0,insidePassed:0,insideDefects:0,
        topChecked:0,   topPassed:0,   topDefects:0,
      })),
      qualitySupervisor:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updH=(i,k,v)=>setF(f=>({...f,hourlyData:f.hourlyData.map((h,hi)=>hi===i?{...h,[k]:parseInt(v)||0}:h)}));
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_TOPSIDE_DHU');

    const totals=F.hourlyData.reduce((t,h)=>({
      insideChecked:t.insideChecked+(h.insideChecked||0),
      insidePassed:t.insidePassed+(h.insidePassed||0),
      insideDefects:t.insideDefects+(h.insideDefects||0),
      topChecked:t.topChecked+(h.topChecked||0),
      topPassed:t.topPassed+(h.topPassed||0),
      topDefects:t.topDefects+(h.topDefects||0),
    }),{insideChecked:0,insidePassed:0,insideDefects:0,topChecked:0,topPassed:0,topDefects:0});
    const insideDHU=totals.insideChecked>0?((totals.insideDefects/totals.insideChecked)*100).toFixed(2):'0.00';
    const topDHU   =totals.topChecked>0?((totals.topDefects/totals.topChecked)*100).toFixed(2):'0.00';

    const handleSave=async()=>{
      await doSave('finishing_topside_dhu',{...F,totals,insideDHU,topDHU,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-59 · Top Side / Inside DHU Report" ref_="Nir/Fin-Qc/08 · Hourly top & inside check"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Line No." value={F.lineNo} onChange={e=>set('lineNo',e.target.value)} options={['',...LINES]}/>
            <Fld label="Order Ref" value={F.orderRef} onChange={e=>set('orderRef',e.target.value)} options={["", ...dbGet("poNumbers",[])]} placeholder="Order / PO..."/>
            <Fld label="Shift" value={F.shift} onChange={e=>set('shift',e.target.value)} options={SHIFTS}/>
            <Fld label="Quality Supervisor" value={F.qualitySupervisor} onChange={e=>set('qualitySupervisor',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
          {[['Inside Checked',totals.insideChecked,C.accent,'#EFF6FF'],['Inside DHU',`${insideDHU}%`,parseFloat(insideDHU)>5?C.danger:C.success,parseFloat(insideDHU)>5?C.dangerBg:C.successBg],
            ['Top Checked',totals.topChecked,C.accent,'#EFF6FF'],['Top DHU',`${topDHU}%`,parseFloat(topDHU)>5?C.danger:C.success,parseFloat(topDHU)>5?C.dangerBg:C.successBg]
          ].map(([l,v,c,b])=>(
            <div key={l} style={{padding:'10px 16px',background:b,borderRadius:R.md,border:`1px solid ${c}22`,textAlign:'center',minWidth:110}}>
              <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
              <div style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase',marginTop:4}}>{l}</div>
            </div>
          ))}
        </div>
        <Card><SH title="Hourly Top / Inside Data" color={C.fin}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:750}}>
              <thead>
                <tr style={{background:C.slate50}}>
                  <th rowSpan={2} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,borderRight:`1px solid ${C.border}`,textAlign:'center'}}>Hour</th>
                  {[['INSIDE CHECK','#1564C0'],['TOP SIDE CHECK','#E65100']].map(([label,col])=>(
                    <th key={label} colSpan={3} style={{padding:'8px',fontWeight:700,color:col,fontSize:10,textTransform:'uppercase',borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,textAlign:'center'}}>{label}</th>
                  ))}
                </tr>
                <tr style={{background:C.slate50}}>
                  {['Checked','Passed','Defects','Checked','Passed','Defects'].map((h,i)=>(
                    <th key={i} style={{padding:'6px 8px',fontWeight:600,color:C.textSub,fontSize:10,borderBottom:`2px solid ${C.border}`,borderRight:`1px solid ${C.border}44`,textAlign:'center'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {F.hourlyData.map((h,i)=>(
                  <tr key={h.hour} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:'6px 8px',textAlign:'center',fontWeight:700,color:C.fin,borderRight:`1px solid ${C.border}`}}>{h.hour}</td>
                    {['insideChecked','insidePassed','insideDefects','topChecked','topPassed','topDefects'].map((k,ki)=>(
                      <td key={k} style={{padding:'4px 5px',borderRight:`1px solid ${C.border}${ki===2?'':'44'}`}}>
                        <input type="number" value={h[k]||''} onChange={e=>updH(i,k,e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center',
                            background:(k==='insideDefects'||k==='topDefects')&&parseInt(h[k])>0?C.dangerBg:C.white}}/>
                      </td>
                    ))}
                  </tr>
                ))}
                <tr style={{background:C.slate50,fontWeight:700}}>
                  <td style={{padding:'8px 10px',textAlign:'center',borderRight:`1px solid ${C.border}`}}>TOTAL</td>
                  {[totals.insideChecked,totals.insidePassed,totals.insideDefects,totals.topChecked,totals.topPassed,totals.topDefects].map((v,i)=>(
                    <td key={i} style={{padding:'8px 10px',textAlign:'center',color:i===2||i===5?C.danger:C.text}}>{v}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-60 · FINISHING QUALITY REPORT SUPERVISOR  (Nir/Fin-Qc/09)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann60({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const fresh=()=>({
      id:uid(),formType:'FIN_QC_SUPERVISOR',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',colour:filters.colour||'',
      lineNo:'',refNo:'',shift:SHIFTS[0],
      defects:FIN_DEFECTS.map(d=>({label:d,qty:0})),
      totalProcessWiseDefect:0,totalQCPass:0,totalQCCheck:0,
      todayDHU:0,
      measProblemGarments:0,sizeMistake:0,measOutTolerance:0,
      totalQuality:0,measFaultDHU:0,
      finishingIncharge:'',remarks:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const setDef=(i,v)=>setF(f=>({...f,defects:f.defects.map((d,di)=>di===i?{...d,qty:v}:d)}));
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_QC_SUPERVISOR');
    const totalDefects=F.defects.reduce((s,d)=>s+(d.qty||0),0);
    const dhu=F.totalQCCheck>0?((totalDefects/F.totalQCCheck)*100).toFixed(2):'0.00';
    const autoRes=()=>parseFloat(dhu)<=5?'PASS':'FAIL';

    const handleSave=async()=>{
      await doSave('finishing_qc_supervisor',{...F,totalDefects,dhu:parseFloat(dhu),result:autoRes(),savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-60 · Finishing Quality Report — Supervisor" ref_="Nir/Fin-Qc/09 · All quality findings"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Supervisor Quality Reports"/><Tbl
            headers={['Date','Style','Line','Buyer','QC Check','Defects','DHU%','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.lineNo||'—',r.buyer||'—',r.totalQCCheck||'—',
              r.totalDefects||0,`${r.dhu||0}%`,<Res result={r.result}/>])} emptyMsg="No records yet."/></Card>
        ):(
          <>
            <Card><SH title="Header" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
                <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Ref No." value={F.refNo} onChange={e=>set('refNo',e.target.value)} placeholder="Ref#..."/>
                <Fld label="Style" value={F.style} onChange={e=>set('style',e.target.value)} placeholder="Style..."/>
                <Fld label="Line No." value={F.lineNo} onChange={e=>set('lineNo',e.target.value)} options={['',...LINES]}/>
                <Fld label="Shift" value={F.shift} onChange={e=>set('shift',e.target.value)} options={SHIFTS}/>
                <Fld label="Total QC Check" type="number" value={F.totalQCCheck} onChange={e=>set('totalQCCheck',parseInt(e.target.value)||0)} placeholder="0"/>
                <Fld label="Total QC Pass" type="number" value={F.totalQCPass}  onChange={e=>set('totalQCPass',parseInt(e.target.value)||0)}  placeholder="0"/>
                <Fld label="Finishing Incharge" value={F.finishingIncharge} onChange={e=>set('finishingIncharge',e.target.value)} placeholder="Name..."/>
              </Grid>
            </Card>
            <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
              {[['QC Check',F.totalQCCheck,C.accent,'#EFF6FF'],['QC Pass',F.totalQCPass,C.success,C.successBg],
                ['Total Defects',totalDefects,C.danger,C.dangerBg],['DHU',`${dhu}%`,parseFloat(dhu)>5?C.danger:C.success,parseFloat(dhu)>5?C.dangerBg:C.successBg]
              ].map(([l,v,c,b])=>(
                <div key={l} style={{padding:'10px 16px',background:b,borderRadius:R.md,border:`1px solid ${c}22`,textAlign:'center',minWidth:100}}>
                  <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
                  <div style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase',marginTop:4}}>{l}</div>
                </div>
              ))}
            </div>
            <Card>
              <DefectGrid defects={F.defects} onChange={setDef} color={C.fin}/>
            </Card>
            <Card><SH title="Measurement Issues" color={C.fin}/>
              <Grid gap={14} min={160}>
                <Fld label="Meas. Problem GMT (+/-)" type="number" value={F.measProblemGarments} onChange={e=>set('measProblemGarments',e.target.value)} placeholder="0"/>
                <Fld label="Size Mistake (Label)" type="number" value={F.sizeMistake} onChange={e=>set('sizeMistake',e.target.value)} placeholder="0"/>
                <Fld label="Meas. Out Tolerance (3/4 & 1)" type="number" value={F.measOutTolerance} onChange={e=>set('measOutTolerance',e.target.value)} placeholder="0"/>
                <Fld label="Total Quality" type="number" value={F.totalQuality} onChange={e=>set('totalQuality',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>
            <Card>
              <Fld label="Remarks" type="textarea" rows={3} value={F.remarks} onChange={e=>set('remarks',e.target.value)} placeholder="Any remarks..."/>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-61 · QC MANAGER SUMMARY  (Nir/Fin-Qc/10)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann61({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const newRow=()=>({id:uid(),lineNo:'',qcPass:0,auditPass:0,totalLot:0,passPct:0,failPct:0});
    const fresh=()=>({
      id:uid(),formType:'FIN_QC_MANAGER',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',
      todayCartonAudited:0,todayAuditPassCtn:0,todayAuditFailCtn:0,totalDHU:0,
      rows:[newRow()],
      checkBy:'',fingMana:'',qgm:'',proGm:'',qaGm:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updR=(i,k,v)=>setF(f=>{
      const rows=[...f.rows];rows[i]={...rows[i],[k]:v};
      const pass=parseInt(rows[i].qcPass)||0;
      const total=parseInt(rows[i].totalLot)||1;
      rows[i].passPct=total>0?((pass/total)*100).toFixed(1):0;
      rows[i].failPct=total>0?(((total-pass)/total)*100).toFixed(1):0;
      return {...f,rows};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_QC_MANAGER');

    const handleSave=async()=>{
      await doSave('finishing_qc_manager',{...F,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-61 · QC Manager Summary" ref_="Nir/Fin-Qc/10 · Total quality activities summary"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
          </Grid>
        </Card>
        <Card><SH title="Carton Audit Summary" color={C.fin}/>
          <Grid gap={14} min={160}>
            <Fld label="Today Carton Audited"   type="number" value={F.todayCartonAudited}  onChange={e=>set('todayCartonAudited',e.target.value)}  placeholder="0"/>
            <Fld label="Today Audit Pass CTN"   type="number" value={F.todayAuditPassCtn}   onChange={e=>set('todayAuditPassCtn',e.target.value)}   placeholder="0"/>
            <Fld label="Today Audit Fail CTN"   type="number" value={F.todayAuditFailCtn}   onChange={e=>set('todayAuditFailCtn',e.target.value)}   placeholder="0"/>
            <Fld label="Total DHU %" type="number" value={F.totalDHU} onChange={e=>set('totalDHU',e.target.value)} placeholder="0.00"/>
          </Grid>
        </Card>
        <Card><SH title="Line-wise QC Status" color={C.fin}
          action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.fin}>＋ Add Line</Btn>}/>
          <div style={{display:'grid',gridTemplateColumns:'100px 100px 100px 100px 80px 80px 30px',gap:6,marginBottom:8}}>
            {['Line No.','QC Pass','Audit Pass','Total Lot','Pass %','Fail %',''].map(h=>(
              <div key={h} style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase'}}>{h}</div>
            ))}
          </div>
          {F.rows.map((r,i)=>(
            <div key={r.id} style={{display:'grid',gridTemplateColumns:'100px 100px 100px 100px 80px 80px 30px',gap:6,marginBottom:5,alignItems:'center'}}>
              <select value={r.lineNo} onChange={e=>updR(i,'lineNo',e.target.value)}
                style={{padding:'7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,fontFamily:'inherit'}}>
                <option value="">Line...</option>
                {LINES.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
              {['qcPass','auditPass','totalLot'].map(k=>(
                <input key={k} type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                  style={{padding:'7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center'}}/>
              ))}
              <div style={{textAlign:'center',fontWeight:700,color:C.success,padding:'7px'}}>{r.passPct}%</div>
              <div style={{textAlign:'center',fontWeight:700,color:parseFloat(r.failPct)>20?C.danger:C.warning,padding:'7px'}}>{r.failPct}%</div>
              <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:12,padding:'6px'}}>✕</button>
            </div>
          ))}
          <Grid gap={14} min={140} style={{marginTop:12}}>
            {[['checkBy','Check By'],['fingMana','Fin. Manager'],['qgm','QGM'],['proGm','Pro GM'],['qaGm','QA GM']].map(([k,l])=>(
              <Fld key={k} label={l} value={F[k]||''} onChange={e=>set(k,e.target.value)} placeholder="Name..."/>
            ))}
          </Grid>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-62 · HOURLY AUDIT REPORT FINISHED GOODS  (Nir/Fin-Qa/01)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann62({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const newHour=()=>({hour:'',lotSize:0,sampleSize:0,allowed:0,defects:0,result:'PASS',
      measurementAttached:'NO',majorDefect:'',reCheck:'',qcSupSign:''});
    const fresh=()=>({
      id:uid(),formType:'FIN_HOURLY_AUDIT',date:TODAY,
      nirRef:'',section:'',lineIncName:'',prevQtyPassed:0,
      poNo:filters.po||'',lineQCSName:'',orQty:filters.qty||'',
      buyer:filters.buyer||'',style:filters.style||'',colour:filters.colour||'',
      aqlLevel:'2.5',
      hourlyRows:HOURS.map(h=>({...newHour(),hour:h})),
      auditorSign:'',auditInchargeSign:'',gmFinalAudit:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updH=(i,k,v)=>setF(f=>{
      const rows=[...f.hourlyRows];rows[i]={...rows[i],[k]:v};
      if(k==='lotSize'){const aql=getAQL(v);rows[i].sampleSize=aql.n;rows[i].allowed=aql.ac;}
      if(k==='defects'||k==='allowed'){const d=parseInt(rows[i].defects)||0,a=parseInt(rows[i].allowed)||0;rows[i].result=d<=a?'PASS':'FAIL';}
      return {...f,hourlyRows:rows};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_HOURLY_AUDIT');
    const passCount=F.hourlyRows.filter(r=>r.lotSize>0&&r.result==='PASS').length;
    const failCount=F.hourlyRows.filter(r=>r.lotSize>0&&r.result==='FAIL').length;

    const handleSave=async()=>{
      await doSave('finishing_hourly_audit',{...F,passCount,failCount,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-62 · Hourly Audit Report for Finished Goods" ref_="Nir/Fin-Qa/01 · AQL 2.5 / 4"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Hourly Audit Records"/><Tbl
            headers={['Date','Client Ref','Style','Buyer','Pass Lots','Fail Lots']}
            rows={recs.map(r=>[r.date,r.clientRefRef||'—',r.style||'—',r.buyer||'—',
              <Bdg color={C.success} bg={C.successBg}>{r.passCount||0}</Bdg>,
              <Bdg color={C.danger}  bg={C.dangerBg }>{r.failCount||0}</Bdg>])}
            emptyMsg="No records yet."/></Card>
        ):(
          <>
            <Card><SH title="Audit Header" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
                <Fld label="Client Ref" value={F.clientRefRef} onChange={e=>set('nirRef',e.target.value)} placeholder="Client Ref..."/>
                <Fld label="Section" value={F.section} onChange={e=>set('section',e.target.value)} placeholder="Section..."/>
                <Fld label="Line Inc. Name" value={F.lineIncName} onChange={e=>set('lineIncName',e.target.value)} placeholder="Name..."/>
                <Fld label="PO No." value={F.poNo} onChange={e=>set('poNo',e.target.value)} options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Order Qty" type="number" value={F.orQty} onChange={e=>set('orQty',e.target.value)} placeholder="Auto from PO"/>
                <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="AQL Level" value={F.aqlLevel} onChange={e=>set('aqlLevel',e.target.value)} options={['1.0','1.5','2.5','4.0']}/>
                <Fld label="Line QC Name" value={F.lineQCSName} onChange={e=>set('lineQCSName',e.target.value)} placeholder="Name..."/>
                <Fld label="Prev. Qty Passed" type="number" value={F.prevQtyPassed} onChange={e=>set('prevQtyPassed',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>
            <div style={{display:'flex',gap:10}}>
              <Bdg color={C.success} bg={C.successBg}>✓ {passCount} Pass</Bdg>
              <Bdg color={C.danger}  bg={C.dangerBg }>✗ {failCount} Fail</Bdg>
            </div>
            <Card><SH title="Hourly Audit Log" color={C.fin}/>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:850}}>
                  <thead><tr style={{background:C.slate50}}>
                    {['Hour','Lot Size','Sample n','Allowed','Defects','Result','Meas?','Major Defect','Re-Check','QC Sup Sign'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {F.hourlyRows.map((r,i)=>(
                      <tr key={r.hour} style={{borderBottom:`1px solid ${C.border}`,
                        background:r.lotSize>0?(r.result==='PASS'?C.successBg:C.dangerBg):C.white}}>
                        <td style={{padding:'6px 8px',textAlign:'center',fontWeight:700,color:C.fin}}>{r.hour}</td>
                        <td style={{padding:'5px 6px'}}><input type="number" value={r.lotSize||''} onChange={e=>updH(i,'lotSize',e.target.value)} placeholder="0"
                          style={{width:70,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/></td>
                        <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:C.accent}}>{r.sampleSize||'—'}</td>
                        <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:C.fin}}>{r.allowed||'—'}</td>
                        <td style={{padding:'5px 6px'}}><input type="number" value={r.defects||''} onChange={e=>updH(i,'defects',e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center',fontWeight:700,
                            color:parseInt(r.defects)>(parseInt(r.allowed)||0)?C.danger:C.success}}/></td>
                        <td style={{padding:'5px 8px',textAlign:'center'}}>{r.lotSize>0?<Res result={r.result}/>:<span style={{color:C.textMuted}}>—</span>}</td>
                        <td style={{padding:'5px 6px'}}>
                          <select value={r.measurementAttached} onChange={e=>updH(i,'measurementAttached',e.target.value)}
                            style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                            {['YES','NO'].map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'5px 6px'}}>
                          <select value={r.majorDefect} onChange={e=>updH(i,'majorDefect',e.target.value)}
                            style={{width:'100%',minWidth:120,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                            <option value="">Select...</option>
                            {FIN_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'5px 6px'}}>
                          <select value={r.reCheck} onChange={e=>updH(i,'reCheck',e.target.value)}
                            style={{width:70,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                            <option value="">—</option>
                            {['PASS','FAIL'].map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'5px 6px'}}><input value={r.qcSupSign} onChange={e=>updH(i,'qcSupSign',e.target.value)} placeholder="Sign..."
                          style={{width:70,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Grid gap={14} min={180} style={{marginTop:12}}>
                <Fld label="Auditor Sign"       value={F.auditorSign}      onChange={e=>set('auditorSign',e.target.value)}      placeholder="Name..."/>
                <Fld label="Audit Incharge Sign" value={F.auditInchargeSign} onChange={e=>set('auditInchargeSign',e.target.value)} placeholder="Name..."/>
                <Fld label="GM Final Audit"      value={F.gmFinalAudit}     onChange={e=>set('gmFinalAudit',e.target.value)}     placeholder="Name..."/>
              </Grid>
              <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-63 · SUMMARY OF FINAL AUDIT — FINISHING  (Nir/Fin-Qa/02)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann63({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const newRow=()=>({id:uid(),line:'',totalOutput:0,totalLot:0,lotPass:0,lotFail:0,
      lotFailPct:0,totalInspQty:0,defects:'',totalDefect:0,button:0,washingDefect:0});
    const fresh=()=>({
      id:uid(),formType:'FIN_AUDIT_SUMMARY',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',colour:filters.colour||'',
      auditor:'',qaMngr:'',
      rows:['LINE-1','LINE-2','LINE-3','LINE-4'].map(l=>({...newRow(),line:l})),
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updR=(i,k,v)=>setF(f=>{
      const rows=[...f.rows];rows[i]={...rows[i],[k]:v};
      const fl=parseInt(rows[i].lotFail)||0,tot=parseInt(rows[i].totalLot)||1;
      rows[i].lotFailPct=((fl/tot)*100).toFixed(1);
      return {...f,rows};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_AUDIT_SUMMARY');
    const totLot=F.rows.reduce((s,r)=>s+(parseInt(r.totalLot)||0),0);
    const totPass=F.rows.reduce((s,r)=>s+(parseInt(r.lotPass)||0),0);
    const totFail=F.rows.reduce((s,r)=>s+(parseInt(r.lotFail)||0),0);
    const overallRes=totLot>0?(totFail/totLot<=0.2?'PASS':'FAIL'):'PENDING';

    const handleSave=async()=>{
      await doSave('finishing_audit_summary',{...F,totLot,totPass,totFail,result:overallRes,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-63 · Summary of Final Audit — Finishing" ref_="Nir/Fin-Qa/02"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Auditor" value={F.auditor} onChange={e=>set('auditor',e.target.value)} placeholder="Name..."/>
            <Fld label="QA Manager" value={F.qaMngr} onChange={e=>set('qaMngr',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
          {[['Total Lots',totLot,C.accent,'#EFF6FF'],['Lot Pass',totPass,C.success,C.successBg],
            ['Lot Fail',totFail,C.danger,C.dangerBg],[overallRes,overallRes,overallRes==='PASS'?C.success:C.danger,overallRes==='PASS'?C.successBg:C.dangerBg]
          ].map(([l,v,c,b])=>(
            <div key={l} style={{padding:'10px 16px',background:b,borderRadius:R.md,border:`1px solid ${c}22`,textAlign:'center',minWidth:90}}>
              <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
              <div style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase',marginTop:4}}>{l}</div>
            </div>
          ))}
        </div>
        <Card><SH title="Line-wise Audit Summary" color={C.fin}
          action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.fin}>＋ Add Line</Btn>}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:850}}>
              <thead><tr style={{background:C.slate50}}>
                {['Line','Total Output','Total Lot','Lot Pass','Lot Fail','Fail %','Insp. Qty','Defect Types','Total Def.','Button','Wash Def.',''].map(h=>(
                  <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.rows.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`,background:parseFloat(r.lotFailPct)>20?C.dangerBg:C.white}}>
                    <td style={{padding:'5px 8px'}}>
                      <select value={r.line} onChange={e=>updR(i,'line',e.target.value)}
                        style={{width:90,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                        {LINES.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                    </td>
                    {['totalOutput','totalLot','lotPass','lotFail'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{width:60,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:parseFloat(r.lotFailPct)>20?C.danger:C.success}}>{r.lotFailPct}%</td>
                    <td style={{padding:'5px 6px'}}>
                      <input type="number" value={r.totalInspQty||''} onChange={e=>updR(i,'totalInspQty',e.target.value)} placeholder="0"
                        style={{width:60,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/>
                    </td>
                    <td style={{padding:'5px 6px'}}>
                      <input value={r.defects||''} onChange={e=>updR(i,'defects',e.target.value)} placeholder="Defect types..."
                        style={{width:'100%',minWidth:120,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/>
                    </td>
                    {['totalDefect','button','washingDefect'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{width:60,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center',
                            background:parseInt(r[k])>0?C.dangerBg:C.white}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 6px'}}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:11,padding:'5px 7px'}}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar result={overallRes} saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-64 · FINAL / PRE-FINAL INSPECTION REPORT  (Nir/Fin-Qa/03)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann64({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const fresh=()=>({
      id:uid(),formType:'FINAL_INSP',date:TODAY,
      contract:'',orQty:filters.qty||'',style:filters.style||'',lineNo:'',
      colour:filters.colour||'',packQty:0,ctnQty:0,
      fabricContent:'',ctnMarking:'',grWt:'',fabricSource:'',ctnMeasurement:'',
      ntWt:'',washing:'',packingRatio:'',prepackRatio:'',
      buyer:filters.buyer||'',brand:filters.brand||'',
      // accessory checklist
      accessories:ACCESSORY_CHECKS.map(l=>({label:l,ok:null,remark:''})),
      // sample plan
      samplePlan:'AQL 2.5',acceptableLevel:'',defectUnitAllowed:'',
      noOfCtnTaken:0,
      // defects found
      defects:[],newDefectName:'',newDefectClassification:'Major',newDefectQty:1,
      // rework
      reworkDue:'',unassertedCtn:'',clrToneMixed:'',brokenPrepack:'',withoutPTkt:'',
      remarks:'',auditorSign:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const setAcc=(i,ok)=>setF(f=>({...f,accessories:f.accessories.map((a,ai)=>ai===i?{...a,ok}:a)}));
    const setAccRmk=(i,r)=>setF(f=>({...f,accessories:f.accessories.map((a,ai)=>ai===i?{...a,remark:r}:a)}));
    const recs=(db.finishing||[]).filter(r=>r.formType==='FINAL_INSP');

    const critCount=F.defects.filter(d=>d.classification==='Critical').reduce((s,d)=>s+(d.qty||0),0);
    const majCount =F.defects.filter(d=>d.classification==='Major').reduce((s,d)=>s+(d.qty||0),0);
    const minCount =F.defects.filter(d=>d.classification==='Minor').reduce((s,d)=>s+(d.qty||0),0);
    const aql=getAQL(F.orQty);
    const result=critCount>0?'FAIL':majCount>=aql.re?'FAIL':majCount>aql.ac?'HOLD':'PASS';

    const addDefect=()=>{
      if(!F.newDefectName) return;
      setF(f=>({...f,defects:[...f.defects,{id:uid(),name:f.newDefectName,
        classification:f.newDefectClassification,qty:parseInt(f.newDefectQty)||1}],
        newDefectName:'',newDefectClassification:'Major',newDefectQty:1}));
    };

    const handleSave=async()=>{
      await doSave('finishing_final_insp',{...F,critCount,majCount,minCount,result,
        sampleSize:aql.n,savedAt:new Date().toISOString()},db,setDb,r=>{
        setDb(prev=>({...prev,finalAudit:[...(prev.finalAudit||[]),r]}));
      });
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-64 · Final / Pre-Final Inspection Report" ref_="Nir/Fin-Qa/03 · Daily Customer CTN Inspection AQL"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Final Inspection Records"/><Tbl
            headers={['Date','Style','Buyer','Colour','Or.Qty','Sample','Critical','Major','Minor','Result']}
            rows={recs.map(r=>[r.date,r.style||'—',r.buyer||'—',r.colour||'—',r.orQty||'—',r.sampleSize||'—',
              <Bdg color='#6A1B9A'>{r.critCount||0}</Bdg>,
              <Bdg color={C.danger}  bg={C.dangerBg}>{r.majCount||0}</Bdg>,
              <Bdg color={C.warning} bg={C.warningBg}>{r.minCount||0}</Bdg>,
              <Res result={r.result}/>])} emptyMsg="No final inspections yet."/></Card>
        ):(
          <>
            <Card><SH title="Order Details" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
                <Fld label="Contract" value={F.contract} onChange={e=>set('contract',e.target.value)} placeholder="Contract#..."/>
                <Fld label="Order Qty" type="number" value={F.orQty} onChange={e=>set('orQty',e.target.value)} placeholder="Auto from PO"/>
                <Fld label="Style #" value={F.style} onChange={e=>set('style',e.target.value)} placeholder="Style..."/>
                <Fld label="Line #" value={F.lineNo} onChange={e=>set('lineNo',e.target.value)} options={['',...LINES]}/>
                <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Brand" value={F.brand} onChange={e=>set('brand',e.target.value)} options={['',...BRANDS]}/>
                <Fld label="Pack Qty" type="number" value={F.packQty} onChange={e=>set('packQty',e.target.value)} placeholder="0"/>
                <Fld label="CTN Qty" type="number" value={F.ctnQty}  onChange={e=>set('ctnQty',e.target.value)}  placeholder="0"/>
                <Fld label="Fabric Content" value={F.fabricContent} onChange={e=>set('fabricContent',e.target.value)} placeholder="100% Cotton..."/>
                <Fld label="Washing"        value={F.washing}       onChange={e=>set('washing',e.target.value)}       placeholder="Wash type..."/>
                <Fld label="Packing Ratio"  value={F.packingRatio}  onChange={e=>set('packingRatio',e.target.value)}  placeholder="S2 M4 L3..."/>
                <Fld label="No. CTN Taken"  type="number" value={F.noOfCtnTaken} onChange={e=>set('noOfCtnTaken',e.target.value)} placeholder="0"/>
              </Grid>
            </Card>

            {/* AQL calculator */}
            <div style={{padding:'14px 18px',background:'#EFF6FF',border:`1px solid ${C.accent}44`,borderRadius:R.lg}}>
              <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:10}}>AQL CALCULATOR — Lot: {F.orQty||'(enter order qty)'}</div>
              <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                {[['Sample Size',aql.n,C.accent],['Accept Qty (Ac)',aql.ac,C.success],['Reject Qty (Re)',aql.re,C.danger],
                  ['Critical Found',critCount,'#6A1B9A'],['Major Found',majCount,C.danger],['Minor Found',minCount,C.warning]
                ].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:'center',padding:'8px 14px',background:C.white,borderRadius:R.md,border:`1px solid ${c}22`}}>
                    <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:'uppercase'}}>{l}</div>
                  </div>
                ))}
                <div style={{padding:'8px 20px',background:result==='PASS'?C.successBg:result==='FAIL'?C.dangerBg:C.warningBg,
                  borderRadius:R.md,display:'flex',alignItems:'center',border:`2px solid ${result==='PASS'?C.successBd:result==='FAIL'?C.dangerBd:C.warningBd}`}}>
                  <div style={{fontSize:20,fontWeight:800,color:result==='PASS'?C.success:result==='FAIL'?C.danger:C.warning}}>
                    {result==='PASS'?'✅ ACCEPTED':result==='FAIL'?'❌ REJECTED':'⚠️ ON HOLD'}
                  </div>
                </div>
              </div>
            </div>

            {/* Accessory checklist */}
            <Card><SH title="Accessory Checklist" color={C.fin}
              action={<div style={{display:'flex',gap:6}}>
                <Bdg color={C.success} bg={C.successBg}>{F.accessories.filter(a=>a.ok===true).length} OK</Bdg>
                <Bdg color={C.danger}  bg={C.dangerBg }>{F.accessories.filter(a=>a.ok===false).length} NG</Bdg>
              </div>}/>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:0}}>
                {F.accessories.map((a,i)=>(
                  <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',padding:'8px 0',borderBottom:`1px solid ${C.border}`}}>
                    <div style={{display:'flex',gap:6,flexShrink:0,paddingTop:2}}>
                      {[['OK',true,C.success],['NG',false,C.danger]].map(([lbl,val,col])=>(
                        <label key={lbl} style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer'}}>
                          <input type="radio" checked={a.ok===val} onChange={()=>setAcc(i,val)}
                            style={{accentColor:col,width:14,height:14}}/>
                          <span style={{fontSize:11,fontWeight:700,color:a.ok===val?col:C.textMuted}}>{lbl}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,color:C.text}}>{a.label}</div>
                      {a.ok===false&&<input placeholder="Remark..." value={a.remark||''} onChange={e=>setAccRmk(i,e.target.value)}
                        style={{width:'100%',padding:'4px 8px',fontSize:11,borderRadius:R.sm,border:`1px solid ${C.dangerBd}`,background:C.dangerBg,marginTop:4}}/>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Defect log */}
            <Card><SH title="Defect Log" color={C.fin}/>
              <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
                <input value={F.newDefectName} onChange={e=>set('newDefectName',e.target.value)} placeholder="Defect description..."
                  style={{flex:2,minWidth:180,padding:'9px 12px',borderRadius:R.md,border:`1px solid ${C.border}`,fontSize:13}}/>
                <select value={F.newDefectClassification} onChange={e=>set('newDefectClassification',e.target.value)}
                  style={{padding:'9px 12px',borderRadius:R.md,border:`1px solid ${C.border}`,fontSize:13,fontFamily:'inherit'}}>
                  {['Critical','Major','Minor'].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                <input type="number" value={F.newDefectQty} onChange={e=>set('newDefectQty',e.target.value)} min={1}
                  style={{width:70,padding:'9px 12px',borderRadius:R.md,border:`1px solid ${C.border}`,fontSize:13,textAlign:'center'}}/>
                <Btn onClick={addDefect} color={C.fin} icon="➕">Add</Btn>
              </div>
              {F.defects.length>0&&<Tbl headers={['#','Defect','Classification','Qty','']}
                rows={F.defects.map((d,i)=>[i+1,d.name,
                  <Bdg color={d.classification==='Critical'?'#6A1B9A':d.classification==='Major'?C.danger:C.warning}>{d.classification}</Bdg>,
                  d.qty,
                  <button onClick={()=>setF(f=>({...f,defects:f.defects.filter((_,j)=>j!==i)}))}
                    style={{background:'none',border:'none',color:C.danger,cursor:'pointer',fontSize:14,fontWeight:700}}>✕</button>])}
                emptyMsg=""/>}
            </Card>

            <Card><SH title="Sign-off" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Remarks" type="textarea" rows={3} value={F.remarks} onChange={e=>set('remarks',e.target.value)} placeholder="Remarks..."/>
                <Fld label="Auditor Signature" value={F.auditorSign} onChange={e=>set('auditorSign',e.target.value)} placeholder="Name..."/>
              </Grid>
              <SaveBar result={result} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-65 · CTN AUDIT REPORT SUMMARY  (Nir/Fin-Qa/04)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann65({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const newRow=()=>({id:uid(),styleOrderNo:'',ttlCtn:0,pcsPerCtn:0,
      nosCtnAudit:0,errorFound:0,pf:'PASS',nosPcsAudit:0,unitAllow:0,pfPcs:'PASS'});
    const fresh=()=>({
      id:uid(),formType:'CTN_AUDIT_SUMMARY',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',
      ctnAuditor:'',rows:[newRow()],
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updR=(i,k,v)=>setF(f=>{
      const rows=[...f.rows];rows[i]={...rows[i],[k]:v};
      if(k==='errorFound'||k==='nosCtnAudit'){
        const err=parseInt(rows[i].errorFound)||0,tot=parseInt(rows[i].nosCtnAudit)||1;
        rows[i].pf=err/tot<=0.05?'PASS':'FAIL';
      }
      return {...f,rows};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='CTN_AUDIT_SUMMARY');
    const overallPass=F.rows.every(r=>r.pf==='PASS');

    const handleSave=async()=>{
      await doSave('finishing_ctn_audit',{...F,result:overallPass?'PASS':'FAIL',savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-65 · CTN Audit Report Summary" ref_="Nir/Fin-Qa/04 · Order-wise carton audit pass rate"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="CTN Auditor" value={F.ctnAuditor} onChange={e=>set('ctnAuditor',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <Card><SH title="Carton Audit Detail" color={C.fin}
          action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.fin}>＋ Add Row</Btn>}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:750}}>
              <thead><tr style={{background:C.slate50}}>
                {['Style / Order No.','Total CTN','Pcs / CTN','CTN Audited','Error Found','CTN P/F','Pcs Audited','Unit Allow','Pcs P/F',''].map(h=>(
                  <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.rows.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`,background:r.pf==='FAIL'?C.dangerBg:C.white}}>
                    <td style={{padding:'5px 6px'}}><input value={r.styleOrderNo} onChange={e=>updR(i,'styleOrderNo',e.target.value)} placeholder="Style/Order..."
                      style={{width:100,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    {['ttlCtn','pcsPerCtn','nosCtnAudit','errorFound'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center',
                            background:k==='errorFound'&&parseInt(r[k])>0?C.dangerBg:C.white}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 8px',textAlign:'center'}}>
                      <Bdg color={r.pf==='PASS'?C.success:C.danger} bg={r.pf==='PASS'?C.successBg:C.dangerBg}>{r.pf}</Bdg>
                    </td>
                    {['nosPcsAudit','unitAllow'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 8px',textAlign:'center'}}>
                      <select value={r.pfPcs} onChange={e=>updR(i,'pfPcs',e.target.value)}
                        style={{padding:'5px 8px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontWeight:700,fontFamily:'inherit',
                          background:r.pfPcs==='PASS'?C.successBg:C.dangerBg,color:r.pfPcs==='PASS'?C.success:C.danger}}>
                        {['PASS','FAIL'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{padding:'5px 6px'}}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:11,padding:'5px 7px'}}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar result={overallPass?'PASS':'FAIL'} saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-66 · HOURLY AUDIT STATUS  (Nir/Fin-Qa/05)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann66({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const fresh=()=>({
      id:uid(),formType:'FIN_HOURLY_STATUS',date:TODAY,
      nirRef:'',style:filters.style||'',buyer:filters.buyer||'',
      colour:filters.colour||'',
      hourlyData:HOURS.map(h=>({hour:h,lotSize:0,sampleSize:0,allowed:0,
        defects:0,result:'PASS',defects_desc:''})),
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updH=(i,k,v)=>setF(f=>{
      const hd=[...f.hourlyData];hd[i]={...hd[i],[k]:v};
      if(k==='lotSize'){const aql=getAQL(v);hd[i].sampleSize=aql.n;hd[i].allowed=aql.ac;}
      if(k==='defects'){hd[i].result=(parseInt(v)||0)<=(parseInt(hd[i].allowed)||0)?'PASS':'FAIL';}
      return {...f,hourlyData:hd};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_HOURLY_STATUS');
    const passLots=F.hourlyData.filter(h=>h.lotSize>0&&h.result==='PASS').length;
    const failLots=F.hourlyData.filter(h=>h.lotSize>0&&h.result==='FAIL').length;

    const handleSave=async()=>{
      await doSave('finishing_hourly_status',{...F,passLots,failLots,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-66 · Hourly Audit Status" ref_="Nir/Fin-Qa/05 · Hourly lot pass rate record"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref" value={F.clientRefRef} onChange={e=>set('nirRef',e.target.value)} placeholder="Client Ref..."/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
          </Grid>
        </Card>
        <div style={{display:'flex',gap:10}}>
          <Bdg color={C.success} bg={C.successBg}>✓ {passLots} Pass</Bdg>
          <Bdg color={C.danger}  bg={C.dangerBg}>✗ {failLots} Fail</Bdg>
        </div>
        <Card><SH title="Hourly Lot Pass Rate" color={C.fin}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:C.slate50}}>
                {['Hour','Lot Size','Sample n','Allowed','Defects','Result','Defect Description'].map(h=>(
                  <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.hourlyData.map((h,i)=>(
                  <tr key={h.hour} style={{borderBottom:`1px solid ${C.border}`,
                    background:h.lotSize>0?(h.result==='PASS'?C.successBg:C.dangerBg):C.white}}>
                    <td style={{padding:'6px 8px',textAlign:'center',fontWeight:700,color:C.fin}}>{h.hour}</td>
                    <td style={{padding:'5px 6px'}}><input type="number" value={h.lotSize||''} onChange={e=>updH(i,'lotSize',e.target.value)} placeholder="0"
                      style={{width:70,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/></td>
                    <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:C.accent}}>{h.sampleSize||'—'}</td>
                    <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:C.fin}}>{h.allowed||'—'}</td>
                    <td style={{padding:'5px 6px'}}><input type="number" value={h.defects||''} onChange={e=>updH(i,'defects',e.target.value)} placeholder="0"
                      style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center',fontWeight:700,
                        color:parseInt(h.defects)>(parseInt(h.allowed)||0)?C.danger:C.success}}/></td>
                    <td style={{padding:'5px 8px',textAlign:'center'}}>{h.lotSize>0?<Res result={h.result}/>:<span style={{color:C.textMuted}}>—</span>}</td>
                    <td style={{padding:'5px 6px'}}><input value={h.defects_desc||''} onChange={e=>updH(i,'defects_desc',e.target.value)} placeholder="Defect description..."
                      style={{width:'100%',minWidth:130,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-67 · HOURLY AUDIT PRODUCTION STATUS  (Nir/Fin-Qa/06)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann67({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const fresh=()=>({
      id:uid(),formType:'FIN_HOURLY_PROD',date:TODAY,
      nirRef:'',style:filters.style||'',buyer:filters.buyer||'',
      colour:filters.colour||'',lineManager:'',productionManager:'',
      qualityManager:'',qualityHOD:'',unitHOD:'',
      hourlyData:HOURS.map(h=>({hour:h,target:0,actual:0,cumTarget:0,cumActual:0,
        lotAudited:0,lotPass:0,lotFail:0,defects:''})),
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updH=(i,k,v)=>setF(f=>({...f,hourlyData:f.hourlyData.map((h,hi)=>hi===i?{...h,[k]:v}:h)}));
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIN_HOURLY_PROD');
    const totalTarget=F.hourlyData.reduce((s,h)=>s+(parseInt(h.target)||0),0);
    const totalActual=F.hourlyData.reduce((s,h)=>s+(parseInt(h.actual)||0),0);
    const totalLotPass=F.hourlyData.reduce((s,h)=>s+(parseInt(h.lotPass)||0),0);
    const totalLotFail=F.hourlyData.reduce((s,h)=>s+(parseInt(h.lotFail)||0),0);

    const handleSave=async()=>{
      await doSave('finishing_hourly_prod',{...F,totalTarget,totalActual,totalLotPass,totalLotFail,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-67 · Hourly Audit Production Status Report" ref_="Nir/Fin-Qa/06 · Hourly production tracking"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Client Ref" value={F.clientRefRef} onChange={e=>set('clientRef',e.target.value)} placeholder="Client Ref..."/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Line Manager" value={F.lineManager} onChange={e=>set('lineManager',e.target.value)} placeholder="Name..."/>
            <Fld label="Production Manager" value={F.productionManager} onChange={e=>set('productionManager',e.target.value)} placeholder="Name..."/>
            <Fld label="Quality Manager" value={F.qualityManager} onChange={e=>set('qualityManager',e.target.value)} placeholder="Name..."/>
            <Fld label="Quality HOD" value={F.qualityHOD} onChange={e=>set('qualityHOD',e.target.value)} placeholder="Name..."/>
            <Fld label="Unit HOD" value={F.unitHOD} onChange={e=>set('unitHOD',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          {[['Target',totalTarget,C.accent,'#EFF6FF'],['Actual',totalActual,C.fin,C.warningBg],
            ['Lot Pass',totalLotPass,C.success,C.successBg],['Lot Fail',totalLotFail,C.danger,C.dangerBg]
          ].map(([l,v,c,b])=>(
            <div key={l} style={{padding:'10px 16px',background:b,borderRadius:R.md,border:`1px solid ${c}22`,textAlign:'center',minWidth:100}}>
              <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
              <div style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase',marginTop:4}}>{l}</div>
            </div>
          ))}
        </div>
        <Card><SH title="Hourly Production & Audit Status" color={C.fin}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:750}}>
              <thead><tr style={{background:C.slate50}}>
                {['Hour','Target','Actual','Cum.Target','Cum.Actual','Lot Audited','Lot Pass','Lot Fail','Defects',''].map(h=>(
                  <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.hourlyData.map((h,i)=>(
                  <tr key={h.hour} style={{borderBottom:`1px solid ${C.border}`,
                    background:parseInt(h.lotFail)>0?C.warningBg:C.white}}>
                    <td style={{padding:'6px 8px',textAlign:'center',fontWeight:700,color:C.fin}}>{h.hour}</td>
                    {['target','actual','cumTarget','cumActual','lotAudited','lotPass','lotFail'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={h[k]||''} onChange={e=>updH(i,k,e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center',
                            background:k==='lotFail'&&parseInt(h[k])>0?C.dangerBg:C.white}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 6px'}}><input value={h.defects||''} onChange={e=>updH(i,'defects',e.target.value)} placeholder="Defects..."
                      style={{width:'100%',minWidth:100,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-68 · INLINE / PRE-FINAL / FINAL INSPECTION  (Nir/Fin-Qa/07)
  //  Buyer-wise / Style-wise internal inspection based on AQL 2.5
  // ═══════════════════════════════════════════════════════════════════════
  function Ann68({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const fresh=()=>({
      id:uid(),formType:'INLINE_FINAL',date:TODAY,
      orderNo:filters.style||'',poNo:filters.po||'',
      buyer:filters.buyer||'',colour:filters.colour||'',
      sampleSize:0,presentation:'',
      inspectionType:'AQL INLINE', // AQL INLINE | AQL PREFINAL | AQL FINAL | PRE SHIPMENT
      sewingFinishingQuality:'',
      // defects
      defects:FIN_DEFECTS.slice(0,15).map(d=>({label:d,minor:0,major:0})),
      totalMinor:0,totalMajor:0,
      remarks:'',qcManager:'',productionManager:'',qualityGM:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updDef=(i,type,v)=>setF(f=>{
      const defects=[...f.defects];defects[i]={...defects[i],[type]:parseInt(v)||0};
      return {...f,defects,
        totalMinor:defects.reduce((s,d)=>s+(d.minor||0),0),
        totalMajor:defects.reduce((s,d)=>s+(d.major||0),0)};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='INLINE_FINAL');
    const aql=getAQL(F.sampleSize);
    const result=F.totalMajor>=aql.re?'FAIL':F.totalMajor>aql.ac?'HOLD':'PASS';

    const handleSave=async()=>{
      await doSave('finishing_inline_final',{...F,result,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-68 · Inline / Pre-Final / Final Inspection" ref_="Nir/Fin-Qa/07 · AQL 2.5 Internal"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Inspection Records"/><Tbl
            headers={['Date','Order No.','Buyer','Type','Sample','Major','Minor','Result']}
            rows={recs.map(r=>[r.date,r.orderNo||'—',r.buyer||'—',
              <Bdg color={C.fin}>{r.inspectionType||'AQL'}</Bdg>,
              r.sampleSize||'—',r.totalMajor||0,r.totalMinor||0,<Res result={r.result}/>])}
            emptyMsg="No records yet."/></Card>
        ):(
          <>
            <Card><SH title="Inspection Header" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
                <Fld label="Order No." value={F.orderNo} onChange={e=>set('orderNo',e.target.value)} options={["", ...dbGet("poNumbers",[])]} placeholder="Order / PO..."/>
                <Fld label="PO No." value={F.poNo} onChange={e=>set('poNo',e.target.value)} options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Sample Size" type="number" value={F.sampleSize} onChange={e=>set('sampleSize',e.target.value)} placeholder="0"/>
                <Fld label="Inspection Type" value={F.inspectionType} onChange={e=>set('inspectionType',e.target.value)}
                  options={['AQL INLINE','AQL PREFINAL','AQL FINAL','PRE SHIPMENT']}/>
                <Fld label="Presentation" value={F.presentation} onChange={e=>set('presentation',e.target.value)} placeholder="Presentation..."/>
              </Grid>
            </Card>
            <div style={{padding:'12px 18px',background:result==='PASS'?C.successBg:result==='FAIL'?C.dangerBg:C.warningBg,
              border:`2px solid ${result==='PASS'?C.successBd:result==='FAIL'?C.dangerBd:C.warningBd}`,
              borderRadius:R.lg,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
              <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                {[['Sample n',aql.n,C.accent],['Accept (Ac)',aql.ac,C.success],['Reject (Re)',aql.re,C.danger],
                  ['Major Found',F.totalMajor,C.danger],['Minor Found',F.totalMinor,C.warning]
                ].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:'center'}}>
                    <div style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
                    <div style={{fontSize:10,color:C.textMuted,textTransform:'uppercase'}}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:18,fontWeight:800,color:result==='PASS'?C.success:result==='FAIL'?C.danger:C.warning}}>
                {result==='PASS'?'✅ PASS':result==='FAIL'?'❌ FAIL':'⚠️ HOLD'}
              </div>
            </div>
            <Card><SH title="Defect Log" color={C.fin}/>
              <div style={{display:'grid',gridTemplateColumns:'1fr 100px 100px',gap:6,marginBottom:8}}>
                {['Defect Name','Minor','Major'].map(h=>(
                  <div key={h} style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase'}}>{h}</div>
                ))}
              </div>
              {F.defects.map((d,i)=>(
                <div key={i} style={{display:'grid',gridTemplateColumns:'1fr 100px 100px',gap:6,marginBottom:5,alignItems:'center',
                  padding:'5px 8px',borderRadius:R.sm,
                  background:(d.minor||d.major)>0?C.dangerBg:C.slate50}}>
                  <div style={{fontSize:12,color:C.text,fontWeight:(d.minor||d.major)>0?600:400}}>{d.label}</div>
                  <input type="number" value={d.minor||''} onChange={e=>updDef(i,'minor',e.target.value)} placeholder="0"
                    style={{padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center',color:C.warning}}/>
                  <input type="number" value={d.major||''} onChange={e=>updDef(i,'major',e.target.value)} placeholder="0"
                    style={{padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center',color:C.danger}}/>
                </div>
              ))}
            </Card>
            <Card><SH title="Sign-off" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Sewing & Finishing Quality" type="textarea" rows={3} value={F.sewingFinishingQuality}
                  onChange={e=>set('sewingFinishingQuality',e.target.value)} placeholder="Quality observations..."/>
                <Fld label="Remarks" type="textarea" rows={3} value={F.remarks}
                  onChange={e=>set('remarks',e.target.value)} placeholder="Remarks..."/>
              </Grid>
              <Grid gap={14} min={180} style={{marginTop:12}}>
                <Fld label="Manager QC" value={F.qcManager} onChange={e=>set('qcManager',e.target.value)} placeholder="Name..."/>
                <Fld label="Manager Production" value={F.productionManager} onChange={e=>set('productionManager',e.target.value)} placeholder="Name..."/>
                <Fld label="Quality GM" value={F.qualityGM} onChange={e=>set('qualityGM',e.target.value)} placeholder="Name..."/>
              </Grid>
              <SaveBar result={result} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-69 · PROCESS CONTROL CHART / FIT TEST  (Nir/Fin-Qa/08)
  // ═══════════════════════════════════════════════════════════════════════
  function Ann69({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const fresh=()=>({
      id:uid(),formType:'FIT_TEST',date:TODAY,
      styleNo:filters.style||'',buyer:filters.buyer||'',colour:filters.colour||'',
      noPcsInspected:32, noPcsNotFitting:0, fitIssues:'',
      acceptedLevel:'32 units inspected — Accept: 02',
      // fit check rows
      fitRows:Array.from({length:5},(_,i)=>({id:uid(),pcsNo:i+1,size:'',frontOk:null,backOk:null,sideOk:null,issue:''})),
      finManager:'',productionGM:'',technicalGM:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updFit=(i,k,v)=>setF(f=>({...f,fitRows:f.fitRows.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
    const recs=(db.finishing||[]).filter(r=>r.formType==='FIT_TEST');
    const failCount=F.noPcsNotFitting;
    const autoRes=()=>failCount<=2?'PASS':'FAIL';

    const handleSave=async()=>{
      await doSave('finishing_fit_test',{...F,result:autoRes(),savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-69 · Fit Audit Report / Process Control Chart" ref_="Nir/Fin-Qa/08 · Fit test on dummy"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Fit Audit Records"/><Tbl
            headers={['Date','Style','Buyer','Inspected','Not Fitting','Fit Issues','Result']}
            rows={recs.map(r=>[r.date,r.styleNo||'—',r.buyer||'—',r.noPcsInspected||32,r.noPcsNotFitting||0,r.fitIssues||'—',<Res result={r.result}/>])}
            emptyMsg="No fit audit records yet."/></Card>
        ):(
          <>
            <Card><SH title="Fit Audit Header" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
                <Fld label="Style No." value={F.styleNo} onChange={e=>set('styleNo',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
                <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="No. Pcs Inspected" type="number" value={F.noPcsInspected} onChange={e=>set('noPcsInspected',e.target.value)} placeholder="32"/>
                <Fld label="No. Pcs NOT Fitting Good" type="number" value={F.noPcsNotFitting} onChange={e=>set('noPcsNotFitting',e.target.value)} placeholder="0"/>
                <Fld label="Accepted Level" value={F.acceptedLevel} onChange={e=>set('acceptedLevel',e.target.value)} placeholder="32 inspected — Accept: 02"/>
                <Fld label="Fit Issues" type="textarea" rows={3} value={F.fitIssues} onChange={e=>set('fitIssues',e.target.value)} placeholder="Describe fit issues found..."/>
              </Grid>
            </Card>
            <div style={{padding:'12px 18px',background:autoRes()==='PASS'?C.successBg:C.dangerBg,
              border:`2px solid ${autoRes()==='PASS'?C.successBd:C.dangerBd}`,borderRadius:R.lg,
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.textSub,textTransform:'uppercase'}}>Fit Audit Result</div>
                <div style={{fontSize:22,fontWeight:800,color:autoRes()==='PASS'?C.success:C.danger}}>{autoRes()}</div>
              </div>
              <div style={{fontSize:12,color:C.textSub}}>
                Not Fitting: <strong style={{color:C.danger}}>{failCount}</strong> / Accepted: 2 max
              </div>
            </div>
            <Card><SH title="Piece-wise Fit Check" color={C.fin}
              action={<Btn sm onClick={()=>setF(f=>({...f,fitRows:[...f.fitRows,{id:uid(),pcsNo:f.fitRows.length+1,size:'',frontOk:null,backOk:null,sideOk:null,issue:''}]}))} color={C.fin}>＋ Add Piece</Btn>}/>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:600}}>
                  <thead><tr style={{background:C.slate50}}>
                    {['Pcs #','Size','Front OK?','Back OK?','Side OK?','Issue',''].map(h=>(
                      <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {F.fitRows.map((r,i)=>(
                      <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`,
                        background:r.frontOk===false||r.backOk===false||r.sideOk===false?C.dangerBg:C.white}}>
                        <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700}}>{r.pcsNo}</td>
                        <td style={{padding:'5px 6px'}}>
                          <select value={r.size} onChange={e=>updFit(i,'size',e.target.value)}
                            style={{width:70,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                            <option value="">Size</option>
                            {SIZES.map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        {['frontOk','backOk','sideOk'].map(k=>(
                          <td key={k} style={{padding:'5px 8px',textAlign:'center'}}>
                            <div style={{display:'flex',gap:4,justifyContent:'center'}}>
                              {[['✓',true,C.success],['✗',false,C.danger]].map(([lbl,val,col])=>(
                                <button key={lbl} onClick={()=>updFit(i,k,val)}
                                  style={{padding:'4px 10px',borderRadius:R.sm,border:`1px solid ${r[k]===val?col:C.border}`,
                                    background:r[k]===val?(val?C.successBg:C.dangerBg):C.white,
                                    color:r[k]===val?col:C.textMuted,cursor:'pointer',fontWeight:700,fontSize:13}}>
                                  {lbl}
                                </button>
                              ))}
                            </div>
                          </td>
                        ))}
                        <td style={{padding:'5px 6px'}}><input value={r.issue} onChange={e=>updFit(i,'issue',e.target.value)} placeholder="Issue..."
                          style={{width:'100%',minWidth:100,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                        <td style={{padding:'5px 6px'}}>
                          <button onClick={()=>setF(f=>({...f,fitRows:f.fitRows.filter((_,ri)=>ri!==i)}))}
                            style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:11,padding:'5px 7px'}}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Grid gap={14} min={180} style={{marginTop:12}}>
                <Fld label="Fin. Manager" value={F.finManager} onChange={e=>set('finManager',e.target.value)} placeholder="Name..."/>
                <Fld label="Production GM" value={F.productionGM} onChange={e=>set('productionGM',e.target.value)} placeholder="Name..."/>
                <Fld label="Technical GM" value={F.technicalGM} onChange={e=>set('technicalGM',e.target.value)} placeholder="Name..."/>
              </Grid>
              <SaveBar result={autoRes()} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-70 · PLANT/FIELD SOURCE AUDIT REPORT — BOTTOMS  (Nir/Fin-Qa/09)
  //  Source Accuracy Report
  // ═══════════════════════════════════════════════════════════════════════
  function Ann70({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const [view,setView]=React.useState('form');
    const fresh=()=>({
      id:uid(),formType:'SOURCE_AUDIT',date:TODAY,
      poItem:'',brand:filters.brand||'',productCode:'',auditNumber:'',
      company:'',vendor:'',country:'',plant:'',
      poIssueDate:'',originalPoQty:filters.qty||'',season:'',
      createdBy:'',updatedBy:'',createDate:TODAY,
      garmentType:'BOTTOMS',dataSource:'Plant',
      wash:'Rigid',result:'PASS',
      auditLocation:'',auditor:'',auditDate:TODAY,
      unitsSampled:0,defectiveUnits:0,noOfDefects:0,
      pctDefectiveUnits:0,pctDefectsDHU:0,
      defects:SOURCE_AUDIT_DEFECTS.map(d=>({...d,qty:0})),
      // accuracy
      cartonsSampled:0,cartonsInError:0,unitsInErrors:0,overage:0,
      // measurements
      measRows:MEAS_POINTS_BTM.map(p=>({point:p,spec:'',actual:'',plusMinus:'',tolerance:'',pairs:Array.from({length:5},(_,i)=>({pairNo:i+1,ticketSize:'',measurement:'',deviation:''}))})),
      comments:'',
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const setDef=(i,v)=>setF(f=>({...f,defects:f.defects.map((d,di)=>di===i?{...d,qty:v}:d)}));
    const recs=(db.finishing||[]).filter(r=>r.formType==='SOURCE_AUDIT');
    const totalDefects=F.defects.reduce((s,d)=>s+(d.qty||0),0);
    const dhu=F.unitsSampled>0?((totalDefects/F.unitsSampled)*100).toFixed(2):'0.00';

    const handleSave=async()=>{
      await doSave('finishing_source_audit',{...F,totalDefects,dhu,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-70 · Plant/Field Source Audit — Bottoms" ref_="Source Accuracy Report — Plant / Field Audit"
          action={<div style={{display:'flex',gap:8}}>
            <Btn sm outline onClick={()=>setView('form')} color={C.fin}>＋ New</Btn>
            <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.fin}>📋 History ({recs.length})</Btn>
          </div>}/>
        {view==='history'?(
          <Card><SH title="Source Audit Records"/><Tbl
            headers={['Audit Date','PO Item','Brand','Vendor','Units Sampled','Defectives','DHU%','Result']}
            rows={recs.map(r=>[r.auditDate,r.poItem||'—',r.brand||'—',r.vendor||'—',
              r.unitsSampled||'—',r.defectiveUnits||'—',`${r.dhu||0}%`,<Res result={r.result}/>])}
            emptyMsg="No source audit records yet."/></Card>
        ):(
          <>
            <Card><SH title="Audit Header" color={C.fin}/>
              <Grid gap={14}>
                <Fld label="PO Item" value={F.poItem} onChange={e=>set('poItem',e.target.value)} placeholder="PO Item..."/>
                <Fld label="Brand" value={F.brand} onChange={e=>set('brand',e.target.value)} options={['',...BRANDS]}/>
                <Fld label="Product Code" value={F.productCode} onChange={e=>set('productCode',e.target.value)} placeholder="Product code..."/>
                <Fld label="Audit Number" value={F.auditNumber} onChange={e=>set('auditNumber',e.target.value)} placeholder="Audit#..."/>
                <Fld label="Company" value={F.company} onChange={e=>set('company',e.target.value)} placeholder="Company..."/>
                <Fld label="Vendor" value={F.vendor} onChange={e=>set('vendor',e.target.value)} placeholder="Vendor..."/>
                <Fld label="Country" value={F.country} onChange={e=>set('country',e.target.value)} placeholder="Country..."/>
                <Fld label="Plant" value={F.plant} onChange={e=>set('plant',e.target.value)} placeholder="Plant..."/>
                <Fld label="Original PO Qty" type="number" value={F.originalPoQty} onChange={e=>set('originalPoQty',e.target.value)} placeholder="0"/>
                <Fld label="Season" value={F.season} onChange={e=>set('season',e.target.value)} placeholder="Season..."/>
                <Fld label="Garment Type" value={F.garmentType} onChange={e=>set('garmentType',e.target.value)} options={['BOTTOMS','TOPS','BOTH']}/>
                <Fld label="Data Source" value={F.dataSource} onChange={e=>set('dataSource',e.target.value)} options={['Plant','Field','Certified','CSC']}/>
                <Fld label="Wash Type" value={F.wash} onChange={e=>set('wash',e.target.value)} options={['Rigid','Infinish','After Dryer','Final Audit']}/>
                <Fld label="Audit Date" type="date" value={F.auditDate} onChange={e=>set('auditDate',e.target.value)}/>
                <Fld label="Auditor" value={F.auditor} onChange={e=>set('auditor',e.target.value)} placeholder="Auditor name..."/>
                <Fld label="Audit Location" value={F.auditLocation} onChange={e=>set('auditLocation',e.target.value)} placeholder="Location..."/>
                <Fld label="Result" value={F.result} onChange={e=>set('result',e.target.value)} options={['PASS','FAIL','HOLD']}/>
              </Grid>
            </Card>
            <Card><SH title="Units & Defect Summary" color={C.fin}/>
              <Grid gap={14} min={130}>
                <Fld label="Units Sampled"    type="number" value={F.unitsSampled}    onChange={e=>set('unitsSampled',e.target.value)}    placeholder="0"/>
                <Fld label="Defective Units"  type="number" value={F.defectiveUnits}  onChange={e=>set('defectiveUnits',e.target.value)}  placeholder="0"
                  note={F.unitsSampled>0?`% Defective: ${((F.defectiveUnits/F.unitsSampled)*100).toFixed(1)}%`:''}/>
                <Fld label="No. of Defects"   type="number" value={F.noOfDefects}     onChange={e=>set('noOfDefects',e.target.value)}     placeholder="0"/>
                <div style={{padding:'10px 14px',background:parseFloat(dhu)>5?C.dangerBg:C.successBg,borderRadius:R.md,textAlign:'center',border:`1px solid ${parseFloat(dhu)>5?C.dangerBd:C.successBd}`}}>
                  <div style={{fontSize:20,fontWeight:800,color:parseFloat(dhu)>5?C.danger:C.success}}>{dhu}%</div>
                  <div style={{fontSize:10,fontWeight:700,color:C.textMuted,textTransform:'uppercase',marginTop:4}}>DHU</div>
                </div>
              </Grid>
            </Card>
            <Card>
              <SH title="Defects by Category" color={C.fin}/>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))',gap:7}}>
                {F.defects.map((d,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:R.sm,
                    background:d.qty>0?C.dangerBg:C.slate50,border:`1px solid ${d.qty>0?C.dangerBd:C.border}`}}>
                    <div style={{fontSize:11,fontWeight:700,color:C.accent,width:24,flexShrink:0}}>{d.code}</div>
                    <div style={{flex:1,fontSize:11,color:C.text}}>{d.name}</div>
                    <input type="number" value={d.qty||''} onChange={e=>setDef(i,parseInt(e.target.value)||0)}
                      style={{width:50,padding:'4px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:12,textAlign:'center',fontWeight:700,color:d.qty>0?C.danger:C.text}}/>
                  </div>
                ))}
              </div>
            </Card>
            <Card><SH title="Accuracy Information" color={C.fin}/>
              <Grid gap={14} min={140}>
                <Fld label="Cartons Sampled"   type="number" value={F.cartonsSampled}  onChange={e=>set('cartonsSampled',e.target.value)}  placeholder="0"/>
                <Fld label="Cartons in Error"  type="number" value={F.cartonsInError}  onChange={e=>set('cartonsInError',e.target.value)}  placeholder="0"/>
                <Fld label="Units in Errors"   type="number" value={F.unitsInErrors}   onChange={e=>set('unitsInErrors',e.target.value)}   placeholder="0"/>
                <Fld label="Overage"           type="number" value={F.overage}         onChange={e=>set('overage',e.target.value)}         placeholder="0"/>
              </Grid>
            </Card>
            <Card>
              <Fld label="Comments" type="textarea" rows={3} value={F.comments} onChange={e=>set('comments',e.target.value)} placeholder="Additional comments..."/>
              <SaveBar result={F.result} saving={saving} saved={saved} onSave={handleSave}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-71 · LOT FAIL % LINE / CTN AUDIT  (Nir/Fin-Qa/10)
  //  Auditor-wise lot failure report
  // ═══════════════════════════════════════════════════════════════════════
  function Ann71({db,setDb,filters}){
    const {saving,saved,doSave}=useSave();
    const newRow=()=>({id:uid(),auditorName:'',lineNo:'',totalLots:0,failedLots:0,
      failPct:0,ctnAudited:0,ctnFail:0,ctnFailPct:0,remarks:''});
    const fresh=()=>({
      id:uid(),formType:'LOT_FAIL_PCT',date:TODAY,
      style:filters.style||'',buyer:filters.buyer||'',colour:filters.colour||'',
      qaManager:'',rows:[newRow()],
    });
    const [F,setF]=React.useState(fresh());
    const set=(k,v)=>setF(f=>({...f,[k]:v}));
    const updR=(i,k,v)=>setF(f=>{
      const rows=[...f.rows];rows[i]={...rows[i],[k]:v};
      const fl=parseInt(rows[i].failedLots)||0,tot=parseInt(rows[i].totalLots)||1;
      rows[i].failPct=((fl/tot)*100).toFixed(1);
      const cf=parseInt(rows[i].ctnFail)||0,ca=parseInt(rows[i].ctnAudited)||1;
      rows[i].ctnFailPct=((cf/ca)*100).toFixed(1);
      return {...f,rows};
    });
    const recs=(db.finishing||[]).filter(r=>r.formType==='LOT_FAIL_PCT');
    const totFail=F.rows.reduce((s,r)=>s+(parseInt(r.failedLots)||0),0);
    const totLots=F.rows.reduce((s,r)=>s+(parseInt(r.totalLots)||0),0);
    const overallFailPct=totLots>0?((totFail/totLots)*100).toFixed(1):0;

    const handleSave=async()=>{
      await doSave('finishing_lot_fail',{...F,totFail,totLots,overallFailPct,savedAt:new Date().toISOString()},db,setDb);
      setF(fresh());
    };

    return(
      <div style={{padding:20,display:'flex',flexDirection:'column',gap:18}}>
        <PgHead title="Ann-71 · Lot Fail % — Line / CTN Audit" ref_="Nir/Fin-Qa/10 · Auditor-wise lot failure report"
          action={<Bdg color={C.fin}>{recs.length} records</Bdg>}/>
        <Card><SH title="Header" color={C.fin}/>
          <Grid gap={14}>
            <Fld label="Date" type="date" value={F.date} onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer" value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
            <Fld label="Colour" value={F.colour} onChange={e=>set('colour',e.target.value)} options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="QA Manager" value={F.qaManager} onChange={e=>set('qaManager',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <Bdg color={C.accent} bg="#EFF6FF">Total Lots: {totLots}</Bdg>
          <Bdg color={C.danger} bg={C.dangerBg}>Failed Lots: {totFail}</Bdg>
          <Bdg color={parseFloat(overallFailPct)>20?C.danger:C.success}
            bg={parseFloat(overallFailPct)>20?C.dangerBg:C.successBg}>
            Overall Fail %: {overallFailPct}%
          </Bdg>
        </div>
        <Card><SH title="Auditor-wise Lot Failure" color={C.fin}
          action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.fin}>＋ Add Auditor</Btn>}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:750}}>
              <thead><tr style={{background:C.slate50}}>
                {['Auditor Name','Line No.','Total Lots','Failed Lots','Lot Fail %','CTN Audited','CTN Fail','CTN Fail %','Remarks',''].map(h=>(
                  <th key={h} style={{padding:'8px 10px',fontWeight:700,color:C.textSub,fontSize:10,textTransform:'uppercase',borderBottom:`2px solid ${C.border}`,textAlign:'center',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.rows.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`,background:parseFloat(r.failPct)>20?C.dangerBg:C.white}}>
                    <td style={{padding:'5px 6px'}}><input value={r.auditorName} onChange={e=>updR(i,'auditorName',e.target.value)} placeholder="Auditor..."
                      style={{width:100,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    <td style={{padding:'5px 6px'}}>
                      <select value={r.lineNo} onChange={e=>updR(i,'lineNo',e.target.value)}
                        style={{width:90,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,fontFamily:'inherit'}}>
                        <option value="">Line</option>
                        {LINES.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                    </td>
                    {['totalLots','failedLots'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center'}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:parseFloat(r.failPct)>20?C.danger:C.success}}>{r.failPct}%</td>
                    {['ctnAudited','ctnFail'].map(k=>(
                      <td key={k} style={{padding:'5px 6px'}}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{width:65,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11,textAlign:'center',
                            background:k==='ctnFail'&&parseInt(r[k])>0?C.dangerBg:C.white}}/>
                      </td>
                    ))}
                    <td style={{padding:'5px 8px',textAlign:'center',fontWeight:700,color:parseFloat(r.ctnFailPct)>20?C.danger:C.success}}>{r.ctnFailPct}%</td>
                    <td style={{padding:'5px 6px'}}><input value={r.remarks||''} onChange={e=>updR(i,'remarks',e.target.value)} placeholder="Remarks..."
                      style={{width:'100%',minWidth:100,padding:'5px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:11}}/></td>
                    <td style={{padding:'5px 6px'}}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{background:C.dangerBg,border:'none',borderRadius:R.sm,cursor:'pointer',color:C.danger,fontWeight:700,fontSize:11,padding:'5px 7px'}}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  FINISHING DEPT SHELL
  // ═══════════════════════════════════════════════════════════════════════
  const SUB = [
    {id:'ann53',label:'Ann-53 Daily Quality',       icon:'📊',Comp:Ann53,ft:'FIN_DAILY_QUALITY'},
    {id:'ann54',label:'Ann-54 Process DHU',         icon:'📉',Comp:Ann54,ft:'FIN_PROCESS_DHU'},
    {id:'ann55',label:'Ann-55 Major 3 Defects',     icon:'⚠️',Comp:Ann55,ft:'FIN_MAJOR3'},
    {id:'ann56',label:'Ann-56 Meas. Status',        icon:'📏',Comp:Ann56,ft:'FIN_MEAS_STATUS'},
    {id:'ann57',label:'Ann-57 Meas. Inspection',    icon:'🔍',Comp:Ann57,ft:'FIN_MEAS_INSP'},
    {id:'ann58',label:'Ann-58 Hourly Meas.',        icon:'⏱️',Comp:Ann58,ft:'FIN_HOURLY_MEAS'},
    {id:'ann59',label:'Ann-59 Top/Inside DHU',      icon:'👕',Comp:Ann59,ft:'FIN_TOPSIDE_DHU'},
    {id:'ann60',label:'Ann-60 QC Supervisor',       icon:'👤',Comp:Ann60,ft:'FIN_QC_SUPERVISOR'},
    {id:'ann61',label:'Ann-61 QC Manager',          icon:'📋',Comp:Ann61,ft:'FIN_QC_MANAGER'},
    {id:'ann62',label:'Ann-62 Hourly Audit',        icon:'🔎',Comp:Ann62,ft:'FIN_HOURLY_AUDIT'},
    {id:'ann63',label:'Ann-63 Audit Summary',       icon:'📑',Comp:Ann63,ft:'FIN_AUDIT_SUMMARY'},
    {id:'ann64',label:'Ann-64 Final Inspection',    icon:'✅',Comp:Ann64,ft:'FINAL_INSP'},
    {id:'ann65',label:'Ann-65 CTN Audit',           icon:'📦',Comp:Ann65,ft:'CTN_AUDIT_SUMMARY'},
    {id:'ann66',label:'Ann-66 Hourly Status',       icon:'🟢',Comp:Ann66,ft:'FIN_HOURLY_STATUS'},
    {id:'ann67',label:'Ann-67 Hourly Prod. Status', icon:'🏭',Comp:Ann67,ft:'FIN_HOURLY_PROD'},
    {id:'ann68',label:'Ann-68 Inline/Final Insp.',  icon:'🎯',Comp:Ann68,ft:'INLINE_FINAL'},
    {id:'ann69',label:'Ann-69 Fit Audit',           icon:'👗',Comp:Ann69,ft:'FIT_TEST'},
    {id:'ann70',label:'Ann-70 Source Audit',        icon:'🌐',Comp:Ann70,ft:'SOURCE_AUDIT'},
    {id:'ann71',label:'Ann-71 Lot Fail %',          icon:'📈',Comp:Ann71,ft:'LOT_FAIL_PCT'},
  ];

  function FinishingDept({db,setDb,filters}){
    const [active,setActive]=React.useState('ann53');
    const counts=SUB.reduce((acc,f)=>({...acc,[f.id]:(db.finishing||[]).filter(r=>r.formType===f.ft).length}),{});
    const ActiveComp=SUB.find(f=>f.id===active)?.Comp;

    return(
      <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
        <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,overflowX:'auto',flexShrink:0}}>
          <div style={{display:'flex',paddingTop:4,paddingLeft:8,minWidth:'max-content'}}>
            {SUB.map(f=>(
              <button key={f.id} onClick={()=>setActive(f.id)} style={{
                padding:'10px 14px',fontSize:12,
                fontWeight:active===f.id?700:400,
                color:active===f.id?C.fin:C.textSub,
                background:'transparent',border:'none',cursor:'pointer',
                borderBottom:active===f.id?`2.5px solid ${C.fin}`:'2.5px solid transparent',
                display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap',
              }}>
                <span>{f.icon}</span>{f.label}
                {counts[f.id]>0&&(
                  <span style={{background:C.fin+'22',color:C.fin,padding:'1px 7px',borderRadius:10,fontSize:10,fontWeight:700}}>
                    {counts[f.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {ActiveComp&&<ActiveComp db={db} setDb={setDb} filters={filters}/>}
        </div>
      </div>
    );
  }

  // ── REGISTER ─────────────────────────────────────────────────────────────
  DEPT_REGISTRY['finishing'] = FinishingDept;
  console.log('[QMS] Dept-07 Finishing registered ✓  (Ann-53 → Ann-71, 19 forms)');

})();

// ════════════════════════════════════════════════════════════════════════════
//  DEPT-08 · MENDING / ALTERATION / SPOTTING  (Ann-72 → Ann-74)  — 3 forms
//  DEPT-09 · KILLED GARMENTS                  (Ann-75 → Ann-79)  — 5 forms
//  DEPT-10 · PROCESS CONTROL                  (Ann-80 → Ann-82)  — 3 forms
//
//  HOW TO USE: Paste this entire block at the very bottom of your file,
//  below the Finishing block  (below the last line: })(); )
//
//  MENDING / ALTERATION / SPOTTING:
//  Ann-72  Defective Garments Alteration Report   Nir/Rep/01 → mending_alteration
//  Ann-73  Spot/Mending/Alteration Inspection     Nir/Rep/02 → mending_spotting
//  Ann-74  Daily Line Wise Mending Report         Nir/Rep/03 → mending_daily
//
//  KILLED GARMENTS:
//  Ann-75  Killed GMT Tag                         Nir/Rej/01 → killed_tag
//  Ann-76  Killed GMT Records (Daily)             Nir/Rej/02 → killed_records
//  Ann-77  Killed Garments Register               Nir/Rej/03 → killed_register
//  Ann-78  Commercial Register                    Nir/Rej/04 → killed_commercial
//  Ann-79  Killed Garment Management Record       Nir/Rej/05 → killed_management
//
//  PROCESS CONTROL:
//  Ann-80  Sewing Line Audit Form                 Nir/Prc/03 → process_sewing_line
//  Ann-81  Exceptional Defect Control Chart       Nir/Prc/04 → process_defect_control
//  Ann-82  Sewing Process Control Register  Nir/Prc/05 → process_sewing_ctrl
// ════════════════════════════════════════════════════════════════════════════

(function registerFinalDepts() {

  // ── palette ───────────────────────────────────────────────────────────────
  const C = {
    white:'#FFFFFF', slate50:'#F8F9FA', slate100:'#F4F6F8',
    border:'#DEE2E6', text:'#1A2A3A', textSub:'#6C757D', textMuted:'#ADB5BD',
    mend  :'#DB2777',   // mending — pink
    killed:'#9F1239',   // killed  — deep red
    proc  :'#0F766E',   // process — teal
    success:'#2E7D32', successBg:'#E8F5E9', successBd:'#A5D6A7',
    danger :'#C62828',  dangerBg :'#FFEBEE', dangerBd :'#FFCDD2',
    warning:'#E65100',  warningBg:'#FFF8E1', warningBd:'#FFE082',
  };
  const R   = { sm:'6px', md:'8px', lg:'12px' };
  const API = 'https://nirvikalp.integrashion.com/api/save_universal.php';
  const TODAY = new Date().toISOString().split('T')[0];
  const HOURS = ['08:00-09:00','09:00-10:00','10:00-11:00','11:00-12:30',
                 '12:30-02:00','02:00-03:00','03:00-04:00','04:00-05:00',
                 '05:00-06:00','06:00-07:00'];

  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }

  // ── master data ───────────────────────────────────────────────────────────
  const BUYERS = dbGet('buyers', _FB_BUYERS);
  const BRANDS = dbGet('brands', _FB_BRANDS);
  const LINES = dbGet('lines', _FB_LINES);
  const SHIFTS  = ['DAY','NIGHT','GENERAL'];
  const SIZES = dbGet('sizes', ['XS','S','M','L','XL','XXL','3XL','28','30','32','34','36','38','40']);

  const ALL_DEFECTS = [
    'Broken Stitch','Open Seam','Skip Stitch','Raw Edge','Thread Tension','Puckering',
    'Uneven Shape','Spot / Oil Spot','Missing Bartack','Fabric Hole','Fabric Flaw',
    'Shade Variation','Wrong Measurement','Wrong Label','Wrong Size','Thread Untrimmed',
    'Button Defect','Zipper Defect','Seam Reverse','W/B Puckering','Iron Defects',
    'Wash Bleeding','Patch Mark','Dirty / Stain','Slanted Parts','Other',
  ];

  const SEWING_OPERATIONS = [
    'Loops fabric tack + bk lining tack', 'Loop making (6 Loops)',
    'Loop cut and bundle', 'Attach piping to waist band',
    'Mark waist band with label position', 'Attach main & fit label to W/B',
    'Front pocket facing att to pkt bag', 'Close front pocket bag w/turn',
    'Pocket bag 1/8 top st (Front)', 'Attach top welt to pkt bag',
    'St darts at bk (1×2)', 'Att welt to bk', 'Turn welt pocket helper',
    'Tack welt corners', 'T/S btm welt facing', 'Press back welt',
    'Button hole BK PKT w/guide', 'Button tab closer att to back',
    'Hidden stitch on pkt', '1/8 TS on back pkt bag',
    'Att Dockers External Label', 'Back pkt tacking & size label joint',
    'St Front inverter Darts L/R', 'Set pocket bag to front side L',
    'Set pocket bag to front side R', 'Tack pkt on side & top',
    'D.fly att to front with zipper', 'Single fly att to front & make D.fly',
    'Match Front & Back + loop', 'Inseam',
    'Side seam (R)', 'Side seam (L)',
    'Piping att to back, front panel and fly', 'Zipper to front by tp st',
    'Join front crotch', 'Attach zipper to left fly facing',
    'Join back rise', 'Waist and loop position mark',
    'Tack loop to waist line (6)', 'WB match and body Hanging',
    'Attach W/B to body', 'Mark hook and bar',
    'Hook and bar attach (1+1)', 'Button Attach (1)',
    'Band end finish (L) + trim excess', 'Band end finish (R) + trim excess',
    'Waist band ditch stitch', 'Waist band end tack',
    'Loops length marking', 'Reverse the garment',
    'J st by pattern', 'Ovl Bottom Hem', 'Bottom Hem',
    'Loop bartack w/fold (12) + Bk pkt', 'B/tack on front pkt + fly + inseam + front inside pkt',
    'Button hole at waist FRONT', 'Thread Trimming',
    'Waist band stretch check', 'Front pkt opening check', 'Front pkt facing check',
  ];

  // ── shared API save ───────────────────────────────────────────────────────
  async function saveRec(category, record, dbKey, db, setDb) {
    try {
      const res = await fetch(API, { method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ category, data:record }) });
      const j = await res.json();
      record.serverId = j.id || Date.now();
    } catch { record.serverId = Date.now(); }
    setDb(prev => {
      const list = prev[dbKey] || [];
      const idx  = list.findIndex(r => r.id === record.id);
      return { ...prev, [dbKey]: idx >= 0
        ? list.map(r => r.id === record.id ? record : r)
        : [...list, record] };
    });
  }

  // ── mini UI ───────────────────────────────────────────────────────────────
  function Fld({ label, value, onChange, type='text', readOnly=false,
    placeholder='', options=null, required=false, note='', rows=3 }) {
    const base = { width:'100%', padding:'9px 11px', fontSize:13, borderRadius:R.md,
      border:`1px solid ${C.border}`, background:readOnly?C.slate50:C.white,
      color:readOnly?C.textSub:C.text, outline:'none', fontFamily:'inherit' };
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        <label style={{ fontSize:11, fontWeight:600, color:C.textSub,
          textTransform:'uppercase', letterSpacing:'.4px' }}>
          {label}{required && <span style={{ color:C.danger }}> *</span>}
        </label>
        {options
          ? <select value={value} onChange={onChange} disabled={readOnly}
              style={{ ...base, cursor:readOnly?'default':'pointer' }}>
              {options.map((o,i) => <option key={i} value={o.value??o}>{o.label??o}</option>)}
            </select>
          : type==='textarea'
          ? <textarea value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} rows={rows} style={{ ...base, resize:'vertical' }}/>
          : <input type={type} value={value} onChange={onChange} readOnly={readOnly}
              placeholder={placeholder} style={base}/>}
        {note && <div style={{ fontSize:11, color:C.textMuted }}>{note}</div>}
      </div>
    );
  }

  function Btn({ children, onClick, color=C.mend, outline=false, sm=false, disabled=false, icon=null }) {
    return (
      <button onClick={onClick} disabled={disabled} style={{
        display:'flex', alignItems:'center', gap:5,
        padding:sm?'7px 13px':'10px 20px', borderRadius:R.md,
        border:outline?`2px solid ${color}`:'none',
        background:disabled?C.slate100:outline?'transparent':color,
        color:disabled?C.textMuted:outline?color:C.white,
        fontWeight:700, fontSize:sm?12:13, cursor:disabled?'default':'pointer',
        fontFamily:'inherit', transition:'all .15s',
      }}>{icon&&<span>{icon}</span>}{children}</button>
    );
  }

  function Bdg({ children, color=C.mend, bg }) {
    return <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
      color, background:bg||color+'22', display:'inline-block', whiteSpace:'nowrap' }}>{children}</span>;
  }

  function Res({ result }) {
    const m = { PASS:{c:C.success,b:C.successBg}, FAIL:{c:C.danger,b:C.dangerBg},
      HOLD:{c:C.warning,b:C.warningBg}, PENDING:{c:C.textMuted,b:C.slate100} };
    const x = m[result?.toUpperCase()] || m.PENDING;
    return <Bdg color={x.c} bg={x.b}>{result||'PENDING'}</Bdg>;
  }

  function Card({ children, style={} }) {
    return <div style={{ background:C.white, border:`1px solid ${C.border}`,
      borderRadius:R.lg, padding:20, ...style }}>{children}</div>;
  }

  function SH({ title, color=C.mend, action=null }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, paddingBottom:12, borderBottom:`2px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:3, height:18, background:color, borderRadius:2 }}/>
          <h3 style={{ fontSize:13, fontWeight:700, color:C.text,
            textTransform:'uppercase', letterSpacing:'.4px' }}>{title}</h3>
        </div>
        {action}
      </div>
    );
  }

  function Grid({ children, gap=14, min=180 }) {
    return <div style={{ display:'grid',
      gridTemplateColumns:`repeat(auto-fit,minmax(${min}px,1fr))`, gap }}>{children}</div>;
  }

  function Tbl({ headers=[], rows=[], emptyMsg='No records found.' }) {
    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead><tr style={{ background:C.slate50 }}>
            {headers.map((h,i) => <th key={i} style={{ padding:'9px 11px', textAlign:'left',
              fontWeight:700, color:C.textSub, fontSize:11, textTransform:'uppercase',
              borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length===0
              ? <tr><td colSpan={headers.length} style={{ padding:24, textAlign:'center',
                  color:C.textMuted, fontStyle:'italic' }}>{emptyMsg}</td></tr>
              : rows.map((row,i) => <tr key={i} style={{ borderBottom:`1px solid ${C.border}`,
                  background:i%2===0?C.white:C.slate50 }}>
                  {row.map((cell,j) => <td key={j} style={{ padding:'9px 11px', color:C.text,
                    verticalAlign:'middle' }}>{cell}</td>)}
                </tr>)}
          </tbody>
        </table>
      </div>
    );
  }

  function PgHead({ title, ref_, action }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
        flexWrap:'wrap', gap:10, marginBottom:18 }}>
        <div>
          <h2 style={{ fontSize:16, fontWeight:800, color:C.text }}>{title}</h2>
          <div style={{ fontSize:11, color:C.textMuted }}>Ref: {ref_}</div>
        </div>
        {action}
      </div>
    );
  }

  function useSave(dbKey) {
    const [saving, setSaving] = React.useState(false);
    const [saved,  setSaved]  = React.useState(false);
    async function doSave(category, record, db, setDb) {
      setSaving(true);
      await saveRec(category, record, dbKey, db, setDb);
      setSaving(false); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    return { saving, saved, doSave };
  }

  function SaveBar({ result, saving, saved, onSave, color }) {
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        flexWrap:'wrap', gap:10, marginTop:16, paddingTop:14,
        borderTop:`1px solid ${C.border}` }}>
        {result
          ? <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ fontSize:11, color:C.textSub }}>Result</div>
              <Res result={result}/>
            </div>
          : <div/>}
        <Btn onClick={onSave} disabled={saving} color={color||C.mend}
          icon={saved?'✓':saving?'…':'💾'}>
          {saved?'Saved!':saving?'Saving…':'Save Report'}
        </Btn>
      </div>
    );
  }

  // ╔══════════════════════════════════════════════════════════════════════╗
  // ║   MENDING / ALTERATION / SPOTTING  (Ann-72 → Ann-74)                ║
  // ╚══════════════════════════════════════════════════════════════════════╝

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-72 · DEFECTIVE GARMENTS ALTERATION REPORT  (Nir/Rep/01)
  //  Hourly altered garments inspection report
  // ═══════════════════════════════════════════════════════════════════════
  function Ann72({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('mending');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'ALTERATION_REPORT', date:TODAY,
      buyerPoNo:filters.po||'', colour:filters.colour||'',
      buyer:filters.buyer||'', style:filters.style||'',
      inspectorName:'', idNo:'',
      noInspected:0, noPassed:0, noFailed:0, noKilled:0, pctFailed:0,
      hourlyData: HOURS.map(h => ({
        hour:h, inspected:0, passed:0, failed:0, defect:'', remarks:''
      })),
      majorDefect1:'', majorDefect2:'', majorDefect3:'',
      qualityManager:'',
    });

    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updH = (i,k,v) => setF(f => ({
      ...f,
      hourlyData: f.hourlyData.map((h,hi) => hi===i ? { ...h,[k]:v } : h),
    }));
    const recs = (db.mending||[]).filter(r => r.formType==='ALTERATION_REPORT');

    const totInsp  = F.hourlyData.reduce((s,h) => s+(parseInt(h.inspected)||0), 0);
    const totPassed= F.hourlyData.reduce((s,h) => s+(parseInt(h.passed)||0),    0);
    const totFailed= F.hourlyData.reduce((s,h) => s+(parseInt(h.failed)||0),    0);
    const failPct  = totInsp>0 ? ((totFailed/totInsp)*100).toFixed(1) : 0;

    const handleSave = async () => {
      await doSave('mending_alteration',
        { ...F, totInsp, totPassed, totFailed, failPct, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-72 · Defective Garments Alteration Report"
          ref_="Nir/Rep/01 · Hourly altered garments inspection — Killed GMT Room"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.mend}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.mend}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Alteration Report Records" color={C.mend}/>
            <Tbl headers={['Date','Style','Buyer','Inspected','Passed','Failed','Fail %']}
              rows={recs.map(r=>[r.date,r.style||'—',r.buyer||'—',r.totInsp||0,
                r.totPassed||0,r.totFailed||0,`${r.failPct||0}%`])}
              emptyMsg="No alteration reports yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Report Header" color={C.mend}/>
              <Grid gap={14}>
                <Fld label="Date"         type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
                <Fld label="Buyer / PO No" value={F.buyerPoNo}   onChange={e=>set('buyerPoNo',e.target.value)}   options={["", ...dbGet("poNumbers",[]).map(p => p)]} placeholder="Buyer / PO..."/>
                <Fld label="Buyer"         value={F.buyer}       onChange={e=>set('buyer',e.target.value)}       options={['',...BUYERS]}/>
                <Fld label="Style No."     value={F.style}       onChange={e=>set('style',e.target.value)}       options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Colour"        value={F.colour}      onChange={e=>set('colour',e.target.value)}      options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Inspector Name" value={F.inspectorName} onChange={e=>set('inspectorName',e.target.value)} placeholder="Name..."/>
                <Fld label="ID No."         value={F.idNo}          onChange={e=>set('idNo',e.target.value)}          placeholder="ID#..."/>
              </Grid>
            </Card>

            {/* Summary tiles */}
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              {[
                ['Total Inspected', totInsp,  '#00897B','#EFF6FF'],
                ['Total Passed',    totPassed, C.success, C.successBg],
                ['Total Failed',    totFailed, C.danger,  C.dangerBg],
                ['Fail %',          `${failPct}%`, parseFloat(failPct)>5?C.danger:C.success,
                  parseFloat(failPct)>5?C.dangerBg:C.successBg],
              ].map(([l,v,c,b]) => (
                <div key={l} style={{ padding:'10px 16px', background:b, borderRadius:R.md,
                  border:`1px solid ${c}22`, textAlign:'center', minWidth:110 }}>
                  <div style={{ fontSize:22, fontWeight:800, color:c }}>{v}</div>
                  <div style={{ fontSize:10, fontWeight:700, color:C.textMuted,
                    textTransform:'uppercase', marginTop:4 }}>{l}</div>
                </div>
              ))}
            </div>

            <Card>
              <SH title="Hourly Alteration Data" color={C.mend}/>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:C.slate50 }}>
                      {['Hour / Time','No. Inspected','No. Passed','No. Failed','Defect','Remarks'].map(h=>(
                        <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                          fontSize:11, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`,
                          textAlign:'center' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {F.hourlyData.map((h,i) => (
                      <tr key={h.hour} style={{ borderBottom:`1px solid ${C.border}`,
                        background:(parseInt(h.failed)||0)>0?C.dangerBg:C.white }}>
                        <td style={{ padding:'6px 10px', textAlign:'center',
                          fontWeight:700, color:C.mend }}>{h.hour}</td>
                        {['inspected','passed','failed'].map(k => (
                          <td key={k} style={{ padding:'5px 8px', textAlign:'center' }}>
                            <input type="number" value={h[k]||''} onChange={e=>updH(i,k,e.target.value)}
                              placeholder="0"
                              style={{ width:75, padding:'5px', borderRadius:R.sm,
                                border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                                background:k==='failed'&&parseInt(h[k])>0?C.dangerBg:C.white }}/>
                          </td>
                        ))}
                        <td style={{ padding:'5px 8px' }}>
                          <select value={h.defect} onChange={e=>updH(i,'defect',e.target.value)}
                            style={{ width:'100%', minWidth:130, padding:'5px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                            <option value="">Select defect...</option>
                            {ALL_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:'5px 8px' }}>
                          <input value={h.remarks||''} onChange={e=>updH(i,'remarks',e.target.value)}
                            placeholder="Remark..."
                            style={{ width:'100%', minWidth:100, padding:'5px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background:C.slate50, fontWeight:700 }}>
                      <td style={{ padding:'8px 10px', textAlign:'center' }}>G. TOTAL</td>
                      <td style={{ padding:'8px 10px', textAlign:'center' }}>{totInsp}</td>
                      <td style={{ padding:'8px 10px', textAlign:'center', color:C.success }}>{totPassed}</td>
                      <td style={{ padding:'8px 10px', textAlign:'center', color:C.danger }}>{totFailed}</td>
                      <td colSpan={2} style={{ padding:'8px 10px', textAlign:'center', color:C.textSub }}>
                        Fail %: <strong style={{ color:parseFloat(failPct)>5?C.danger:C.success }}>{failPct}%</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            <Card>
              <SH title="Major 3 Defects & Sign-off" color={C.mend}/>
              <Grid gap={14}>
                <Fld label="#1 Major Defect" value={F.majorDefect1} onChange={e=>set('majorDefect1',e.target.value)} options={['',...ALL_DEFECTS]}/>
                <Fld label="#2 Major Defect" value={F.majorDefect2} onChange={e=>set('majorDefect2',e.target.value)} options={['',...ALL_DEFECTS]}/>
                <Fld label="#3 Major Defect" value={F.majorDefect3} onChange={e=>set('majorDefect3',e.target.value)} options={['',...ALL_DEFECTS]}/>
                <Fld label="Quality Manager" value={F.qualityManager} onChange={e=>set('qualityManager',e.target.value)} placeholder="Name..."/>
              </Grid>
              <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.mend}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-73 · SPOT / MENDING / ALTERATION INSPECTION  (Nir/Rep/02)
  //  Hourly quality inspection & pass rate after spotting/mending
  // ═══════════════════════════════════════════════════════════════════════
  function Ann73({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('mending');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'SPOTTING_INSP', date:TODAY,
      buyer:filters.buyer||'', style:filters.style||'',
      colour:filters.colour||'', po:filters.po||'',
      inspectorName:'', idNo:'', unit:'UNIT-2',
      inspType:'SPOTTING', // SPOTTING | MENDING | ALTERATION
      hourlyData: HOURS.map(h => ({
        hour:h,
        insideChecked:0, insidePassed:0,
        outsideChecked:0, outsidePassed:0,
        backPart:0, frontPart:0,
        pktLeft:0, pktRight:0,
        getUp:'', qcSign:'',
      })),
      qualitySupervisor:'',
    });

    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updH = (i,k,v) => setF(f => ({
      ...f, hourlyData: f.hourlyData.map((h,hi) => hi===i ? { ...h,[k]:v } : h),
    }));
    const recs = (db.mending||[]).filter(r => r.formType==='SPOTTING_INSP');

    const totInsideChk = F.hourlyData.reduce((s,h)=>s+(parseInt(h.insideChecked)||0), 0);
    const totInsidePas = F.hourlyData.reduce((s,h)=>s+(parseInt(h.insidePassed)||0), 0);
    const totOutsideChk= F.hourlyData.reduce((s,h)=>s+(parseInt(h.outsideChecked)||0), 0);
    const totOutsidePas= F.hourlyData.reduce((s,h)=>s+(parseInt(h.outsidePassed)||0), 0);

    const handleSave = async () => {
      await doSave('mending_spotting',
        { ...F, totInsideChk, totInsidePas, totOutsideChk, totOutsidePas,
          savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-73 · Spot / Mending / Alteration Inspection"
          ref_="Nir/Rep/02 · Hourly quality inspection after spotting/mending/alteration"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.mend}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.mend}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Spotting / Mending Records" color={C.mend}/>
            <Tbl headers={['Date','Style','Buyer','Type','Inside Chk','Inside Pass','Outside Chk','Outside Pass']}
              rows={recs.map(r=>[r.date,r.style||'—',r.buyer||'—',
                <Bdg color={C.mend}>{r.inspType||'SPOTTING'}</Bdg>,
                r.totInsideChk||0,r.totInsidePas||0,r.totOutsideChk||0,r.totOutsidePas||0])}
              emptyMsg="No spotting inspection records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Header" color={C.mend}/>
              <Grid gap={14}>
                <Fld label="Date"          type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
                <Fld label="Buyer"         value={F.buyer}         onChange={e=>set('buyer',e.target.value)}         options={['',...BUYERS]}/>
                <Fld label="Style No."     value={F.style}         onChange={e=>set('style',e.target.value)}         options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Colour"        value={F.colour}        onChange={e=>set('colour',e.target.value)}        options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="PO No."        value={F.po}            onChange={e=>set('po',e.target.value)}            options={["",...dbGet("poNumbers",[])]} placeholder="PO No..."/>
                <Fld label="Inspection Type" value={F.inspType}    onChange={e=>set('inspType',e.target.value)}      options={['SPOTTING','MENDING','ALTERATION']}/>
                <Fld label="Inspector Name" value={F.inspectorName} onChange={e=>set('inspectorName',e.target.value)} placeholder="Name..."/>
                <Fld label="ID No."         value={F.idNo}          onChange={e=>set('idNo',e.target.value)}          placeholder="ID#..."/>
                <Fld label="Unit"           value={F.unit}          onChange={e=>set('unit',e.target.value)}          options={['UNIT-1','UNIT-2','UNIT-3']}/>
              </Grid>
            </Card>

            <Card>
              <SH title="Hourly Inside / Outside Check" color={C.mend}/>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:800 }}>
                  <thead>
                    <tr style={{ background:C.slate50 }}>
                      <th rowSpan={2} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                        fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`,
                        borderRight:`1px solid ${C.border}`, textAlign:'center' }}>Hour</th>
                      {[['INSIDE CHECK','#DB2777'],['OUTSIDE CHECK','#9333EA'],
                        ['PARTS','#00897B'],['GET-UP','#2E7D32']].map(([l,c])=>(
                        <th key={l} colSpan={l==='PARTS'?4:l==='GET-UP'?2:2}
                          style={{ padding:'8px', fontWeight:700, color:c, fontSize:10,
                            textTransform:'uppercase', borderBottom:`1px solid ${C.border}`,
                            borderRight:`1px solid ${C.border}`, textAlign:'center' }}>{l}</th>
                      ))}
                    </tr>
                    <tr style={{ background:C.slate50 }}>
                      {['Checked','Passed','Checked','Passed',
                        'Back','Front','Pkt L','Pkt R','Get Up','QC Sign'].map((h,i)=>(
                        <th key={i} style={{ padding:'6px 8px', fontWeight:600, color:C.textSub,
                          fontSize:10, borderBottom:`2px solid ${C.border}`,
                          borderRight:`1px solid ${C.border}44`, textAlign:'center' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {F.hourlyData.map((h,i) => (
                      <tr key={h.hour} style={{ borderBottom:`1px solid ${C.border}` }}>
                        <td style={{ padding:'6px 8px', textAlign:'center',
                          fontWeight:700, color:C.mend, borderRight:`1px solid ${C.border}` }}>
                          {h.hour}
                        </td>
                        {['insideChecked','insidePassed','outsideChecked','outsidePassed',
                          'backPart','frontPart','pktLeft','pktRight'].map(k => (
                          <td key={k} style={{ padding:'4px 5px', borderRight:`1px solid ${C.border}44` }}>
                            <input type="number" value={h[k]||''} onChange={e=>updH(i,k,e.target.value)}
                              placeholder="0"
                              style={{ width:55, padding:'4px', borderRadius:R.sm,
                                border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                          </td>
                        ))}
                        <td style={{ padding:'4px 5px' }}>
                          <input value={h.getUp||''} onChange={e=>updH(i,'getUp',e.target.value)}
                            placeholder="OK/NG"
                            style={{ width:60, padding:'4px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                        <td style={{ padding:'4px 5px' }}>
                          <input value={h.qcSign||''} onChange={e=>updH(i,'qcSign',e.target.value)}
                            placeholder="Sign..."
                            style={{ width:70, padding:'4px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background:C.slate50, fontWeight:700 }}>
                      <td style={{ padding:'8px 10px', textAlign:'center',
                        borderRight:`1px solid ${C.border}` }}>TOTAL</td>
                      {[totInsideChk, totInsidePas, totOutsideChk, totOutsidePas].map((v,i) => (
                        <td key={i} style={{ padding:'8px 10px', textAlign:'center' }}>{v}</td>
                      ))}
                      <td colSpan={6}/>
                    </tr>
                  </tbody>
                </table>
              </div>
              <Grid gap={14} min={200} style={{ marginTop:12 }}>
                <Fld label="Quality Supervisor" value={F.qualitySupervisor}
                  onChange={e=>set('qualitySupervisor',e.target.value)} placeholder="Name..."/>
              </Grid>
              <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.mend}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-74 · DAILY LINE WISE MENDING REPORT  (Nir/Rep/03)
  //  Hourly mending garments tracking report
  // ═══════════════════════════════════════════════════════════════════════
  function Ann74({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('mending');
    const newRow = () => ({
      id:uid(), orderRef:'', ttlRcvd:0, ttlCutDamage:0,
      ttlSend:0, ttlKilled:0, balance:0,
      hourlyMending: HOURS.map(h => ({ hour:h, qty:0 })),
    });
    const fresh = () => ({
      id:uid(), formType:'DAILY_MENDING', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      inspectorName:'', date2:TODAY,
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set  = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updR = (i,k,v) => setF(f => {
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      // auto-calc balance
      const rcvd=parseInt(rows[i].ttlRcvd)||0;
      const sent=parseInt(rows[i].ttlSend)||0;
      const killed=parseInt(rows[i].ttlKilled)||0;
      rows[i].balance=rcvd-sent-killed;
      return {...f,rows};
    });
    const updHourly = (ri,hi,v) => setF(f => {
      const rows=[...f.rows];
      rows[ri].hourlyMending=rows[ri].hourlyMending.map((h,hIdx)=>hIdx===hi?{...h,qty:parseInt(v)||0}:h);
      return {...f,rows};
    });
    const recs = (db.mending||[]).filter(r => r.formType==='DAILY_MENDING');
    const totalKilled = F.rows.reduce((s,r)=>s+(parseInt(r.ttlKilled)||0),0);

    const handleSave = async () => {
      await doSave('mending_daily',
        { ...F, totalKilled, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-74 · Daily Line Wise Mending Report"
          ref_="Nir/Rep/03 · Hourly mending garments tracking — Killed GMT Room QC"
          action={<Bdg color={C.mend}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.mend}/>
          <Grid gap={14}>
            <Fld label="Date"           type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."      value={F.style}         onChange={e=>set('style',e.target.value)}         options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"          value={F.buyer}         onChange={e=>set('buyer',e.target.value)}         options={['',...BUYERS]}/>
            <Fld label="Inspector Name" value={F.inspectorName} onChange={e=>set('inspectorName',e.target.value)} placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Order-wise Mending Summary" color={C.mend}
            action={
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <Bdg color={C.danger} bg={C.dangerBg}>Total Killed: {totalKilled}</Bdg>
                <Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.mend}>＋ Add Order</Btn>
              </div>
            }/>
          {F.rows.map((r,ri) => (
            <div key={r.id} style={{ border:`1px solid ${C.border}`, borderRadius:R.md,
              padding:14, marginBottom:12, background:C.slate50 }}>
              <Grid gap={10} min={110}>
                <Fld label="Order Ref"     value={r.orderRef}     onChange={e=>updR(ri,'orderRef',e.target.value)}     options={["", ...dbGet("poNumbers",[])]} placeholder="Order/Ref..."/>
                <Fld label="TTL Rcvd"      type="number" value={r.ttlRcvd}     onChange={e=>updR(ri,'ttlRcvd',e.target.value)}     placeholder="0"/>
                <Fld label="TTL Cut Damage" type="number" value={r.ttlCutDamage} onChange={e=>updR(ri,'ttlCutDamage',e.target.value)} placeholder="0"/>
                <Fld label="TTL Send"      type="number" value={r.ttlSend}     onChange={e=>updR(ri,'ttlSend',e.target.value)}     placeholder="0"/>
                <Fld label="TTL Killed"    type="number" value={r.ttlKilled}   onChange={e=>updR(ri,'ttlKilled',e.target.value)}   placeholder="0"/>
                <div style={{ padding:'10px 14px', background:r.balance<0?C.dangerBg:C.successBg,
                  borderRadius:R.md, textAlign:'center' }}>
                  <div style={{ fontSize:18, fontWeight:800,
                    color:r.balance<0?C.danger:C.success }}>{r.balance}</div>
                  <div style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase' }}>Balance</div>
                </div>
              </Grid>
              {/* Hourly mending qty */}
              <div style={{ marginTop:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textSub,
                  textTransform:'uppercase', marginBottom:8 }}>Hourly Mending Qty</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {r.hourlyMending.map((h,hi) => (
                    <div key={h.hour} style={{ textAlign:'center', minWidth:80 }}>
                      <div style={{ fontSize:9, color:C.textMuted, marginBottom:3 }}>{h.hour}</div>
                      <input type="number" value={h.qty||''} onChange={e=>updHourly(ri,hi,e.target.value)}
                        placeholder="0"
                        style={{ width:70, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                          background:parseInt(h.qty)>0?C.warningBg:C.white }}/>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop:8, display:'flex', justifyContent:'flex-end' }}>
                <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,i)=>i!==ri)}))}
                  style={{ background:C.dangerBg, border:`1px solid ${C.dangerBd}`,
                    borderRadius:R.sm, cursor:'pointer', color:C.danger,
                    fontSize:11, padding:'5px 10px', fontWeight:700 }}>Remove Row</button>
              </div>
            </div>
          ))}
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.mend}/>
        </Card>
      </div>
    );
  }


  // ╔══════════════════════════════════════════════════════════════════════╗
  // ║   KILLED GARMENTS  (Ann-75 → Ann-79)                                ║
  // ╚══════════════════════════════════════════════════════════════════════╝

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-75 · KILLED GMT TAG  (Nir/Rej/01)
  //  Tag/notice a garment as Killed
  // ═══════════════════════════════════════════════════════════════════════
  function Ann75({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('killed');
    const fresh = () => ({
      id:uid(), formType:'KILLED_TAG', date:TODAY,
      orderRef:filters.style||'', colour:filters.colour||'',
      buyer:filters.buyer||'', style:filters.style||'',
      size:'', qty:1, defectReason:'', killedBy:'',
      authorizedBy:'', tagNo:uid().slice(-8).toUpperCase(),
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const recs = (db.killed||[]).filter(r => r.formType==='KILLED_TAG');

    const handleSave = async () => {
      await doSave('killed_tag',
        { ...F, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-75 · Killed GMT Tag"
          ref_="Nir/Rej/01 · Notice a garment as Killed"
          action={<Bdg color={C.killed}>{recs.length} tags issued</Bdg>}/>

        {/* Tag preview card */}
        <div style={{ maxWidth:400, border:`3px solid ${C.danger}`, borderRadius:R.lg,
          padding:20, background:C.dangerBg }}>
          <div style={{ textAlign:'center', marginBottom:16 }}>
            <div style={{ fontSize:28 }}>☠️</div>
            <div style={{ fontSize:18, fontWeight:800, color:C.danger,
              letterSpacing:'2px', textTransform:'uppercase' }}>KILLED GARMENT</div>
            <div style={{ fontSize:12, color:C.textSub, marginTop:4 }}>Tag No: <strong style={{ fontFamily:'monospace' }}>{F.tagNo}</strong></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:12 }}>
            {[['Order Ref',F.orderRef||'—'],['Colour',F.colour||'—'],
              ['Size',F.size||'—'],['Qty',F.qty||1],['Date',F.date],['Buyer',F.buyer||'—']
            ].map(([l,v]) => (
              <div key={l}>
                <div style={{ fontSize:10, color:C.textSub, textTransform:'uppercase' }}>{l}</div>
                <div style={{ fontWeight:700, color:C.text }}>{v}</div>
              </div>
            ))}
          </div>
          {F.defectReason && (
            <div style={{ marginTop:10, padding:'8px 10px', background:C.dangerBg,
              borderRadius:R.sm, border:`1px solid ${C.dangerBd}` }}>
              <div style={{ fontSize:10, color:C.textSub, textTransform:'uppercase' }}>Defect Reason</div>
              <div style={{ fontWeight:700, color:C.danger, fontSize:12 }}>{F.defectReason}</div>
            </div>
          )}
        </div>

        <Card>
          <SH title="Tag Details" color={C.killed}/>
          <Grid gap={14}>
            <Fld label="Date"          type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
            <Fld label="Tag No."       value={F.tagNo}         onChange={e=>set('tagNo',e.target.value)}         placeholder="Auto-generated..."/>
            <Fld label="Order Ref"     value={F.orderRef}      onChange={e=>set('orderRef',e.target.value)}      options={["", ...dbGet("poNumbers",[])]} placeholder="Order/Style..."/>
            <Fld label="Buyer"         value={F.buyer}         onChange={e=>set('buyer',e.target.value)}         options={['',...BUYERS]}/>
            <Fld label="Colour"        value={F.colour}        onChange={e=>set('colour',e.target.value)}        options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Size"          value={F.size}          onChange={e=>set('size',e.target.value)}          options={['',...dbGet('sizes',['XS','S','M','L','XL','XXL','3XL'])]}/>
            <Fld label="Quantity"      type="number" value={F.qty} onChange={e=>set('qty',e.target.value)} placeholder="1"/>
            <Fld label="Defect Reason" value={F.defectReason}  onChange={e=>set('defectReason',e.target.value)}  options={['',...ALL_DEFECTS]}/>
            <Fld label="Killed By"     value={F.killedBy}      onChange={e=>set('killedBy',e.target.value)}      placeholder="Name..."/>
            <Fld label="Authorized By" value={F.authorizedBy}  onChange={e=>set('authorizedBy',e.target.value)}  placeholder="Name..."/>
          </Grid>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.killed}/>
        </Card>

        <Card>
          <SH title="Tags Issued" color={C.killed}/>
          <Tbl headers={['Date','Tag No.','Order Ref','Colour','Size','Qty','Defect Reason','Killed By']}
            rows={recs.map(r=>[r.date,
              <strong style={{ fontFamily:'monospace', color:C.danger }}>{r.tagNo}</strong>,
              r.orderRef||'—',r.colour||'—',r.size||'—',r.qty||1,r.defectReason||'—',r.killedBy||'—'])}
            emptyMsg="No killed GMT tags issued yet."/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-76 · KILLED GMT RECORDS  (Nir/Rej/02)
  //  Daily order-wise / colour-wise killed garment report
  // ═══════════════════════════════════════════════════════════════════════
  function Ann76({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('killed');
    const newRow = () => ({
      id:uid(), date:TODAY, orderRef:'', colour:'',
      opening:0, todayKilled:0, totalKilled:0,
      sizes: SIZES.slice(0,8).map(s=>({ size:s, qty:0 })),
    });
    const fresh = () => ({
      id:uid(), formType:'KILLED_RECORDS', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      killedIncharge:'', dataBy:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updR = (i,k,v) => setF(f => {
      const rows=[...f.rows]; rows[i]={...rows[i],[k]:v};
      rows[i].totalKilled=(parseInt(rows[i].opening)||0)+(parseInt(rows[i].todayKilled)||0);
      return {...f,rows};
    });
    const updSize = (ri,si,v) => setF(f => {
      const rows=[...f.rows];
      rows[ri].sizes=rows[ri].sizes.map((s,idx)=>idx===si?{...s,qty:parseInt(v)||0}:s);
      return {...f,rows};
    });
    const recs = (db.killed||[]).filter(r => r.formType==='KILLED_RECORDS');
    const grandTotal = F.rows.reduce((s,r)=>s+(parseInt(r.totalKilled)||0), 0);

    const handleSave = async () => {
      await doSave('killed_records',
        { ...F, grandTotal, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-76 · Killed GMT Records (Daily)"
          ref_="Nir/Rej/02 · Daily order-wise killed garments update"
          action={<Bdg color={C.killed}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.killed}/>
          <Grid gap={14}>
            <Fld label="Date"            type="date" value={F.date}          onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."       value={F.style}         onChange={e=>set('style',e.target.value)}         options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"           value={F.buyer}         onChange={e=>set('buyer',e.target.value)}         options={['',...BUYERS]}/>
            <Fld label="Killed Incharge" value={F.killedIncharge} onChange={e=>set('killedIncharge',e.target.value)} placeholder="Name..."/>
            <Fld label="Data Maintained By" value={F.dataBy}    onChange={e=>set('dataBy',e.target.value)}         placeholder="Name..."/>
          </Grid>
        </Card>

        <div style={{ padding:'10px 18px', background:C.dangerBg, borderRadius:R.md,
          border:`2px solid ${C.dangerBd}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.danger }}>Grand Total Killed Garments</div>
          <div style={{ fontSize:28, fontWeight:800, color:C.danger,
            fontFamily:'monospace' }}>{grandTotal}</div>
        </div>

        <Card>
          <SH title="Order / Colour-wise Killed Data" color={C.killed}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.killed}>＋ Add Row</Btn>}/>
          {F.rows.map((r,ri) => (
            <div key={r.id} style={{ border:`1px solid ${C.border}`, borderRadius:R.md,
              padding:14, marginBottom:12, background:C.slate50 }}>
              <Grid gap={10} min={120}>
                <Fld label="Date"         type="date" value={r.date}        onChange={e=>updR(ri,'date',e.target.value)}/>
                <Fld label="Order Ref"    value={r.orderRef}  onChange={e=>updR(ri,'orderRef',e.target.value)}  options={["", ...dbGet("poNumbers",[])]} placeholder="Order..."/>
                <Fld label="Colour"       value={r.colour}    onChange={e=>updR(ri,'colour',e.target.value)}    options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Opening Qty"  type="number" value={r.opening}      onChange={e=>updR(ri,'opening',e.target.value)}      placeholder="0"/>
                <Fld label="Today Killed" type="number" value={r.todayKilled}  onChange={e=>updR(ri,'todayKilled',e.target.value)}  placeholder="0"/>
                <div style={{ padding:'10px 14px', background:C.dangerBg, borderRadius:R.md,
                  textAlign:'center', border:`1px solid ${C.dangerBd}` }}>
                  <div style={{ fontSize:18, fontWeight:800, color:C.danger }}>{r.totalKilled}</div>
                  <div style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase' }}>Total Killed</div>
                </div>
              </Grid>
              {/* size-wise breakdown */}
              <div style={{ marginTop:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textSub,
                  textTransform:'uppercase', marginBottom:8 }}>Size-wise Killed Qty</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {r.sizes.map((s,si) => (
                    <div key={s.size} style={{ textAlign:'center', minWidth:60 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:C.textSub,
                        textTransform:'uppercase', marginBottom:3 }}>{s.size}</div>
                      <input type="number" value={s.qty||''} onChange={e=>updSize(ri,si,e.target.value)}
                        placeholder="0"
                        style={{ width:55, padding:'5px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:12, textAlign:'center',
                          background:parseInt(s.qty)>0?C.dangerBg:C.white,
                          color:parseInt(s.qty)>0?C.danger:C.text, fontWeight:700 }}/>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop:8, display:'flex', justifyContent:'flex-end' }}>
                <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,i)=>i!==ri)}))}
                  style={{ background:C.dangerBg, border:`1px solid ${C.dangerBd}`,
                    borderRadius:R.sm, cursor:'pointer', color:C.danger,
                    fontSize:11, padding:'5px 10px', fontWeight:700 }}>Remove Row</button>
              </div>
            </div>
          ))}
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.killed}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-77 · KILLED GARMENTS REGISTER  (Nir/Rej/03)
  //  Defect-wise killed garments register — avoid short shipment
  // ═══════════════════════════════════════════════════════════════════════
  function Ann77({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('killed');
    const newEntry = () => ({
      id:uid(), date:TODAY, orderRef:'', colour:'', size:'',
      defect:'', qty:0, killedBy:'', remarks:'',
      wrinkleFree:false, status:'KILLED',
    });
    const fresh = () => ({
      id:uid(), formType:'KILLED_REGISTER', fileRef:'',
      prodRef:'', opening:0, continueFrom:'', goTo:'',
      style:filters.style||'', buyer:filters.buyer||'',
      totalReceived:0, totalReturned:0, overallRejection:0,
      killedIncharge:'', checkedBy:'', authorizedBy:'',
      entries:[newEntry()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updE = (i,k,v) => setF(f => ({ ...f,
      entries:f.entries.map((e,ei)=>ei===i?{...e,[k]:v}:e) }));
    const recs = (db.killed||[]).filter(r => r.formType==='KILLED_REGISTER');
    const totalKilled = F.entries.reduce((s,e)=>s+(parseInt(e.qty)||0), 0);

    const handleSave = async () => {
      await doSave('killed_register',
        { ...F, totalKilled, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-77 · Killed Garments Register"
          ref_="Nir/Rej/03 · Defect-wise killed garments — prevent short shipment"
          action={<Bdg color={C.killed}>{recs.length} registers</Bdg>}/>

        <Card>
          <SH title="Register Header" color={C.killed}/>
          <Grid gap={14}>
            <Fld label="File Ref"   value={F.fileRef}  onChange={e=>set('fileRef',e.target.value)}  placeholder="File ref..."/>
            <Fld label="Prod Ref"   value={F.prodRef}  onChange={e=>set('prodRef',e.target.value)}  placeholder="Prod ref..."/>
            <Fld label="Style No."  value={F.style}    onChange={e=>set('style',e.target.value)}    options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"      value={F.buyer}    onChange={e=>set('buyer',e.target.value)}    options={['',...BUYERS]}/>
            <Fld label="Opening Qty" type="number" value={F.opening} onChange={e=>set('opening',e.target.value)} placeholder="0"/>
            <Fld label="Total Received" type="number" value={F.totalReceived} onChange={e=>set('totalReceived',e.target.value)} placeholder="0"/>
            <Fld label="Total Returned" type="number" value={F.totalReturned} onChange={e=>set('totalReturned',e.target.value)} placeholder="0"/>
            <div style={{ padding:'10px 14px', background:C.dangerBg, borderRadius:R.md,
              textAlign:'center', border:`1px solid ${C.dangerBd}` }}>
              <div style={{ fontSize:20, fontWeight:800, color:C.danger }}>{totalKilled}</div>
              <div style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase' }}>Total Killed</div>
            </div>
            <Fld label="Killed Incharge" value={F.killedIncharge} onChange={e=>set('killedIncharge',e.target.value)} placeholder="Name..."/>
            <Fld label="Checked By"      value={F.checkedBy}      onChange={e=>set('checkedBy',e.target.value)}      placeholder="Name..."/>
            <Fld label="Authorized By"   value={F.authorizedBy}   onChange={e=>set('authorizedBy',e.target.value)}   placeholder="Name..."/>
          </Grid>
        </Card>

        <Card>
          <SH title="Killed Garment Entries" color={C.killed}
            action={<Btn sm onClick={()=>setF(f=>({...f,entries:[...f.entries,newEntry()]}))} color={C.killed}>＋ Add Entry</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:700 }}>
              <thead><tr style={{ background:C.slate50 }}>
                {['Date','Order Ref','Colour','Size','Defect Reason','Qty','WF?','Status','Killed By','Remarks',''].map(h=>(
                  <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                    fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.entries.map((e,i) => (
                  <tr key={e.id} style={{ borderBottom:`1px solid ${C.border}`,
                    background:parseInt(e.qty)>0?C.dangerBg:C.white }}>
                    <td style={{ padding:'5px 6px' }}>
                      <input type="date" value={e.date} onChange={ee=>updE(i,'date',ee.target.value)}
                        style={{ padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={e.orderRef} onChange={ee=>updE(i,'orderRef',ee.target.value)} placeholder="Ref..." list="po-datalist"
                        style={{ width:80, padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={e.colour} onChange={ee=>updE(i,'colour',ee.target.value)} placeholder="Colour"
                        style={{ width:70, padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={e.size} onChange={ee=>updE(i,'size',ee.target.value)}
                        style={{ width:65, padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">Size</option>
                        {SIZES.map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={e.defect} onChange={ee=>updE(i,'defect',ee.target.value)}
                        style={{ width:'100%', minWidth:120, padding:'4px', borderRadius:R.sm,
                          border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                        <option value="">Select reason...</option>
                        {ALL_DEFECTS.map(d=><option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input type="number" value={e.qty||''} onChange={ee=>updE(i,'qty',ee.target.value)} placeholder="0"
                        style={{ width:55, padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:12,
                          textAlign:'center', fontWeight:700, color:C.danger }}/>
                    </td>
                    <td style={{ padding:'5px 8px', textAlign:'center' }}>
                      <input type="checkbox" checked={e.wrinkleFree||false} onChange={ee=>updE(i,'wrinkleFree',ee.target.checked)}
                        style={{ width:16, height:16, accentColor:C.killed, cursor:'pointer' }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <select value={e.status} onChange={ee=>updE(i,'status',ee.target.value)}
                        style={{ width:80, padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                          fontSize:11, fontWeight:700, fontFamily:'inherit',
                          background:e.status==='KILLED'?C.dangerBg:C.successBg,
                          color:e.status==='KILLED'?C.danger:C.success }}>
                        {['KILLED','REPAIRED','RETURNED'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={e.killedBy||''} onChange={ee=>updE(i,'killedBy',ee.target.value)} placeholder="Name..."
                        style={{ width:80, padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={e.remarks||''} onChange={ee=>updE(i,'remarks',ee.target.value)} placeholder="Remarks..."
                        style={{ width:90, padding:'4px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,entries:f.entries.filter((_,ei)=>ei!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm, cursor:'pointer',
                          color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.killed}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-78 · COMMERCIAL REGISTER  (Nir/Rej/04)
  //  Total update on killed garments — track for further requirement
  // ═══════════════════════════════════════════════════════════════════════
  function Ann78({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('killed');
    const newRow = () => ({
      id:uid(), cartonBagNo:'', nirRef:'', sizes:'', grade:'',
      reducedQty:0, finalQty:0, sentBy:'', rcvedBy:'', returnedBy:'', returnRcvdBy:'',
    });
    const fresh = () => ({
      id:uid(), formType:'COMMERCIAL_REGISTER', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updR = (i,k,v) => setF(f => ({ ...f,
      rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r) }));
    const recs = (db.killed||[]).filter(r => r.formType==='COMMERCIAL_REGISTER');
    const totalFinal = F.rows.reduce((s,r)=>s+(parseInt(r.finalQty)||0), 0);

    const handleSave = async () => {
      await doSave('killed_commercial',
        { ...F, totalFinal, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-78 · Commercial Register"
          ref_="Nir/Rej/04 · Total killed garment tracking for further requirement"
          action={<Bdg color={C.killed}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.killed}/>
          <Grid gap={14}>
            <Fld label="Date"      type="date" value={F.date}  onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style} onChange={e=>set('style',e.target.value)} options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"     value={F.buyer} onChange={e=>set('buyer',e.target.value)} options={['',...BUYERS]}/>
          </Grid>
        </Card>

        <Card>
          <SH title="Commercial Register Entries" color={C.killed}
            action={
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <Bdg color={C.killed}>Total Final: {totalFinal}</Bdg>
                <Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.killed}>＋ Add Row</Btn>
              </div>
            }/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:800 }}>
              <thead><tr style={{ background:C.slate50 }}>
                {['CTN/Bag No.','Client Ref','Sizes','Grade','Reduced Qty','Final Qty',
                  'Sent By','Rcvd By','Returned By','Return Rcvd By',''].map(h=>(
                  <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                    fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    {['cartonBagNo','nirRef','sizes','grade'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="..."
                          style={{ width:80, padding:'5px', borderRadius:R.sm,
                            border:`1px solid ${C.border}`, fontSize:11 }}/>
                      </td>
                    ))}
                    {['reducedQty','finalQty'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{ width:70, padding:'5px', borderRadius:R.sm,
                            border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    {['sentBy','rcvedBy','returnedBy','returnRcvdBy'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="Name..."
                          style={{ width:80, padding:'5px', borderRadius:R.sm,
                            border:`1px solid ${C.border}`, fontSize:11 }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm,
                          cursor:'pointer', color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.killed}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-79 · KILLED GARMENT MANAGEMENT RECORD  (Nir/Rej/05)
  //  All-over killed garment management
  // ═══════════════════════════════════════════════════════════════════════
  function Ann79({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('killed');
    const newRow = () => ({
      id:uid(), jobCardNo:'', reqToCut:0, actualCut:0,
      actionToBeTaken:'', cutting:0, washingWF:0, sewingManager:'',
      ppcHead:'', unitHead:'',
    });
    const fresh = () => ({
      id:uid(), formType:'KILLED_MANAGEMENT', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      colour:filters.colour||'', orderQty:filters.qty||'',
      folder:'', trimCard:'', folderApproval:'', ftaFinishing:'',
      ftaPacking:'', shadeBand:'', handFeelStandard:'',
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updR = (i,k,v) => setF(f => ({ ...f,
      rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r) }));
    const recs = (db.killed||[]).filter(r => r.formType==='KILLED_MANAGEMENT');
    const totalReqCut = F.rows.reduce((s,r)=>s+(parseInt(r.reqToCut)||0), 0);
    const totalActCut = F.rows.reduce((s,r)=>s+(parseInt(r.actualCut)||0), 0);

    const handleSave = async () => {
      await doSave('killed_management',
        { ...F, totalReqCut, totalActCut, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-79 · Killed Garment Management Record"
          ref_="Nir/Rej/05 · All-over killed garment management"
          action={<Bdg color={C.killed}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Order Details" color={C.killed}/>
          <Grid gap={14}>
            <Fld label="Date"          type="date" value={F.date}         onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No."     value={F.style}        onChange={e=>set('style',e.target.value)}        options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"         value={F.buyer}        onChange={e=>set('buyer',e.target.value)}        options={['',...BUYERS]}/>
            <Fld label="Colour"        value={F.colour}       onChange={e=>set('colour',e.target.value)}       options={["",...dbGet("colours",_FB_COLOURS)]}/>
            <Fld label="Order Qty"     type="number" value={F.orderQty} onChange={e=>set('orderQty',e.target.value)} placeholder="Auto from PO"/>
          </Grid>
        </Card>

        <Card>
          <SH title="Pre-Production Status" color={C.killed}/>
          <Grid gap={14} min={160}>
            {[['folder','Folder'],['trimCard','Trim Card'],['folderApproval','Folder Approval'],
              ['ftaFinishing','FTA – Finishing'],['ftaPacking','FTA – Packing'],
              ['shadeBand','Shade Band'],['handFeelStandard','Hand Feel Standard']
            ].map(([k,l]) => (
              <Fld key={k} label={l} value={F[k]||''} onChange={e=>set(k,e.target.value)}
                options={['','APPROVED','PENDING','NOT AVAILABLE','NA']}/>
            ))}
          </Grid>
        </Card>

        <Card>
          <SH title="Job Card-wise Management" color={C.killed}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.killed}>＋ Add Job Card</Btn>}/>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:800 }}>
              <thead><tr style={{ background:C.slate50 }}>
                {['Job Card No.','Req. to Cut','Actual Cut','Action to be Taken',
                  'Cutting','Washing / WF','Sewing Mgr','PPC Head','Unit Head',''].map(h=>(
                  <th key={h} style={{ padding:'8px 10px', fontWeight:700, color:C.textSub,
                    fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, textAlign:'center' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {F.rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.jobCardNo||''} onChange={e=>updR(i,'jobCardNo',e.target.value)} placeholder="Job card#"
                        style={{ width:90, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    {['reqToCut','actualCut'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <input value={r.actionToBeTaken||''} onChange={e=>updR(i,'actionToBeTaken',e.target.value)} placeholder="Action..."
                        style={{ width:'100%', minWidth:120, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                    </td>
                    {['cutting','washingWF'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input type="number" value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="0"
                          style={{ width:70, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                      </td>
                    ))}
                    {['sewingManager','ppcHead','unitHead'].map(k=>(
                      <td key={k} style={{ padding:'5px 6px' }}>
                        <input value={r[k]||''} onChange={e=>updR(i,k,e.target.value)} placeholder="Name..."
                          style={{ width:80, padding:'5px', borderRadius:R.sm, border:`1px solid ${C.border}`, fontSize:11 }}/>
                      </td>
                    ))}
                    <td style={{ padding:'5px 6px' }}>
                      <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                        style={{ background:C.dangerBg, border:'none', borderRadius:R.sm,
                          cursor:'pointer', color:C.danger, fontWeight:700, fontSize:11, padding:'5px 7px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:10, display:'flex', gap:16, flexWrap:'wrap' }}>
            <Bdg color={C.killed}>Req. to Cut: {totalReqCut}</Bdg>
            <Bdg color={C.danger} bg={C.dangerBg}>Actual Cut: {totalActCut}</Bdg>
          </div>
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.killed}/>
        </Card>
      </div>
    );
  }


  // ╔══════════════════════════════════════════════════════════════════════╗
  // ║   PROCESS CONTROL  (Ann-80 → Ann-82)                                ║
  // ╚══════════════════════════════════════════════════════════════════════╝

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-80 · SEWING LINE AUDIT FORM  (Nir/Prc/03)
  //  Process control — out of specs, needle/SPI/thread tex
  // ═══════════════════════════════════════════════════════════════════════
  function Ann80({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('process');
    const [view, setView] = React.useState('form');

    const newOp = () => ({
      id:uid(), slNo:'', operationName:'',
      mcNo:'', needleType:'', threadTension:'', seamAppearance:'',
      bundleHandling:'', housekeeping:'', needleMargin:'',
      gaugeOrGuide:'', threadTexNeedle:'', threadTexBobbin:'',
      onSpec:'YES', comments:'',
    });

    const fresh = () => ({
      id:uid(), formType:'SEWING_LINE_AUDIT', date:TODAY,
      lineNo:'', style:filters.style||'', buyer:filters.buyer||'',
      nirRef:'', colour:filters.colour||'',
      lineManager:'', productionManager:'', processControlSup:'',
      operations: SEWING_OPERATIONS.slice(0,15).map((op,i) => ({
        ...newOp(), slNo:`${i+1}`, operationName:op,
      })),
    });

    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updOp = (i,k,v) => setF(f => ({
      ...f, operations:f.operations.map((op,oi)=>oi===i?{...op,[k]:v}:op),
    }));
    const recs = (db.process||[]).filter(r => r.formType==='SEWING_LINE_AUDIT');
    const outOfSpec = F.operations.filter(op=>op.onSpec==='NO').length;

    const handleSave = async () => {
      await doSave('process_sewing_line',
        { ...F, outOfSpec, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-80 · Sewing Line Audit Form"
          ref_="Nir/Prc/03 · Process Control — Out of Specs, Needle/SPI/Thread Tex"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.proc}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.proc}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Sewing Line Audit Records" color={C.proc}/>
            <Tbl headers={['Date','Line','Style','Buyer','Operations','Out of Spec']}
              rows={recs.map(r=>[r.date,r.lineNo||'—',r.style||'—',r.buyer||'—',
                (r.operations||[]).length,
                <Bdg color={r.outOfSpec>0?C.danger:C.success}
                  bg={r.outOfSpec>0?C.dangerBg:C.successBg}>{r.outOfSpec||0}</Bdg>])}
              emptyMsg="No sewing line audit records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Audit Header" color={C.proc}/>
              <Grid gap={14}>
                <Fld label="Date"              type="date" value={F.date}               onChange={e=>set('date',e.target.value)}/>
                <Fld label="Line No."          value={F.lineNo}           onChange={e=>set('lineNo',e.target.value)}           options={['',...LINES]}/>
                <Fld label="Style No."         value={F.style}            onChange={e=>set('style',e.target.value)}            options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Client Ref"          value={F.clientRefRef}          onChange={e=>set('nirRef',e.target.value)}          placeholder="Client Ref..."/>
                <Fld label="Buyer"             value={F.buyer}            onChange={e=>set('buyer',e.target.value)}            options={['',...BUYERS]}/>
                <Fld label="Colour"            value={F.colour}           onChange={e=>set('colour',e.target.value)}           options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Line Manager"      value={F.lineManager}      onChange={e=>set('lineManager',e.target.value)}      placeholder="Name..."/>
                <Fld label="Production Manager" value={F.productionManager} onChange={e=>set('productionManager',e.target.value)} placeholder="Name..."/>
                <Fld label="Process Control Sup" value={F.processControlSup} onChange={e=>set('processControlSup',e.target.value)} placeholder="Name..."/>
              </Grid>
            </Card>

            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <Bdg color={C.proc}>Total Operations: {F.operations.length}</Bdg>
              <Bdg color={outOfSpec>0?C.danger:C.success}
                bg={outOfSpec>0?C.dangerBg:C.successBg}>Out of Spec: {outOfSpec}</Bdg>
            </div>

            <Card>
              <SH title="Operation-wise Audit" color={C.proc}
                action={<Btn sm onClick={()=>setF(f=>({...f,operations:[...f.operations,{...newOp(),slNo:String(f.operations.length+1)}]}))} color={C.proc}>＋ Add Op</Btn>}/>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, minWidth:1100 }}>
                  <thead><tr style={{ background:C.slate50 }}>
                    {['Sl#','Operation Name','M/C No.','Needle Type','Thread Tension','Seam Appear.',
                      'Bundle Hdl.','Housekeeping','Needle Margin','Gauge/Guide',
                      'Thread Tex-Needle','Thread Tex-Bobbin','On Spec?','Comments',''].map(h=>(
                      <th key={h} style={{ padding:'7px 9px', fontWeight:700, color:C.textSub,
                        fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`,
                        textAlign:'center', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {F.operations.map((op,i) => (
                      <tr key={op.id} style={{ borderBottom:`1px solid ${C.border}`,
                        background:op.onSpec==='NO'?C.dangerBg:C.white }}>
                        <td style={{ padding:'4px 6px', textAlign:'center', fontWeight:700 }}>{op.slNo}</td>
                        <td style={{ padding:'4px 6px' }}>
                          <select value={op.operationName} onChange={e=>updOp(i,'operationName',e.target.value)}
                            style={{ width:'100%', minWidth:160, padding:'4px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11, fontFamily:'inherit' }}>
                            {SEWING_OPERATIONS.map(o=><option key={o} value={o}>{o}</option>)}
                          </select>
                        </td>
                        {['mcNo','needleType','threadTension','seamAppearance','bundleHandling',
                          'housekeeping','needleMargin','gaugeOrGuide','threadTexNeedle','threadTexBobbin'].map(k=>(
                          <td key={k} style={{ padding:'4px 5px' }}>
                            <input value={op[k]||''} onChange={e=>updOp(i,k,e.target.value)}
                              placeholder="—"
                              style={{ width:70, padding:'4px', borderRadius:R.sm,
                                border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                          </td>
                        ))}
                        <td style={{ padding:'4px 6px', textAlign:'center' }}>
                          <select value={op.onSpec} onChange={e=>updOp(i,'onSpec',e.target.value)}
                            style={{ padding:'4px 8px', borderRadius:R.sm, border:`1px solid ${C.border}`,
                              fontSize:11, fontWeight:700, fontFamily:'inherit',
                              background:op.onSpec==='YES'?C.successBg:C.dangerBg,
                              color:op.onSpec==='YES'?C.success:C.danger }}>
                            {['YES','NO'].map(s=><option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:'4px 5px' }}>
                          <input value={op.comments||''} onChange={e=>updOp(i,'comments',e.target.value)}
                            placeholder="Comments..."
                            style={{ width:'100%', minWidth:100, padding:'4px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                        <td style={{ padding:'4px 6px' }}>
                          <button onClick={()=>setF(f=>({...f,operations:f.operations.filter((_,oi)=>oi!==i)}))}
                            style={{ background:C.dangerBg, border:'none', borderRadius:R.sm,
                              cursor:'pointer', color:C.danger, fontWeight:700, fontSize:11, padding:'4px 7px' }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.proc}/>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-81 · EXCEPTIONAL DEFECT CONTROL CHART  (Nir/Prc/04)
  //  Track & reduce major defects on a specific order in a specific area
  // ═══════════════════════════════════════════════════════════════════════
  function Ann81({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('process');
    const newRow = () => ({
      id:uid(), qcName:'', lineNo:'', defect:'', qty:0,
      rootCause:'', correctiveAction:'', targetDate:TODAY,
      responsiblePerson:'', status:'OPEN', verifiedBy:'',
    });
    const fresh = () => ({
      id:uid(), formType:'EXCEPTIONAL_DEFECT', date:TODAY,
      style:filters.style||'', buyer:filters.buyer||'',
      area:'', shift:SHIFTS[0],
      rows:[newRow()],
    });
    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updR = (i,k,v) => setF(f => ({ ...f,
      rows:f.rows.map((r,ri)=>ri===i?{...r,[k]:v}:r) }));
    const recs = (db.process||[]).filter(r => r.formType==='EXCEPTIONAL_DEFECT');
    const openCount = F.rows.filter(r=>r.status==='OPEN').length;

    const handleSave = async () => {
      await doSave('process_defect_control',
        { ...F, openCount, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    const sCol = s => s==='CLOSED'?C.success:s==='OPEN'?C.danger:C.warning;
    const sBg  = s => s==='CLOSED'?C.successBg:s==='OPEN'?C.dangerBg:C.warningBg;

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-81 · Exceptional Defect Control Chart"
          ref_="Nir/Prc/04 · Reduce major defects on specific order/area"
          action={<Bdg color={C.proc}>{recs.length} records</Bdg>}/>

        <Card>
          <SH title="Header" color={C.proc}/>
          <Grid gap={14}>
            <Fld label="Date"      type="date" value={F.date}  onChange={e=>set('date',e.target.value)}/>
            <Fld label="Style No." value={F.style}  onChange={e=>set('style',e.target.value)}  options={["",...dbGet("styles",[])]} placeholder="Style..."/>
            <Fld label="Buyer"     value={F.buyer}  onChange={e=>set('buyer',e.target.value)}  options={['',...BUYERS]}/>
            <Fld label="Area"      value={F.area}   onChange={e=>set('area',e.target.value)}   placeholder="Department / area..."/>
            <Fld label="Shift"     value={F.shift}  onChange={e=>set('shift',e.target.value)}  options={SHIFTS}/>
          </Grid>
        </Card>

        <div style={{ display:'flex', gap:10 }}>
          <Bdg color={C.danger} bg={C.dangerBg}>Open: {openCount}</Bdg>
          <Bdg color={C.success} bg={C.successBg}>Closed: {F.rows.filter(r=>r.status==='CLOSED').length}</Bdg>
          <Bdg color={C.proc}>Total: {F.rows.length}</Bdg>
        </div>

        <Card>
          <SH title="Exceptional Defect Log" color={C.proc}
            action={<Btn sm onClick={()=>setF(f=>({...f,rows:[...f.rows,newRow()]}))} color={C.proc}>＋ Add Row</Btn>}/>
          {F.rows.map((r,i) => (
            <div key={r.id} style={{ border:`1px solid ${sBg(r.status)}`,
              borderRadius:R.md, padding:14, marginBottom:12,
              background:sBg(r.status) }}>
              <Grid gap={10} min={150}>
                <Fld label="QC Name"     value={r.qcName}     onChange={e=>updR(i,'qcName',e.target.value)}     placeholder="QC name..."/>
                <Fld label="Line No."    value={r.lineNo}     onChange={e=>updR(i,'lineNo',e.target.value)}     options={['',...LINES]}/>
                <Fld label="Defect"      value={r.defect}     onChange={e=>updR(i,'defect',e.target.value)}     options={['',...ALL_DEFECTS]}/>
                <Fld label="Qty"         type="number" value={r.qty} onChange={e=>updR(i,'qty',e.target.value)} placeholder="0"/>
                <Fld label="Root Cause"  value={r.rootCause}  onChange={e=>updR(i,'rootCause',e.target.value)}  placeholder="Root cause..."/>
                <Fld label="Corrective Action" value={r.correctiveAction} onChange={e=>updR(i,'correctiveAction',e.target.value)} placeholder="Action taken..."/>
                <Fld label="Target Date" type="date" value={r.targetDate} onChange={e=>updR(i,'targetDate',e.target.value)}/>
                <Fld label="Responsible" value={r.responsiblePerson} onChange={e=>updR(i,'responsiblePerson',e.target.value)} placeholder="Person name..."/>
                <Fld label="Verified By" value={r.verifiedBy} onChange={e=>updR(i,'verifiedBy',e.target.value)} placeholder="Name..."/>
                <div>
                  <div style={{ fontSize:11, fontWeight:600, color:C.textSub,
                    textTransform:'uppercase', marginBottom:4 }}>Status</div>
                  <div style={{ display:'flex', gap:6 }}>
                    {['OPEN','IN PROGRESS','CLOSED'].map(s => (
                      <button key={s} onClick={()=>updR(i,'status',s)}
                        style={{ padding:'6px 12px', borderRadius:R.md, fontSize:11, fontWeight:700,
                          cursor:'pointer', border:`1px solid ${r.status===s?sCol(s):C.border}`,
                          background:r.status===s?sBg(s):C.white,
                          color:r.status===s?sCol(s):C.textMuted }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </Grid>
              <div style={{ marginTop:8, display:'flex', justifyContent:'flex-end' }}>
                <button onClick={()=>setF(f=>({...f,rows:f.rows.filter((_,ri)=>ri!==i)}))}
                  style={{ background:C.dangerBg, border:`1px solid ${C.dangerBd}`,
                    borderRadius:R.sm, cursor:'pointer', color:C.danger,
                    fontSize:11, padding:'5px 10px', fontWeight:700 }}>Remove</button>
              </div>
            </div>
          ))}
          <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.proc}/>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ANN-82 · SEWING PROCESS CONTROL REGISTER  (Nir/Prc/05)
  //  Full operation-wise process control
  // ═══════════════════════════════════════════════════════════════════════
  function Ann82({ db, setDb, filters }) {
    const { saving, saved, doSave } = useSave('process');
    const [view, setView] = React.useState('form');

    const fresh = () => ({
      id:uid(), formType:'SEWING_PROCESS_CTRL', date:TODAY,
      lineNo:'', style:filters.style||'', buyer:filters.buyer||'',
      nirRef:'', colour:filters.colour||'',
      styleRef:'', clientRef:'', description:'', exFtyDate:'', washType:'',
      // all operations with audit
      operations: SEWING_OPERATIONS.map((op,i) => ({
        id:uid(), slNo:i+1, operationName:op,
        opIdNo:'', mcRpm:'', shadeLotLabel:'', alterationBacklog:'',
        spi:'', needleType:'', threadTex:'', seamAppearance:'',
        auditedSample:0, performancePct:0, remarks:'',
      })),
      managerQuality:'', gmProduction:'', gmQuality:'',
    });

    const [F, setF] = React.useState(fresh());
    // Auto-fill PO-linked data when style or PO changes
    React.useEffect(() => {
      if (!db) return;
      const styleKey = F.style || F.styleNo || F.styleRef || '';
      const poKey    = F.po    || F.poNo    || '';
      if (!styleKey && !poKey) return;
      const fill = getPoAutoFill(db, styleKey, poKey);
      setF(prev => ({
        ...prev,
        // Only fill if field is currently empty (don't overwrite manual entry)
        ...(fill.qty && !prev.orderQty  ? { orderQty:  String(fill.qty)  } : {}),
        ...(fill.qty && !prev.orQty     ? { orQty:     String(fill.qty)  } : {}),
        ...(fill.qty && !prev.ordQty    ? { ordQty:    String(fill.qty)  } : {}),
        ...(fill.exFac && !prev.exFac   ? { exFac:     fill.exFac        } : {}),
        ...(fill.exFac && !prev.exFtyDate ? { exFtyDate: fill.exFac      } : {}),
        ...(fill.colours[0] && !prev.colour ? { colour: fill.colours[0]  } : {}),
      }));
    }, [F.style, F.styleNo, F.po, F.poNo]); // eslint-disable-line
    const set = (k,v) => setF(f => ({ ...f, [k]:v }));
    const updOp = (i,k,v) => setF(f => ({
      ...f, operations:f.operations.map((op,oi)=>oi===i?{...op,[k]:v}:op),
    }));
    const recs = (db.process||[]).filter(r => r.formType==='SEWING_PROCESS_CTRL');
    const auditedOps = F.operations.filter(op=>parseInt(op.auditedSample)>0).length;
    const avgPerf = auditedOps>0
      ? (F.operations.filter(op=>op.performancePct>0).reduce((s,op)=>s+(parseFloat(op.performancePct)||0),0)
        / F.operations.filter(op=>op.performancePct>0).length).toFixed(1)
      : 0;

    const handleSave = async () => {
      await doSave('process_sewing_ctrl',
        { ...F, auditedOps, avgPerf, savedAt:new Date().toISOString() }, db, setDb);
      setF(fresh());
    };

    return (
      <div style={{ padding:20, display:'flex', flexDirection:'column', gap:18 }}>
        <PgHead title="Ann-82 · Sewing Process Control Register"
          ref_="Nir/Prc/05 · Full operation-wise process control"
          action={
            <div style={{ display:'flex', gap:8 }}>
              <Btn sm outline onClick={()=>setView('form')} color={C.proc}>＋ New</Btn>
              <Btn sm outline={view!=='history'} onClick={()=>setView('history')} color={C.proc}>
                📋 History ({recs.length})
              </Btn>
            </div>
          }/>

        {view==='history' ? (
          <Card>
            <SH title="Process Control Records" color={C.proc}/>
            <Tbl headers={['Date','Line','Style','Client Ref','Operations Audited','Avg Performance']}
              rows={recs.map(r=>[r.date,r.lineNo||'—',r.style||'—',r.clientRefRef||'—',r.auditedOps||0,
                <Bdg color={parseFloat(r.avgPerf||0)<80?C.danger:C.success}
                  bg={parseFloat(r.avgPerf||0)<80?C.dangerBg:C.successBg}>{r.avgPerf||0}%</Bdg>])}
              emptyMsg="No process control records yet."/>
          </Card>
        ) : (
          <>
            <Card>
              <SH title="Order & Style Details" color={C.proc}/>
              <Grid gap={14}>
                <Fld label="Date"          type="date" value={F.date}       onChange={e=>set('date',e.target.value)}/>
                <Fld label="Line No."      value={F.lineNo}     onChange={e=>set('lineNo',e.target.value)}     options={['',...LINES]}/>
                <Fld label="Style No."     value={F.style}      onChange={e=>set('style',e.target.value)}      options={["",...dbGet("styles",[])]} placeholder="Style..."/>
                <Fld label="Client Ref"      value={F.clientRefRef}    onChange={e=>set('nirRef',e.target.value)}    placeholder="Client Ref..."/>
                <Fld label="Buyer"         value={F.buyer}      onChange={e=>set('buyer',e.target.value)}      options={['',...BUYERS]}/>
                <Fld label="Colour"        value={F.colour}     onChange={e=>set('colour',e.target.value)}     options={["",...dbGet("colours",_FB_COLOURS)]}/>
                <Fld label="Description"   value={F.description} onChange={e=>set('description',e.target.value)} placeholder="e.g. 5 Pocket Pants..."/>
                <Fld label="Ex-Fty Date"   type="date" value={F.exFtyDate}  onChange={e=>set('exFtyDate',e.target.value)} note="Auto-fills from PO"/>
                <Fld label="Wash Type"     value={F.washType}   onChange={e=>set('washType',e.target.value)}   placeholder="Wash type..."/>
              </Grid>
            </Card>

            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              <Bdg color={C.proc}>Total Operations: {F.operations.length}</Bdg>
              <Bdg color={C.accent} bg="#EFF6FF">Audited: {auditedOps}</Bdg>
              <Bdg color={parseFloat(avgPerf)<80?C.danger:C.success}
                bg={parseFloat(avgPerf)<80?C.dangerBg:C.successBg}>Avg Performance: {avgPerf}%</Bdg>
            </div>

            <Card>
              <SH title="Operation-wise Process Control" color={C.proc}/>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, minWidth:1000 }}>
                  <thead><tr style={{ background:C.slate50 }}>
                    {['Sl#','Operation Name','Op ID No.','M/C RPM','Shade Lot Label',
                      'Alt. Backlog','SPI','Needle Type','Thread Tex','Seam Appear.',
                      'Audited Sample','Performance %','Remarks'].map(h=>(
                      <th key={h} style={{ padding:'7px 9px', fontWeight:700, color:C.textSub,
                        fontSize:10, textTransform:'uppercase', borderBottom:`2px solid ${C.border}`,
                        textAlign:'center', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {F.operations.map((op,i) => (
                      <tr key={op.id} style={{ borderBottom:`1px solid ${C.border}`,
                        background:parseFloat(op.performancePct)>0&&parseFloat(op.performancePct)<80
                          ?C.warningBg:C.white }}>
                        <td style={{ padding:'4px 6px', textAlign:'center', fontWeight:700,
                          color:C.proc }}>{op.slNo}</td>
                        <td style={{ padding:'4px 6px' }}>
                          <div style={{ fontSize:11, color:C.text, minWidth:160 }}>{op.operationName}</div>
                        </td>
                        {['opIdNo','mcRpm','shadeLotLabel','alterationBacklog',
                          'spi','needleType','threadTex','seamAppearance'].map(k=>(
                          <td key={k} style={{ padding:'4px 5px' }}>
                            <input value={op[k]||''} onChange={e=>updOp(i,k,e.target.value)}
                              placeholder="—"
                              style={{ width:65, padding:'4px', borderRadius:R.sm,
                                border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                          </td>
                        ))}
                        <td style={{ padding:'4px 5px' }}>
                          <input type="number" value={op.auditedSample||''} onChange={e=>updOp(i,'auditedSample',e.target.value)}
                            placeholder="0"
                            style={{ width:65, padding:'4px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11, textAlign:'center' }}/>
                        </td>
                        <td style={{ padding:'4px 5px' }}>
                          <input type="number" value={op.performancePct||''} onChange={e=>updOp(i,'performancePct',e.target.value)}
                            placeholder="0"
                            style={{ width:65, padding:'4px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11, textAlign:'center',
                              fontWeight:700,
                              color:parseFloat(op.performancePct)<80&&op.performancePct?C.danger:C.success }}/>
                        </td>
                        <td style={{ padding:'4px 5px' }}>
                          <input value={op.remarks||''} onChange={e=>updOp(i,'remarks',e.target.value)}
                            placeholder="Remarks..."
                            style={{ width:'100%', minWidth:90, padding:'4px', borderRadius:R.sm,
                              border:`1px solid ${C.border}`, fontSize:11 }}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Grid gap={14} min={180} style={{ marginTop:12 }}>
                <Fld label="Manager Quality"  value={F.managerQuality}  onChange={e=>set('managerQuality',e.target.value)}  placeholder="Name..."/>
                <Fld label="GM Production"    value={F.gmProduction}    onChange={e=>set('gmProduction',e.target.value)}    placeholder="Name..."/>
                <Fld label="GM Quality"       value={F.gmQuality}       onChange={e=>set('gmQuality',e.target.value)}       placeholder="Name..."/>
              </Grid>
              <SaveBar saving={saving} saved={saved} onSave={handleSave} color={C.proc}/>
            </Card>
          </>
        )}
      </div>
    );
  }


  // ════════════════════════════════════════════════════════════════════════
  //  DEPT SHELLS
  // ════════════════════════════════════════════════════════════════════════

  function makeDeptShell(subForms, dbKey, deptColor) {
    return function DeptShell({ db, setDb, filters }) {
      const [active, setActive] = React.useState(subForms[0].id);
      const counts = subForms.reduce((acc,f) => ({
        ...acc, [f.id]:(db[dbKey]||[]).filter(r=>r.formType===f.ft).length,
      }), {});
      const ActiveComp = subForms.find(f=>f.id===active)?.Comp;

      return (
        <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
          <div style={{ background:'#FFFFFF', borderBottom:`1px solid ${C.border}`,
            overflowX:'auto', flexShrink:0 }}>
            <div style={{ display:'flex', paddingTop:4, paddingLeft:8, minWidth:'max-content' }}>
              {subForms.map(f => (
                <button key={f.id} onClick={() => setActive(f.id)} style={{
                  padding:'10px 14px', fontSize:12,
                  fontWeight: active===f.id ? 700 : 400,
                  color: active===f.id ? deptColor : C.textSub,
                  background:'transparent', border:'none', cursor:'pointer',
                  borderBottom: active===f.id ? `2.5px solid ${deptColor}` : '2.5px solid transparent',
                  display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap',
                }}>
                  <span>{f.icon}</span>{f.label}
                  {counts[f.id] > 0 && (
                    <span style={{ background:deptColor+'22', color:deptColor,
                      padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>
                      {counts[f.id]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div style={{ flex:1, overflowY:'auto' }}>
            {ActiveComp && <ActiveComp db={db} setDb={setDb} filters={filters}/>}
          </div>
        </div>
      );
    };
  }

  const MEND_FORMS = [
    { id:'ann72', label:'Ann-72 Alteration Report',      icon:'🔧', Comp:Ann72, ft:'ALTERATION_REPORT' },
    { id:'ann73', label:'Ann-73 Spot/Mending Insp.',     icon:'🔍', Comp:Ann73, ft:'SPOTTING_INSP'     },
    { id:'ann74', label:'Ann-74 Daily Mending Report',   icon:'📋', Comp:Ann74, ft:'DAILY_MENDING'     },
  ];

  const KILL_FORMS = [
    { id:'ann75', label:'Ann-75 Killed GMT Tag',         icon:'🏷️', Comp:Ann75, ft:'KILLED_TAG'        },
    { id:'ann76', label:'Ann-76 Killed Records',         icon:'📊', Comp:Ann76, ft:'KILLED_RECORDS'    },
    { id:'ann77', label:'Ann-77 Killed Register',        icon:'📒', Comp:Ann77, ft:'KILLED_REGISTER'   },
    { id:'ann78', label:'Ann-78 Commercial Register',    icon:'💼', Comp:Ann78, ft:'COMMERCIAL_REGISTER'},
    { id:'ann79', label:'Ann-79 Management Record',      icon:'⚙️', Comp:Ann79, ft:'KILLED_MANAGEMENT' },
  ];

  const PROC_FORMS = [
    { id:'ann80', label:'Ann-80 Sewing Line Audit',      icon:'🪡', Comp:Ann80, ft:'SEWING_LINE_AUDIT'  },
    { id:'ann81', label:'Ann-81 Defect Control Chart',   icon:'📉', Comp:Ann81, ft:'EXCEPTIONAL_DEFECT' },
    { id:'ann82', label:'Ann-82 Process Control Register',  icon:'🎛️', Comp:Ann82, ft:'SEWING_PROCESS_CTRL' },
  ];

  // ── REGISTER ALL THREE ────────────────────────────────────────────────────
  DEPT_REGISTRY['mending'] = makeDeptShell(MEND_FORMS, 'mending', C.mend);
  DEPT_REGISTRY['killed']  = makeDeptShell(KILL_FORMS, 'killed',  C.killed);
  DEPT_REGISTRY['process'] = makeDeptShell(PROC_FORMS, 'process', C.proc);

  console.log('[QMS] Dept-08 Mending registered ✓   (Ann-72 → Ann-74, 3 forms)');
  console.log('[QMS] Dept-09 Killed GMT registered ✓ (Ann-75 → Ann-79, 5 forms)');
  console.log('[QMS] Dept-10 Process Control registered ✓ (Ann-80 → Ann-82, 3 forms)');
  console.log('[QMS] ══════════════════════════════════════════════════════');
  console.log('[QMS] ALL 10 DEPARTMENTS REGISTERED — 82 FORMS COMPLETE ✓');
  console.log('[QMS] ══════════════════════════════════════════════════════');

})();
